/**
 * 次世代 GoalView Web アプリ — Phase 1。
 *
 * この版でできること
 *   - 会場データ（競技地図の校正・ゴール地点）の読み込み
 *   - IGC フライトログの表示（高度で色分けした航跡）
 *   - ベース地図の切り替え（なし / PMTiles / 地理院）
 *   - 役割モードの切り替え（パイロット / クルー / オーディエンス）
 *
 * データの解析はすべて gv-core が行う。この層は**描くことに専念する**。
 */

import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import 'maplibre-gl/dist/maplibre-gl.css';

import { BASEMAPS, buildStyle, type BasemapId, type MapTheme } from './map/basemap.ts';
import {
  ALTITUDE_STOPS,
  AREA_SOURCE,
  DECLARED_SOURCE,
  DEFAULT_ALTITUDE_STEP_FT,
  DEFAULT_HAZARD_ROAD_LEVEL,
  DEFAULT_HEIGHT_SCALE,
  GOAL_3D_SOURCE,
  GOAL_BALL_3D_SOURCE,
  GOAL_SOURCE,
  HAZARD_SOURCE,
  LIVE_COLORS,
  LIVE_MARK_SOURCE,
  LIVE_POINT_3D_SOURCE,
  LIVE_POINT_BALL_3D_SOURCE,
  LIVE_POINT_SOURCE,
  LIVE_PREDICT_SOURCE,
  LIVE_TRACK_3D_SOURCE,
  LIVE_TRACK_SOURCE,
  NOTAM_REF_SOURCE,
  NOTAM_SOURCE,
  POINT_SOURCE,
  AIRSPACE_SOURCE,
  PZSA_POLY_SOURCE,
  PZSA_SOURCE,
  SKETCH_SOURCE,
  SECTION_SOURCE,
  SELF_SOURCE,
  TRACK_3D_SOURCE,
  TRACK_SOURCE,
  UTM_GRID_SOURCE,
  LRN3DT_PLAN_SOURCE,
  LRN3DT_PLAN_3D_SOURCE,
  GOAL_BEARING_SOURCE,
  MANUAL_POSITION_SOURCE,
  MEASURE_SOURCE,
  TASKBOARD_CURSOR_SOURCE,
  taskboardCursorToGeoJson,
  LRN3DT_POINT_SOURCE,
  addOverlayLayers,
  applyAltitudeColors,
  applyCompMapOpacity,
  applyCompMapVisibility,
  applyHeightScale,
  type LiveTrackColorMode,
  type LiveTrackGlowMode,
  DEFAULT_COMPMAP_OPACITY,
  setCompMapSource,
  type AltitudeMode,
  type CompMapLayerConfig,
  airspaceToGeoJson,
  areasToGeoJson,
  boundsOf,
  buildAltitudeStops,
  colorForAltitude,
  colorForAltitudeMode,
  colorForTerminal,
  declaredGoalBalls3dToGeoJson,
  declaredGoals3dToGeoJson,
  declaredToGeoJson,
  endpointsToGeoJson,
  geoCircleRing,
  goalsToGeoJson,
  hazardRoadLevelFilter,
  hexToRgba01,
  linesToWallPolygons,
  liveTrackRidgeLine,
  PLAN_3D_RIBBON_WIDTH_M,
  liveMarksToGeoJson,
  livePointArrow3dToGeoJson,
  livePointBall3dToGeoJson,
  livePointsToGeoJson,
  livePredictToGeoJson,
  liveTracks3dToGeoJson,
  simplifyFixesFor3d,
  normalizeThreeDDetail,
  THREE_D_DETAIL_PRESETS,
  threeDDetailPresetId,
  type ThreeDDetail,
  liveTracksToGeoJson,
  lrn3dtPickPointsToGeoJson,
  manualPositionToGeoJson,
  notamReferenceCirclesToGeoJson,
  notamToGeoJson,
  pzSaPolygonsToGeoJson,
  pzSaToGeoJson,
  selfToGeoJson,
  setSourceData,
  setLiveTrackColorMode,
  setLiveTrackGlow,
  setTerminalColorOverrides,
  setTerrainSource,
  TERRAIN_SOURCE,
  track3dToGeoJson,
  trackRidgeLine,
  trackToGeoJson,
  updatePzSaTubeMesh,
} from './map/layers.ts';
import type { RidgeLinePoint } from './map/ridge-line-layer.ts';
import { LiveConnection, type LiveSnapshot } from './live/relay.ts';
import { BridgeConnection } from './live/bridge.ts';
import { SelfPositionTransmitter, staleSinceMessage } from './live/transmit.ts';
import { getOwnPositionOnce, watchOwnPosition, type GeoFix } from './live/geolocation.ts';
import { terminalIdUse } from './live/terminal-id.ts';
import { setupBalloonFinder } from './finder/balloon-finder.ts';
import { applySnapshot, loadBackupMeta, loadLocalSnapshots, SettingsBackup, type SettingsSnapshot } from './settings-backup.ts';
import { openNavigation } from './nav-link.ts';
import { isFreshForAlert, selectAlerts } from './notam-alerts.ts';
import { ScreenWakeLockKeeper } from './live/wakelock.ts';
import { mapConfigOf, venueLinksOf } from './data/venues.ts';
import { deleteOfflineMap, downloadOfflineMap, offlineMapStatus } from './offline-map.ts';
import { utmGridGeoJson } from './map/utm-grid.ts';
import { archiveFileName, loadArchiveFlights } from './data/archive.ts';
import {
  parseTaskBoardFlight,
  resolveTaskBoardCoordinates,
  taskBoardGoalKeyPrefix,
  taskBoardRadiusM,
  type TaskBoardFlightData,
  type TaskBoardFlightResponse,
  type TaskBoardFlightsResponse,
} from './data/taskboard.ts';
import {
  isTerminalShown,
  loadFavorites,
  loadShowNonFavorites,
  resolveTerminalIdInput,
  saveFavorites,
  saveShowNonFavorites,
  setShowNonFavorites,
  setTerminalShown,
  sortFavoritesFirst,
  type TerminalVisibility,
} from './live/favorites.ts';
import { setupTaskboardPanel, type TaskBoardTargetMessage } from './taskboard-panel.ts';
import { setupPibalPanel } from './pibal-panel.ts';
import { OfficialPibalFeed } from './data/official-pibal-feed.ts';
import {
  EMPTY_MEASURE_STORE,
  measureShapesToGeoJson,
  measureSummary,
  parseMeasureStore,
  type MeasureShape,
  type MeasureStore,
  type MeasureTool,
} from './data/measure.ts';
import {
  parseTaskCircleImport,
  resolveTaskCircleCode,
  taskCirclesToAreas,
  TASK_CIRCLE_COLOR_PRESETS,
  type TaskCircleGoal,
} from './data/task-circles.ts';
import {
  clearSavedSketches,
  loadSavedSketches,
  loadSketchFromFile,
  saveSketches,
  sketchesToGeoJson,
  type SketchEntry,
} from './data/sketch.ts';
import { parseWindviewImport } from './data/windview-import.ts';
import {
  loadFlightFromFile,
  loadVenue,
  loadVenueList,
  type LoadedFlight,
  type NotamAreaData,
  type NotamRegime,
  type PzSaZone,
  type Venue,
  type VenueManifest,
} from './data/venue.ts';
import {
  buildIgc,
  checkNotamIncursion,
  DEFAULT_APT_INNER_BAND_FT,
  DEFAULT_APT_OUTER_BAND_FT,
  deriveMotion,
  effectiveCeilingFt,
  fixesToWindSamples,
  fromUtm,
  getMagneticDeclination,
  gridWindSamples,
  magneticToTrueHeading,
  parseLatLon,
  adviseLrn,
  planThreeDtRoute,
  resolveNotamRegime,
  scoreApt,
  scoreThreeD,
  toUtm,
  calibrateKFromReference,
  pibalGeometry,
  pibalQuality,
  sanitizeFixes,
  windSpeedFromTilt,
  type AirspaceVolume,
  type AltitudeProfilePoint,
  type AptTask,
  type CircleArea,
  type FixSample,
  type LrnCircleTask,
  type LrnAdvice,
  type LrnPlan,
  type LrnPoint,
  type LrnTimeTask,
  type LrnWindLayer,
  type MarkPoint,
  type NotamFix,
  type ThreeDtPlan,
  type ThreeDtRing,
  type WindSample,
} from '@gv4d/core';
import {
  detectCompassSupport,
  hasCompassPermission,
  requestCompassPermission,
  subscribeCompassHeading,
  type CompassReading,
} from './live/compass.ts';
import { subscribeTilt } from './live/tilt.ts';
import { loadDeclination, saveDeclination } from './declination-store.ts';
import { loadPibalSamples, loadPibalSettings, savePibalSamples, savePibalSettings } from './pibal-store.ts';
import { loadWindviewSamples, saveWindviewSamples } from './windview-store.ts';
import {
  bearingDeg,
  distanceMeters,
  eastingDigitsOf,
  findMarkByGridCode,
  goalFromMark,
  gridCodeDigitCount,
  gridCodeForDisplay,
  gridCodeOf,
  gridEastingDigits,
  goalBearingLineToGeoJson,
  loadGoals,
  makeGoal,
  officialGoalKey,
  resolveGridCode,
  venueReferencePoint,
  saveGoals,
  type DeclaredGoal,
} from './goals.ts';
import { summarize } from './ui/summary.ts';
import {
  buildBarbLegendSvg,
  buildDirectionArrowSvg,
  buildNsColorLegendSvg,
  buildWindProfileSvg,
  PAD as WIND_PROFILE_PAD,
  topAltitudeM,
  type WindProfileSeries,
} from './ui/wind-profile.ts';
import {
  buildAptChartSvg,
  formatAccumulatedSec,
  type AptChartTrack,
} from './ui/apt-chart.ts';
import {
  buildLrnAdviceHtml,
  buildLrnAdviceText,
  buildLrnPlanSummaryHtml,
  buildLrnPlanSummaryText,
  buildThreeDtPlanSummaryHtml,
  buildThreeDtPlanSummaryText,
} from './ui/lrn3dt-panel.ts';
import {
  buildCrossSectionSvg,
  projectToSection,
  sectionBearingLabel,
  sectionToGeoJson,
  type CrossSectionCeiling,
  type CrossSectionTrack,
  type CrossSectionWindSample,
  type SectionGeometry,
} from './ui/cross-section.ts';
import { MODES, type ModeId } from './ui/modes.ts';
import { feetToMeters, metersToFeet } from './units.ts';
import { DEFAULT_TIME_ZONE, TIME_ZONES, toZonedTime, type TimeZoneId } from './ui/timezone.ts';
import {
  BUILD_ID,
  checkForUpdate,
  formatBytes,
  isInstalled,
  requestPersistentStorage,
  resetAndReload,
  setupPwa,
  type StorageState,
} from './pwa.ts';

// PMTiles を maplibre のソースとして扱えるようにする
maplibregl.addProtocol('pmtiles', new Protocol().tile);

const el = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

/** 地図をタップしたときの意味（排他）。詳細は `state.mapTapMode` のコメント参照 */
type MapTapMode = 'normal' | 'pick-position' | 'measure' | 'pick-lrn3dt';

const VENUE_STORAGE_KEY = 'gv4d-venue';
/**
 * 最後に見ていた会場。
 *
 * ★ ゴール一覧は会場ごとに保存しているので、ここを憶えていないと
 *   アプリを開き直したとき既定の会場（一覧の先頭）に戻り、
 *   別会場で入れたゴールが「消えた」ように見える。
 */
function loadLastVenue(): string | null {
  try {
    return localStorage.getItem(VENUE_STORAGE_KEY);
  } catch {
    return null;
  }
}

const TZ_STORAGE_KEY = 'gv4d-timezone';
/** 世界中から選べる必要はない。実際に使う場所（日本／9月からポーランド）だけ憶えておく */
function loadTimeZone(): TimeZoneId {
  try {
    const v = localStorage.getItem(TZ_STORAGE_KEY);
    if (TIME_ZONES.some((z) => z.id === v)) return v as TimeZoneId;
  } catch {
    // localStorage が使えない環境でも既定値で動かす
  }
  return DEFAULT_TIME_ZONE;
}

const MAP_THEME_KEY = 'gv4d-map-theme';
/** PMTiles の配色（屋内・屋外）。光の条件で読みやすさが変わるので選べるようにしてある */
function loadMapTheme(): MapTheme {
  try {
    const v = localStorage.getItem(MAP_THEME_KEY);
    if (v === 'indoor' || v === 'outdoor') return v;
  } catch {
    // localStorage が使えない環境でも既定値で動かす
  }
  return 'indoor';
}

const NOTAM_MUTED_KEY = 'gv4d-notam-muted';
/** Blue PZ の天井超過の警告を止めた端末ID（クルーの車など、警告が要らない機体） */
function loadNotamMuted(): Set<string> {
  try {
    const raw = localStorage.getItem(NOTAM_MUTED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveNotamMuted(ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(NOTAM_MUTED_KEY, JSON.stringify([...ids]));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

const TERMINAL_COLORS_KEY = 'gv4d-terminal-colors';
/** 端末IDごとに手動で選んだ航跡の色。無ければ従来通りIDのハッシュから自動で決まる */
function loadTerminalColors(): Map<string, string> {
  try {
    const raw = localStorage.getItem(TERMINAL_COLORS_KEY);
    if (!raw) return new Map();
    return new Map(Object.entries(JSON.parse(raw) as Record<string, string>));
  } catch {
    return new Map();
  }
}
function saveTerminalColors(colors: Map<string, string>): void {
  try {
    localStorage.setItem(TERMINAL_COLORS_KEY, JSON.stringify(Object.fromEntries(colors)));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

const FOLLOW_ME_KEY = 'gv4d-follow-me';
const FOLLOW_TARGET_KEY = 'gv4d-follow-target';
/**
 * 地図の中心を追従させる対象。
 *
 * - `''`        追従しない
 * - `'me'`      自分の位置
 * - それ以外    そのライブ端末ID（気球）
 *
 * ★ 「自分」だけでなく気球も選べるのは、パイロットからの要望
 *   （「気球中心にして考えることの方が多い」2026-08-29）。クルーが
 *   自機の気球を追いかける使い方でも、自分のGPSに関係なく追える。
 */
type FollowTarget = string;

function loadFollowTarget(): FollowTarget {
  try {
    const saved = localStorage.getItem(FOLLOW_TARGET_KEY);
    if (saved !== null) return saved;
    // 追従先を選べるようにする前は「自分に追従するか」の真偽値だった。
    // 以前オンにしていた人がオフに戻ってしまわないよう引き継ぐ
    return localStorage.getItem(FOLLOW_ME_KEY) === '1' ? 'me' : '';
  } catch {
    return '';
  }
}

const NO_TRANSMIT_KEY = 'gv4d-my-no-transmit';
/**
 * GPSは取得して地図には表示するが、共有サーバーへは送信しないモードか。
 *
 * オブザーバーや、自分の位置を他チームへ送りたくない係員向け。
 * `watchOwnPosition()` 自体は送信の有無と無関係に動く作りなので、
 * ここは `SelfPositionTransmitter` の生成・送信だけを止める（setupTransmit参照）。
 */
function loadNoTransmit(): boolean {
  try {
    return localStorage.getItem(NO_TRANSMIT_KEY) === '1';
  } catch {
    return false;
  }
}

const UPLOAD_INTERVAL_SEC_KEY = 'gv4d-upload-interval-sec';
/**
 * GPSソースで自機位置を送信する間隔 [秒] の既定値。
 *
 * 表示（地図・読み取り窓）はGPSが届くたび即座に更新するが、送信は
 * 帯域・共有サーバーの負荷を抑えるためこの間隔まで間引く。以前は60秒
 * 固定だった（クルーが車で移動する想定の暫定値）が、気球自身が送る
 * 場合など状況によって最適な間隔が変わるため、画面から入力できるように
 * した（`#my-upload-interval`。利用者からの要望、2026-08-22）。
 */
const DEFAULT_UPLOAD_INTERVAL_SEC = 60;
function loadUploadIntervalSec(): number {
  try {
    const raw = localStorage.getItem(UPLOAD_INTERVAL_SEC_KEY);
    // ★ 未保存(null)を先に弾くこと。Number(null)は0であり、
    //   loadViewPitchで踏んだのと同じ罠になる
    if (raw == null) return DEFAULT_UPLOAD_INTERVAL_SEC;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 5 && v <= 600 ? v : DEFAULT_UPLOAD_INTERVAL_SEC;
  } catch {
    return DEFAULT_UPLOAD_INTERVAL_SEC;
  }
}

const HAZARD_ROAD_LEVEL_KEY = 'gv4d-hazard-road-level';
/**
 * 幹線道路の表示詳細度（1=trunkのみ 〜 4=tertiary/unclassifiedまで）。
 * データ自体は全レベル取得済みなので、ここはあくまで表示側のフィルター。
 */
function loadHazardRoadLevel(): number {
  try {
    const raw = Number(localStorage.getItem(HAZARD_ROAD_LEVEL_KEY));
    return raw >= 1 && raw <= 4 ? raw : DEFAULT_HAZARD_ROAD_LEVEL;
  } catch {
    return DEFAULT_HAZARD_ROAD_LEVEL;
  }
}

const HEIGHT_SCALE_KEY = 'gv4d-height-scale';
const HEIGHT_SCALE_MIN = 1;
const HEIGHT_SCALE_MAX = 30;
/** 3D表示の高さの誇張倍率。3D表示のON/OFF自体はセッション限りだが、この倍率だけは覚えておく */
function loadHeightScale(): number {
  try {
    const raw = Number(localStorage.getItem(HEIGHT_SCALE_KEY));
    return raw >= HEIGHT_SCALE_MIN && raw <= HEIGHT_SCALE_MAX ? raw : DEFAULT_HEIGHT_SCALE;
  } catch {
    return DEFAULT_HEIGHT_SCALE;
  }
}

const ALTITUDE_STEP_KEY = 'gv4d-altitude-step-ft';
const ALTITUDE_STEP_MIN = 100;
const ALTITUDE_STEP_MAX = 2000;
/**
 * 高度の色分けの刻み幅[ft]。会場の既定値（VenueMapConfig.altitudeStepFeet）
 * とは別に、利用者の好みとして永続化する（heightScaleと同じ考え方）。
 * 標高の高い会場では既定の500ft刻み（上限2500ft）だと低空でも色が
 * 頭打ちになるため、利用者が広げられるようにする（2026-09-13の指摘）
 */
function loadAltitudeStepFt(): number {
  try {
    const raw = Number(localStorage.getItem(ALTITUDE_STEP_KEY));
    return raw >= ALTITUDE_STEP_MIN && raw <= ALTITUDE_STEP_MAX ? raw : DEFAULT_ALTITUDE_STEP_FT;
  } catch {
    return DEFAULT_ALTITUDE_STEP_FT;
  }
}

const ALTITUDE_MODE_KEY = 'gv4d-altitude-mode';
/** 高度の色分けの基準（海抜/対地）。heightScaleと同じく好みとして永続化する */
function loadAltitudeMode(): AltitudeMode {
  const raw = (() => {
    try {
      return localStorage.getItem(ALTITUDE_MODE_KEY);
    } catch {
      return null;
    }
  })();
  return raw === 'agl' ? 'agl' : 'msl';
}

/**
 * QNH補正（利用者からの要望、2026-09-13）。
 *
 * 大会本部はタスクシートでQNH（現地の海面気圧）を指定し、競技者はそれを
 * 気圧高度計（IGCの記録計）に入力して補正する運用になっている。一方、
 * IGCのB レコードが持つ気圧高度は、多くの記録計で**標準気圧
 * （1013.25hPa）を基準に生センサー値から機械的に算出した値**であり、
 * 記録計自体にQNHを教えても（`.ATS`ヘッダに残ることはあっても）
 * Bレコードの値そのものには反映されないことが多い。そのため、
 * 表示側で標準気圧からのズレぶんを別途足し込む必要がある。
 *
 * ★ 補正量は「1hPaにつき約27ft（≒8.23m）」という、航空の現場で広く
 *   使われる近似（QNH設定変更時の指示高度変化の目安）を使う。海面付近
 *   でのおおよその値で、気圧高度そのものの厳密な気圧高度式とは別物だが、
 *   本アプリの他の数値（LRN/3DT等）と同じ「参考値」の位置付けとして
 *   十分な精度とする。
 */
const STANDARD_QNH_HPA = 1013.25;
const METERS_PER_HPA = 8.23;
/** QNHとして受け付ける範囲[hPa]。index.htmlのqnh-inputのmin/maxと揃えること */
const QNH_MIN_HPA = 900;
const QNH_MAX_HPA = 1100;

function qnhCorrectionM(qnhHpa: number | null): number {
  return qnhHpa == null ? 0 : (qnhHpa - STANDARD_QNH_HPA) * METERS_PER_HPA;
}

/**
 * QNHを変更する（設定・変更・クリアの共通口。手入力・IGCヘッダーからの
 * 自動設定のどちらもここを通す）。既に読み込み済みの全フライトの
 * altPressureに、旧設定との差分ぶんだけ足し引きする（新規に読み込む
 * フライトは生値なので、setupFileInput側で現在のQNHぶんを別途1回だけ
 * 足す。これにより「フライトごとにいくら補正済みか」を別途記録しなくても、
 * 常に「現在のstate.qnhHpaで補正した状態」を保てる）。
 *
 * ★ HTML側のmin/max（900〜1100）はフォーム送信を経由しない`onchange`では
 *   強制されないため、ここでも改めて検査する（コードレビュー指摘、
 *   2026-09-13）。範囲外・非数は既存の設定・データを一切変えずに拒否する
 *   （手入力の誤打鍵で高度が数千m単位で狂うのを防ぐ）。
 */
function setQnh(newQnh: number | null): void {
  if (newQnh != null && (!Number.isFinite(newQnh) || newQnh < QNH_MIN_HPA || newQnh > QNH_MAX_HPA)) {
    setStatus(`QNHは${QNH_MIN_HPA}〜${QNH_MAX_HPA}hPaの範囲で入力してください`, 'warn');
    el<HTMLInputElement>('qnh-input').value = state.qnhHpa == null ? '' : String(state.qnhHpa);
    return;
  }
  const diff = qnhCorrectionM(newQnh) - qnhCorrectionM(state.qnhHpa);
  if (diff !== 0) {
    for (const f of state.flights) {
      for (const fix of f.fixes) {
        if (fix.altPressure != null) fix.altPressure += diff;
      }
    }
    // 稜線は各fixのaltPressureをCPU側の頂点バッファへ焼き込んでいる。
    // updateRidgeLines()の通常の署名だけでは配列内の値の変更を検出できないため、
    // QNH補正後は明示的に保存済み航跡のキャッシュを無効化する。
    invalidateTrackRidgeLines();
  }
  state.qnhHpa = newQnh;
  el<HTMLInputElement>('qnh-input').value = newQnh == null ? '' : String(newQnh);
  redraw();
}

const COMPMAP_OPACITY_KEY = 'gv4d-compmap-opacity';
/** 競技地図オーバーレイの不透明度。表示ON/OFF自体はセッション限りだが、heightScaleと同じくこの好みだけは覚えておく */
function loadCompMapOpacity(): number {
  try {
    const raw = localStorage.getItem(COMPMAP_OPACITY_KEY);
    // ★ loadViewPitchと同じ罠: 0は正当な値（完全に透明）なので、
    //   未保存(null)を先に弾かないと Number(null)===0 と取り違える
    if (raw == null) return DEFAULT_COMPMAP_OPACITY;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : DEFAULT_COMPMAP_OPACITY;
  } catch {
    return DEFAULT_COMPMAP_OPACITY;
  }
}

/**
 * カメラを寝かせられる上限 [deg]。
 *
 * ★ MapLibre の既定は 60 で、このアプリは `maxPitch` を指定していなかった。
 *   「3Dはpitchに上限があるから真横から見られない」と長く書いてきたが、
 *   それは**上限を上げていなかっただけ**だった（v5.24 の setMaxPitch は
 *   0〜180 を受け付ける）。
 *
 * ★ なぜ 90 ではなく 89 か（実測、2026-08-21）
 *   60→90 を1度刻みで測ったところ、**pitch 90 ちょうどで縮尺表示（ScaleControl）
 *   が `NaN m` になる**。カメラが完全に水平になると地面が真横から見た線に潰れ、
 *   画面中央での水平距離が求まらなくなるため。89 以下では正常（`5 km` 等）。
 *   航跡の壁・柱・球は 90 でも描画自体は壊れなかったが、89 と 90 の見た目の
 *   差は実質無いので、壊れない側の 89 を採る。
 *   MapLibre 自身も「60度超は experimental」と注記している領域なので、
 *   他に破綻が見つかったらこの値を下げる（下げるのはここ1箇所でよい）。
 */
const MAX_PITCH = 89;

const VIEW_PITCH_KEY = 'gv4d-view-pitch';
/**
 * 3D表示にしたときのカメラの傾き [deg]。
 *
 * heightScale と同じ考え方で、3D表示のON/OFF自体はセッション限りだが
 * 「どれくらい寝かせて見たいか」の好みだけは覚えておく
 * （地図右上の「3D」ボタン・チェックボックスどちらから入っても、
 * ここで覚えた角度に開き、「2D」で戻すと元の角度に戻る。
 * setupInteractions() の setShow3d() 参照）。
 *
 * ★ 既定は45（利用者からの指定）。以前は60（過去にハードコードされていた
 * 値をそのまま既定にしていた）だったが、45の方が水平と真上のバランスが
 * よく見えるとのこと。
 */
const DEFAULT_VIEW_PITCH = 45;
function loadViewPitch(): number {
  try {
    const raw = localStorage.getItem(VIEW_PITCH_KEY);
    // ★ 未保存(null)を先に弾くこと。`Number(null)` は 0 であり、この設定は
    //   0（真上から）が正当な値なので、loadHeightScale と同じ書き方をすると
    //   「未保存」と「0を保存済み」を取り違えて既定値が効かなくなる
    //   （あちらは下限が1なので偶然この罠を免れている）。実際に踏んだ。
    if (raw == null) return DEFAULT_VIEW_PITCH;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= MAX_PITCH ? v : DEFAULT_VIEW_PITCH;
  } catch {
    return DEFAULT_VIEW_PITCH;
  }
}

const SHOW_3D_SMOOTH_KEY = 'gv4d-show-3d-smooth';
/**
 * 3D航跡の壁を「滑らか」に作るか（既定は従来どおりの階段状）。
 * 3D表示のON/OFF自体はセッション限りだが、この見た目の好みだけは覚えておく
 * （heightScaleと同じ考え方）。気に入っている利用者もいるため既定はfalse。
 */
function loadShow3dSmooth(): boolean {
  try {
    return localStorage.getItem(SHOW_3D_SMOOTH_KEY) === '1';
  } catch {
    return false;
  }
}

const LIVE_TRACK_COLOR_KEY = 'gv4d-live-track-color';
/**
 * ライブ航跡の色の付け方（docs/08 §218）。'altitude'＝高度の色分け（既定）、'terminal'＝機体ごとの単色
 * （アイコンと同じ色）。航跡が重なって自分のチームの線が見分けにくくなったときに切り替える。
 * 見た目の好みなので覚えておく
 */
function loadLiveTrackColorMode(): LiveTrackColorMode {
  try {
    return localStorage.getItem(LIVE_TRACK_COLOR_KEY) === 'terminal' ? 'terminal' : 'altitude';
  } catch {
    return 'altitude';
  }
}

const LIVE_TRACK_GLOW_KEY = 'gv4d-live-track-glow';
/** ライブ航跡の光彩（白い縁取り、docs/08 §232）。既定は★のお気に入りだけ */
function loadLiveTrackGlow(): LiveTrackGlowMode {
  try {
    const v = localStorage.getItem(LIVE_TRACK_GLOW_KEY);
    return v === 'off' || v === 'all' ? v : 'favorites';
  } catch {
    return 'favorites';
  }
}

const THREE_D_DETAIL_KEY = 'gv4d-3d-detail';
/**
 * 3Dの壁・稜線の細かさ（間引きの刻み、docs/08 §197）。マシンの性能で
 * 許容できる細かさが違うので、端末ごとに好みとして保存する
 * （PCでは細かく、スマホでは荒く）。既定は 25m / 5m
 */
function loadThreeDDetail(): ThreeDDetail {
  try {
    const raw = localStorage.getItem(THREE_D_DETAIL_KEY);
    return normalizeThreeDDetail(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeThreeDDetail(null);
  }
}

const APT_KEY = 'gv4d-apt-task';
/**
 * APT（高度プロファイルタスク）のタスクデータ。
 * 毎フライト変わるが、フライト中のリロードで消えると入れ直しになるので保存する。
 * 帯幅の既定は規定 15.21 の例示値（内側50ft / 外側150ft）。
 */
function loadApt(): {
  profile: AltitudeProfilePoint[];
  innerBandFt: number;
  outerBandFt: number;
  startMs: number | null;
} {
  const fallback = {
    profile: [] as AltitudeProfilePoint[],
    innerBandFt: DEFAULT_APT_INNER_BAND_FT,
    outerBandFt: DEFAULT_APT_OUTER_BAND_FT,
    startMs: null as number | null,
  };
  try {
    const raw = localStorage.getItem(APT_KEY);
    if (!raw) return fallback;
    const v = JSON.parse(raw) as Partial<typeof fallback>;
    return {
      profile: Array.isArray(v.profile)
        ? v.profile.filter(
            (p): p is AltitudeProfilePoint =>
              typeof p?.tSec === 'number' && typeof p?.altFt === 'number',
          )
        : [],
      innerBandFt: Number.isFinite(v.innerBandFt) ? Number(v.innerBandFt) : fallback.innerBandFt,
      outerBandFt: Number.isFinite(v.outerBandFt) ? Number(v.outerBandFt) : fallback.outerBandFt,
      startMs: typeof v.startMs === 'number' ? v.startMs : null,
    };
  } catch {
    return fallback;
  }
}

const AIRSPACE_KEY = 'gv4d-airspaces';
/** 3DT の設定空域。APT と同じ理由で保存する */
function loadAirspaces(): AirspaceVolume[] {
  try {
    const raw = localStorage.getItem(AIRSPACE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.filter(
      (a): a is AirspaceVolume =>
        typeof a?.label === 'string' && Array.isArray(a?.points) && a.points.length >= 3,
    );
  } catch {
    return [];
  }
}

const MEASURE_KEY = 'gv4d-measure-shapes';

/**
 * 計測モードの図形。APT・3DT空域・タスクサークルと同じ理由で保存する。
 * 読み直しの検証は `parseMeasureStore`（data/measure.ts）に寄せてあり、
 * 1件の不備で全体を捨てない。
 */
function loadMeasureStore(): MeasureStore {
  try {
    const raw = localStorage.getItem(MEASURE_KEY);
    if (!raw) return { ...EMPTY_MEASURE_STORE };
    return parseMeasureStore(JSON.parse(raw));
  } catch {
    return { ...EMPTY_MEASURE_STORE };
  }
}

function saveMeasureStore(): void {
  try {
    localStorage.setItem(MEASURE_KEY, JSON.stringify(state.measure));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

const TASK_CIRCLE_KEY = 'gv4d-task-circles';

/**
 * TaskView（パイロット側ソフト）の課題表を手入力したもの。
 * APT/3DTと同じ理由（毎フライト変わるが、フライト中のリロードで消えると困る）で保存する。
 */
function loadTaskCircleGoals(): TaskCircleGoal[] {
  try {
    const raw = localStorage.getItem(TASK_CIRCLE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.filter(
      (g): g is TaskCircleGoal =>
        typeof g?.name === 'string' &&
        Number.isFinite(g?.lat) &&
        Number.isFinite(g?.lon) &&
        typeof g?.color === 'string' &&
        Number.isFinite(g?.rMinKm) &&
        Number.isFinite(g?.rMaxKm),
    );
  } catch {
    return [];
  }
}

const LRN3DT_KEY = 'gv4d-lrn3dt-task';

/** LRN/3DT戦略プランナーの入力フォームの値。計算結果自体は保存しない（毎回再計算する）。 */
interface Lrn3dtFormState {
  mode: 'lrn-time' | 'lrn-circle' | '3dt';
  /** 風プロファイルの入力元。'tracks'=見えている全機・読み込んだ航跡、'windview'=WindView貼り付け */
  windSource: 'tracks' | 'windview';
  /** WindView貼り付け時のAGL→MSL変換に使う地上標高 [m]（自動取得源が無いため手入力） */
  groundElevationM: number;
  limitSec: number;
  altMinFt: number;
  altMaxFt: number;
  climbMps: number;
  descentMps: number;
  startCode: string;
  circleCenterCode: string;
  circleRadiusKm: number;
  ringCenterCode: string;
  ringRMinKm: number;
  ringRMaxKm: number;
  entryCode: string;
  entryAltFt: number;
}

/** プランナーの入力欄のうち、地図の位置を指定するもの（タップ・ドラッグで置ける） */
type Lrn3dtPointField = 'start' | 'circleCenter' | 'ringCenter' | 'entry';

const DEFAULT_LRN3DT_FORM: Lrn3dtFormState = {
  mode: 'lrn-time',
  windSource: 'tracks',
  groundElevationM: 0,
  limitSec: 3600,
  altMinFt: 0,
  altMaxFt: 5000,
  climbMps: 3,
  descentMps: 3,
  startCode: '',
  circleCenterCode: '',
  circleRadiusKm: 1,
  ringCenterCode: '',
  ringRMinKm: 0,
  ringRMaxKm: 1,
  entryCode: '',
  entryAltFt: 1000,
};

/**
 * APT/3DT設定空域と同じ理由（毎フライト変わるが、フライト中のリロードで
 * 消えると入れ直しになる）でフォーム値だけ保存する。
 */
function loadLrn3dtForm(): Lrn3dtFormState {
  try {
    const raw = localStorage.getItem(LRN3DT_KEY);
    if (!raw) return { ...DEFAULT_LRN3DT_FORM };
    const v = JSON.parse(raw) as Partial<Lrn3dtFormState>;
    const mode: Lrn3dtFormState['mode'] =
      v.mode === 'lrn-time' || v.mode === 'lrn-circle' || v.mode === '3dt' ? v.mode : DEFAULT_LRN3DT_FORM.mode;
    const windSource: Lrn3dtFormState['windSource'] =
      v.windSource === 'tracks' || v.windSource === 'windview' ? v.windSource : DEFAULT_LRN3DT_FORM.windSource;
    const num = (x: unknown, fallback: number): number => (Number.isFinite(x) ? Number(x) : fallback);
    const str = (x: unknown, fallback: string): string => (typeof x === 'string' ? x : fallback);
    return {
      mode,
      windSource,
      groundElevationM: num(v.groundElevationM, DEFAULT_LRN3DT_FORM.groundElevationM),
      limitSec: num(v.limitSec, DEFAULT_LRN3DT_FORM.limitSec),
      altMinFt: num(v.altMinFt, DEFAULT_LRN3DT_FORM.altMinFt),
      altMaxFt: num(v.altMaxFt, DEFAULT_LRN3DT_FORM.altMaxFt),
      climbMps: num(v.climbMps, DEFAULT_LRN3DT_FORM.climbMps),
      descentMps: num(v.descentMps, DEFAULT_LRN3DT_FORM.descentMps),
      startCode: str(v.startCode, DEFAULT_LRN3DT_FORM.startCode),
      circleCenterCode: str(v.circleCenterCode, DEFAULT_LRN3DT_FORM.circleCenterCode),
      circleRadiusKm: num(v.circleRadiusKm, DEFAULT_LRN3DT_FORM.circleRadiusKm),
      ringCenterCode: str(v.ringCenterCode, DEFAULT_LRN3DT_FORM.ringCenterCode),
      ringRMinKm: num(v.ringRMinKm, DEFAULT_LRN3DT_FORM.ringRMinKm),
      ringRMaxKm: num(v.ringRMaxKm, DEFAULT_LRN3DT_FORM.ringRMaxKm),
      entryCode: str(v.entryCode, DEFAULT_LRN3DT_FORM.entryCode),
      entryAltFt: num(v.entryAltFt, DEFAULT_LRN3DT_FORM.entryAltFt),
    };
  } catch {
    return { ...DEFAULT_LRN3DT_FORM };
  }
}

function saveLrn3dtForm(): void {
  try {
    localStorage.setItem(LRN3DT_KEY, JSON.stringify(state.lrn3dtForm));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

/** LRN/3DTプランナーの計算結果。地図描画に必要な参照情報（UTMゾーン・3DTリングの緯度経度）も一緒に持つ */
type Lrn3dtResult =
  | {
      kind: 'lrn';
      plan: LrnPlan;
      /**
       * 層選定の助言（採用しなかった候補と、行動の時系列）。
       * `plan` は `advice.best` と同じものを指す（`adviseLrn` が
       * 既存プランナーと同じ探索を共有しているため）。
       */
      advice: LrnAdvice;
      refZone: number;
      /**
       * 周回開始モードで使った円（時間切りモードには無い）。
       * 「計算に使った中心点と円も地図に出してほしい」という実機からの
       * 要望で持たせた。何を前提に出した答えなのかを地図上で確かめられる。
       */
      circle?: { lat: number; lon: number; radiusKm: number };
      /** 時間切りモードで使った開始点（周回開始モードには無い）。結果の共有テキストに要る */
      start?: { lat: number; lon: number };
    }
  | {
      kind: '3dt';
      plan: ThreeDtPlan;
      /** plan.path の e/n を経緯度へ戻すのに要る（LRN側と同じ） */
      refZone: number;
      ring: { lat: number; lon: number; rMinKm: number; rMaxKm: number };
      entry: { lat: number; lon: number };
    };

const UTM_WIDE_EASTING_KEY = 'gv4d-utm-wide-easting';
/**
 * グリッド表記の東距を5桁にするか。
 * 大会によっては UTM ゾーンの境目付近の会場で、区画の曖昧さを減らすため
 * この拡張表記を使う（GV4D独自、原典GoalViewには無い）。
 */
function loadUtmWideEasting(): boolean {
  try {
    return localStorage.getItem(UTM_WIDE_EASTING_KEY) === '1';
  } catch {
    return false;
  }
}

const WIND_RANGE_KEY = 'gv4d-wind-range-min';
/**
 * ウィンドプロファイラで見る時間の範囲 [分]。0 は「全体」。
 * 飛行中は直近が知りたく、飛行後は全体を眺めたい、と使い分けるので憶えておく。
 */
function loadWindRangeMin(): number {
  try {
    const raw = localStorage.getItem(WIND_RANGE_KEY);
    // ★ 未設定(null)を Number() に通すと 0 になり、0 は「全体」の番兵値と
    //   区別が付かない。既定の60分ではなく全体表示で開いてしまうので、
    //   null は必ず先に弾く
    if (raw === null) return 60;
    const n = Number(raw);
    return [0, 30, 60, 180].includes(n) ? n : 60;
  } catch {
    return 60;
  }
}

const UTM_WIDE_NORTHING_KEY = 'gv4d-utm-wide-northing';
/** グリッド表記の北距を5桁にするか。東距側と独立に切り替えられる */
function loadUtmWideNorthing(): boolean {
  try {
    return localStorage.getItem(UTM_WIDE_NORTHING_KEY) === '1';
  } catch {
    return false;
  }
}

const MY_TERMINAL_ID_KEY = 'gv4d-my-terminal-id';
/**
 * 自分が最後に使った端末ID。
 * 送信を止めても、ブリッジ側は「最後に受信した位置」としてしばらく
 * その端末IDを覚え続ける（reset/タイムアウトまで消えない）。切断後の
 * 再接続を「他人との衝突」と誤検知しないよう、自分のIDだけは除外する。
 */
function loadMyTerminalId(): string {
  try {
    return localStorage.getItem(MY_TERMINAL_ID_KEY) ?? '';
  } catch {
    return '';
  }
}
function saveMyTerminalId(id: string): void {
  try {
    localStorage.setItem(MY_TERMINAL_ID_KEY, id);
  } catch {
    // 保存できなくても今回のセッション中は動く
  }
}

const state = {
  venues: [] as VenueManifest[],
  venue: null as Venue | null,
  flights: [] as LoadedFlight[],
  // 過去ログ（アーカイブ）が持っていたマーク点・競技エリア円。
  // ライブ受信ぶん（liveMarks()・circles変数）とは別枠で溜め、地図には
  // 両方まとめて出す（過去ログを読み込むたびに追加、flightsと同じ流儀）
  archiveMarks: [] as MarkPoint[],
  archiveCircles: [] as CircleArea[],
  activeFlight: 0,
  // 表示を消したフライトの index。ここに入っていなければ表示する
  hiddenFlights: new Set<number>(),
  // タスクシート（TaskBoard）でタップしたターゲットのカーソル（docs/08 §194）。その場限り
  taskboardCursor: null as { lat: number; lon: number; label: string; color: string; radiusM: number | null } | null,
  // ライブ端末の表示・非表示。お気に入り（自チームの気球・チェイスカー等）と
  // 「お気に入り以外を表示するか」は保存し、一覧のチェックでの個別の切り替えは
  // その場限り。判定は terminalShown()（live/favorites.ts）
  terminalVisibility: {
    favorites: loadFavorites(),
    showNonFavorites: loadShowNonFavorites(),
    hidden: new Set<string>(),
    revealed: new Set<string>(),
  } as TerminalVisibility,
  // デバッグ用のダミー航跡（実際の受信データではない）。terminalId をキーにする
  debugTerminals: new Map<string, { terminalId: string; fixes: FixSample[]; latest: FixSample }>(),
  basemap: 'none' as BasemapId,
  mapTheme: loadMapTheme(),
  // 3D表示で地形（起伏）を使うか。既定ON（basemapと同じく永続化しない。
  // 会場ぶんの地形データが無い・オフラインで未ダウンロードの場合は、
  // ONにしていても地形無し＝今まで通り平坦なままフォールバックする。
  // オンラインならMapterhornの惑星全体データをそのまま使うので、
  // ほとんどの状況で既定ONのまま問題なく動く）
  terrainEnabled: true,
  // 高度の色分け・凡例の刻み幅。会場ごとに高度制限が違うため、会場切替時に差し替える
  altitudeStops: ALTITUDE_STOPS,
  // 刻み幅[ft]・基準（海抜/対地）は利用者の好みとして永続化する
  // （heightScaleと同じ考え方）。会場切替時、まだ一度も変えていなければ
  // 会場の既定値で上書きする（loadAltitudeStepFt/loadAltitudeMode参照）
  altitudeStepFt: loadAltitudeStepFt(),
  altitudeMode: loadAltitudeMode(),
  /**
   * 対地高度（AGL）の基準標高[ft]。nullなら未設定（表示ダイアログで
   * 海抜/対地を切り替えたときに、地形データから自動で候補値を入れる）。
   * QNHと同じく当日・会場ごとに変わる値なので永続化しない
   */
  groundRefFt: null as number | null,
  /**
   * QNH[hPa]。気圧高度計（IGCの記録計）を使うフライトの高度補正に使う。
   * 標準気圧(1013.25hPa)からのズレぶんを`altPressure`に足し込む
   * （main.tsのsetQnh参照）。天候で毎日変わる値なので永続化しない
   */
  qnhHpa: null as number | null,
  mode: 'crew' as ModeId,
  showGoals: true,
  // 現在地→選択中ゴールの点線＋方位/距離モード。他の表示チェックボックスと
  // 同じくセッション限り（開き直すたびOFFに戻る）
  showGoalBearingLine: false,
  showGrid: true,
  // PZ（禁止・要注意区域）/ SA（要注意エリア）。安全に関わる情報なので既定は表示
  showPzSa: true,
  // NOTAM高度制限区域（BluePZ）。以前は安全情報として既定表示していたが、
  // 見たいときだけ自分でチェックを入れる形にしてほしいとの要望を受けて
  // 既定OFFに変更（利用者からの要望、2026-08-22）
  showNotam: false,
  showNotam3d: false,
  /** 会場のnotam配列と対応する、体制の手動上書き（indexが一致） */
  notamOverrides: [] as Array<{ regimeId: string | null; treatAsHoliday: boolean }>,
  /** 有効化された条件付き高度引き上げ（NotamAltitudeOverride.id）の集合。idはグローバルにユニークなのでエリア横断で1つで足りる */
  notamActiveOverrideIds: new Set<string>(),
  /** 現在天井超過中の機体。超過解消で自動的にエントリが消える（=バナーも消える） */
  notamViolations: new Map<string, { key: string; terminalId: string | null; label: string; areaTitle: string; zoneLabel: string; marginFt: number }>(),
  /** 利用者が「消す」にした警告の鍵。超過が解消したら外れる（docs/08 §228） */
  notamDismissed: new Set<string>(),
  /** 利用者が「この機体は止める」にした端末ID。端末に保存される */
  notamMutedTerminals: loadNotamMuted(),
  // 拡大鏡（カーソル位置を常に高倍率で見せる）。既定でON、メニューで切れる
  showMagnifier: true,
  // 送電線・鉄道・道路。送電線は安全に関わるのでPZ/SAと同じく既定は表示
  showHazards: true,
  // 幹線道路の表示詳細度（1=trunkのみ〜3=secondaryまで）。データは全レベル
  // 取得済みなので、ここを変えるだけでOverpassへの再問い合わせ無しに切り替わる
  hazardRoadLevel: loadHazardRoadLevel(),
  // 3D表示（航跡を高度に応じて空中にプロット）。他の表示チェックボックスと
  // 同じくセッション限りで、開き直すたびOFFに戻る
  show3d: false,
  // ライブ航跡の色の付け方（高度の色分け／機体ごとの単色）。地図右のボタンで切り替え、覚えておく
  liveTrackColorMode: loadLiveTrackColorMode(),
  liveTrackGlow: loadLiveTrackGlow(),
  // 高さの誇張倍率だけは覚えておく（3D ON/OFF自体とは別に永続化）
  heightScale: loadHeightScale(),
  // 3D航跡の壁を階段状ではなく滑らかに作るか。これも見た目の好みなので
  // heightScaleと同じく永続化する（3D ON/OFF自体とは独立）
  show3dSmooth: loadShow3dSmooth(),
  // 3Dの壁・稜線の細かさ（間引きの刻み）。端末ごとの好みとして永続化する
  threeDDetail: loadThreeDDetail(),
  // 3D表示ON時、既に平面(pitch<5°)だった場合だけ退避する。OFFに戻すときの復元用。
  // 既にユーザーが自分で傾けていた場合はnullのままにし、OFFにしても勝手に戻さない
  pitchBeforeShow3d: null as number | null,
  // 3D表示にしたときのカメラの傾き。heightScaleと同じく好みとして永続化する
  viewPitch: loadViewPitch(),
  // 競技地図（原典GoalViewの公式地図画像）オーバーレイ。セッション限りで、
  // 開き直すたび既定に戻る。既定は利用者の要望でオン（docs/08 §187。
  // 当初はdocs/04 §4のとおり既定オフ）。index.htmlのcheckedと揃えること
  showCompMap: true,
  // 不透明度だけはheightScaleと同じく永続化する
  compMapOpacity: loadCompMapOpacity(),
  /**
   * 鉛直断面ビューの断面の定義。中心は「開いた時点の地図の中心」で、
   * 以後は地図を動かしても追従しない（見比べている最中に断面が動くと
   * 高さの比較にならないため）。取り直しは明示的なボタンで行う。
   * centerがnullの間はまだ一度も開かれていない。
   */
  crossSection: {
    center: null as { lat: number; lon: number } | null,
    bearingDeg: 0,
    depthM: 1000,
    halfLengthM: 5000,
    /** 断面に風を重ねるか。矢印が増えて見づらい場面もあるので既定はOFF */
    showWind: false,
  },
  /**
   * APT（高度プロファイルタスク、AXMER 15.21）のタスクデータ。
   * ブリーフィングで配られる値なので毎フライト変わる。会場ファイルではなく
   * 画面から手入力し、リロードで消えないようlocalStorageに置く。
   */
  apt: loadApt(),
  /**
   * 3DT（3Dシェイプタスク、15.20）の設定空域。こちらも毎フライト変わるので
   * 手入力＋localStorage。会場に複数の空域があり得るため配列にしておく
   * （入力UIは現状1件ぶんだが、採点・描画側は最初から複数を扱える）。
   */
  airspaces: loadAirspaces(),
  /**
   * TaskView（パイロット側ソフト）の課題表を手入力したもの。写真でしか
   * 届かないため（docs/08参照）、地図にスコアリング円として重ねて表示する
   */
  taskCircleGoals: loadTaskCircleGoals(),
  /**
   * LRN/3DT戦略プランナー（gv-core の lrn.ts / three-dt-strategy.ts）の
   * 入力フォームと計算結果。フォーム値だけ保存し、結果はダイアログを
   * 開くたびの「計算」で作り直す（毎回入力が変わりうるため）。
   */
  lrn3dtForm: loadLrn3dtForm(),
  lrn3dtResult: null as Lrn3dtResult | null,
  /**
   * プランナーの入力点（開始点・円の中心・リング中心・進入点）を地図に置いた
   * 実際の座標。`lrn3dtForm`側のコード文字列（保存・計算に使う）とは別に持ち、
   * タップ・ドラッグ・手入力のどれで決めても地図側の点はここから描く
   */
  lrn3dtPoints: {} as Partial<Record<Lrn3dtPointField, { lat: number; lon: number }>>,
  /** 「地図で選ぶ」でタップ待ちにしているのはどの点か（nullなら待っていない） */
  lrn3dtPickField: null as Lrn3dtPointField | null,
  /** 端末IDごとに手動で選んだ航跡の色。「端末ごとの色を設定」画面で編集する */
  terminalColors: loadTerminalColors(),
  /** 会場ごとに1回だけ組み立てる（1km方眼は静的なので、redraw() のたびに計算し直さない） */
  gridGeoJson: null as GeoJSON.FeatureCollection | null,
  live: null as LiveConnection | null,
  liveSnapshot: null as LiveSnapshot | null,
  bridge: null as BridgeConnection | null,
  // 自機位置の送信（手動座標入力モードで使う、最後に指定した座標）
  manualCoords: null as { lat: number; lon: number } | null,
  /**
   * 地図をタップしたときの意味。`normal`以外の間は、ゴール・航跡・PZ/SA等の
   * 既存のタップ（ポップアップ）を止めて、そのモードの操作だけを通す。
   * - `pick-position` … 次の1タップで自機位置の手動座標を決める（1回で解除）
   * - `measure` … 計測モード。タップのたびに図形の点が増える
   * - `pick-lrn3dt` … 次の1タップでプランナーの入力点（`lrn3dtPickField`が
   *   示す1つ）を決める（1回で解除）
   *
   * ★ 以前は `manualPositionPicking` という真偽値だったが、それだと
   *   レイヤー限定のクリックハンドラが止まらず、位置指定中にゴールを
   *   タップすると「位置が決まる」と「ゴールのポップアップが開く」が
   *   同時に起きていた（既存の不具合）。排他をここ1箇所で表す形に直した。
   */
  mapTapMode: 'normal' as MapTapMode,
  /**
   * 計測モードで描いた図形。「消す」まで残り、`visible`で表示だけ切り替える。
   * APT・3DT空域・タスクサークルと同じ理由（毎フライト変わるが、飛行中の
   * リロードで消えると描き直しになる）でlocalStorageに保存する。
   */
  measure: loadMeasureStore(),
  /** 選んでいる計測の道具 */
  measureTool: 'line' as MeasureTool,
  /** いま描き足している図形のid。nullなら次のタップで新しい図形を始める */
  measureDraftId: null as string | null,
  /**
   * インポートしたスケッチ（TV/TaskviewのXDD等、data/sketch.ts参照）。
   * 大会中に日・タスクごと何度も読み込むため、置き換えず一覧として溜め、
   * まとめて消せるようにしてある（利用者からの指摘、2026-09-13）。
   * ゴールと同じく会場ごとに保存する（selectVenue()で読み直す）ので、
   * ここでの初期値は空配列（起動直後の会場に切り替わる際に読み込まれる）
   */
  sketches: [] as SketchEntry[],
  /** 消さずに一時的に隠せるようにする（計測の図形と同じ理由） */
  sketchVisible: true,
  transmitter: null as SelfPositionTransmitter | null,
  /** 自機のGPS位置。送信の間引き（1分）とは独立に、取れた瞬間から即座に更新する */
  myFix: null as GeoFix | null,
  /** 自機の航跡ログ（保存用）。送信中かどうかに関わらず、GPSを見ている間は貯める */
  myTrackFixes: [] as GeoFix[],
  /** 地図の中心を追従させる対象（''=しない / 'me'=自分 / それ以外=ライブ端末ID） */
  followTarget: loadFollowTarget() as FollowTarget,
  /**
   * 直近で追従のために中心へ寄せた位置。同じ位置で何度も easeTo を呼ぶと
   * 地図が固まって操作できなくなるため、実際に動いたときだけ寄せる
   * （redraw() は位置と無関係な理由でも頻繁に呼ばれる）
   */
  followLastCenter: null as { lat: number; lon: number } | null,
  /** GPSは取得・表示するが、共有サーバーへは送信しないモードか */
  noTransmit: loadNoTransmit(),
  /** GPSソースで自機位置を送信する間隔 [秒]。手入力ソースは対象外（原典互換の2.5秒固定） */
  uploadIntervalSec: loadUploadIntervalSec(),
  /**
   * 追従中でも、この時刻までは自動での中心移動を止める（ミリ秒epoch）。
   * 手で地図を動かした直後に自動で引き戻されると操作にならないため、
   * 動かした瞬間から一定時間だけ追従を休ませる。
   */
  followMePausedUntil: 0,
  /** グリッド表記の東距・北距を5桁にするか（4/4・5/4・4/5・5/5を独立に切り替え） */
  utmWideEasting: loadUtmWideEasting(),
  utmWideNorthing: loadUtmWideNorthing(),
  /** ウィンドプロファイラで見る時間の範囲 [分]（0=全体）。範囲だけ永続化する */
  windRangeMin: loadWindRangeMin(),
  /**
   * ウィンドプロファイラの拡大・パン状態。null なら全体表示（拡大なし）。
   * from/to[ms]・altMin/altMax[m] は「実際に画面に見えている」絶対範囲。
   * セッション限り（保存しない）。シートを開き直す・範囲ドロップダウンを
   * 変えるたびにリセットする。
   */
  windProfileZoom: null as { from: number; to: number; altMin: number; altMax: number } | null,
  /**
   * 新しい版が待機中なら、それを適用する関数。
   * ★ 状態表示（#status）だけに出すと、後続の「会場を読み込みました」等に
   *   上書きされて利用者が永久に気付けない。実機で古い版に閉じ込められる
   *   事故が起きたので、消えないパネル側にも必ず出す。
   */
  updateApply: null as (() => void) | null,
  timeZone: loadTimeZone(),
  /** 狙っているゴールの一覧。選択（赤い輪）は常に 1 点だけ */
  goals: [] as DeclaredGoal[],
  selectedGoalKey: null as string | null,
  /** 一覧カードを畳んでいるか。地図を広く見たいときのために閉じられる */
  goalCardClosed: false,
};

// colorForTerminal() が state.terminalColors を直接読めないので、layers.ts側の
// 上書き先へ最初に一度渡しておく（変更のたびに main.ts から呼び直す）
setTerminalColorOverrides(state.terminalColors);
setLiveTrackColorMode(state.liveTrackColorMode);
setLiveTrackGlow(null, state.liveTrackGlow, state.terminalVisibility.favorites);

const map = new maplibregl.Map({
  container: 'map',
  style: buildStyle('none'),
  center: [139.66, 36.25],
  zoom: 11,
  attributionControl: { compact: true },
  // 拡大鏡（長押しの微調整）がこのcanvasを直接クロップして拡大表示するため必要
  canvasContextAttributes: { preserveDrawingBuffer: true },
  // 既定の60のままだと真横に近い視点にできず、3D表示で高さが読み取れない
  maxPitch: MAX_PITCH,
});
map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');

/**
 * ダブルタップでの意図しない拡大を止め、2本指ピンチは「回転」だけを
 * 止めて「ズーム」は残す（利用者からの要望、2026-09-14:「操作中に
 * 時々画面が拡大されてしまう」）。
 *
 * ★★ 重要な落とし穴（コードレビューで指摘・実際に踏んだ、2026-09-15）:
 *   当初は`touchZoomRotate.disable()`でピンチのズーム・回転を丸ごと
 *   止めていたが、これが**さらに悪い**別の不具合を生んでいた。
 *   MapLibre純正のCSS（`maplibre-gl.css`）は、キャンバスの
 *   `touch-action`を次のように、有効なハンドラの組み合わせで自動的に
 *   切り替えている:
 *   - `touchZoomRotate`と`touchDragPan`が両方有効 → `touch-action: none`
 *     （ブラウザ標準のジェスチャーを一切渡さず、MapLibre自身のJSが
 *     ピンチ・パンを丸ごと処理する）
 *   - `touchDragPan`だけが有効（`touchZoomRotate`が無効） →
 *     `touch-action: pinch-zoom`（**ブラウザ標準の2本指ピンチズームに
 *     明示的に処理を渡す**指定）
 *   `touchZoomRotate.disable()`はこのハンドラ自体を無効化し、対応する
 *   CSSクラス（`maplibregl-touch-zoom-rotate`）を外すため、上記の
 *   2番目の状態（`touch-action: pinch-zoom`）に切り替わってしまい、
 *   ページ全体をブラウザが直接ピンチズームするようになった
 *   （地図の中だけでなく、サイドバー・ボタンごと画面全体が拡大され、
 *   レイアウトが崩れる。実機のスクリーンショットで確認済み）。
 *   これはMapLibre側のズーム・回転ハンドラが完全に無効化された状態で
 *   起きるため、「ピンチで画面が拡大される」という利用者の指摘は
 *   直らないどころか、アプリの地図ズームではなくページ全体のネイティブ
 *   ズームという、より深刻な形で再発していた。
 *
 *   対策として、`touchZoomRotate.disable()`ではなく
 *   `touchZoomRotate.disableRotation()`を使う。このAPIは`_touchRotate`
 *   だけを止め、ハンドラ自体（`_touchZoom`）とCSSクラスは変更しない
 *   （MapLibre本体のソースで確認済み）。これにより:
 *   - `maplibregl-touch-zoom-rotate`クラスは付いたまま＝
 *     `touch-action: none`が維持され、ブラウザのネイティブページズームは
 *     引き続きブロックされる
 *   - ピンチズーム自体はMapLibre自身のJSハンドラが処理し続けるので、
 *     意図した地図ズームとして機能する（利用者が期待する「ピンチで
 *     地図を拡大縮小できる」操作を復元する）
 *   - 2本指回転だけは引き続き無効（回転はボタン、下記の
 *     `PitchControl`内の回転ボタンに置き換え済み）
 * ★ 止めるのは`doubleClickZoom`（ダブルタップ／ダブルクリック）と
 *   ピンチの「回転」だけ。`scrollZoom`（ホイール）・`dragPan`（1本指
 *   パン）・`dragRotate`（PCの右クリック/Ctrl+ドラッグ回転）・
 *   `touchPitch`（2本指の上下ドラッグで傾ける、3D表示で使用中）・
 *   `keyboard`・`boxZoom`はいずれも今回の指摘とは無関係で、他の既存
 *   機能が動かなくなるのを避けるため触れない。
 *   （地図タップの長押し・ドラッグ等、アプリ独自のポインタ処理も
 *   MapLibre純正ハンドラとは別物なので影響しない）
 */
map.doubleClickZoom.disable();
map.touchZoomRotate.disableRotation();

/**
 * 「表示」と「接続と送信」を開くボタン。ズーム＋／－と同じ右上の列に並ぶ。
 *
 * ★ どちらも元はハンバーガーのパネル（フライト中に使うメニュー）に
 *   置いていたが、パネルが縦に伸びすぎて目的の項目に届かない、という
 *   実機からの指摘で地図側のアイコンへ出した。ダイアログの中身は
 *   index.html でそのまま移しただけなので、配線（el('toggle-...')など）は
 *   一切変わっていない。
 *
 * ★ ズームのすぐ下へ入れる。列の一番下は横向きの狭い画面で高度の凡例と
 *   重なる位置なので、そこには当たりの弱い傾きボタンを残す
 *   （740×360で実測。並び順を変えるだけで、よく使うものは凡例に隠れない）。
 *
 * ★ 1つの`maplibregl-ctrl-group`に複数のボタンを入れると、枠線が繋がって
 *   隣り合ったボタンの境目が分からなくなる。「ボタンが繋がっていて押しにくい」
 *   との指摘を受け、**1ボタンにつき1組**にして間隔を空けている
 *   （ズーム＋／－のように、意味の対になっているものだけ1組にまとめる）。
 *
 * ★ 開閉は他のダイアログと同じく hidden 属性で行う（.goal-entry の規約）。
 */
class SheetOpenControl implements maplibregl.IControl {
  private container?: HTMLDivElement;

  constructor(private readonly buttons: Array<{ id: string; label: string; svg: string; open: () => void }>) {}

  onAdd(): HTMLElement {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    for (const spec of this.buttons) {
      const button = document.createElement('button');
      button.type = 'button';
      button.id = spec.id;
      button.className = 'maplibregl-ctrl-icon';
      button.setAttribute('aria-label', spec.label);
      // ズーム＋／－やLocateMeControlと同じ、20×20のビューボックス＋#333で揃える
      button.innerHTML =
        `<svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 20 20" ` +
        `fill="none" stroke="#333" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">${spec.svg}</svg>`;
      button.onclick = spec.open;
      this.container.appendChild(button);
    }
    return this.container;
  }

  onRemove(): void {
    this.container?.remove();
    this.container = undefined;
  }
}

map.addControl(
  new SheetOpenControl([
    {
      id: 'display-open',
      label: '表示（地図に重ねるもの・3D）',
      // 重ねた紙＝地図に重ねるもの（レイヤー）の定番の絵
      svg: '<path d="M10 2.6 17.4 6.4 10 10.2 2.6 6.4Z"/><path d="M2.6 10 10 13.8 17.4 10"/>',
      open: () => toggleDisplaySheet?.(),
    },
  ]),
  'top-right',
);
map.addControl(
  new SheetOpenControl([
    {
      id: 'live-open',
      label: '接続と自機位置の送信',
      // 電波を出している点＝ライブ接続と送信
      svg:
        '<circle cx="10" cy="10" r="1.8" fill="#333" stroke="none"/>' +
        '<path d="M6.4 13.6a5 5 0 0 1 0-7.2"/><path d="M13.6 6.4a5 5 0 0 1 0 7.2"/>' +
        '<path d="M4 16a8.5 8.5 0 0 1 0-12"/><path d="M16 4a8.5 8.5 0 0 1 0 12"/>',
      open: () => toggleLiveSheet?.(),
    },
  ]),
  'top-right',
);
map.addControl(
  new SheetOpenControl([
    {
      id: 'taskboard-open',
      label: 'タスクシート（TaskBoard）',
      // クリップボードに挟んだ紙＝タスクシート
      svg:
        '<rect x="4" y="3" width="12" height="15" rx="1.6"/><path d="M7.5 3V2h5v1"/>' +
        '<path d="M7 8h6"/><path d="M7 11h6"/><path d="M7 14h4"/>',
      open: () => toggleTaskboard?.(),
    },
    {
      id: 'official-wind-open',
      label: '風（大会のパイバル）',
      // 流れる風の線＝風（大会本部のパイバル、docs/08 §198）
      svg:
        '<path d="M2.5 7.5h9.5a2.4 2.4 0 1 0-2.4-2.4"/><path d="M2.5 11h12.5a2.4 2.4 0 1 1-2.4 2.4"/>' +
        '<path d="M2.5 14.5h5.5"/>',
      open: () => togglePibal?.(),
    },
  ]),
  'top-right',
);
/*
 * ★ この列にボタンを足すときは列の高さに注意。横持ちの短い画面
 *   （852×393）では、列の下端が高度の凡例と重なる位置まで来ている
 *   （当たりの弱いボタンを最後に置くことで意図的に許容している）。
 *   これ以上気軽に組を増やさないこと（実測、docs/08参照）。
 */

/**
 * 3D表示の入り切りボタン。
 *
 * ★ 3D表示は「表示」の中のチェックボックスにしか無く、そこにあると
 *   分かりにくい、という利用者からの指摘で地図側にも出した。
 *   実体は`setShow3d()`（setupInteractions内）ひとつで、チェックボックスと
 *   このボタンはどちらから切り替えても互いに追随する。
 *
 * ★ 文字は「押すとどうなるか」を出す（2D表示中は「3D」、3D表示中は「2D」）。
 *   アイコンではなく文字にしたのは利用者の指定。
 */
class Show3dControl implements maplibregl.IControl {
  private container?: HTMLDivElement;

  onAdd(): HTMLElement {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'view-3d-toggle';
    // 文字を出すので maplibregl-ctrl-icon（背景画像用）ではなく専用クラス
    button.className = 'map-text-ctrl';
    button.setAttribute('aria-label', '3D表示にする');
    button.setAttribute('aria-pressed', 'false');
    button.textContent = '3D';
    button.onclick = () => applyShow3d?.(!state.show3d);
    this.container.appendChild(button);

    // ライブ航跡の色（高度の色分け／機体ごとの単色）を切り替えるボタン（docs/08 §218）。
    // ★ 航跡が重なってくると、高度の色分けのままでは同じ高度の線どうしが同じ色で重なり、自分のチームの
    //   航跡が見分けられなくなる、との指摘（2026-10-29）。ワンボタンで切り替える。
    // ★ 3Dボタンと同じグループに入れる。縦が短い横向き画面では、このグループを2つ横に並べて
    //   列を伸ばさない（別のグループにすると列が下にはみ出して、傾き・回転のボタンが切れた。実測）。
    // ★ 文字は3Dボタンと同じく「押すとどうなるか」（高度の色分け中は「単色」、単色中は「高度」）。
    const colorButton = document.createElement('button');
    colorButton.type = 'button';
    colorButton.id = 'track-color-toggle';
    colorButton.className = 'map-text-ctrl';
    colorButton.onclick = () => applyLiveTrackColor?.(state.liveTrackColorMode === 'terminal' ? 'altitude' : 'terminal');
    this.container.appendChild(colorButton);
    renderTrackColorButton(colorButton);

    // お気に入りの機体だけ表示／すべて表示（一覧の「お気に入り以外を隠す」と同じ状態、docs/08 §226）。
    // ★ 一覧は操作パネルの中なので、飛行中にすぐ押せるよう、色のボタンと同じグループ（地図の右）にも置く。
    //   文字は他のボタンと同じく「押すとどうなるか」（すべて表示中は「★」＝お気に入りだけにする、お気に入りだけのときは「全機」）
    const favButton = document.createElement('button');
    favButton.type = 'button';
    favButton.id = 'fav-only-toggle';
    favButton.className = 'map-text-ctrl';
    favButton.onclick = () => applyShowNonFavorites(!state.terminalVisibility.showNonFavorites);
    this.container.appendChild(favButton);
    renderFavOnlyButton(favButton);
    return this.container;
  }

  onRemove(): void {
    this.container?.remove();
    this.container = undefined;
  }
}
map.addControl(new Show3dControl(), 'top-right');

/** ボタンの文字・説明を今の状態に合わせる（押すとどうなるかを出す） */
function renderTrackColorButton(button: HTMLButtonElement | null = document.getElementById('track-color-toggle') as HTMLButtonElement | null): void {
  if (!button) return;
  const single = state.liveTrackColorMode === 'terminal';
  button.textContent = single ? '高度' : '単色';
  const label = single ? '航跡を高度の色分けにする（いまは機体ごとの単色）' : '航跡を機体ごとの単色にする（いまは高度の色分け）';
  button.setAttribute('aria-label', label);
  button.title = label;
  button.setAttribute('aria-pressed', single ? 'true' : 'false');
}

/** 「★」（お気に入りだけ表示）ボタンの文字・説明を今の状態に合わせる（押すとどうなるかを出す） */
function renderFavOnlyButton(button: HTMLButtonElement | null = document.getElementById('fav-only-toggle') as HTMLButtonElement | null): void {
  if (!button) return;
  const onlyFav = !state.terminalVisibility.showNonFavorites;
  button.textContent = onlyFav ? '全機' : '★';
  const label = onlyFav ? '他の機体も表示する（いまはお気に入りだけ）' : 'お気に入りの機体だけ表示する（いまは全機）';
  button.setAttribute('aria-label', label);
  button.title = label;
  button.setAttribute('aria-pressed', onlyFav ? 'true' : 'false');
}

/** お気に入り以外の機体を表示するかを切り替える（一覧のボタンと地図右のボタンで共通） */
function applyShowNonFavorites(show: boolean): void {
  setShowNonFavorites(state.terminalVisibility, show);
  saveShowNonFavorites(show);
  renderFavOnlyButton();
  renderLive();
  redraw();
}

/** ライブ航跡の色の付け方を切り替える実体（setupInteractions()が入れる） */
let applyLiveTrackColor: ((mode: LiveTrackColorMode) => void) | undefined;

/** 3D表示を切り替える実体（setupInteractions()が入れる。ボタンはそれより前に作られる） */
let applyShow3d: ((on: boolean) => void) | undefined;

/**
 * 「自分の位置に地図を移動」ボタン。ズーム＋／－の下に並ぶ。
 * 自機位置の送信中かどうかに関わらず使える、その場限りの一回読み
 * （継続追従は別の「自分の位置に地図を追従させる」チェックボックスが担当）。
 */
class LocateMeControl implements maplibregl.IControl {
  private container?: HTMLDivElement;

  onAdd(): HTMLElement {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'maplibregl-ctrl-icon';
    button.setAttribute('aria-label', '自分の位置に地図を移動');
    // maplibre-gl.css 内蔵のGeolocateControl用アイコンと同じパスを使い、
    // 見た目をズーム＋／－と揃える
    button.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" fill="#333" viewBox="0 0 20 20">' +
      '<path d="M10 4C9 4 9 5 9 5v.1A5 5 0 0 0 5.1 9H5s-1 0-1 1 1 1 1 1h.1A5 5 0 0 0 9 14.9v.1s0 1 1 1 1-1 1-1v-.1a5 5 0 0 0 3.9-3.9h.1s1 0 1-1-1-1-1-1h-.1A5 5 0 0 0 11 5.1V5s0-1-1-1m0 2.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 1 1 0-7"/>' +
      '<circle cx="10" cy="10" r="2"/></svg>';
    button.onclick = () => locateMe();
    this.container.appendChild(button);
    // 気球を探す（カメラ、docs/08 §234）。クルーモードの縦持ちだけ出す（style.css）。押したときの処理は setupFinder() が入れる
    const finder = document.createElement('button');
    finder.type = 'button';
    finder.id = 'finder-open';
    finder.setAttribute('aria-label', '気球を探す（カメラの映像に気球の位置を重ねる）');
    finder.title = '気球を探す（カメラ）';
    finder.innerHTML =
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#333" aria-hidden="true">' +
      '<rect x="2.5" y="6.5" width="19" height="13" rx="2.5" stroke-width="1.7"/>' +
      '<path d="M8.5 6.5 L10 4 H14 L15.5 6.5" stroke-width="1.6"/>' +
      '<ellipse cx="12" cy="11.6" rx="3" ry="3.5" stroke-width="1.6"/>' +
      '<path d="M12 15.1 L12 17" stroke-width="1.4"/></svg>';
    this.container.appendChild(finder);
    return this.container;
  }

  onRemove(): void {
    this.container?.remove();
    this.container = undefined;
  }
}
map.addControl(new LocateMeControl(), 'top-right');

/** 1回のタップで動かす角度 [deg]。スライダーの1刻みでは細かすぎるため、ボタンは大きめに刻む */
const PITCH_STEP_DEG = 15;

/**
 * カメラの傾き（pitch）を上下できるボタン。ズーム＋／－の下に並ぶ。
 *
 * ★ 設定パネルのスライダー（view-pitch-slider）と同じ値を動かす。
 *   既存の `map.on('pitch'/'pitchend')` リスナーが表示・保存の両方を
 *   面倒見てくれるので、ここは `map.easeTo({ pitch })` を呼ぶだけでよい。
 *
 * ★ 「常に画面に出しておいてほしい」との要望（2026-08-22）。断面ビューを
 *   別に開かなくても、地図を見ながらその場で高さの読み取りやすさを
 *   調整できるようにする。3D表示OFF中は他の傾き操作（スライダー）と同じく
 *   意味を持たないので無効化する（`toggle-3d` のonchangeで同期）。
 *
 * ★ 上ボタン＝pitchを上げる（寝かせる・水平線に近づく）、
 *   下ボタン＝pitchを下げる（起こす・真上に近づく）という向き。
 *   多くの地図アプリの3D傾き操作と同じ対応にした。
 */
/** 1回のタップで回す角度 [deg]。bearingは0〜360を回るので、pitchより大きめに刻む */
const ROTATE_STEP_DEG = 30;

/**
 * 傾き（pitch）と向き（bearing）の上下左右ボタンをまとめた1つのコントロール。
 *
 * ★ 回転ボタンは利用者からの要望（2026-09-14:「回転ボタンを追加して、
 *   地図の中心を起点に回転させたい」）で追加。当初はpitchボタンとは別の
 *   `.maplibregl-ctrl-group`として実装したが、コードレビューで「地図右上の
 *   ボタン列は9個並ぶと740×360ではみ出す」という既存の既知の制約
 *   （下記CSSコメント参照）にさらに2個積み増す形になり、実際に短い
 *   横画面（852×393）で凡例と重なる・ボタン自体が画面から切れることが
 *   確認された。別グループにすると`.maplibregl-ctrl-group`同士の余白
 *   （グループ間マージン）ぶん余分に高さを食うため、pitchボタンと同じ
 *   1つのグループにまとめて、その余白1つぶんの高さを節約する。
 * ★ 2本指のピンチ回転（TouchZoomRotateHandler）は「なかなか大変」
 *   「特にPCはどう操作するか分からない」との指摘。desktop でのbearing
 *   回転は既定で右ドラッグ／Ctrl+ドラッグ（DragRotateHandler）だが、
 *   直感的でなく気付きにくい。ボタンにすれば指1本・マウスクリックだけで
 *   誰でも回せる。
 * ★ 回転の中心は「地図の中心」（`map.easeTo({ bearing })` は既定で画面
 *   中心を軸に回す。他の見せ方——例えば自機位置を軸にする——は要望に
 *   無いので、素のeaseToのままにしてある）。
 * ★ NavigationControl のコンパスボタン（既存、ズームのすぐ上）は
 *   「押すと真北へ戻す」専用で、任意の角度に少しずつ回す用途には向かない。
 *   このボタンはその補完（役割が違うので両方残す）。
 * ★ pitch（傾き）と紛れないよう、回転ボタンはpitchではなくbearingだけを動かす。
 *
 * ★ 設定パネルのスライダー（view-pitch-slider）と同じ値を動かす。
 *   既存の `map.on('pitch'/'pitchend')` リスナーが表示・保存の両方を
 *   面倒見てくれるので、ここは `map.easeTo({ pitch })` を呼ぶだけでよい。
 *
 * ★ 「常に画面に出しておいてほしい」との要望（2026-08-22）。断面ビューを
 *   別に開かなくても、地図を見ながらその場で高さの読み取りやすさを
 *   調整できるようにする。3D表示OFF中は他の傾き操作（スライダー）と同じく
 *   意味を持たないので無効化する（`toggle-3d` のonchangeで同期）。pitch
 *   ボタンとは違い、回転（bearing）は3D表示OFFでも意味があるため無効化しない。
 *
 * ★ 上ボタン＝pitchを上げる（寝かせる・水平線に近づく）、
 *   下ボタン＝pitchを下げる（起こす・真上に近づく）という向き。
 *   多くの地図アプリの3D傾き操作と同じ対応にした。
 */
class PitchControl implements maplibregl.IControl {
  private container?: HTMLDivElement;

  onAdd(): HTMLElement {
    this.container = document.createElement('div');
    // pitch-ctrl-group: 縦が短い画面では2×2に並べて列を短くする（style.css）
    this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group pitch-ctrl-group';

    const makePitchButton = (id: string, label: string, glyph: string, deltaDeg: number): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.id = id;
      button.className = 'maplibregl-ctrl-icon';
      button.setAttribute('aria-label', label);
      button.disabled = !state.show3d;
      button.textContent = glyph;
      button.onclick = () => {
        const next = Math.min(MAX_PITCH, Math.max(0, map.getPitch() + deltaDeg));
        map.easeTo({ pitch: next, duration: 200 });
      };
      return button;
    };

    const makeRotateButton = (id: string, label: string, glyph: string, deltaDeg: number): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.id = id;
      button.className = 'maplibregl-ctrl-icon';
      button.setAttribute('aria-label', label);
      button.textContent = glyph;
      button.onclick = () => {
        // bearingは0〜360で折り返す。±360しても値としては同じ向きだが、
        // 表示（コンパス）・保存値を素直な範囲に保つため剰余を取る
        const next = (((map.getBearing() + deltaDeg) % 360) + 360) % 360;
        map.easeTo({ bearing: next, duration: 200 });
      };
      return button;
    };

    this.container.append(
      makePitchButton('pitch-up-btn', '視点を寝かせる（pitchを上げる）', '⌃', PITCH_STEP_DEG),
      makePitchButton('pitch-down-btn', '視点を起こす（pitchを下げる）', '⌄', -PITCH_STEP_DEG),
      makeRotateButton('rotate-left-btn', '地図を反時計回りに回す', '↺', -ROTATE_STEP_DEG),
      makeRotateButton('rotate-right-btn', '地図を時計回りに回す', '↻', ROTATE_STEP_DEG),
    );
    return this.container;
  }

  onRemove(): void {
    this.container?.remove();
    this.container = undefined;
  }
}
map.addControl(new PitchControl(), 'top-right');

/*
 * ダイアログを開く実体。setup*() の中で組み立てるので、それまでは undefined
 * （地図のコントロールは boot() より前に作られるため、?.() で受ける）。
 */
let openDisplaySheet: (() => void) | undefined;
let openLiveSheet: (() => void) | undefined;
/** 地図右上のアイコン用。PCでは開いていれば閉じる（setupMapSheets参照） */
let toggleDisplaySheet: (() => void) | undefined;
/** ライブ端末の3Dの壁・稜線の作り直しの間引き（updateLive3d、docs/08 §195） */
const LIVE_3D_MIN_INTERVAL_MS = 3000;
let live3dSignature = '';
let live3dBuiltAt = 0;
let live3dTimer: ReturnType<typeof setTimeout> | null = null;
/** タスクシート（TaskBoard）の開閉。setupTaskboardPanel()で設定（docs/08 §193） */
let toggleTaskboard: (() => void) | undefined;
/** 大会本部のパイバル（風）のシートの開閉。setupOfficialPibal()で設定（docs/08 §198） */
let togglePibal: (() => void) | undefined;
let toggleLiveSheet: (() => void) | undefined;

/**
 * PCのレイアウト（サイドパネルがある画面）かどうか。style.cssでスマホ用の
 * `@media (max-width: 700px), (max-height: 450px) and (orientation: landscape)`
 * のちょうど裏返し。同ファイルの「表示」「接続と送信」をPCで左上に出す
 * @mediaと同じ条件を保つこと。
 */
const DESKTOP_LAYOUT_QUERY = '(min-width: 701px) and (min-height: 451px), (min-width: 701px) and (orientation: portrait)';
function isDesktopLayout(): boolean {
  return window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;
}

/**
 * 地図右上のアイコンとパネルの近道ボタンから開く2つのダイアログ（表示・接続と送信）。
 * 中身は index.html から移してきたものがそのまま入っているので、ここは開閉だけを見る。
 */
function setupMapSheets(): void {
  // 最小化（見出し行だけ残して畳み、地図を触れるようにする）。
  // #lrn3dt-view.lrn3dt-minimized と同じ手法（style.css の`.view-minimized`
  // 参照）。元は下のパネルにあってドラッグで自由に畳めた「表示」「接続と
  // 送信」が、ボタン化で畳めなくなった、という指摘への対応（2026-09-12）。
  const setMinimized = (sheet: HTMLElement, minimizeBtn: HTMLButtonElement, minimized: boolean): void => {
    sheet.classList.toggle('view-minimized', minimized);
    minimizeBtn.textContent = minimized ? '設定に戻る' : '地図を見る';
  };
  const wire = (
    viewId: string,
    closeId: string,
    minimizeId: string,
    panelButtonId: string,
    otherViewId: string,
  ): { open: () => void; toggle: () => void } => {
    const sheet = el(viewId);
    const minimizeBtn = el<HTMLButtonElement>(minimizeId);
    const open = (): void => {
      // 「表示」「接続と送信」は同じ位置・同じ重なり順の別ダイアログ。
      // 片方を開いたままもう片方を開くと、HTMLで後にある方がタップ可能な
      // まま前面に残り続けた（コードレビュー指摘、2026-09-13）。開いたら
      // もう片方は必ず閉じる
      el(otherViewId).hidden = true;
      sheet.hidden = false;
      setMinimized(sheet, minimizeBtn, false);
      closePanel?.();
    };
    el(closeId).onclick = () => {
      sheet.hidden = true;
    };
    minimizeBtn.onclick = () => setMinimized(sheet, minimizeBtn, !sheet.classList.contains('view-minimized'));
    // 外側のタップでも閉じる（他のダイアログと同じ約束事。最小化中は暗幕
    // 自体がpointer-events:noneなのでここには来ない）
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet) sheet.hidden = true;
    });
    el(panelButtonId).onclick = open;
    // 地図右上のアイコンは、PCではもう一度押すと閉じる（利用者の要望、
    // 2026-10-06）。最小化中に押したときは閉じずに元の大きさへ戻す
    // （見出しだけ残して畳んでいる状態から、さらに消したいとは限らないため）。
    // スマホは従来どおり常に開くだけ（スマホの操作は変えない、との指定）
    const toggle = (): void => {
      if (isDesktopLayout() && !sheet.hidden && !sheet.classList.contains('view-minimized')) {
        sheet.hidden = true;
        return;
      }
      open();
    };
    return { open, toggle };
  };
  const display = wire('display-view', 'display-close', 'display-minimize', 'display-open-from-panel', 'live-view');
  const live = wire('live-view', 'live-close', 'live-minimize', 'live-open-from-panel', 'display-view');
  openDisplaySheet = display.open;
  openLiveSheet = live.open;
  toggleDisplaySheet = display.toggle;
  toggleLiveSheet = live.toggle;
}

/**
 * ↑キーで拡大、↓キーで縮小（利用者の要望、2026-10-06）。MapLibre標準では
 * ↑↓は地図を南北に動かす操作だが、それを置き換える（←→の左右移動、
 * Shift＋矢印の回転・傾きは標準のまま）。
 * ★ 捕捉フェーズで先に受けて伝播を止めること。止めないとMapLibreの
 *   キーボード操作（地図コンテナで受ける）にも届き、拡大と同時に地図が動く。
 * ★ 入力欄・選択欄では横取りしない（数値入力・スライダーは矢印キーで
 *   値を変える）。
 */
function setupArrowKeyZoom(): void {
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, select, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'ArrowUp') map.zoomIn();
      else map.zoomOut();
    },
    { capture: true },
  );
}

function locateMe(): void {
  getOwnPositionOnce()
    .then((fix) => {
      map.easeTo({ center: [fix.lon, fix.lat], offset: mapCenterOffset(), duration: 600 });
    })
    .catch((err: Error) => setStatus(err.message, 'warn'));
}

/** ベース地図のタイル取得に失敗しても、航跡表示は生かしたまま状況だけ伝える */
map.on('error', (e) => {
  const message = String((e as { error?: Error }).error?.message ?? e);
  if (/tile|fetch|network|Failed/i.test(message)) {
    setStatus('地図タイルを取得できません（オフライン、または未取得）', 'warn');
  }
});

/** 追従中に手で地図を動かしたら、しばらく追従を休ませる */
const FOLLOW_ME_PAUSE_MS = 5000;
map.on('dragstart', () => {
  if (state.followTarget) state.followMePausedUntil = Date.now() + FOLLOW_ME_PAUSE_MS;
});

/** 追従先の現在位置。対象がいない・位置が無い場合は null */
function followTargetPosition(): { lat: number; lon: number } | null {
  if (!state.followTarget) return null;
  if (state.followTarget === 'me') {
    return state.myFix ? { lat: state.myFix.lat, lon: state.myFix.lon } : null;
  }
  const terminal = liveTerminals().find((t) => t.terminalId === state.followTarget);
  return terminal ? { lat: terminal.latest.lat, lon: terminal.latest.lon } : null;
}

/**
 * 追従先が動いていれば地図の中心を寄せる。`redraw()` から呼ぶ。
 *
 * ★ redraw() は位置と無関係な理由（チェックボックスの切り替えなど）でも
 *   頻繁に呼ばれるので、**前回寄せた位置から動いたときだけ** easeTo する。
 *   毎回呼ぶと、手で地図を動かしている最中に引き戻され続けて操作できない。
 */
function applyMapFollow(): void {
  const pos = followTargetPosition();
  if (!pos) {
    state.followLastCenter = null;
    return;
  }
  if (Date.now() < state.followMePausedUntil) return;

  const last = state.followLastCenter;
  if (last && last.lat === pos.lat && last.lon === pos.lon) return;
  state.followLastCenter = pos;
  map.easeTo({ center: [pos.lon, pos.lat], offset: mapCenterOffset(), duration: 400 });
}

function setStatus(
  text: string,
  kind: 'info' | 'warn' = 'info',
  action?: { label: string; onClick: () => void },
): void {
  const node = el('status');
  node.textContent = text; // 以前の内容（ボタンを含む）はここで消える
  node.dataset.kind = kind;
  if (action) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'status-action';
    btn.textContent = action.label;
    btn.onclick = action.onClick;
    node.append(' ', btn);
  }
}

// --- 描画 -------------------------------------------------------------------

/**
 * いま表示すべきライブ端末（実際の受信データ + デバッグ用ダミー）。
 * 共有サーバーは日本時間12時にデータを消すため、実データが無くても
 * ダミー航跡でレイヤー・一覧・タップ詳細・保存の動作を確認できるようにしてある。
 */
function liveTerminals(): LiveTerminal[] {
  return [...sanitizedRealTerminals(), ...state.debugTerminals.values()];
}

/** 表示に使うライブ端末。`rejected`は異常値として除いた・高度を空にした点の数（デバッグ用ダミーは無い） */
interface LiveTerminal {
  terminalId: string;
  fixes: FixSample[];
  latest: FixSample;
  rejected?: number;
}

/**
 * 受信中の端末（ダミーを除く）から、**異常値を弾いたもの**を返す（docs/08 §229）。
 * 位置が気球の動ける範囲を超えて飛んだ点は捨て、高度だけの異常は高度を空にする
 * （地上のクルーの端末に 4528ft が入り、天井超過の警告が出た件）。
 * 点が増えるたびに全点を見直すと重いので、点の数・最初と最後の時刻が同じ間は前回の結果を使う。
 */
const sanitizeCache = new Map<string, { n: number; firstT: number; lastT: number; terminal: LiveTerminal }>();

function sanitizedRealTerminals(): LiveTerminal[] {
  const real = state.bridge ? state.bridge.terminals : (state.liveSnapshot?.terminals ?? []);
  const seen = new Set<string>();
  const out = real.map((t) => {
    seen.add(t.terminalId);
    const fixes = t.fixes;
    const key = { n: fixes.length, firstT: fixes[0]?.t ?? 0, lastT: fixes[fixes.length - 1]?.t ?? 0 };
    const cached = sanitizeCache.get(t.terminalId);
    if (cached && cached.n === key.n && cached.firstT === key.firstT && cached.lastT === key.lastT) return cached.terminal;
    const r = sanitizeFixes(fixes);
    const terminal: LiveTerminal = {
      terminalId: t.terminalId,
      fixes: r.fixes,
      // 最後の点は、弾いたあとの最後（高度だけ異常なら高度が空の最後の点）。点が全部弾かれたら、受信した最新のまま
      latest: r.fixes[r.fixes.length - 1] ?? t.latest,
      rejected: r.droppedPositions + r.clearedAltitudes,
    };
    sanitizeCache.set(t.terminalId, { ...key, terminal });
    return terminal;
  });
  for (const id of sanitizeCache.keys()) if (!seen.has(id)) sanitizeCache.delete(id);
  return out;
}

/**
 * 他機が宣言したゴール（$_TVMARK）。原典GVでは○で表示されるが、GV4Dは
 * 受信はしていても描画するコードが無かった（実機テストで発覚、要修正）。
 */
function liveMarks(): MarkPoint[] {
  if (state.bridge) return state.bridge.marks;
  if (state.liveSnapshot) return [...state.liveSnapshot.marks.values()].flat();
  return [];
}

/**
 * 会場のNOTAMエリア（state.venue.notam）ごとに、現在どの体制が有効かを
 * 解決する。`state.notamOverrides[i]`（会場読み込み時にエリア数ぶん
 * リサイズ済み）で自動判定/手動上書きが決まる。
 */
interface ActiveNotamRegime {
  area: NotamAreaData;
  regime: NotamRegime;
  regimeSource: 'auto' | 'manual-regime' | 'manual-holiday';
}

function currentNotamRegimes(): ActiveNotamRegime[] {
  if (!state.venue?.notam) return [];
  const nowUtcMs = Date.now();
  return state.venue.notam
    .map((area, i): ActiveNotamRegime | null => {
      const override = state.notamOverrides[i] ?? { regimeId: null, treatAsHoliday: false };
      const resolution = resolveNotamRegime({
        nowUtcMs,
        weekdayRegimeId: area.weekdayRegimeId,
        weekendRegimeId: area.weekendRegimeId,
        saturdayCutoffHourJst: area.saturdayCutoffHourJst,
        override,
      });
      const regime = area.regimes.find((r) => r.id === resolution.regimeId);
      return regime ? { area, regime, regimeSource: resolution.source } : null;
    })
    .filter((x): x is ActiveNotamRegime => x != null);
}

/**
 * BluePZへの侵入警告。自機位置（GPS由来のみ。手入力座標は対象外）と
 * ライブ受信中の他機（デバッグ端末含む）の両方を監視する。
 * ★ overrideが有効化されている間はその高いほうの天井を使って判定する
 * ので、条件が終わったらメニューでチェックを外す運用が前提（安全側に
 * 倒すためチェックを自動で外したりはしない）。
 */
function updateNotamViolations(): void {
  state.notamViolations.clear();
  const regimes = currentNotamRegimes();
  if (regimes.length === 0) {
    renderNotamBanner();
    return;
  }
  const nowMs = Date.now();
  const terminals = liveTerminals();
  for (const { area, regime } of regimes) {
    // 自機（GPSの位置が新しいときだけ。止まった端末の古い位置では警告しない）
    if (state.myFix && isFreshForAlert(state.myFix.t, nowMs)) {
      const fix: NotamFix = { lat: state.myFix.lat, lon: state.myFix.lon, altM: state.myFix.altMsl ?? 0 };
      const hit = checkNotamIncursion(fix, regime, state.notamActiveOverrideIds);
      if (hit) {
        const key = `self:${area.title}`;
        state.notamViolations.set(key, {
          key,
          terminalId: null,
          label: '自機',
          areaTitle: area.title,
          zoneLabel: (hit.zone as { label?: string }).label ?? '',
          marginFt: Math.round(hit.marginFt),
        });
      }
    }
    for (const t of terminals) {
      // ★ 最新の位置が古い端末は判定しない。送信をやめた端末の最後の位置が残り続け、夜になっても
      //   「天井を超過」と出続けた（2026-11-01、docs/08 §228）
      if (!isFreshForAlert(t.latest.t, nowMs)) continue;
      const altM = t.latest.altPressure ?? t.latest.altMsl ?? 0;
      const fix: NotamFix = { lat: t.latest.lat, lon: t.latest.lon, altM };
      const hit = checkNotamIncursion(fix, regime, state.notamActiveOverrideIds);
      if (hit) {
        const key = `${t.terminalId}:${area.title}`;
        state.notamViolations.set(key, {
          key,
          terminalId: t.terminalId,
          label: t.terminalId,
          areaTitle: area.title,
          zoneLabel: (hit.zone as { label?: string }).label ?? '',
          marginFt: Math.round(hit.marginFt),
        });
      }
    }
  }
  renderNotamBanner();
}

function renderNotamBanner(): void {
  const banner = el('notam-banner');
  // 「消す」にした警告・止めた端末の警告は出さない（超過が解消した「消す」は外れる）
  const shown = selectAlerts([...state.notamViolations.values()], state.notamDismissed, state.notamMutedTerminals);
  if (shown.length === 0) {
    banner.hidden = true;
    banner.innerHTML = '';
    return;
  }
  banner.hidden = false;
  banner.innerHTML = shown
    .map(
      (v) =>
        `<div class="notam-line"><span>⚠ ${escapeHtml(v.label)}: ${escapeHtml(v.areaTitle)}「${escapeHtml(v.zoneLabel)}」の制限高度を${v.marginFt}ft超過</span>` +
        `<span class="notam-actions">` +
        `<button type="button" data-notam-dismiss="${escapeHtml(v.key)}" title="超過が解消するまで、この警告を消す">消す</button>` +
        (v.terminalId
          ? `<button type="button" data-notam-mute="${escapeHtml(v.terminalId)}" title="この機体の警告を止める（クルーの車など。「お気に入りと色の設定」で戻せる）">この機体は止める</button>`
          : '') +
        `</span></div>`,
    )
    .join('');
}

/** 気球を探す（クルーモード・縦持ち、docs/08 §234）の閉じる処理。モードの切り替え・横持ちで閉じる */
let closeFinder: (() => void) | null = null;

function setupFinder(): void {
  const finder = setupBalloonFinder({
    terminals: () => liveTerminals(),
    favorites: () => state.terminalVisibility.favorites,
    colorFor: (id) => colorForTerminal(id),
    ownTerminalId: () => loadMyTerminalId(),
    selfFix: () => state.myFix,
    declination: (lat, lon) =>
      (state.venue ? loadDeclination(state.venue.id)?.declinationDeg : undefined) ?? getMagneticDeclination(lat, lon).declinationDeg,
    setStatus: (m, kind) => setStatus(m, kind ?? 'info'),
  });
  closeFinder = finder.close;
  // ★ 開く処理（許可の要求）は、押した処理の中で直接呼ぶ（iPhone の許可の制約）
  const openBtn = el<HTMLButtonElement>('finder-open');
  openBtn.onclick = () => {
    if (finder.isOpen()) finder.close();
    else void finder.open();
    openBtn.setAttribute('aria-pressed', String(finder.isOpen()));
  };
  new MutationObserver(() => openBtn.setAttribute('aria-pressed', String(finder.isOpen()))).observe(el('balloon-finder'), {
    attributes: true,
    attributeFilter: ['hidden'],
  });
  // 横持ちにしたら閉じる（縦持ちだけの画面）
  window.matchMedia('(orientation: landscape)').addEventListener('change', (e) => {
    if (e.matches && finder.isOpen()) finder.close();
  });
}

/** 警告のバナーの「消す」「この機体は止める」（バナーを作り直すので、押された側で受ける） */
function setupNotamBanner(): void {
  el('notam-banner').addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const dismiss = t.closest<HTMLElement>('[data-notam-dismiss]');
    if (dismiss?.dataset.notamDismiss) {
      state.notamDismissed.add(dismiss.dataset.notamDismiss);
      renderNotamBanner();
      return;
    }
    const mute = t.closest<HTMLElement>('[data-notam-mute]');
    if (mute?.dataset.notamMute) {
      state.notamMutedTerminals.add(mute.dataset.notamMute);
      saveNotamMuted(state.notamMutedTerminals);
      setStatus(`${mute.dataset.notamMute} の天井超過の警告を止めました（「お気に入りと色の設定」で戻せます）`);
      renderNotamBanner();
      renderTerminalColors?.();
    }
  });
  // 新しい位置が来なくなった端末の警告は、時間が経つだけで消える。再描画を待たずに見直す
  setInterval(updateNotamViolations, 15_000);
}

function redraw(): void {
  // チェックを外したフライトは航跡から除く。活動中（activeFlight）かどうかとは別軸
  const visibleFlights = state.flights.filter((_, i) => !state.hiddenFlights.has(i));

  setSourceData(map, TRACK_SOURCE, {
    type: 'FeatureCollection',
    features: visibleFlights.flatMap((f) => trackToGeoJson(f.fixes).features),
  });
  setSourceData(map, POINT_SOURCE, {
    type: 'FeatureCollection',
    features: visibleFlights.flatMap((f) => endpointsToGeoJson(f.fixes).features),
  });
  setSourceData(
    map,
    TRACK_3D_SOURCE,
    state.show3d
      ? withGroundElevation({
          type: 'FeatureCollection',
          features: visibleFlights.flatMap(
            (f) => track3dToGeoJson(f.fixes, 50, state.show3dSmooth, state.threeDDetail).features,
          ),
        })
      : EMPTY_GEOJSON,
  );
  // 壁の天面に沿わせる稜線ライン（RidgeLineLayer、カスタムWebGLレイヤー）。
  // fill-extrusionのpaintプロパティと違い、頂点バッファは都度作り直す必要がある
  updateRidgeLines();

  const declaredEights = new Set(state.goals.map((g) => g.eight));
  setSourceData(
    map,
    GOAL_SOURCE,
    goalsToGeoJson(state.showGoals && state.venue ? state.venue.goals : [], declaredEights),
  );
  setSourceData(
    map,
    GOAL_3D_SOURCE,
    state.show3d ? withGroundElevation(declaredGoals3dToGeoJson(state.goals, state.selectedGoalKey)) : EMPTY_GEOJSON,
  );
  setSourceData(
    map,
    GOAL_BALL_3D_SOURCE,
    state.show3d
      ? withGroundElevation(declaredGoalBalls3dToGeoJson(state.goals, state.selectedGoalKey))
      : EMPTY_GEOJSON,
  );
  // ライブ受信中の他機（＋デバッグ用ダミー）と、共有された競技エリア
  const terminals = liveTerminals().filter((t) => terminalShown(t.terminalId));
  // 一覧のチェックで航跡を隠した機は、その機が共有したゴール・競技エリアも隠す。
  // 以前は航跡だけ消えてゴールが残り、隠した機のゴール（別の色の輪）が表示中の
  // 機の同じゴールに重なって、色が航跡に追従していないように見えた（docs/08 §189）
  const shownByTerminal = (x: { terminalId?: string }): boolean =>
    !x.terminalId || terminalShown(x.terminalId);
  // ★ 過去ログ（state.archiveCircles）ぶんも合わせて出す（2026-09-15、
  //   loadArchiveFlightsが航跡以外も返すようになった）
  const circles = [
    ...(state.bridge ? state.bridge.circles : state.liveSnapshot ? [...state.liveSnapshot.circles.values()].flat() : []),
    ...state.archiveCircles,
  ].filter(shownByTerminal);
  setSourceData(map, LIVE_TRACK_SOURCE, liveTracksToGeoJson(terminals));
  // 3Dの壁・稜線は重いので、点が増えただけなら最短3秒おきにまとめて作り直す（updateLive3d）
  updateLive3d();
  setSourceData(map, LIVE_POINT_SOURCE, livePointsToGeoJson(terminals));
  setSourceData(
    map,
    LIVE_POINT_3D_SOURCE,
    state.show3d ? withGroundElevation(livePointArrow3dToGeoJson(terminals)) : EMPTY_GEOJSON,
  );
  setSourceData(
    map,
    LIVE_POINT_BALL_3D_SOURCE,
    state.show3d ? withGroundElevation(livePointBall3dToGeoJson(terminals)) : EMPTY_GEOJSON,
  );
  setSourceData(map, LIVE_PREDICT_SOURCE, livePredictToGeoJson(terminals));
  setSourceData(map, TASKBOARD_CURSOR_SOURCE, taskboardCursorToGeoJson(state.taskboardCursor));
  setSourceData(
    map,
    LIVE_MARK_SOURCE,
    liveMarksToGeoJson([...liveMarks(), ...state.archiveMarks].filter(shownByTerminal)),
  );
  setSourceData(
    map,
    AREA_SOURCE,
    areasToGeoJson([...circles, ...taskCirclesToAreas(state.taskCircleGoals), ...lrn3dtRingAreas()]),
  );
  setSourceData(map, LRN3DT_PLAN_SOURCE, lrn3dtPlanToGeoJson());
  setSourceData(map, LRN3DT_PLAN_3D_SOURCE, withGroundElevation(lrn3dtPlan3dToGeoJson()));
  refreshPzSaLayers();
  setSourceData(
    map,
    NOTAM_SOURCE,
    withGroundElevation({
      type: 'FeatureCollection',
      features: state.showNotam
        ? currentNotamRegimes().flatMap(
            ({ regime }) => notamToGeoJson(regime, state.notamActiveOverrideIds).features,
          )
        : [],
    }),
  );
  // 飛行場中心の参考円（宇都宮飛行場5NM/10NM/15NMなど）。BluePZ本体と同じ
  // showNotamトグルに相乗りするが、体制（regime）には依存しない参考情報
  // なので侵入警告（updateNotamViolations）には一切関わらない
  setSourceData(map, NOTAM_REF_SOURCE, {
    type: 'FeatureCollection',
    features: state.showNotam
      ? currentNotamRegimes().flatMap(
          ({ area }) => notamReferenceCirclesToGeoJson(area.referenceCircles ?? []).features,
        )
      : [],
  });
  // 2D（fill/line/label）とNOTAM_SOURCEを共有するため、3Dの薄板だけは
  // show3d/showNotam3dの積でレイヤーのvisibilityを切り替える
  // （データ自体は2D表示中と同じものを使い続ける）
  if (map.getLayer('gv-notam-3d-cap')) {
    map.setLayoutProperty(
      'gv-notam-3d-cap',
      'visibility',
      state.show3d && state.showNotam3d ? 'visible' : 'none',
    );
  }
  // PZ/SAの3D円筒も同じ仕組み（データはPZSA_SOURCE/PZSA_POLY_SOURCEを共有、
  // 2D表示中と同じくshowPzSaで既に空にされているので、ここではshow3dだけ見る）
  if (map.getLayer('gv-pzsa-3d')) {
    map.setLayoutProperty('gv-pzsa-3d', 'visibility', state.show3d ? 'visible' : 'none');
  }
  if (map.getLayer('gv-pzsa-poly-3d')) {
    map.setLayoutProperty('gv-pzsa-poly-3d', 'visibility', state.show3d ? 'visible' : 'none');
  }
  setSourceData(
    map,
    HAZARD_SOURCE,
    state.showHazards && state.venue?.hazards ? state.venue.hazards : EMPTY_GEOJSON,
  );
  setSourceData(map, DECLARED_SOURCE, declaredToGeoJson(state.goals, state.selectedGoalKey));
  setSourceData(
    map,
    UTM_GRID_SOURCE,
    state.showGrid && state.gridGeoJson ? state.gridGeoJson : EMPTY_GEOJSON,
  );
  setSourceData(map, SELF_SOURCE, selfToGeoJson(state.myFix));
  // 断面線もここで面倒を見る。スタイル差し替え（ベースマップ切替）のあとは
  // ソースが作り直されるため、redraw() を通らないと復元されない
  redrawSectionLine();
  redrawGoalBearingLine();
  redrawMeasure();
  redrawSketch();
  redrawLrn3dtPoints();
  updateNotamViolations();
  // 体制（regime）は曜日・時間帯で自動的に切り替わるため、設定パネルの表示も
  // ここで追随させる（selectVenue()/override変更時だけだと切り替わりを検知できない）
  renderNotamAreas();

  renderSummary();
  renderGoalCard();
  // 開いているときだけ描き直す（閉じていれば即 return する）
  renderWindProfile();
  renderCrossSection();
  renderApt();
  renderAirspace();
  renderLrn3dt();
  // 追従はここに集約する。自分のGPS・ライブ端末・ダミー航跡のどの経路で
  // 位置が更新されても redraw() は必ず通るため
  applyMapFollow();
}

// --- ライブ接続 --------------------------------------------------------------

/** 追従先セレクトに載っている端末IDの並び。作り直しの要否を判断するために覚えておく */
let followOptionsSignature = '';

/**
 * 追従先セレクトの選択肢を、いま受信できている端末に合わせて更新する。
 *
 * ★ 受信のたびに無条件で作り直さないこと。選択中の項目が入れ替わったり、
 *   スマホで開いているドロップダウンが閉じたりする。端末の顔ぶれが
 *   変わったときだけ作り直す。
 * ★ 追従中の端末が受信できなくなっても選択肢としては残す。競技中に一時的な
 *   受信断で追従先が勝手に外れると、復帰しても追い直してくれないため。
 */
function renderFollowTargetOptions(): void {
  const select = document.getElementById('follow-target') as HTMLSelectElement | null;
  if (!select) return;

  const ids = liveTerminals().map((t) => t.terminalId);
  // 受信が途切れている追従先も選択肢に残す
  if (state.followTarget && state.followTarget !== 'me' && !ids.includes(state.followTarget)) {
    ids.push(state.followTarget);
  }
  ids.sort();

  const signature = ids.join(' ');
  if (signature === followOptionsSignature && select.value === state.followTarget) return;
  followOptionsSignature = signature;

  const live = new Set(liveTerminals().map((t) => t.terminalId));
  select.innerHTML =
    '<option value="">追従しない</option>' +
    '<option value="me">自分の位置</option>' +
    ids
      .map(
        (id) =>
          `<option value="${escapeHtml(id)}">${escapeHtml(id)}${live.has(id) ? '' : '（受信なし）'}</option>`,
      )
      .join('');
  select.value = state.followTarget;
  // 保存されていた端末がまだ一度も現れていない場合、value の代入は無視される。
  // その場合でも state 側は保持したままにする（現れた時点で追従が再開する）
}

/**
 * 受信中の他機（ライブ）の一覧。
 *
 * ★ 同じ内容を2箇所に出す（利用者からの指摘、2026-09-14:「ハンバーガーを
 *   押したらすぐ航跡の一覧が出ると言ったのに出なかった。接続と送信の方には
 *   出ている」）。
 *     ① 「接続と送信」画面（#live-view）の中の#relay-info（元からある場所）
 *     ② 常に開いているパネル先頭「フライト中に使うメニュー」の
 *       #relay-info-quick（ハンバーガー1回で見える場所。利用者が
 *       「航跡の一覧」と呼んでいたのはこちら＝受信中の他機であって、
 *       読み込んだIGCフライトの一覧（renderFlightList）ではなかった）
 *   `.js-relay-info` クラスを持つ要素すべてへ同じHTML・同じ配線を繰り返す。
 *   中身は毎回 state から作り直すので、2箇所の表示がズレる余地は無い。
 */
/** その端末を地図・一覧の集計に出すか（お気に入り・一括表示・個別のチェック。live/favorites.ts） */
function terminalShown(terminalId: string): boolean {
  return isTerminalShown(state.terminalVisibility, terminalId);
}

/** お気に入りに追加・削除して保存し、一覧・地図・設定画面を描き直す */
function toggleFavorite(terminalId: string, favorite = !state.terminalVisibility.favorites.has(terminalId)): void {
  if (!terminalId) return;
  const favorites = state.terminalVisibility.favorites;
  if (favorite) favorites.add(terminalId);
  else favorites.delete(terminalId);
  saveFavorites(favorites);
  // 光彩を「★だけ」にしているとき、付ける機体が変わる
  setLiveTrackGlow(map, state.liveTrackGlow, favorites);
  renderLive();
  renderTerminalColors?.();
  redraw();
}

/** タスクシートのカーソルの既定色（ターゲットに色が無いとき）。地図の他の色と紛れない赤紫 */
const TASKBOARD_CURSOR_COLOR = '#ff2d95';

/**
 * 大会本部のパイバル（風）。ブリッジがオクレンジャーから取り込んだものを
 * 1分おきに見に行き、地図右のボタンのシートに出す（docs/08 §198）。
 */
function setupOfficialPibal(): void {
  // 接続先がブリッジでない（本家の中継サーバーへ直接）ときは、このアプリと同じサーバーに聞く
  const relayUrl = () => {
    const v = el<HTMLInputElement>('relay-url').value.trim();
    return /\/cgi-bin\//.test(v) ? '' : v;
  };
  const feed = new OfficialPibalFeed(relayUrl);
  const panel = setupPibalPanel({
    feed,
    venueId: () => state.venue?.id ?? '',
    // 会場のゴールのうち、名前がCLP（CLP1・CLP-2なども）のもの
    clpCandidates: () =>
      (state.venue?.goals ?? [])
        .filter((g) => /^clp\b|^clp[\s-]?\d|launch/i.test(g.name))
        .map((g) => ({ name: g.name, lat: g.lat, lon: g.lon, elevM: g.alt })),
    // 地形（3D表示の起伏）を使っているときだけ。地形タイルがまだ読めていない
    // 間は0が返るので、0は「分からない」とみなす（実測、一関のCLPで0が返った）
    elevationAt: (lon, lat) => {
      if (!state.terrainEnabled) return null;
      const e = map.queryTerrainElevation([lon, lat]);
      return e == null || e === 0 ? null : e;
    },
    colorForMsl: (altMslM) => colorForAltitudeMode(altMslM, state.altitudeStops, state.altitudeMode, currentGroundRefM()),
    ensureAuth: () => ensureBridgeAuth(relayUrl()),
    // 計測のお知らせのターゲット（グリッド表記）は、タスクシートのターゲットと同じ形式
    onShowTarget: (coordinates, label, cover) =>
      showTaskboardTarget({ coordinates, name: label, mma: '', task: '', color: '' }, cover, '計測のお知らせ'),
  });
  togglePibal = panel.toggle;
  panel.refreshBadge();
  feed.startPolling(60_000);
  // Androidの共有メニューからGV4Dに本文が渡された（manifest の share_target、docs/08 §202）。
  // URLから消してから（開き直しで二重に送らないよう）、風のシートを開いて全員に送る
  const params = new URLSearchParams(location.search);
  const shared = [params.get('share_title'), params.get('share_text'), params.get('share_url')].filter(Boolean).join('\n');
  if (shared.trim()) {
    for (const k of ['share_title', 'share_text', 'share_url']) params.delete(k);
    const rest = params.toString();
    history.replaceState(null, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
    void panel.receiveShared(shared);
  }
}

/**
 * タスクシート（TaskBoard）でターゲットの座標がタップされたら、地図をそこへ
 * 動かしてカーソルを出す（docs/08 §194）。座標はグリッド表記なので、取込みと
 * 同じく選択中の会場を基準に解決する。下からのシートに隠れないよう、
 * シートの上に見えている部分の中心へ寄せる
 */
function showTaskboardTarget(target: TaskBoardTargetMessage, coverBottomPx: number, source = 'タスクシート'): void {
  const r = resolveTaskBoardCoordinates(target.coordinates, gridReferencePoint());
  if (!r.ok) {
    setStatus(`${source}の座標を地図に出せません: ${target.coordinates}（${r.reason}）`, 'warn');
    return;
  }
  const label = [target.task, target.name, target.coordinates].filter(Boolean).join(' ');
  state.taskboardCursor = {
    lat: r.lat,
    lon: r.lon,
    label,
    color: target.color || TASKBOARD_CURSOR_COLOR,
    radiusM: taskBoardRadiusM(target.mma),
  };
  redraw();
  map.easeTo({
    center: [r.lon, r.lat],
    zoom: Math.max(map.getZoom(), 14),
    offset: [mapCenterOffset()[0], -coverBottomPx / 2],
    duration: 700,
  });
  setStatus(`${source}の ${label} を地図に表示しました（カーソルをタップで消せます）`);
}

function renderLive(): void {
  const boxes = document.querySelectorAll<HTMLElement>('.js-relay-info');

  // 端末の顔ぶれが変わったら追従先の選択肢も追従させる（未接続時も通す）
  renderFollowTargetOptions();

  const connected = state.bridge ?? state.live;
  if (!connected && state.debugTerminals.size === 0) {
    for (const box of boxes) box.innerHTML = '<p class="muted">未接続</p>';
    return;
  }

  const favorites = state.terminalVisibility.favorites;
  // お気に入りを先頭に（自チームの機体を探さなくて済むように）
  const list = sortFavoritesFirst(liveTerminals(), favorites);
  const nonFavoriteCount = list.filter((t) => !favorites.has(t.terminalId)).length;

  const rows = list
    .map((t) => {
      const speedKmh = t.latest.speed == null ? null : (t.latest.speed * 3.6).toFixed(1);
      const heading = t.latest.heading == null ? null : Math.round(t.latest.heading);
      const z = toZonedTime(t.latest.t, state.timeZone);
      return (
        `<div class="live-terminal" data-center-terminal-id="${escapeHtml(t.terminalId)}" title="タップすると現在位置へ移動">` +
        `<button type="button" class="fav-btn${favorites.has(t.terminalId) ? ' active' : ''}" ` +
        `data-fav-terminal-id="${escapeHtml(t.terminalId)}" ` +
        `aria-label="${escapeHtml(t.terminalId)}をお気に入り${favorites.has(t.terminalId) ? 'から外す' : 'に追加'}">` +
        `${favorites.has(t.terminalId) ? '★' : '☆'}</button>` +
        `<input type="checkbox" data-terminal-id="${escapeHtml(t.terminalId)}" ${
          terminalShown(t.terminalId) ? 'checked' : ''
        } title="地図上の航跡を表示/非表示" />` +
        `<i style="background:${colorForTerminal(t.terminalId)}"></i>` +
        `<span class="tid">${escapeHtml(t.terminalId)}</span>` +
        // 高度が空（異常値として空にした）ときは「0 ft」ではなく「—」
        `<span class="alt">${
          (t.latest.altPressure ?? t.latest.altMsl) == null ? '—' : Math.round(metersToFeet((t.latest.altPressure ?? t.latest.altMsl) as number))
        } ft</span>` +
        `<button type="button" class="save-btn" data-save-terminal-id="${escapeHtml(t.terminalId)}" title="このデータをIGCで保存（サーバー側のリセット前のデバッグ用）">保存</button>` +
        `<span class="metrics">` +
        `対地速度 ${speedKmh ?? '—'} km/h ・ 方位 ${heading ?? '—'}° ・ ${z.time} ${z.zone}` +
        (t.rejected ? ` ・ <span title="位置が飛んだ点や、高度が物理的にあり得ない点を除いた（高度だけの異常は高度を空にした）">異常値${t.rejected}件除外</span>` : '') +
        `</span>` +
        `</div>`
      );
    })
    .join('');

  let meta: string;
  if (state.bridge) {
    const s = state.bridge.stats;
    const circles = state.bridge.circles.length;
    meta = [
      `SSE 経由 / ${s.updates} 回更新`,
      circles > 0 ? `競技エリア ${circles} 件` : null,
      s.passthroughCount > 0 ? `未知センテンス ${s.passthroughCount} 行（保持中）` : null,
    ]
      .filter(Boolean)
      .join('<br>');
  } else if (state.live) {
    const snap = state.liveSnapshot;
    meta = [
      `ポーリング / ${snap?.polls ?? 0} 回受信 / 直近 ${(snap?.lastBytes ?? 0).toLocaleString()} バイト`,
      snap && snap.circles.size > 0 ? `競技エリア ${[...snap.circles.values()].flat().length} 件` : null,
      snap && snap.passthroughCount > 0 ? `未知センテンス ${snap.passthroughCount} 行（保持中）` : null,
    ]
      .filter(Boolean)
      .join('<br>');
  } else {
    meta = '未接続（デバッグ用ダミー航跡のみ表示中）';
  }

  const body =
    (rows || '<p class="muted">まだ他機のデータがありません</p>') +
    `<div class="live-meta">${meta}</div>`;

  for (const box of boxes) {
    box.innerHTML = body;

    for (const cb of box.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
      cb.onchange = () => {
        const id = cb.dataset.terminalId ?? '';
        setTerminalShown(state.terminalVisibility, id, cb.checked);
        // ★ 同じ端末のチェックボックスが、もう一方の一覧（#relay-info /
        //   #relay-info-quick）にも複製されている。次のライブ更新を待たず、
        //   ここで揃えないと片方だけ古い表示のまま残る（実際に確認済み）
        for (const other of document.querySelectorAll<HTMLInputElement>(
          '.js-relay-info input[type="checkbox"]',
        )) {
          if (other !== cb && other.dataset.terminalId === id) other.checked = cb.checked;
        }
        redraw();
      };
    }

    for (const btn of box.querySelectorAll<HTMLButtonElement>('button[data-fav-terminal-id]')) {
      btn.onclick = () => toggleFavorite(btn.dataset.favTerminalId ?? '');
    }
    // 共有サーバーは日本時間12時にデータをリセットするので、消える前に
    // IGC として保存できるようにしておく（デバッグ用）
    for (const btn of box.querySelectorAll<HTMLButtonElement>('button[data-save-terminal-id]')) {
      btn.onclick = () => {
        const id = btn.dataset.saveTerminalId ?? '';
        const terminal = list.find((t) => t.terminalId === id);
        if (!terminal) return;
        const safeId = id.replace(/[^A-Za-z0-9_-]/g, '_');
        const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');
        downloadTextFile(`${safeId}_${stamp}.igc`, buildIgc(id, terminal.fixes));
      };
    }

    // 行をタップすると、その機の現在位置へ地図を寄せる。どこにいるか瞬時に分かるように
    for (const row of box.querySelectorAll<HTMLElement>('[data-center-terminal-id]')) {
      row.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('input, button')) return; // チェックボックス・保存ボタンは別動作
        const id = row.dataset.centerTerminalId ?? '';
        const t = list.find((x) => x.terminalId === id);
        if (!t) return;
        map.easeTo({
          center: [t.latest.lon, t.latest.lat],
          zoom: Math.max(map.getZoom(), 13),
          offset: mapCenterOffset(),
          duration: 600,
        });
      });
    }
  }
}

/** 下から出るシートの止め位置。半開きは全開と全閉のちょうど中間の意味ではなく、
 *  「地図を半分ほど残したまま中身に触れる」ための独立した第3の位置 */
type PanelState = 'closed' | 'half' | 'open';

/**
 * 下から出るシートを、つまみを掴んで開閉できるようにする。
 *
 * つまみが見えているのにドラッグに反応しないと「掴めるはずなのに動かない」
 * という誤解を招く。閉じているときも `--peek` の分だけ残してあるので、
 * 下端から引き上げる操作がそのまま効く。
 *
 * 全閉・半開・全開の3段階で止まる（全開だと地図が四分の一ほどしか見えず、
 * 半分だけ地図を見ながら中身も触りたいという要望から追加）。
 *
 * 横向き（左からの引き出し）は軸が違うので対象外。
 */
function setupSheetDrag(setPanelState: (state: PanelState) => void): void {
  const panel = el('panel');
  const handle = panel.querySelector<HTMLElement>('.panel-handle');
  if (!handle) return;

  // 下から出るシートになる条件。横に寝かせた状態（左引き出し）は除く
  const bottomSheet = window.matchMedia('(max-width: 700px) and (min-height: 451px)');

  const peekPx = () => {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--peek');
    return Number.parseFloat(v) || 30;
  };

  /**
   * 開いたときの高さ [px]。中身の自然な高さと 72vh の小さいほう。
   * `scrollHeight` はコンテナ自身の overflow による切り詰めを受けない
   * （閉じていて max-height で切られていても、中身の本来の高さを返す）。
   */
  const openHeightPx = () => Math.min(panel.scrollHeight, window.innerHeight * 0.72);

  const currentState = (): PanelState => {
    if (document.body.classList.contains('panel-open')) return 'open';
    if (document.body.classList.contains('panel-half')) return 'half';
    return 'closed';
  };

  let dragging = false;
  let startY = 0;
  let startAt = 0;
  /**
   * 実際に引いたか。`pointerup` の後には必ず `click` が来るので、
   * これを見てタップとドラッグを区別する。
   * ★ 経過時間で区別してはいけない。ゆっくり引くと「タップ」と誤判定され、
   *   開いた直後に click で閉じ直してしまう（実際にそうなっていた）。
   */
  let moved = false;
  /** つまみを掴んだ時点の状態。ドラッグ中の基準点として使う */
  let startState: PanelState = 'closed';
  /** 閉じた高さ [px]（つまみだけ見えている状態） */
  let closedH = 0;
  /** 半開きの高さ [px] */
  let halfH = 0;
  /** 開いた高さ [px] */
  let openH = 0;
  /** いま適用している高さ [px] */
  let current = 0;

  const heightOf = (state: PanelState): number => (state === 'open' ? openH : state === 'half' ? halfH : closedH);

  const onDown = (e: PointerEvent) => {
    if (!bottomSheet.matches) return;
    startState = currentState();
    closedH = peekPx();
    openH = Math.max(openHeightPx(), closedH + 1);
    // 中身が短くopenHがclosedHに近い場合でも、半開きが全開を追い越さないようにする
    halfH = Math.min(Math.max(window.innerHeight * 0.45, closedH + 1), openH);
    current = heightOf(startState);
    startY = e.clientY;
    startAt = Date.now();
    moved = false;
    dragging = true;
    document.body.classList.add('panel-dragging');
    handle.setPointerCapture(e.pointerId);
  };

  /** これ以上動かしたらタップではなくドラッグとみなす */
  const DRAG_THRESHOLD_PX = 8;

  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    const dy = e.clientY - startY; // 上へ引く（画面上で負）ほど開く方向
    if (Math.abs(dy) > DRAG_THRESHOLD_PX) moved = true;
    current = Math.min(Math.max(heightOf(startState) - dy, closedH), openH);
    panel.style.maxHeight = `${current}px`;
  };

  const onUp = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    document.body.classList.remove('panel-dragging');
    // CSS 側（panel-open/panel-half の有無）の指定に戻す
    panel.style.maxHeight = '';

    // 動かしていなければタップ扱い。後続の click に任せる
    if (!moved) return;

    const dy = e.clientY - startY;
    const dt = Math.max(Date.now() - startAt, 1);
    const velocity = dy / dt; // px/ms（下方向が正）

    const order: PanelState[] = ['closed', 'half', 'open'];
    let next: PanelState;
    if (Math.abs(velocity) > 0.4) {
      // 勢いよく弾いたら、掴んだ時点の状態から向きの方向へ1段階だけ動かす
      const startIdx = order.indexOf(startState);
      const dir = velocity < 0 ? 1 : -1; // 上へ引く(負)ほど開く方向へ1段階
      next = order[Math.min(Math.max(startIdx + dir, 0), order.length - 1)];
    } else {
      // ゆっくり動かしたなら、指を離した位置に一番近い段へ
      next = order.reduce((a, b) => (Math.abs(current - heightOf(b)) < Math.abs(current - heightOf(a)) ? b : a));
    }
    setPanelState(next);
  };

  handle.addEventListener('pointerdown', onDown);
  handle.addEventListener('pointermove', onMove);
  handle.addEventListener('pointerup', onUp);
  handle.addEventListener('pointercancel', onUp);

  // つまみを軽く叩いただけでも開閉できるようにする（ドラッグが苦手な場面向け）。
  // 半開きはドラッグでしか出さない位置なので、タップは全閉⇔全開のみ切り替える
  handle.addEventListener('click', () => {
    if (!bottomSheet.matches) return;
    // 引いたのなら onUp が既に決めているので、ここでは何もしない。
    // 引いた量で判定する（時間で見るとゆっくりの操作を取りこぼす）
    if (moved) {
      moved = false;
      return;
    }
    setPanelState(currentState() === 'closed' ? 'open' : 'closed');
  });
}

/**
 * パネルを閉じる（狭い画面の引き出しシートも含む）。setupPanelToggle() の
 * setPanelState を外から呼べるようにするための橋渡し。地図をタップする
 * 系の機能（自機位置の地図タップ登録など）が、パネルの中のボタンから
 * 起動されたときに使う。
 * ★ 狭い画面では、開いたパネル（またはpanel-backdrop）が地図をまるごと
 *   覆う。「地図をタップして…」という案内をパネルの中に置いても、閉じない
 *   限りタップは地図まで届かない（実機で発生。パネルの中のボタンを押した
 *   だけでは地図が触れる状態にならない）。
 */
let closePanel: (() => void) | null = null;

/**
 * 計測モードを畳む（`setupMeasure()` が入れる）。自機位置の「地図をタップして
 * 登録」と計測モードは地図タップの意味が競合するので、互いに排他にするための橋渡し。
 */
let exitMeasureMode: (() => void) | null = null;

/**
 * 狭い画面ではパネルを下から引き出すシートにする。
 * 開閉のたびに地図の寸法を取り直さないと、MapLibre が古い大きさのまま描く。
 */
function setupPanelToggle(): void {
  const toggle = el<HTMLButtonElement>('panel-toggle');
  const backdrop = el('panel-backdrop');
  const selectors = el('selectors');
  const slot = el('selectors-slot');
  const topbar = document.querySelector('.topbar')!;
  const connIndicator = el('conn-indicator');

  /**
   * 狭い画面では、会場・地図の選択を上部バーからパネルへ移す。
   * 上部バーに全部並べるとどれも潰れて押せなくなるため。
   * DOM を移すので、選択状態や結び付けたイベントはそのまま生きる。
   */
  const narrow = window.matchMedia('(max-width: 700px), (max-height: 450px) and (orientation: landscape)');
  const placeSelectors = () => {
    if (narrow.matches) {
      if (selectors.parentElement !== slot) slot.appendChild(selectors);
      slot.hidden = false;
    } else {
      if (selectors.parentElement !== topbar) topbar.insertBefore(selectors, connIndicator);
      slot.hidden = true;
    }
  };
  placeSelectors();
  narrow.addEventListener('change', placeSelectors);

  const panel = el('panel');

  /**
   * 閉じたときに見える「ペック」の高さを、つまみの実測サイズから決める。
   *
   * CSS の固定値（旧: 30px）だと、つまみ自体の高さを変えるたびに数値が
   * ずれ、次のセクション（会場・地図の選択欄）の先頭が透けて見える
   * 事故が起きていた（実機で発生）。つまみの実際の下端に厳密に合わせれば、
   * 次のセクションの中身が漏れることはない。
   */
  const syncPeekHeight = () => {
    const handle = panel.querySelector<HTMLElement>('.panel-handle');
    if (!handle || getComputedStyle(handle).display === 'none') return;
    const peek = Math.ceil(handle.offsetTop + handle.offsetHeight);
    if (peek > 0) document.documentElement.style.setProperty('--peek', `${peek}px`);
  };
  syncPeekHeight();
  narrow.addEventListener('change', syncPeekHeight);
  window.addEventListener('resize', syncPeekHeight);

  const setPanelState = (state: PanelState) => {
    document.body.classList.toggle('panel-open', state === 'open');
    document.body.classList.toggle('panel-half', state === 'half');
    toggle.setAttribute('aria-expanded', String(state !== 'closed'));
    // 半開きのときも、外側をタップすれば閉じられるようにする
    backdrop.hidden = state === 'closed';
    if (state === 'closed') {
      // ★ 閉じたときに見える「ペック」は、パネル要素を transform で
      //   ずらしているだけ。中身をスクロールしたまま閉じると、その
      //   スクロール位置の途中の文字がそのままペックの窓に覗いてしまう
      //   （実機で発生: 「アプリ」欄まで見ていた状態で閉じたら、
      //   そこの文字が残って見えた）。閉じるときは必ず先頭へ戻す。
      panel.scrollTop = 0;
    }
    // 遷移が終わってから寸法を取り直す
    setTimeout(() => map.resize(), 250);
  };
  /** 全閉⇔全開の二択で切り替えたい呼び出し元（外側のボタン等）向けの薄いラッパー */
  const setOpen = (open: boolean) => setPanelState(open ? 'open' : 'closed');
  closePanel = () => setOpen(false);

  toggle.onclick = () => setOpen(!document.body.classList.contains('panel-open'));
  backdrop.onclick = () => setOpen(false);
  setupSheetDrag(setPanelState);

  // 引き出し表示でなくなったら開閉状態を捨てる（サイドパネルに戻るため）
  narrow.addEventListener('change', (e) => {
    if (!e.matches) setOpen(false);
  });

  window.addEventListener('resize', () => map.resize());
  // 端末の回転にも追従する
  window.addEventListener('orientationchange', () => setTimeout(() => map.resize(), 200));
}

let dummyTerminalCounter = 0;

/** 気球らしい高度の山（離陸〜最高高度〜着陸）が一巡する周期 [秒] */
const DUMMY_CYCLE_SEC = 2400; // 40分

/** ダミー航跡の、次の1点を作るための最小限の状態（合成のたびに引き継ぐ） */
interface DummySim {
  lat: number;
  lon: number;
  heading: number;
  /** 周期的な高度の山を計算するための、開始からの経過秒 */
  tick: number;
}

/** タイマーで継続更新中のダミー端末のシミュレーション状態。terminalId をキーにする */
const dummySim = new Map<string, DummySim>();
let dummyTimer: ReturnType<typeof setInterval> | null = null;

function dummyAltitudeAt(tick: number): number {
  const phase = (tick % DUMMY_CYCLE_SEC) / DUMMY_CYCLE_SEC;
  return 80 + Math.sin(phase * Math.PI) * 450; // 離陸〜最高高度〜着陸の山なりを繰り返す
}

/**
 * デバッグ用のダミー航跡を1本作る。
 *
 * 共有サーバーは日本時間12時にライブデータを消してしまうため、実データが
 * 無い間もチェックボックス・タップ詳細・IGC保存などの動作を確認できるよう、
 * 気球らしく緩やかに漂う航跡を地図の中心付近に合成する。
 * 40分ぶんの履歴を一度に作ったうえで、以降はタイマーが1秒おきに1点ずつ
 * 継続して延ばす（ensureDummyTimer 参照）。
 */
function makeDummyTerminal(
  centerLat: number,
  centerLon: number,
): { terminalId: string; fixes: FixSample[]; latest: FixSample } {
  dummyTerminalCounter += 1;
  const terminalId = `DUMMY-${dummyTerminalCounter}`;

  const R_LAT = 110574;
  const R_LON = 111320 * Math.cos((centerLat * Math.PI) / 180);
  const stepSec = 10;
  const n = 240; // 40分ぶん
  const now = Date.now();

  let lat = centerLat;
  let lon = centerLon;
  let heading = Math.random() * 360;

  const fixes: FixSample[] = [];
  for (let i = 0; i < n; i++) {
    if (i > 0) {
      const speed = 2 + Math.random() * 1.5; // m/s。気球らしい低速
      heading = (heading + (Math.random() - 0.5) * 10 + 360) % 360;
      const dx = Math.sin((heading * Math.PI) / 180) * speed * stepSec;
      const dy = Math.cos((heading * Math.PI) / 180) * speed * stepSec;
      lon += dx / R_LON;
      lat += dy / R_LAT;
    }
    fixes.push({
      terminalId,
      t: now - (n - 1 - i) * stepSec * 1000,
      timeQuality: 'derived',
      lat,
      lon,
      altMsl: dummyAltitudeAt(i * stepSec),
      altPressure: dummyAltitudeAt(i * stepSec),
      speed: null,
      heading: null,
      verticalSpeed: null,
      accuracy: 3,
      altAccuracy: 3,
      source: 'balloon',
    });
  }
  deriveMotion(fixes);

  dummySim.set(terminalId, { lat, lon, heading, tick: (n - 1) * stepSec });
  return { terminalId, fixes, latest: fixes[fixes.length - 1] };
}

/** ダミー端末の航跡を1秒ぶんだけ延ばす（タイマーから毎秒呼ばれる） */
function stepDummyTerminal(terminal: { terminalId: string; fixes: FixSample[] }, sim: DummySim): void {
  const R_LAT = 110574;
  const R_LON = 111320 * Math.cos((sim.lat * Math.PI) / 180);
  const speed = 2 + Math.random() * 1.5; // m/s
  sim.heading = (sim.heading + (Math.random() - 0.5) * 3 + 360) % 360;
  sim.lon += (Math.sin((sim.heading * Math.PI) / 180) * speed) / R_LON;
  sim.lat += (Math.cos((sim.heading * Math.PI) / 180) * speed) / R_LAT;
  sim.tick += 1;

  const alt = dummyAltitudeAt(sim.tick);
  terminal.fixes.push({
    terminalId: terminal.terminalId,
    t: Date.now(),
    timeQuality: 'derived',
    lat: sim.lat,
    lon: sim.lon,
    altMsl: alt,
    altPressure: alt,
    speed: null,
    heading: null,
    verticalSpeed: null,
    accuracy: 3,
    altAccuracy: 3,
    source: 'balloon',
  });
  // 1時間ぶんを超えたら古い点を落とす（実端末の maxTrackPoints と同じ考え方）
  if (terminal.fixes.length > 3600) terminal.fixes.splice(0, terminal.fixes.length - 3600);
  deriveMotion(terminal.fixes);
}

/** ダミー端末が1つでもあれば、1秒おきに継続更新するタイマーを動かす */
function ensureDummyTimer(): void {
  if (dummyTimer) return;
  dummyTimer = setInterval(() => {
    if (state.debugTerminals.size === 0) return;
    for (const [id, sim] of dummySim) {
      const terminal = state.debugTerminals.get(id);
      if (!terminal) {
        dummySim.delete(id);
        continue;
      }
      stepDummyTerminal(terminal, sim);
      terminal.latest = terminal.fixes[terminal.fixes.length - 1];
    }
    renderLive();
    redraw();
  }, 1000);
}

function stopDummyTimer(): void {
  if (dummyTimer) clearInterval(dummyTimer);
  dummyTimer = null;
  dummySim.clear();
}

// --- GV Bridgeの合言葉ゲート -------------------------------------------------
//
// GV Bridgeに合言葉（--passphrase）を設定した大会でだけ効く。未設定なら
// `/api/snapshot`は常に200を返すので、ダイアログは一生出ない
// （既定オフ・ローカル開発やゲート未設定の運用に影響しない）。

/** ダイアログの入力待ち。ライブ接続・送信開始の両方から同時に呼ばれても1つで足りる */
let bridgeAuthWaiters: Array<(ok: boolean) => void> = [];
/** このセッションで一度通れば、以後は確認自体を省く（Cookieが切れるまで） */
let bridgeAuthOk = false;

function openBridgeAuthGate(): void {
  const sheet = el('bridge-auth-gate');
  const input = el<HTMLInputElement>('bridge-auth-input');
  const errBox = el('bridge-auth-error');
  input.value = '';
  errBox.hidden = true;
  errBox.classList.remove('error');
  sheet.hidden = false;
  input.focus();
}

function setupBridgeAuthGate(): void {
  const sheet = el('bridge-auth-gate');
  const input = el<HTMLInputElement>('bridge-auth-input');
  const errBox = el('bridge-auth-error');
  const submitBtn = el<HTMLButtonElement>('bridge-auth-submit');

  const finish = (ok: boolean): void => {
    sheet.hidden = true;
    const waiters = bridgeAuthWaiters;
    bridgeAuthWaiters = [];
    for (const w of waiters) w(ok);
  };

  const submit = async (): Promise<void> => {
    const passphrase = input.value;
    if (!passphrase) return;
    submitBtn.disabled = true;
    try {
      const res = await fetch(`${location.origin}/api/login?passphrase=${encodeURIComponent(passphrase)}`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (res.ok) {
        bridgeAuthOk = true;
        finish(true);
        return;
      }
      errBox.textContent = '合言葉が違います';
      errBox.classList.add('error');
      errBox.hidden = false;
      input.select();
    } catch {
      errBox.textContent = '確認できません（オフライン、または接続エラー）';
      errBox.classList.add('error');
      errBox.hidden = false;
    } finally {
      submitBtn.disabled = false;
    }
  };

  submitBtn.onclick = () => void submit();
  el('bridge-auth-cancel').onclick = () => finish(false);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void submit();
  });
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) finish(false);
  });
}

/**
 * ライブ接続・送信を始める直前に呼ぶ。ゲートが無い（または既に通っている）
 * 場合は即座に`true`で返る。未認証なら合言葉ダイアログを出し、入力を待つ。
 * `/api/snapshot`はゲート判定にしか使わない軽いGETで、結果の中身は捨てる。
 */
async function ensureBridgeAuth(baseUrl: string): Promise<boolean> {
  if (bridgeAuthOk) return true;
  const base = (baseUrl || location.origin).replace(/\/+$/, '');
  try {
    const check = await fetch(`${base}/api/snapshot`, { credentials: 'same-origin' });
    if (check.ok) {
      bridgeAuthOk = true;
      return true;
    }
    // 401以外（ネットワークエラー等）は、後続の接続処理側の通常のエラー表示に任せる
    if (check.status !== 401) return true;
  } catch {
    return true;
  }

  return new Promise<boolean>((resolve) => {
    bridgeAuthWaiters.push(resolve);
    openBridgeAuthGate();
  });
}

// --- アプリを開くときの合言葉ゲート -------------------------------------------
//
// ★ 経緯: 元はCaddyのBasic認証で静的ファイル（PWA本体）ごと守っていたが、
//   **HTTP Basic認証には「覚えておく」仕組みが無い**。ブラウザは入力した
//   ID/パスワードをその閲覧セッションの間だけメモリに持つので、ホーム画面
//   から起動し直すたび・更新して再読み込みするたびに聞かれてしまう。
//   利用者から「更新のたびにIDとパスワードを聞かれるのを止めたい」との
//   指摘を受け、こちらへ置き換えた（docs/07 §3.6・docs/08 §104）。
//
// ★ 何を守れて、何を守れないか。ここで止まるのは**アプリの画面**だけで、
//   HTML/JS自体はURLを知っていれば取得できる（Caddy側の認証を外したため）。
//   大会データ（位置・航跡）はGV Bridge側の合言葉ゲートが引き続き
//   サーバーで守る。この整理は利用者と相談のうえ選んだもの。
//
// ★ 正解の判定は必ずサーバーに任せる（`POST /api/login`）。合言葉を
//   このアプリに埋め込むと、配布物を読むだけで分かってしまう。

const APP_UNLOCKED_KEY = 'gv4d-app-unlocked';

/** この端末で一度でも合言葉を通したか（合言葉そのものは保存しない） */
function appUnlockedOnThisDevice(): boolean {
  try {
    return localStorage.getItem(APP_UNLOCKED_KEY) === '1';
  } catch {
    // プライベートモード等で読めないだけなら、ゲートを出して確認すればよい
    return false;
  }
}

function rememberAppUnlocked(): void {
  try {
    localStorage.setItem(APP_UNLOCKED_KEY, '1');
  } catch {
    // 覚えられなくても今回のセッションは通す（次回また聞かれるだけ）
  }
}

/**
 * アプリの入り口。合言葉ゲートが要るなら、通るまでここで待つ。
 *
 * ★ 一度通った端末では**ネットワークを一切見ない**。オフラインでも
 *   これまで通り起動できる必要があるため（PWAとして当然の要件）。
 *
 * ★ サーバーの状態が読めないとき（オフライン・GV Bridgeが落ちている・
 *   開発サーバーでAPIが無い等）は通す。ここで固まると、ゲートが目的では
 *   ない場面（機内でのオフライン利用、Bridgeの再起動中）でアプリごと
 *   使えなくなるため。守りたいのは「URLを開いた通りすがりの人」で、
 *   その人にとってサーバーは必ず生きている。
 */
async function ensureAppUnlocked(): Promise<void> {
  if (appUnlockedOnThisDevice()) return;

  try {
    const check = await fetch(`${location.origin}/api/snapshot`, { credentials: 'same-origin' });
    // 200 = ゲート未設定、またはCookieで既に通っている
    if (check.ok) {
      rememberAppUnlocked();
      return;
    }
    if (check.status !== 401) return;
  } catch {
    return;
  }

  await new Promise<void>((resolve) => {
    const sheet = el('app-gate');
    const input = el<HTMLInputElement>('app-gate-input');
    const errBox = el('app-gate-error');
    const submitBtn = el<HTMLButtonElement>('app-gate-submit');

    const submit = async (): Promise<void> => {
      const passphrase = input.value;
      if (!passphrase) return;
      submitBtn.disabled = true;
      try {
        const res = await fetch(`${location.origin}/api/login?passphrase=${encodeURIComponent(passphrase)}`, {
          method: 'POST',
          credentials: 'same-origin',
        });
        if (res.ok) {
          rememberAppUnlocked();
          // ログインでCookieが張られたので、ライブ接続側の確認も済んでいる
          bridgeAuthOk = true;
          sheet.hidden = true;
          resolve();
          return;
        }
        errBox.textContent = '合言葉が違います';
      } catch {
        errBox.textContent = '確認できません（オフライン、または接続エラー）';
      }
      errBox.classList.add('error');
      errBox.hidden = false;
      input.select();
      submitBtn.disabled = false;
    };

    submitBtn.onclick = () => void submit();
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void submit();
    });
    input.value = '';
    errBox.hidden = true;
    sheet.hidden = false;
    input.focus();
  });
}

/**
 * 上部バーの接続・送信インジケーター（利用者からの要望、2026-09-13）。
 * 操作パネルを開かなくても、ライブ接続・自機送信が動いているかを
 * 一目で分かるようにする（黒=止まっている、緑=動いている）。
 *
 * ★ 当初、狭い画面では文字ラベルを隠す・さらに狭い画面ではハンバーガー
 *   アイコンの隅の点だけにする、という案にしていたが、「点だけでは意味が
 *   分からない」との指摘を受け、常に文字ラベルを出す方針にした。その
 *   代わり役割モードの3ボタンをドロップダウン化して幅を空けている
 *   （renderModeSelect参照。利用者からの要望、2026-09-13）。
 */
function setConnDot(on: boolean): void {
  el('conn-dot').classList.toggle('on', on);
  el('conn-label').textContent = on ? '接続' : '非接続';
  syncQuickLive?.();
}

/**
 * ★ コードレビュー指摘（2026-09-13）: 当初`!!state.transmitter`（送信オブジェクトが
 *   あるかどうか）だけで判定していたが、`SelfPositionTransmitter`はGPSの権限拒否や
 *   通信エラー（HTTP 500等）が起きても`stop()`するまで存在し続けるため、実際には
 *   1件も送れていないのに緑の「送信中」になってしまっていた。
 *   実際に送信できている(`status==='sending'`)かどうかで判定し、エラー中は
 *   （黒の「停止」と紛れないよう）別の色で「送信エラー」だと分かるようにする。
 */
function setTxDot(status: 'off' | 'sending' | 'error'): void {
  const dot = el('tx-dot');
  dot.classList.toggle('on', status === 'sending');
  dot.classList.toggle('warn', status === 'error');
  el('tx-label').textContent =
    status === 'sending' ? '送信中' : status === 'error' ? '送信エラー' : '送信停止';
  syncQuickLive?.();
}

/**
 * 上部バーのワンアクション操作（「接続と送信」「接続のみ」）。
 *
 * ★ なぜ既存のボタンを「押す」のではなく関数を共有するのか
 *   （利用者からの要望、2026-09-14:「ワンアクションで接続と送信をオンオフ
 *   出来るボタンを用意」）。接続・送信の開始には、合言葉ゲート・端末IDの
 *   未入力／重複確認・画面ロックといった前提条件がいくつも噛んでいる。
 *   ここで手続きを書き直すと必ず取りこぼすので、`setupLive()` /
 *   `setupTransmit()` が持っているハンドラそのものを呼ぶ。
 *   `HTMLElement.click()` でも同じことはできるが、`async`なハンドラの完了を
 *   待てず「接続が済んでから送信を始める」順番を守れないため使わない。
 */
let toggleLiveConnection: (() => Promise<void>) | null = null;
let toggleSelfTransmit: (() => Promise<void>) | null = null;
let isSelfTransmitActive: (() => boolean) | null = null;
/**
 * 「必ず送信する」意図を反映する（＝送信しないチェックを外す）。起動時に
 * 自動開始した「GPS表示のみ」が動いている状態で上部バーの「接続と送信」を
 * 押したときに、`toggleSelfTransmit()`を呼ぶ前に使う
 */
let requestSelfTransmit: (() => void) | null = null;
/** ボタンの見た目を今の状態に合わせ直す。setConnDot/setTxDot から呼ばれる */
let syncQuickLive: (() => void) | null = null;

function setupQuickBar(): void {
  const both = el<HTMLButtonElement>('quick-live');
  const connOnly = el<HTMLButtonElement>('quick-conn');

  const isConnected = (): boolean => !!(state.live || state.bridge);
  const isTransmitting = (): boolean => isSelfTransmitActive?.() ?? false;
  // オーディエンスは自機位置を発信しない（#my-position-section 自体が隠れる）。
  // 「接続と送信」ボタン自体も出していない（style.css）が、保険として見る
  const canTransmit = (): boolean => state.mode !== 'audience';

  /*
   * ★ 2つのボタンは「別々の関心事のトグル」にしてある
   *   （利用者からの追加要望、2026-09-14:「位置情報を送らないで、接続だけ
   *   したいときも多い」）。
   *     ・#quick-live … **両方動いているときだけ**「入」。押すと両方そろえる／両方止める
   *     ・#quick-conn … ライブ接続が動いていれば「入」。押すと接続だけを入り切りする
   *   こうしておくと「接続のみで始めて、あとから送信も足す」（＝#quick-live を
   *   押す）、「送信は続けたまま受信だけ止める」（＝#quick-conn を押す）が
   *   どちらも自然に出せる。
   *
   * ★ この2つより長い文言・多いボタンは入らない。縦持ち393px幅の2段目は、
   *   状態表示（最悪ケースは「非接続」＋「送信エラー」）とこの2つで
   *   ちょうど埋まる（実測）。
   */
  const sync = (): void => {
    const conn = isConnected();
    const bothOn = conn && isTransmitting();
    both.classList.toggle('on', bothOn);
    both.textContent = bothOn ? 'すべて停止' : '接続と送信';
    both.setAttribute('aria-pressed', String(bothOn));
    connOnly.classList.toggle('on', conn);
    connOnly.textContent = conn ? '切断' : '接続のみ';
    connOnly.setAttribute('aria-pressed', String(conn));
  };
  sync();
  syncQuickLive = sync;

  /** 合言葉の確認などで待たされる間、両方のボタンの二度押しを止める */
  const guard = async (run: () => Promise<void>): Promise<void> => {
    both.disabled = true;
    connOnly.disabled = true;
    try {
      await run();
    } finally {
      both.disabled = false;
      connOnly.disabled = false;
      sync();
    }
  };

  both.onclick = () =>
    void guard(async () => {
      if (isConnected() && isTransmitting()) {
        await toggleLiveConnection?.();
        await toggleSelfTransmit?.();
        setStatus('接続と送信を止めました');
        return;
      }
      if (!isConnected()) await toggleLiveConnection?.();
      if (!canTransmit()) return;
      if (!isTransmitting()) {
        // 起動時に自動開始した「GPS表示のみ」が動いていても、ここは必ず
        // 実際の送信を始める（利用者が明示的に「接続と送信」を押した意図）
        requestSelfTransmit?.();
        await toggleSelfTransmit?.();
      }
      // 端末IDの未入力・重複や合言葉の失敗で送信が始まらなかったとき。
      // 理由は各ハンドラが setStatus で出しているので、ここでは「どこで
      // 直せばいいか」だけを補い、設定画面をそのまま開く
      if (!isTransmitting()) {
        openLiveSheet?.();
        return;
      }
      // ★ 成功したことも必ず言う。言わないと、直前の失敗（「端末IDを入力して
      //   ください」等）が状態表示に残ったままになり、実際には動いているのに
      //   止まっているように読めてしまう（実測で確認）
      setStatus('接続と送信を始めました');
    });

  connOnly.onclick = () =>
    void guard(async () => {
      // 送信には一切触れない。送信中に押せば「送信は続けたまま受信だけ止める」
      const wasConnected = isConnected();
      await toggleLiveConnection?.();
      if (wasConnected) setStatus('ライブ接続を切りました（送信の設定はそのまま）');
    });

}

function setupLive(): void {
  const button = el<HTMLButtonElement>('relay-toggle');
  const urlInput = el<HTMLInputElement>('relay-url');

  const kindSelect = el<HTMLSelectElement>('relay-kind');

  // GV Bridge は vite.config.ts の proxy で /api・/cgi-bin をこのアプリと
  // 同一オリジンに載せてあるので、既定値は「いま開いているURL」でよい。
  // LAN の IP でも、ngrok 等のトンネル URL でも、そのままつながる
  // （HTTPS のトンネルを1本張るだけで4Gなど LAN 外からのテストもできる）。
  const defaultBridgeUrl = () => location.origin;

  // 接続方式を変えたら、既定の接続先も入れ替える
  kindSelect.onchange = () => {
    urlInput.value =
      kindSelect.value === 'bridge' ? defaultBridgeUrl() : 'http://127.0.0.1:8080/cgi-bin/sr.cgi';
  };
  if (!urlInput.value.trim() && kindSelect.value === 'bridge') {
    urlInput.value = defaultBridgeUrl();
  }

  const disconnect = () => {
    state.live?.stop();
    state.bridge?.stop();
    state.live = null;
    state.bridge = null;
    state.liveSnapshot = null;
    button.textContent = '接続';
    button.classList.remove('connected');
    setConnDot(false);
    renderLive();
    redraw();
  };

  /**
   * ライブ接続が「合言葉が切れているのに気付けず再接続を繰り返すだけ」に
   * ならないようにする仕組み（2026-09-12、PWAで実際に発生）。
   *
   * `BridgeConnection`は、`EventSource`がエラーを繰り返して回復しない
   * ときにこの関数を呼び、`fetch`でステータスコードを読める形で
   * 本当に未認証（401）かどうかを確かめる。ここでは判定だけを返し、
   * ゲートを開く・繋ぎ直すのは`onStatus`側（'auth-error'受信時）の責務にする
   * （bridge.ts側にUIの知識を持たせないため）。
   */
  const quickAuthCheck = async (url: string): Promise<boolean> => {
    try {
      const res = await fetch(`${(url || location.origin).replace(/\/+$/, '')}/api/snapshot`, {
        credentials: 'same-origin',
      });
      return res.status !== 401;
    } catch {
      // ネットワークエラー等は判定不能。誤ってゲートを出さない（既存の
      // ensureBridgeAuth・ensureAppUnlockedと同じ「守れない場面では通す」方針）
      return true;
    }
  };

  /** 合言葉切れの回復処理を二重に走らせない（複数の経路から呼ばれうるため） */
  let authRecoveryInFlight = false;

  const recoverFromAuthFailure = (url: string): void => {
    if (authRecoveryInFlight) return;
    authRecoveryInFlight = true;
    // 一度メモリに残った「認証済み」を捨てて必ず確認し直す。
    // ensureBridgeAuth自身はbridgeAuthOkがtrueだと確認をスキップして
    // しまうため、ここで明示的に作り直す（これが今回の根本原因）
    bridgeAuthOk = false;
    disconnect();
    setStatus('ライブ接続: 合言葉の確認が必要です', 'warn');
    void (async () => {
      try {
        if (await ensureBridgeAuth(url)) connectBridge(url);
      } finally {
        authRecoveryInFlight = false;
      }
    })();
  };

  const connectBridge = (url: string): void => {
    // ブリッジ経由。上流のポーリングはブリッジ側が行うので、
    // こちらは押し出しを待つだけでよい
    const bridge = new BridgeConnection(url, { checkAuth: () => quickAuthCheck(url) });
    bridge.onUpdate = () => {
      renderLive();
      redraw();
    };
    bridge.onStatus = (status, message) => {
      if (status === 'auth-error') {
        recoverFromAuthFailure(url);
        return;
      }
      onStatus(status, message);
    };
    bridge.start();
    state.bridge = bridge;
  };

  /**
   * PWAをホーム画面から開き直した（バックグラウンドから復帰した）ときの
   * 保険。iOSはバックグラウンドのPWAをそのままの状態で凍結することがあり、
   * その間に合言葉Cookieが切れても、`EventSource`のエラーは（何も動いて
   * いないので）一切発火せず、`BridgeConnection`側の`checkAuth`にも
   * 辿り着けない。画面に戻ってきたタイミングで能動的に確認する
   * （利用者から「PWAを消して入れ直すと合言葉を聞かれた」という報告を
   * 受けての対応、2026-09-12）。
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const bridge = state.bridge;
    if (!bridge) return;
    const url = urlInput.value.trim();
    void (async () => {
      if (!(await quickAuthCheck(url))) recoverFromAuthFailure(url);
    })();
  });

  const onStatus = (status: string, message: string) => {
    setStatus(`ライブ接続: ${message}`, status === 'error' ? 'warn' : 'info');
    if (status === 'error') button.classList.remove('connected');
    else if (status === 'connected') button.classList.add('connected');
    setConnDot(status === 'connected');
  };

  /**
   * 「接続」ボタンの本体。上部バーの「まとめて開始／停止」からも同じものを
   * 呼ぶので、名前を付けて `toggleLiveConnection` に渡せるようにしてある
   * （合言葉ゲート等の前提条件を二重に書かないため）。
   */
  const toggle = async (): Promise<void> => {
    if (state.live || state.bridge) {
      disconnect();
      return;
    }

    const url = urlInput.value.trim();
    if (kindSelect.value === 'bridge') {
      // 合言葉ゲートが掛かっている大会なら、ここで一度だけ確認する
      // （未設定・既に通っていれば即座に素通り）
      if (!(await ensureBridgeAuth(url))) {
        setStatus('合言葉が確認できないため接続していません', 'warn');
        return;
      }
      connectBridge(url);
    } else {
      // 中継サーバーへ直結。ダミーサーバー相手の確認用で、
      // 本物のサーバーへはブラウザからつながらない（docs/05）
      const venueMap = state.venue ? mapConfigOf(state.venue.id) : null;
      const live = new LiveConnection({
        serverUrl: url,
        pollingMs: MODES.find((m) => m.id === state.mode)!.pollingMs,
        zone: venueMap?.utmZone,
      });
      live.onUpdate = (snapshot) => {
        state.liveSnapshot = snapshot;
        renderLive();
        redraw();
      };
      live.onStatus = onStatus;
      live.start();
      state.live = live;
    }

    button.textContent = '切断';
    renderLive();
  };
  button.onclick = toggle;
  toggleLiveConnection = toggle;

  // サーバー側のリセットや未接続時でも動作確認できるよう、ダミー航跡を
  // その場で合成して足せるようにしておく（実データではない）。
  // 追加後は1秒おきに1点ずつ延び続け、実際のライブ受信と同じ見え方になる
  el<HTMLButtonElement>('live-add-dummy').onclick = () => {
    const c = map.getCenter();
    const dummy = makeDummyTerminal(c.lat, c.lng);
    state.debugTerminals.set(dummy.terminalId, dummy);
    ensureDummyTimer();
    renderLive();
    redraw();
  };
  el<HTMLButtonElement>('live-clear-dummy').onclick = () => {
    state.debugTerminals.clear();
    stopDummyTimer();
    renderLive();
    redraw();
  };

  renderLive();
}

/**
 * 「端末ごとの色を設定」画面。既定は端末IDのハッシュから自動で決まるが、
 * 毎回違う色になると分かりにくいとの指摘があり、パレットから固定できるようにする。
 * 現在ライブ受信中の端末に加えて、過去に手動で色を決めた端末（今はオフラインでも）
 * も一覧に出す（あとで戻ってきたときに設定が見えなくならないように）。
 */
/** 「お気に入りと航跡の色」画面を開いていれば描き直す（setupTerminalColorsで設定） */
let renderTerminalColors: (() => void) | undefined;

function setupTerminalColors(): void {
  const sheet = el('terminal-colors');
  const list = el('terminal-colors-list');
  const idInput = el<HTMLInputElement>('favorite-id-input');

  const knownIds = (): string[] => {
    const ids = new Set<string>();
    for (const t of liveTerminals()) ids.add(t.terminalId);
    for (const id of state.terminalColors.keys()) ids.add(id);
    // お気に入りは今受信していなくても出す（あとで戻ってきたときのため・削除できるように）
    for (const id of state.terminalVisibility.favorites) ids.add(id);
    // 警告を止めた端末も、今は受信していなくても出す（戻せるように）
    for (const id of state.notamMutedTerminals) ids.add(id);
    return sortFavoritesFirst(
      [...ids].map((terminalId) => ({ terminalId })),
      state.terminalVisibility.favorites,
    ).map((x) => x.terminalId);
  };

  // 航跡の光彩（なし／★だけ／すべて、docs/08 §232）
  const glowBox = el('terminal-glow-modes');
  const renderGlow = (): void => {
    for (const b of glowBox.querySelectorAll<HTMLButtonElement>('button[data-glow]')) {
      const on = b.dataset.glow === state.liveTrackGlow;
      b.classList.toggle('active', on);
      b.setAttribute('aria-pressed', String(on));
    }
  };
  glowBox.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-glow]');
    const mode = b?.dataset.glow as LiveTrackGlowMode | undefined;
    if (!mode) return;
    state.liveTrackGlow = mode;
    try {
      localStorage.setItem(LIVE_TRACK_GLOW_KEY, mode);
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    setLiveTrackGlow(map, mode, state.terminalVisibility.favorites);
    renderGlow();
  });
  renderGlow();

  const render = (): void => {
    const ids = knownIds();
    if (ids.length === 0) {
      list.innerHTML = '<p class="muted">まだ受信した端末がありません。上の欄に端末IDを入れるとお気に入りに追加できます</p>';
      return;
    }
    list.innerHTML = ids
      .map((id) => {
        const current = state.terminalColors.get(id);
        const fav = state.terminalVisibility.favorites.has(id);
        const muted = state.notamMutedTerminals.has(id);
        const swatches = LIVE_COLORS.map(
          (c) =>
            `<button type="button" class="swatch${c === current ? ' active' : ''}" ` +
            `data-terminal="${escapeHtml(id)}" data-color="${c}" style="background:${c}" ` +
            `aria-label="${escapeHtml(id)}を${c}にする"></button>`,
        ).join('');
        return (
          `<div class="terminal-color-row">` +
          `<button type="button" class="fav-btn${fav ? ' active' : ''}" data-fav-terminal-id="${escapeHtml(id)}" ` +
          `aria-label="${escapeHtml(id)}をお気に入り${fav ? 'から外す' : 'に追加'}">${fav ? '★' : '☆'}</button>` +
          `<span class="terminal-color-name">${escapeHtml(id)}</span>` +
          `<button type="button" class="notam-mute-btn${muted ? ' active' : ''}" data-notam-mute-toggle="${escapeHtml(id)}" ` +
          `title="Blue PZ の天井超過の警告（${muted ? '止めている。押すと出す' : '出している。押すと止める'}）" ` +
          `aria-pressed="${muted}">${muted ? '🔕' : '🔔'}</button>` +
          `<div class="terminal-color-swatches">${swatches}` +
          `<button type="button" class="swatch reset${current ? '' : ' active'}" ` +
          `data-terminal="${escapeHtml(id)}" data-color="">自動</button>` +
          `</div></div>`
        );
      })
      .join('');

    for (const btn of list.querySelectorAll<HTMLButtonElement>('button[data-terminal]')) {
      btn.onclick = () => {
        const id = btn.dataset.terminal ?? '';
        const color = btn.dataset.color ?? '';
        if (color) state.terminalColors.set(id, color);
        else state.terminalColors.delete(id);
        saveTerminalColors(state.terminalColors);
        setTerminalColorOverrides(state.terminalColors);
        redraw();
        renderLive();
        render();
      };
    }
    for (const btn of list.querySelectorAll<HTMLButtonElement>('button[data-fav-terminal-id]')) {
      btn.onclick = () => toggleFavorite(btn.dataset.favTerminalId ?? '');
    }
    for (const btn of list.querySelectorAll<HTMLButtonElement>('button[data-notam-mute-toggle]')) {
      btn.onclick = () => {
        const id = btn.dataset.notamMuteToggle ?? '';
        if (state.notamMutedTerminals.has(id)) state.notamMutedTerminals.delete(id);
        else state.notamMutedTerminals.add(id);
        saveNotamMuted(state.notamMutedTerminals);
        updateNotamViolations();
        render();
      };
    }
  };
  renderTerminalColors = () => {
    if (!sheet.hidden) render();
  };

  const addFavoriteFromInput = (): void => {
    const id = resolveTerminalIdInput(idInput.value, knownIds());
    if (!id) return;
    idInput.value = '';
    toggleFavorite(id, true);
    setStatus(`${id} をお気に入りに追加しました`);
  };
  el<HTMLButtonElement>('favorite-id-add').onclick = addFavoriteFromInput;
  idInput.onkeydown = (e) => {
    if (e.key === 'Enter') addFavoriteFromInput();
  };

  const openSheet = (): void => {
    render();
    sheet.hidden = false;
  };
  // 「接続と送信」の画面の中のボタンと、メニューの先頭のボタン（docs/08 §227）の、どちらからも同じ画面を開く
  el<HTMLButtonElement>('terminal-colors-open').onclick = openSheet;
  el<HTMLButtonElement>('terminal-colors-open-quick').onclick = () => {
    closePanel?.();
    openSheet();
  };
  el('terminal-colors-close').onclick = () => {
    sheet.hidden = true;
  };
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.hidden = true;
  });
}

// --- ウィンドプロファイラ ----------------------------------------------------

/**
 * 高度ビンの幅 [m]。
 *
 * ★ 原典GoalViewの norden_bombsight は 20m 固定だったが（docs/01 §6）、
 *   あれは落下経路を積分するための**計算**の粒度。ここは**表示**の粒度で、
 *   20mだと 2500m を 125 段に刻むことになり、400px の画面では矢印が
 *   3px 間隔で重なって読めない（実機で確認）。100m にすると 25 段前後になり、
 *   高度色の刻み（500ft ≒ 152m）ともほどよく噛み合う。
 */
const WIND_ALT_BAND_M = 100;
/** 時間ビンの幅 [ms]。1Hzの観測をこの粒度に間引いて矢印が潰れないようにする */
const WIND_TIME_BUCKET_MS = 60_000;
/** ピンチズームで縮められる最小の時間スパン [ms]（間引き粒度未満に縮めても意味がない） */
const WIND_MIN_TIME_SPAN_MS = WIND_TIME_BUCKET_MS;
/** ピンチズームで縮められる最小の高度スパン [m] */
const WIND_MIN_ALT_SPAN_M = WIND_ALT_BAND_M * 2;
/**
 * 戦略プランナー用の風プロファイルを作るときの時間ビン幅 [ms]。
 * 十分大きな値を渡してgridWindSamplesの時間ビンを1つに畳み、
 * 「高度ごとに1層」のプロファイルにする（collectAllTrackWindSamples参照）。
 */
const WIND_PROFILE_SINGLE_TIME_BUCKET_MS = Number.MAX_SAFE_INTEGER;

/** ウィンドプロファイラの表示範囲（時間・高度とも絶対値） */
interface WindViewRange {
  from: number;
  to: number;
  altMin: number;
  altMax: number;
}

/**
 * ズーム・パン後の表示範囲を、範囲ドロップダウンで決まる基準範囲に対して
 * クランプする。スパンが基準範囲を超えて広がる（＝ドロップダウンより
 * ズームアウトする）ことはできない。ドロップダウンは「どのデータを対象に
 * するか」、ピンチズームは「その中のどこを拡大して見るか」という役割分担。
 */
function clampWindZoom(zoom: WindViewRange, base: WindViewRange): WindViewRange {
  const clampSpan = (span: number, min: number, max: number): number =>
    Math.min(Math.max(span, min), Math.max(min, max));

  const baseTimeSpan = base.to - base.from;
  const timeSpan = clampSpan(zoom.to - zoom.from, WIND_MIN_TIME_SPAN_MS, baseTimeSpan);
  let from = Math.min(Math.max(zoom.from, base.from), base.to - timeSpan);
  from = Math.max(from, base.from);
  const to = from + timeSpan;

  const baseAltSpan = base.altMax - base.altMin;
  const altSpan = clampSpan(zoom.altMax - zoom.altMin, WIND_MIN_ALT_SPAN_M, baseAltSpan);
  let altMin = Math.min(Math.max(zoom.altMin, base.altMin), base.altMax - altSpan);
  altMin = Math.max(altMin, base.altMin);
  const altMax = altMin + altSpan;

  return { from, to, altMin, altMax };
}

/**
 * 直近に実際に描画した範囲とピクセル寸法。ジェスチャー側でピクセル⇔データの
 * 変換とクランプに使う（`renderWindProfile()`の末尾で更新する）。
 * `base` はズーム前のドロップダウン由来の範囲（クランプの外枠）。
 */
let lastWindRender: (WindViewRange & { base: WindViewRange; plotW: number; plotH: number }) | null = null;

/**
 * 表示中の全機ぶんの風サンプルを集める。
 *
 * ライブ端末と読み込み済みIGCの両方を、同じ「風の観測点」として並べて扱う
 * （どちらも中身は FixSample[] なので同じ計算に通せる）。
 * 地図で非表示にした端末はここでも除き、見えているものと揃える。
 */
function collectWindSeries(): WindProfileSeries[] {
  const out: WindProfileSeries[] = [];

  for (const t of liveTerminals()) {
    if (!terminalShown(t.terminalId)) continue;
    const samples = gridWindSamples(fixesToWindSamples(t.fixes), {
      altBandM: WIND_ALT_BAND_M,
      timeBucketMs: WIND_TIME_BUCKET_MS,
    });
    if (samples.length > 0) {
      out.push({ terminalId: t.terminalId, label: t.terminalId, samples });
    }
  }

  for (const [i, f] of state.flights.entries()) {
    if (state.hiddenFlights.has(i)) continue;
    const samples = gridWindSamples(fixesToWindSamples(f.fixes), {
      altBandM: WIND_ALT_BAND_M,
      timeBucketMs: WIND_TIME_BUCKET_MS,
    });
    if (samples.length > 0) {
      out.push({ terminalId: f.terminalId, label: f.label, samples });
    }
  }

  // パイバル観測（クルーが手動計測して記録した分）。会場ごとに保存されている
  // ので、その場で読む（他のvenue依存データと同じく localStorage を正として読む）
  if (state.venue) {
    const pibalSamples = loadPibalSamples(state.venue.id);
    if (pibalSamples.length > 0) {
      out.push({ terminalId: 'pibal', label: 'パイバル', samples: pibalSamples });
    }
  }

  return out;
}

/**
 * LRN/3DT戦略プランナー用の風プロファイル（windSource: 'tracks'）。
 *
 * collectWindSeries()は端末ごとに個別にgridWindSamples()を呼ぶ（=端末を
 * またいだ平均はしない）が、こちらは「見えている全機の航跡をまとめて
 * 1本の風プロファイルにする」ため、terminalIdを揃えてから1回だけ
 * gridWindSamples()に通す。gridWindSamples()のセル分けキーは
 * `${terminalId ?? ''}\0${altBin}\0${timeBin}`（wind.ts）なので、
 * terminalIdを空にして1回で通せば、同じ高度帯・時間帯にいる複数機の
 * 観測がベクトル平均で自然に合成される。
 *
 * ★ 時間ビンも1つに畳むこと（WIND_PROFILE_SINGLE_TIME_BUCKET_MS）。
 *   プランナーが欲しいのは「高度→風」の1本のプロファイルであって、
 *   ウィンドプロファイラのような「時間×高度」の面ではない。時間ビンを
 *   残すと同じ高度に何十層も並び、planThreeDtRouteの層並び総当たり
 *   （層数nに対してO(n^4)）が組み合わせ爆発してタブごと落ちる
 *   （71分のIGC1本で73層→2,700万通り。実機でこれを踏み、読み込み済みの
 *   航跡ごと初期画面に戻る不具合として報告された）。
 */
function collectAllTrackWindSamples(): WindSample[] {
  const pooled: WindSample[] = [];
  for (const t of liveTerminals()) {
    if (!terminalShown(t.terminalId)) continue;
    for (const s of fixesToWindSamples(t.fixes)) pooled.push({ ...s, terminalId: undefined });
  }
  for (const [i, f] of state.flights.entries()) {
    if (state.hiddenFlights.has(i)) continue;
    for (const s of fixesToWindSamples(f.fixes)) pooled.push({ ...s, terminalId: undefined });
  }
  return gridWindSamples(pooled, {
    altBandM: WIND_ALT_BAND_M,
    timeBucketMs: WIND_PROFILE_SINGLE_TIME_BUCKET_MS,
  });
}

/**
 * ウィンドプロファイラの描き直し。
 *
 * redraw() の末尾から呼ばれるので、シートが閉じている間は即座に戻って
 * 計算コストを一切払わないこと（1秒ごとに走る経路のため）。
 */
function renderWindProfile(): void {
  const sheet = el('wind-profile');
  if (sheet.hidden) return;

  const chart = el('wind-profile-chart');
  const series = collectWindSeries();

  // 時間の範囲。0（全体）なら実データの端から端まで
  let to = Date.now();
  let from = to - state.windRangeMin * 60_000;
  if (state.windRangeMin === 0) {
    let min = Infinity;
    let max = -Infinity;
    for (const s of series) {
      for (const w of s.samples) {
        if (w.t < min) min = w.t;
        if (w.t > max) max = w.t;
      }
    }
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
      from = min;
      to = max;
    } else {
      from = to - 3600_000;
    }
  }

  // 凡例には「いま見えているもの」だけを出す。範囲外の航跡まで並べると
  // 「載っているのに矢印が無い」ことになって却って分かりにくい。
  // ★ ここは常に基準範囲（ドロップダウン由来）で判定する。ズームで絞ると
  //   凡例がちらつくため、あえてズームの影響を受けないようにしてある。
  const visible = series.filter((s) => s.samples.some((w) => w.t >= from && w.t <= to));

  const base: WindViewRange = { from, to, altMin: 0, altMax: topAltitudeM(visible, state.altitudeStops) };
  let view = base;
  if (state.windProfileZoom) {
    view = clampWindZoom(state.windProfileZoom, base);
    state.windProfileZoom = view; // クランプ後の値を書き戻し、毎フレームのドリフトを防ぐ
  }

  const rect = chart.getBoundingClientRect();
  const width = Math.max(320, Math.round(rect.width));
  const height = Math.max(200, Math.round(rect.height));
  chart.innerHTML = buildWindProfileSvg({
    series: visible,
    from: view.from,
    to: view.to,
    altMin: view.altMin,
    altMax: view.altMax,
    stops: state.altitudeStops,
    tz: state.timeZone,
    width,
    height,
    // 背景の南北成分色帯を、サンプルの間引き格子（gridWindSamples）と
    // 同じ大きさで描けるように渡す
    altBandM: WIND_ALT_BAND_M,
    timeBucketMs: WIND_TIME_BUCKET_MS,
  });
  lastWindRender = {
    ...view,
    base,
    plotW: Math.max(1, width - WIND_PROFILE_PAD.left - WIND_PROFILE_PAD.right),
    plotH: Math.max(1, height - WIND_PROFILE_PAD.top - WIND_PROFILE_PAD.bottom),
  };

  el('wind-profile-legend').innerHTML = visible
    .map(
      (s) =>
        `<span class="wp-terminal" style="color:${colorForTerminal(s.terminalId)}">` +
        `<span class="wp-swatch"></span>${escapeHtml(s.label)}</span>`,
    )
    .join('');
}

/** 選択した矢印の向きを示す拡大版。塗り・輪郭の色分けはチャート内の矢印と揃える */
function windDirectionArrowHtml(terminalId: string, alt: number, dir: number, size: number): string {
  return buildDirectionArrowSvg(dir, {
    size,
    fill: colorForAltitude(alt, state.altitudeStops),
    stroke: colorForTerminal(terminalId),
  });
}

/**
 * 選択した矢印（=風のサンプル1点）の読み値。下の窓・ポップアップの両方から
 * 同じものを使う（`arrowSize` だけ呼び分ける）。
 * 地図の `fixReadoutHtml` と同じ単位・書式に揃える（高度=ft、速度=km/h、
 * 方位=度、時刻=会場のタイムゾーン）。昇降率は WindSample に無いので出さない。
 */
function windSampleDetailsHtml(
  terminalId: string,
  label: string,
  w: { t: number; dir: number; speed: number; alt: number },
  arrowSize: number,
): string {
  const z = toZonedTime(w.t, state.timeZone);
  return (
    `<span class="wp-readout-arrow">${windDirectionArrowHtml(terminalId, w.alt, w.dir, arrowSize)}</span>` +
    `<span><b>${escapeHtml(label)}</b></span>` +
    `<span>${z.time} ${z.zone}</span>` +
    `<span>方位 <b>${Math.round(w.dir)}</b>°</span>` +
    `<span>風速 <b>${(w.speed * 3.6).toFixed(1)}</b> km/h</span>` +
    `<span>高度 <b>${Math.round(metersToFeet(w.alt))}</b> ft</span>`
  );
}

/** コンパス窓が開いている間だけ方位センサーを購読する。applyMode() から
 *  クルー以外に切り替わったときに閉じられるよう、外から呼べる形にしておく */
let closeCompassIfOpen: (() => void) | null = null;

/** パイバル窓も同じ理由（applyMode() から閉じられるよう、外から呼べる形にしておく） */
let closePibalIfOpen: (() => void) | null = null;

/** 下の窓（読み値ボックス）の矢印サイズ。テキストと1行に並ぶので控えめに */
const WIND_READOUT_ARROW_SIZE = 34;
/** クリック位置に出すポップアップの矢印サイズ。こちらが「一目で分かる」主役 */
const WIND_POPUP_ARROW_SIZE = 56;
/** ポップアップがチャートの端で見切れないようにする余白 [px] */
const WIND_POPUP_MARGIN = 8;
/** これ以上動かしたらタップ/クリックではなくドラッグ・ピンチとみなす（パネルの引き出しつまみと同じ値） */
const WIND_DRAG_THRESHOLD_PX = 8;
/** ダブルタップ/ダブルクリックとみなす、1回目のタップからの経過時間の上限 [ms] */
const WIND_DOUBLE_TAP_MS = 350;
/** ダブルタップとみなす、1回目・2回目のタップ位置のずれの上限 [px] */
const WIND_DOUBLE_TAP_PX = 24;

function setupWindProfile(): void {
  const sheet = el('wind-profile');
  const rangeSelect = el<HTMLSelectElement>('wind-profile-range');
  const canvas = el('wind-profile-canvas');
  const chart = el('wind-profile-chart');
  const readout = el('wind-profile-readout');
  const popup = el<HTMLDivElement>('wind-profile-popup');

  // 羽根の目盛り・南北成分の色帯は定数で決まるので、開閉のたびではなく
  // 起動時に1回だけ組み立てる（redraw()は1秒ごとに走るため、ここに置くと無駄）
  el('wind-profile-barb-legend').innerHTML = buildBarbLegendSvg('currentColor');
  el('wind-profile-ns-legend').innerHTML = buildNsColorLegendSvg(160);

  const clearSelection = (): void => {
    readout.hidden = true;
    readout.innerHTML = '';
    popup.hidden = true;
    popup.innerHTML = '';
  };

  rangeSelect.value = String(state.windRangeMin);
  rangeSelect.onchange = () => {
    state.windRangeMin = Number(rangeSelect.value);
    try {
      localStorage.setItem(WIND_RANGE_KEY, String(state.windRangeMin));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    clearSelection(); // 表示される矢印の顔ぶれが変わるので、古い選択を残さない
    state.windProfileZoom = null; // 範囲が変われば基準範囲も変わるので、拡大状態も一緒にリセットする
    renderWindProfile();
  };

  // --- ピンチズーム／パン／ダブルタップ(クリック)リセット ----------------
  //
  // ジェスチャーライブラリは使わず、Pointer Eventsを直接組み立てる（引き出し
  // つまみ`setupSheetDrag()`の`moved`/`DRAG_THRESHOLD_PX`/`setPointerCapture`
  // と同じ流儀）。ピンチもパンも「基準点(anchor)のデータ値を、現在の中点の
  // 画面位置へ写像し続ける」という同じ式で表せる（指1本なら倍率1のパンに、
  // 指2本なら倍率が指の間隔比になるピンチになる）ので、1本指・2本指を
  // 分けて実装せず共通の計算にしている。
  const activePointers = new Map<number, { x: number; y: number }>();
  /** ジェスチャー（またはその継ぎ足し）を開始した時点のズーム状態と、そのときの指の距離・中点 */
  let gestureStart: { zoom: WindViewRange; dist: number; midX: number; midY: number } | null = null;
  /** このポインタ列でしきい値を超える移動があったか（タップ/クリックとの区別用） */
  let dragMoved = false;
  let rafPending = false;
  let lastTap: { at: number; x: number; y: number } | null = null;

  /** 現在の実効ズーム範囲（未ズームなら直近の描画結果＝基準範囲を使う） */
  const effectiveZoom = (): WindViewRange | null =>
    state.windProfileZoom ??
    (lastWindRender
      ? { from: lastWindRender.from, to: lastWindRender.to, altMin: lastWindRender.altMin, altMax: lastWindRender.altMax }
      : null);

  const midpoint = (): { x: number; y: number } => {
    const pts = [...activePointers.values()];
    if (pts.length === 1) return pts[0];
    const [a, b] = pts;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };
  const pointerDistance = (): number => {
    const pts = [...activePointers.values()];
    if (pts.length < 2) return 0;
    const [a, b] = pts;
    return Math.hypot(b.x - a.x, b.y - a.y);
  };

  /** 現在の指の構成から、ジェスチャーの基準点を取り直す（開始・継ぎ足しの両方で使う） */
  const reseedGesture = (): void => {
    const zoom = effectiveZoom();
    if (!zoom) {
      gestureStart = null;
      return;
    }
    const mid = midpoint();
    gestureStart = { zoom, dist: pointerDistance(), midX: mid.x, midY: mid.y };
  };

  const scheduleGestureRender = (): void => {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      renderWindProfile();
    });
  };

  chart.addEventListener('pointerdown', (e: PointerEvent) => {
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    chart.setPointerCapture(e.pointerId);
    dragMoved = false;
    reseedGesture();
  });

  chart.addEventListener('pointermove', (e: PointerEvent) => {
    if (!activePointers.has(e.pointerId)) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!gestureStart || !lastWindRender) return;

    // 全体表示のまま指1本で動かしても実質パンできない（クランプで基準範囲へ
    // 戻る）ので、無駄な再描画をしないよう早期returnする
    if (activePointers.size === 1 && state.windProfileZoom === null) return;

    const mid = midpoint();
    if (Math.hypot(mid.x - gestureStart.midX, mid.y - gestureStart.midY) > WIND_DRAG_THRESHOLD_PX) {
      dragMoved = true;
    }

    const { plotW, plotH, base } = lastWindRender;
    const startZoom = gestureStart.zoom;
    const scaleFactor =
      activePointers.size === 2 && gestureStart.dist > 0 ? gestureStart.dist / Math.max(1, pointerDistance()) : 1;
    const newTimeSpan = (startZoom.to - startZoom.from) * scaleFactor;
    const newAltSpan = (startZoom.altMax - startZoom.altMin) * scaleFactor;

    const chartRect = chart.getBoundingClientRect();
    // 基準点（ジェスチャー開始時の中点）が指すデータ値。ズーム後もこの値が
    // 「現在の」中点の画面位置に来るよう、from/to・altMin/altMaxを求める
    const anchorRelX = (gestureStart.midX - chartRect.left - WIND_PROFILE_PAD.left) / plotW;
    const anchorRelY = (gestureStart.midY - chartRect.top - WIND_PROFILE_PAD.top) / plotH;
    const anchorT = startZoom.from + anchorRelX * (startZoom.to - startZoom.from);
    const anchorAlt = startZoom.altMax - anchorRelY * (startZoom.altMax - startZoom.altMin);

    const curRelX = (mid.x - chartRect.left - WIND_PROFILE_PAD.left) / plotW;
    const curRelY = (mid.y - chartRect.top - WIND_PROFILE_PAD.top) / plotH;
    const newFrom = anchorT - curRelX * newTimeSpan;
    const newAltMax = anchorAlt + curRelY * newAltSpan;

    state.windProfileZoom = clampWindZoom(
      { from: newFrom, to: newFrom + newTimeSpan, altMin: newAltMax - newAltSpan, altMax: newAltMax },
      base,
    );
    scheduleGestureRender();
  });

  const onPointerEnd = (e: PointerEvent): void => {
    const wasTap = activePointers.size === 1 && !dragMoved;
    activePointers.delete(e.pointerId);

    if (activePointers.size > 0) {
      reseedGesture(); // 残った指で継続（2本→1本のパンへ切り替わる場合など）
      return;
    }
    gestureStart = null;
    if (!wasTap) {
      lastTap = null;
      return;
    }
    const now = Date.now();
    if (
      lastTap &&
      now - lastTap.at <= WIND_DOUBLE_TAP_MS &&
      Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) <= WIND_DOUBLE_TAP_PX
    ) {
      // ダブルタップ／ダブルクリック（マウスもpointerイベントを発するのでここで一緒に拾える）
      state.windProfileZoom = null;
      renderWindProfile();
      lastTap = null;
    } else {
      lastTap = { at: now, x: e.clientX, y: e.clientY };
    }
  };
  chart.addEventListener('pointerup', onPointerEnd);
  chart.addEventListener('pointercancel', (e: PointerEvent) => {
    activePointers.delete(e.pointerId);
    lastTap = null;
    if (activePointers.size > 0) reseedGesture();
    else gestureStart = null;
  });

  // 矢印（.wp-arrow）・当たり判定の円（.wp-hit）のどちらから拾っても同じ
  // data-* を持っているので、選ばず data-t の有無だけで判定する
  chart.addEventListener('click', (e) => {
    if (dragMoved) {
      // 直前のドラッグ/ピンチの終端で発生するclickはポップアップを開かない
      dragMoved = false;
      return;
    }
    const target = (e.target as HTMLElement).closest<SVGElement>('[data-t]');
    if (!target) {
      // 矢印以外（チャートの背景）をクリックしたらポップアップを閉じる。
      // 下の窓はそのまま残す（前回選んだものの詳細を見返せるように）
      popup.hidden = true;
      return;
    }
    const terminalId = target.getAttribute('data-terminal') ?? '';
    const label = target.getAttribute('data-label') ?? '';
    const t = Number(target.getAttribute('data-t'));
    const dir = Number(target.getAttribute('data-dir'));
    const speed = Number(target.getAttribute('data-speed'));
    const alt = Number(target.getAttribute('data-alt'));
    const sample = { t, dir, speed, alt };

    readout.innerHTML = windSampleDetailsHtml(terminalId, label, sample, WIND_READOUT_ARROW_SIZE);
    readout.hidden = false;

    popup.innerHTML = windSampleDetailsHtml(terminalId, label, sample, WIND_POPUP_ARROW_SIZE);
    popup.hidden = false;
    // 一旦左上に置いてから実測して調整する（幅・高さは中身が入るまで分からない）
    popup.style.left = '0px';
    popup.style.top = '0px';
    const canvasRect = canvas.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const rawX = e.clientX - canvasRect.left;
    const rawY = e.clientY - canvasRect.top;
    // クリック位置を中心に置き、チャートの外へはみ出さないようクランプする
    const maxX = Math.max(WIND_POPUP_MARGIN, canvasRect.width - popupRect.width - WIND_POPUP_MARGIN);
    const maxY = Math.max(WIND_POPUP_MARGIN, canvasRect.height - popupRect.height - WIND_POPUP_MARGIN);
    const x = Math.min(Math.max(rawX - popupRect.width / 2, WIND_POPUP_MARGIN), maxX);
    const y = Math.min(Math.max(rawY - popupRect.height - 14, WIND_POPUP_MARGIN), maxY);
    popup.style.left = `${x}px`;
    popup.style.top = `${y}px`;
  });

  el<HTMLButtonElement>('wind-profile-open').onclick = () => {
    sheet.hidden = false;
    clearSelection(); // 前回開いていたときの選択を持ち越さない
    state.windProfileZoom = null; // 前回のズーム状態も持ち越さない
    // 開いた瞬間に描く（次の redraw を待たない）
    renderWindProfile();
  };
  el('wind-profile-close').onclick = () => {
    sheet.hidden = true;
  };
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.hidden = true;
  });
  // 画面の回転・リサイズでSVGの寸法が変わるので描き直す
  window.addEventListener('resize', renderWindProfile);
}

// --- 鉛直断面ビュー ----------------------------------------------------------

/** 高度[m]。気圧高度があればそちらを優先する（地図・3D航跡と同じ規約） */
function fixAltM(f: FixSample): number {
  return f.altPressure ?? f.altMsl ?? 0;
}

/** 断面に写す航跡を集める。ライブ端末と読み込み済みフライトの両方が対象 */
function collectCrossSectionTracks(): CrossSectionTrack[] {
  const out: CrossSectionTrack[] = [];

  for (const t of liveTerminals()) {
    if (!terminalShown(t.terminalId)) continue;
    out.push({
      terminalId: t.terminalId,
      label: t.terminalId,
      points: t.fixes.map((f) => ({ lat: f.lat, lon: f.lon, alt: fixAltM(f), t: f.t })),
      latest: {
        lat: t.latest.lat,
        lon: t.latest.lon,
        alt: fixAltM(t.latest),
        t: t.latest.t,
      },
    });
  }

  for (const [i, f] of state.flights.entries()) {
    if (state.hiddenFlights.has(i)) continue;
    out.push({
      terminalId: f.terminalId,
      label: f.label,
      points: f.fixes.map((x) => ({ lat: x.lat, lon: x.lon, alt: fixAltM(x), t: x.t })),
      // 過去のフライトに「現在位置」は無い（最新点を現在地として強調すると
      // ライブ中の機体と見分けがつかなくなる）
      latest: null,
    });
  }

  return out;
}

/**
 * 断面に描く天井を集める。現在有効な体制のゾーンだけを対象にし、
 * 実効天井（条件付き引き上げを考慮した値）を使う。
 * ここが地図のバナー・侵入判定と食い違うと「断面では余裕があるのに
 * 警告が出ている」ことになるので、必ず同じ effectiveCeilingFt を通す。
 */
function collectCrossSectionCeilings(): CrossSectionCeiling[] {
  if (!state.showNotam) return [];
  const out: CrossSectionCeiling[] = [];
  for (const { regime } of currentNotamRegimes()) {
    for (const zone of regime.zones) {
      out.push({
        label: zone.label,
        ceilingFt: effectiveCeilingFt(zone, regime.id, state.notamActiveOverrideIds),
        points: zone.points,
      });
    }
  }
  return out;
}

/**
 * 断面に重ねる風。ウィンドプロファイラと同じく、気球そのものを風の観測点として
 * 使う（航跡から速度・方位を引き直したもの）。
 *
 * ★ こちらは時間ではなく**位置**で切るので、gridWindSamples（時間バケット）は
 *   通さず生のサンプルを渡す。断面座標での格子化は binSectionWind が行う。
 *   開発メモの「位置軸を足した4D風場」に一歩踏み込む部分。
 */
function collectCrossSectionWind(): CrossSectionWindSample[] {
  const out: CrossSectionWindSample[] = [];
  const push = (fixes: FixSample[]): void => {
    for (const w of fixesToWindSamples(fixes)) {
      out.push({ lat: w.lat, lon: w.lon, alt: w.alt, dir: w.dir, speed: w.speed });
    }
  };
  for (const t of liveTerminals()) {
    if (!terminalShown(t.terminalId)) continue;
    push(t.fixes);
  }
  for (const [i, f] of state.flights.entries()) {
    if (state.hiddenFlights.has(i)) continue;
    push(f.fixes);
  }
  return out;
}

/** いまの state から断面の定義を組み立てる。中心がまだ無ければ null */
function currentSectionGeometry(): SectionGeometry | null {
  const c = state.crossSection.center;
  if (!c) return null;
  return {
    centerLat: c.lat,
    centerLon: c.lon,
    bearingDeg: state.crossSection.bearingDeg,
    halfLengthM: state.crossSection.halfLengthM,
    depthM: state.crossSection.depthM,
  };
}

/**
 * 鉛直断面ビューの描き直し。
 *
 * redraw() の末尾から呼ばれるので、シートが閉じている間は即座に戻って
 * 計算コストを一切払わないこと（1秒ごとに走る経路のため）。
 */
function renderCrossSection(): void {
  const sheet = el('cross-section');
  if (sheet.hidden) return;
  const geom = currentSectionGeometry();
  if (!geom) return;

  const chart = el('cross-section-chart');
  const tracks = collectCrossSectionTracks();
  const rect = chart.getBoundingClientRect();

  chart.innerHTML = buildCrossSectionSvg({
    geom,
    tracks,
    ceilings: collectCrossSectionCeilings(),
    volumes: state.airspaces.map((a) => ({
      label: a.label,
      points: a.points,
      floorFt: a.floorFt,
      ceilingFt: a.ceilingFt,
    })),
    wind: state.crossSection.showWind ? collectCrossSectionWind() : [],
    // 高度を持つ宣言ゴール＝空中ゴール。判定容積の寸法は大会のタスクデータ
    // 側の値なので、アプリが既定値を作らない（無ければ点として描かれる）
    aerialGoals: state.goals
      .filter((g) => g.altFt != null)
      .map((g) => ({
        label: g.name,
        lat: g.lat,
        lon: g.lon,
        altFt: g.altFt as number,
        radiusM: g.goalRadiusM ?? null,
        altToleranceFt: g.altToleranceFt ?? null,
        selected: g.key === state.selectedGoalKey,
      })),
    stops: state.altitudeStops,
    width: Math.max(320, Math.round(rect.width)),
    height: Math.max(200, Math.round(rect.height)),
  });

  el('cross-section-bearing-label').textContent = sectionBearingLabel(geom.bearingDeg);

  // 凡例は「実際に断面へ写っているもの」だけにする（写っていない機体まで
  // 並べると、線が無いのに名前があることになって却って分かりにくい）
  const visible = tracks.filter((t) =>
    t.points.some((p) => {
      const { along, across } = projectToSection(p.lat, p.lon, geom);
      return Math.abs(across) <= geom.depthM && Math.abs(along) <= geom.halfLengthM;
    }),
  );
  el('cross-section-legend').innerHTML = visible
    .map(
      (t) =>
        `<span class="xs-terminal" style="color:${colorForTerminal(t.terminalId)}">` +
        `<span class="xs-swatch"></span>${escapeHtml(t.label)}</span>`,
    )
    .join('');
}

function setupCrossSection(): void {
  const sheet = el('cross-section');
  const bearingSlider = el<HTMLInputElement>('cross-section-bearing');
  const bearingInput = el<HTMLInputElement>('cross-section-bearing-input');
  const depthSelect = el<HTMLSelectElement>('cross-section-depth');
  const lengthSelect = el<HTMLSelectElement>('cross-section-length');

  const applyBearing = (raw: number): void => {
    // 0〜359に丸め込む。スライダーの端をまたいで回しても破綻させない
    const v = ((Math.round(raw) % 360) + 360) % 360;
    state.crossSection.bearingDeg = v;
    bearingSlider.value = String(v);
    bearingInput.value = String(v);
    renderCrossSection();
    redrawSectionLine();
  };

  bearingSlider.oninput = (e) => applyBearing(Number((e.target as HTMLInputElement).value));
  bearingInput.oninput = (e) => applyBearing(Number((e.target as HTMLInputElement).value));

  depthSelect.onchange = () => {
    state.crossSection.depthM = Number(depthSelect.value);
    renderCrossSection();
    redrawSectionLine();
  };
  lengthSelect.onchange = () => {
    state.crossSection.halfLengthM = Number(lengthSelect.value);
    renderCrossSection();
    redrawSectionLine();
  };

  const windToggle = el<HTMLInputElement>('cross-section-wind');
  windToggle.checked = state.crossSection.showWind;
  windToggle.onchange = () => {
    state.crossSection.showWind = windToggle.checked;
    renderCrossSection();
  };

  el<HTMLButtonElement>('cross-section-bearing-map').onclick = () => {
    applyBearing(map.getBearing());
  };
  el<HTMLButtonElement>('cross-section-recenter').onclick = () => {
    const c = map.getCenter();
    state.crossSection.center = { lat: c.lat, lon: c.lng };
    renderCrossSection();
    redrawSectionLine();
  };

  el<HTMLButtonElement>('cross-section-open').onclick = () => {
    // 開いた時点の地図の中心・向きを断面の初期値にする
    const c = map.getCenter();
    state.crossSection.center = { lat: c.lat, lon: c.lng };
    sheet.hidden = false;
    applyBearing(map.getBearing()); // 中で描画まで済ませる
  };
  el('cross-section-close').onclick = () => {
    sheet.hidden = true;
    redrawSectionLine(); // 閉じたら地図の断面線も消す
  };
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) {
      sheet.hidden = true;
      redrawSectionLine();
    }
  });
  window.addEventListener('resize', renderCrossSection);
}

/**
 * 地図に断面線（と奥行きの帯）を描く。シートを閉じたあとに「どこを切ったか」
 * が地図上で分かるようにするためのもの。シートが閉じていれば消す。
 */
function redrawSectionLine(): void {
  const geom = currentSectionGeometry();
  const show = !el('cross-section').hidden && geom !== null;
  setSourceData(map, SECTION_SOURCE, show ? sectionToGeoJson(geom!) : EMPTY_GEOJSON);
}

/**
 * 選択中ゴールへの点線モード。
 *
 * 自機位置を「手動で座標を入力」にしていて仮の点が置いてある間は、
 * その点を起点にして常に表示する（利用者要望: タップして点を置いた瞬間から
 * 見たい、そのたびに別途「線を表示」を押し直したくない）。それ以外は
 * 従来通り、「線を表示」トグルON・現在地既知（`goalMetricsHtml`と同じ
 * `currentPosition()`）のときだけ描く。
 */
function redrawGoalBearingLine(): void {
  const goal = state.goals.find((g) => g.key === state.selectedGoalKey);
  const manualHere =
    el<HTMLSelectElement>('my-position-source').value === 'manual' ? state.manualCoords : null;
  const here = manualHere ?? currentPosition();
  const show = (manualHere != null || state.showGoalBearingLine) && goal != null && here != null;
  setSourceData(map, GOAL_BEARING_SOURCE, show ? goalBearingLineToGeoJson(here, goal) : EMPTY_GEOJSON);
}

// --- プランナーの入力点（タップ・ドラッグで置く） -----------------------------

const LRN3DT_FIELD_LABELS: Record<Lrn3dtPointField, string> = {
  start: '開始点',
  circleCenter: '円の中心',
  ringCenter: 'リング中心',
  entry: '進入点',
};

/** いまのモードで意味のある入力点だけを描く（他モードの点は隠す。3dtだけ2点） */
function lrn3dtVisiblePointFields(): Lrn3dtPointField[] {
  const mode = state.lrn3dtForm.mode;
  if (mode === 'lrn-time') return ['start'];
  if (mode === 'lrn-circle') return ['circleCenter'];
  return ['ringCenter', 'entry'];
}

/**
 * プランナーの入力点を地図へ流す。`redraw()` の末尾から呼ぶ
 * （ベースマップ切替でソースが作り直されるため）。モードを閉じても消さない
 * （計算済みのルートと同じ扱い。§93/94のmeasureと同じ理由）。
 */
function redrawLrn3dtPoints(): void {
  const points = lrn3dtVisiblePointFields()
    .map((field) => {
      const coords = state.lrn3dtPoints[field];
      return coords ? { field, label: LRN3DT_FIELD_LABELS[field], ...coords } : null;
    })
    .filter((p): p is { field: Lrn3dtPointField; label: string; lat: number; lon: number } => p !== null);
  setSourceData(map, LRN3DT_POINT_SOURCE, lrn3dtPickPointsToGeoJson(points));
}

/** インポートしたスケッチの表示を、state.sketches/state.sketchVisibleに合わせて描き直す */
function redrawSketch(): void {
  const show = state.sketches.length > 0 && state.sketchVisible;
  setSourceData(map, SKETCH_SOURCE, show ? sketchesToGeoJson(state.sketches) : EMPTY_GEOJSON);
}

// --- 計測モード（直線・円・面積） ---------------------------------------------

/** 新しい円を置いたときの半径 [m]。数値欄でも地図のタップでもすぐ直せる */
const MEASURE_DEFAULT_RADIUS_M = 500;
/** 頂点を掴める距離・スナップが効く距離 [画面px]。実機の指の精度を見込んで広め */
const MEASURE_GRAB_PX = 22;
const MEASURE_SNAP_PX = 20;

/**
 * 計測の図形を地図へ流す。`redraw()` の末尾から呼ぶ
 * （ベースマップ切替でソースが作り直されるため）。
 * ★ モードを抜けても消さない。消えるのは「消す」か「表示」を外したときだけ。
 */
function redrawMeasure(): void {
  const show = state.measure.visible && state.measure.shapes.length > 0;
  setSourceData(map, MEASURE_SOURCE, show ? measureShapesToGeoJson(state.measure.shapes) : EMPTY_GEOJSON);
}

/** いま描き足している図形（無ければnull） */
function measureDraft(): MeasureShape | null {
  return state.measure.shapes.find((s) => s.id === state.measureDraftId) ?? null;
}

/**
 * タップ位置を、近くの「意味のある点」へ吸着させる。
 * 会場のゴール・宣言ゴール・自機位置・手動の仮の点・ライブ他機が候補。
 * ★ `queryRenderedFeatures` は使わない。レイヤーの有無（表示のON/OFFや
 *   スタイル差し替え）に左右されず、候補を自分で持つほうが読みやすい。
 */
function snapMeasurePoint(lngLat: maplibregl.LngLat): { lat: number; lon: number; label: string | null } {
  const candidates: Array<{ lat: number; lon: number; label: string }> = [];
  for (const g of state.venue?.goals ?? []) candidates.push({ lat: g.lat, lon: g.lon, label: `ゴール${g.name}` });
  for (const g of state.goals) candidates.push({ lat: g.lat, lon: g.lon, label: `ゴール${g.name}` });
  if (state.myFix) candidates.push({ lat: state.myFix.lat, lon: state.myFix.lon, label: '自機位置' });
  if (state.manualCoords) {
    candidates.push({ lat: state.manualCoords.lat, lon: state.manualCoords.lon, label: '仮の点' });
  }
  for (const t of liveTerminals()) {
    candidates.push({ lat: t.latest.lat, lon: t.latest.lon, label: t.terminalId });
  }

  const tapped = map.project(lngLat);
  let best: { lat: number; lon: number; label: string; d: number } | null = null;
  for (const c of candidates) {
    const p = map.project([c.lon, c.lat]);
    const d = Math.hypot(p.x - tapped.x, p.y - tapped.y);
    if (d <= MEASURE_SNAP_PX && (!best || d < best.d)) best = { ...c, d };
  }
  return best
    ? { lat: best.lat, lon: best.lon, label: best.label }
    : { lat: lngLat.lat, lon: lngLat.lng, label: null };
}

/** カードの「結果」欄。いま描いている図形を主役にし、他は件数だけ伝える */
function renderMeasureCard(): void {
  const card = el('measure-card');
  if (card.hidden) return;

  for (const tool of ['line', 'circle', 'polygon'] as MeasureTool[]) {
    const btn = el<HTMLButtonElement>(`measure-tool-${tool}`);
    const on = state.measureTool === tool;
    btn.classList.toggle('subtle', !on);
    btn.setAttribute('aria-pressed', String(on));
  }
  el('measure-radius-row').hidden = state.measureTool !== 'circle';

  const visToggle = el<HTMLButtonElement>('measure-visibility-toggle');
  const hidden = !state.measure.visible;
  visToggle.textContent = hidden ? '表示する' : '隠す';
  visToggle.classList.toggle('subtle', !hidden);
  visToggle.setAttribute('aria-pressed', String(hidden));

  const draft = measureDraft();
  const shown = draft ?? state.measure.shapes[state.measure.shapes.length - 1] ?? null;
  if (shown?.kind === 'circle') {
    const radiusInput = el<HTMLInputElement>('measure-radius');
    // 入力中の欄は書き換えない（打っている途中の値が消えてしまうため）
    if (document.activeElement !== radiusInput) {
      radiusInput.value = (shown.radiusM / 1000).toFixed(2);
    }
  }

  const lines: string[] = [];
  if (shown) lines.push(`<div>${escapeHtml(measureSummary(shown))}</div>`);
  if (state.measure.shapes.length > 1) {
    lines.push(`<div class="muted">図形 ${state.measure.shapes.length} 件</div>`);
  }
  if (!shown) {
    lines.push('<div class="muted">地図をタップすると点を置きます（近くのゴールに吸着します）</div>');
  }
  if (!state.measure.visible) {
    lines.push('<div class="muted">いまは非表示にしています（上のボタンで戻せます）</div>');
  }
  el('measure-readout').innerHTML = lines.join('');
  // ★ 中身を入れてから測ること。先に測るとカードがまだ低く、凡例・縮尺
  //   ラベルが計測カードの裏に潜る（実際に一度潜らせた）
  syncGoalCardHeight();
}

/** 図形を変えたあとに必ず通す（保存・地図・カードをまとめて更新する） */
function commitMeasure(): void {
  saveMeasureStore();
  redrawMeasure();
  renderMeasureCard();
}

function setupMeasure(): void {
  const openBtn = el<HTMLButtonElement>('measure-open');
  const card = el('measure-card');
  const radiusInput = el<HTMLInputElement>('measure-radius');
  let nextId = 1;
  const makeId = (): string => `m${Date.now().toString(36)}-${nextId++}`;

  const setMeasureMode = (on: boolean): void => {
    state.mapTapMode = on ? 'measure' : 'normal';
    state.measureDraftId = null;
    card.hidden = !on;
    openBtn.setAttribute('aria-pressed', String(on));
    map.getCanvas().style.cursor = on ? 'crosshair' : '';
    // 計測カードもゴールカードと同じく、凡例・縮尺ラベルを押し上げる
    // （開いたときは renderMeasureCard() の末尾で測り直す）
    if (on) renderMeasureCard();
    else syncGoalCardHeight();
  };
  exitMeasureMode = () => {
    if (state.mapTapMode === 'measure') setMeasureMode(false);
  };

  openBtn.onclick = () => {
    const next = state.mapTapMode !== 'measure';
    if (next) {
      // 狭い画面ではパネルが地図を覆っているので、開いたままだとタップが届かない
      closePanel?.();
      setStatus('計測モード: 地図をタップして点を置きます');
    }
    setMeasureMode(next);
  };
  el('measure-close').onclick = () => setMeasureMode(false);

  for (const tool of ['line', 'circle', 'polygon'] as MeasureTool[]) {
    el<HTMLButtonElement>(`measure-tool-${tool}`).onclick = () => {
      // 同じ道具をもう一度押したら「新しい図形を始める」。道具を変えたときも同じ
      state.measureTool = tool;
      state.measureDraftId = null;
      renderMeasureCard();
    };
  }

  el('measure-undo').onclick = () => {
    const draft = measureDraft();
    if (draft && draft.kind !== 'circle' && draft.points.length > 1) {
      draft.points.pop();
    } else {
      // 描きかけが無い（または点が1つ）なら、図形ごと最後の1件を取り消す
      const target = draft ?? state.measure.shapes[state.measure.shapes.length - 1];
      if (!target) return;
      state.measure.shapes = state.measure.shapes.filter((s) => s.id !== target.id);
      state.measureDraftId = null;
    }
    commitMeasure();
  };

  el('measure-clear').onclick = () => {
    if (state.measure.shapes.length === 0) return;
    if (!confirm(`計測の図形${state.measure.shapes.length}件をすべて消します。よろしいですか？`)) return;
    state.measure.shapes = [];
    state.measureDraftId = null;
    commitMeasure();
    setStatus('計測の図形を消しました');
  };

  // 消さずに一時的に隠す。パネルの #toggle-measure と同じ state.measure.visible を
  // 介して連動する（モードを抜けたあとはパネル側が唯一の切替口になる）
  el<HTMLButtonElement>('measure-visibility-toggle').onclick = () => {
    state.measure.visible = !state.measure.visible;
    el<HTMLInputElement>('toggle-measure').checked = state.measure.visible;
    commitMeasure();
  };

  // 半径の数値入力。地図のタップで置いた円もここで直せる
  radiusInput.oninput = () => {
    const km = Number(radiusInput.value);
    if (!Number.isFinite(km) || km <= 0) return;
    const draft = measureDraft();
    const target = draft ?? state.measure.shapes[state.measure.shapes.length - 1];
    if (target?.kind !== 'circle') return;
    target.radiusM = km * 1000;
    commitMeasure();
  };

  map.on('click', (e) => {
    if (state.mapTapMode !== 'measure' || suppressNextClick) return;
    const snapped = snapMeasurePoint(e.lngLat);
    const point = { lat: snapped.lat, lon: snapped.lon };
    const draft = measureDraft();

    if (state.measureTool === 'circle') {
      if (draft?.kind === 'circle') {
        // 2回目のタップで半径が決まり、そこで1つの円が完成する
        draft.radiusM = Math.max(1, distanceMeters(draft.center, point));
        state.measureDraftId = null;
      } else {
        const id = makeId();
        state.measure.shapes.push({ kind: 'circle', id, center: point, radiusM: MEASURE_DEFAULT_RADIUS_M });
        state.measureDraftId = id;
      }
    } else if (draft && draft.kind === state.measureTool) {
      draft.points.push(point);
    } else {
      const id = makeId();
      state.measure.shapes.push({ kind: state.measureTool, id, points: [point] });
      state.measureDraftId = id;
    }

    // 描いたのに見えない、が起きないように（非表示のまま点を置いたら戻す）
    if (!state.measure.visible) {
      state.measure.visible = true;
      el<HTMLInputElement>('toggle-measure').checked = true;
    }
    commitMeasure();
    if (snapped.label) setStatus(`${snapped.label}にスナップしました`);
  });

  // --- 頂点のドラッグ -------------------------------------------------------
  // 仮の点のドラッグ（setupTransmit）と同じ流儀。掴んだ頂点だけを動かす
  const container = map.getCanvasContainer();
  let grabbed: { shapeId: string; index: number } | null = null;

  const posOf = (e: PointerEvent): maplibregl.LngLat => {
    const rect = container.getBoundingClientRect();
    return map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
  };

  /** 押した位置から `MEASURE_GRAB_PX` 以内で最も近い頂点を探す */
  const findVertex = (e: PointerEvent): { shapeId: string; index: number } | null => {
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // ★ 最寄りを1つの変数に代入していく形にすると、代入がクロージャの中に
    //   なるためTSが型を追えない（never に狭まる）。候補を集めてから選ぶ。
    const hits: Array<{ shapeId: string; index: number; d: number }> = [];
    const consider = (shapeId: string, index: number, lat: number, lon: number): void => {
      const p = map.project([lon, lat]);
      const d = Math.hypot(p.x - x, p.y - y);
      if (d <= MEASURE_GRAB_PX) hits.push({ shapeId, index, d });
    };
    for (const s of state.measure.shapes) {
      if (s.kind === 'circle') {
        consider(s.id, 0, s.center.lat, s.center.lon);
        // 外周の掴み手は、中心の真北・半径ぶんの位置に置いている（data/measure.ts）
        const ring = geoCircleRing(s.center.lat, s.center.lon, s.radiusM);
        consider(s.id, 1, ring[0][1], ring[0][0]);
      } else {
        s.points.forEach((p, i) => consider(s.id, i, p.lat, p.lon));
      }
    }
    hits.sort((a, b) => a.d - b.d);
    const best = hits[0];
    return best ? { shapeId: best.shapeId, index: best.index } : null;
  };

  container.addEventListener('pointerdown', (e) => {
    if (state.mapTapMode !== 'measure' || e.button !== 0) return;
    const hit = findVertex(e);
    if (!hit) return;
    grabbed = hit;
    map.dragPan.disable(); // ドラッグ中は地図自体を動かさせない
  });

  container.addEventListener('pointermove', (e) => {
    if (!grabbed) return;
    const { lat, lng } = posOf(e);
    const shape = state.measure.shapes.find((s) => s.id === grabbed!.shapeId);
    if (!shape) return;
    if (shape.kind === 'circle') {
      // 中心（0）を掴んだら円ごと動かし、外周（1）を掴んだら半径を変える
      if (grabbed.index === 0) shape.center = { lat, lon: lng };
      else shape.radiusM = Math.max(1, distanceMeters(shape.center, { lat, lon: lng }));
    } else {
      shape.points[grabbed.index] = { lat, lon: lng };
    }
    // ★ ドラッグ中もラベル・カードの数字を更新する（仮の点のドラッグで
    //   「置いた位置から変わらない」と指摘を受けたのと同じ考え方）
    redrawMeasure();
    renderMeasureCard();
  });

  const endVertexDrag = (): void => {
    if (!grabbed) return;
    grabbed = null;
    map.dragPan.enable();
    suppressNextClick = true; // 指離しが「点を追加するタップ」として二重発火しないように
    commitMeasure();
  };
  container.addEventListener('pointerup', endVertexDrag);
  container.addEventListener('pointercancel', () => {
    // dragPan は経路を問わず必ず戻す（戻し忘れると地図が固まる）
    grabbed = null;
    map.dragPan.enable();
  });

  // 表示/非表示。他の表示チェックボックスと同じ配線
  const visibleToggle = el<HTMLInputElement>('toggle-measure');
  visibleToggle.checked = state.measure.visible;
  visibleToggle.onchange = () => {
    state.measure.visible = visibleToggle.checked;
    commitMeasure();
  };
  // ★ ここで redrawMeasure() を呼んでも空振りする。setupMeasure() は boot() の
  //   中で addOverlayLayers() より**前**に走るため、まだソースが無く
  //   setSourceData が黙って何もしない。保存済みの図形の初回描画は
  //   addOverlayLayers() の直後で行う（boot() 参照）。
}

// --- APT（高度プロファイルタスク、AXMER 15.21） --------------------------------

/** state.apt を gv-core の AptTask にする */
function currentAptTask(): AptTask {
  return {
    profile: state.apt.profile,
    innerBandFt: state.apt.innerBandFt,
    outerBandFt: state.apt.outerBandFt,
  };
}

function saveApt(): void {
  try {
    localStorage.setItem(APT_KEY, JSON.stringify(state.apt));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

/** APTチャートに重ねる航跡。ライブ端末と読み込み済みフライトの両方 */
function collectAptTracks(): AptChartTrack[] {
  const out: AptChartTrack[] = [];
  for (const t of liveTerminals()) {
    if (!terminalShown(t.terminalId)) continue;
    out.push({
      terminalId: t.terminalId,
      label: t.terminalId,
      points: t.fixes.map((x) => ({ t: x.t, alt: fixAltM(x) })),
    });
  }
  for (const [i, fl] of state.flights.entries()) {
    if (state.hiddenFlights.has(i)) continue;
    out.push({
      terminalId: fl.terminalId,
      label: fl.label,
      points: fl.fixes.map((x) => ({ t: x.t, alt: fixAltM(x) })),
    });
  }
  return out;
}

/**
 * APTビューの描き直し。
 * redraw() の末尾から呼ばれるので、閉じている間は即座に戻ること。
 */
function renderApt(): void {
  const sheet = el('apt-view');
  if (sheet.hidden) return;

  const task = currentAptTask();
  const tracks = collectAptTracks();
  const chart = el('apt-chart');
  const rect = chart.getBoundingClientRect();

  chart.innerHTML = buildAptChartSvg({
    task,
    startMs: state.apt.startMs,
    tracks,
    nowMs: Date.now(),
    stops: state.altitudeStops,
    width: Math.max(320, Math.round(rect.width)),
    height: Math.max(200, Math.round(rect.height)),
  });

  // 経過時間の表示
  const elapsedBox = el('apt-elapsed');
  if (state.apt.startMs == null) {
    elapsedBox.textContent = '開始（電子マーク）未設定';
  } else {
    const sec = Math.floor((Date.now() - state.apt.startMs) / 1000);
    elapsedBox.textContent = sec < 0 ? '開始前' : `経過 ${formatAccumulatedSec(sec)}`;
  }

  // 機体ごとの累積時間（参考値）
  const scores = el('apt-scores');
  if (state.apt.startMs == null || task.profile.length === 0) {
    scores.innerHTML = '';
    return;
  }
  const startMs = state.apt.startMs;
  scores.innerHTML = tracks
    .map((tr) => {
      const score = scoreApt(
        task,
        tr.points.map((p) => ({ t: p.t, altFt: metersToFeet(p.alt) })),
        { startMs },
      );
      if (score.weightedSec === 0 && score.outsideSec === 0) return null;
      const skipped =
        score.skippedSec > 0
          ? `<span class="muted">（欠測 ${formatAccumulatedSec(score.skippedSec)}）</span>`
          : '';
      return (
        `<span class="apt-score" style="color:${colorForTerminal(tr.terminalId)}">` +
        `<span class="apt-swatch"></span>` +
        `<span><b>${escapeHtml(tr.label)}</b> ${formatAccumulatedSec(score.weightedSec)}` +
        ` <span class="muted">(A ${formatAccumulatedSec(score.innerSec)} ×2 ／ ` +
        `B ${formatAccumulatedSec(score.outerSec)})</span>${skipped}</span></span>`
      );
    })
    .filter(Boolean)
    .join('');
}

/** プロファイル入力の行を描き直す */
function renderAptProfileRows(): void {
  const box = el('apt-profile-rows');
  box.innerHTML = state.apt.profile
    .map(
      (p, i) =>
        `<div class="apt-profile-row">` +
        `<input type="number" class="input" data-apt-min="${i}" step="0.5" ` +
        `value="${p.tSec / 60}" aria-label="経過分" />` +
        `<input type="number" class="input" data-apt-ft="${i}" step="50" ` +
        `value="${p.altFt}" aria-label="目標高度ft" />` +
        `<button type="button" class="subtle" data-apt-del="${i}" aria-label="この行を削除">×</button>` +
        `</div>`,
    )
    .join('');
}

function setupApt(): void {
  const sheet = el('apt-view');
  const startBox = el<HTMLInputElement>('apt-start');
  const innerBox = el<HTMLInputElement>('apt-inner');
  const outerBox = el<HTMLInputElement>('apt-outer');

  const renderStart = (): void => {
    startBox.value =
      state.apt.startMs == null
        ? ''
        : `${toZonedTime(state.apt.startMs, state.timeZone).time} ${toZonedTime(state.apt.startMs, state.timeZone).zone}`;
  };

  const refresh = (): void => {
    renderStart();
    renderAptProfileRows();
    renderApt();
  };

  innerBox.value = String(state.apt.innerBandFt);
  outerBox.value = String(state.apt.outerBandFt);

  innerBox.oninput = () => {
    const v = Number(innerBox.value);
    if (Number.isFinite(v) && v > 0) {
      state.apt.innerBandFt = v;
      saveApt();
      renderApt();
    }
  };
  outerBox.oninput = () => {
    const v = Number(outerBox.value);
    if (Number.isFinite(v) && v > 0) {
      state.apt.outerBandFt = v;
      saveApt();
      renderApt();
    }
  };

  el<HTMLButtonElement>('apt-start-now').onclick = () => {
    state.apt.startMs = Date.now();
    saveApt();
    refresh();
  };

  el<HTMLButtonElement>('apt-row-add').onclick = () => {
    // 末尾の1分後・同じ高度を初期値にする（続けて入れやすいように）
    const last = state.apt.profile[state.apt.profile.length - 1];
    state.apt.profile.push({
      tSec: last ? last.tSec + 60 : 0,
      altFt: last ? last.altFt : 1500,
    });
    saveApt();
    refresh();
  };

  el<HTMLButtonElement>('apt-reset').onclick = () => {
    state.apt.profile = [];
    saveApt();
    refresh();
  };

  // 行の編集・削除は委譲で1回だけ貼る（行は描き直しでDOMごと作り替わる）
  el('apt-profile-rows').addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement;
    const minIdx = target.dataset.aptMin;
    const ftIdx = target.dataset.aptFt;
    const v = Number(target.value);
    if (!Number.isFinite(v)) return;
    if (minIdx != null && state.apt.profile[Number(minIdx)]) {
      state.apt.profile[Number(minIdx)].tSec = v * 60;
    } else if (ftIdx != null && state.apt.profile[Number(ftIdx)]) {
      state.apt.profile[Number(ftIdx)].altFt = v;
    } else {
      return;
    }
    // 節点は時刻の昇順である前提なので、入力のたびに並べ替える
    state.apt.profile.sort((a, b) => a.tSec - b.tSec);
    saveApt();
    renderApt();
  });
  el('apt-profile-rows').addEventListener('click', (e) => {
    const idx = (e.target as HTMLElement).dataset.aptDel;
    if (idx == null) return;
    state.apt.profile.splice(Number(idx), 1);
    saveApt();
    refresh();
  });

  el<HTMLButtonElement>('apt-open').onclick = () => {
    sheet.hidden = false;
    refresh();
  };
  el('apt-close').onclick = () => {
    sheet.hidden = true;
  };
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.hidden = true;
  });
  window.addEventListener('resize', renderApt);
}

// --- 3DT 設定空域（AXMER 15.20） ----------------------------------------------

function saveAirspaces(): void {
  try {
    localStorage.setItem(AIRSPACE_KEY, JSON.stringify(state.airspaces));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

/** 3DT空域の描画と、累積水平距離（参考値）の表示 */
function renderAirspace(): void {
  setSourceData(map, AIRSPACE_SOURCE, withGroundElevation(airspaceToGeoJson(state.airspaces)));
  if (map.getLayer('gv-airspace-3d')) {
    map.setLayoutProperty(
      'gv-airspace-3d',
      'visibility',
      state.show3d && state.airspaces.length > 0 ? 'visible' : 'none',
    );
  }

  const info = el('airspace-info');
  if (state.airspaces.length === 0) {
    info.innerHTML = '<p class="muted">未設定</p>';
    return;
  }

  const rows = state.airspaces.map((a) => {
    const floor = a.floorFt == null ? '地面' : `${a.floorFt}ft`;
    const ceiling = a.ceilingFt == null ? '無制限' : `${a.ceilingFt}ft`;
    return `<div><b>${escapeHtml(a.label)}</b> ・ ${a.points.length}頂点 ・ ${floor}〜${ceiling}</div>`;
  });

  // 機体ごとの累積水平距離（参考値）
  for (const t of liveTerminals()) {
    if (!terminalShown(t.terminalId)) continue;
    const score = scoreThreeD(
      state.airspaces,
      t.fixes.map((f) => ({
        t: f.t,
        lat: f.lat,
        lon: f.lon,
        altFt: metersToFeet(fixAltM(f)),
      })),
    );
    if (score.pointsInside === 0) continue;
    rows.push(
      `<div style="color:${colorForTerminal(t.terminalId)}">` +
        `<b>${escapeHtml(t.terminalId)}</b> 空域内 ${(score.distanceM / 1000).toFixed(2)} km` +
        ` <span class="muted">(参考値)</span></div>`,
    );
  }

  info.innerHTML = rows.join('');
}

function setupAirspace(): void {
  const labelBox = el<HTMLInputElement>('airspace-label');
  const pointsBox = el<HTMLTextAreaElement>('airspace-points');
  const floorBox = el<HTMLInputElement>('airspace-floor');
  const ceilingBox = el<HTMLInputElement>('airspace-ceiling');

  el<HTMLButtonElement>('airspace-apply').onclick = () => {
    // グリッド表記は会場のどこを指すかを決めるのに基準点が要る。
    // ゴール入力と同じく選択中の会場（競技地図）を基準にする
    const ref = gridReferencePoint();
    const codes = pointsBox.value
      .split('\n')
      .map((s) => s.replace(/\D/g, ''))
      .filter(Boolean);
    const digitCount = gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing);

    const points: Array<{ lat: number; lon: number }> = [];
    const bad: string[] = [];
    for (const code of codes) {
      if (code.length !== digitCount) {
        bad.push(code);
        continue;
      }
      const p = resolveGridCode(code, ref, state.utmWideEasting, state.utmWideNorthing);
      if (p) points.push(p);
      else bad.push(code);
    }

    if (bad.length > 0) {
      setStatus(`読めない頂点があります（${digitCount}デジット）: ${bad.join(', ')}`, 'warn');
      return;
    }
    if (points.length < 3) {
      setStatus('空域には頂点が3つ以上必要です', 'warn');
      return;
    }

    const floor = floorBox.value.trim();
    const ceiling = ceilingBox.value.trim();
    state.airspaces = [
      {
        label: labelBox.value.trim() || '3DT',
        points,
        floorFt: floor === '' ? null : Number(floor),
        ceilingFt: ceiling === '' ? null : Number(ceiling),
      },
    ];
    saveAirspaces();
    renderAirspace();
    renderCrossSection();
    setStatus(`3DT空域を設定しました（${points.length}頂点）`);
  };

  el<HTMLButtonElement>('airspace-clear').onclick = () => {
    state.airspaces = [];
    saveAirspaces();
    renderAirspace();
    renderCrossSection();
  };

  // 保存済みの空域があれば入力欄にも戻しておく（何が入っているか分かるように）
  const first = state.airspaces[0];
  if (first) {
    labelBox.value = first.label;
    floorBox.value = first.floorFt == null ? '' : String(first.floorFt);
    ceilingBox.value = first.ceilingFt == null ? '' : String(first.ceilingFt);
    pointsBox.value = first.points
      .map((p) => gridCodeOf(p.lat, p.lon, state.utmWideEasting, state.utmWideNorthing))
      .join('\n');
  }
  renderAirspace();
}

// --- TaskViewの課題表（写真から手入力） ---------------------------------------

function saveTaskCircleGoals(): void {
  try {
    localStorage.setItem(TASK_CIRCLE_KEY, JSON.stringify(state.taskCircleGoals));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

const taskCircleColorOptionsHtml = (selected: string): string =>
  TASK_CIRCLE_COLOR_PRESETS.map(
    (c) => `<option value="${c.value}" ${c.value === selected ? 'selected' : ''}>${escapeHtml(c.label)}</option>`,
  ).join('');

/** 入力済みの行を描き直す */
function renderTaskCircleRows(): void {
  const box = el('task-circle-rows');
  box.innerHTML = state.taskCircleGoals
    .map((g, i) => {
      const code = gridCodeOf(g.lat, g.lon, state.utmWideEasting, state.utmWideNorthing);
      return (
        `<div class="task-circle-row">` +
        `<div class="task-circle-row-head">` +
        `<b>${escapeHtml(g.name)}</b>` +
        `<span class="muted">${escapeHtml(code)}</span>` +
        `<button type="button" class="subtle" data-task-circle-del="${i}" aria-label="この行を削除">×</button>` +
        `</div>` +
        `<div class="task-circle-row-fields">` +
        `<input type="number" class="input" data-task-circle-alt="${i}" step="50" ` +
        `placeholder="高度ft" value="${g.altFt ?? ''}" aria-label="高度ft" />` +
        `<select class="input" data-task-circle-color="${i}" aria-label="色">` +
        `${taskCircleColorOptionsHtml(g.color)}</select>` +
        `<input type="number" class="input" data-task-circle-rmin="${i}" step="0.1" min="0" ` +
        `value="${g.rMinKm}" aria-label="Rmin (km)" />` +
        `<input type="number" class="input" data-task-circle-rmax="${i}" step="0.1" min="0" ` +
        `value="${g.rMaxKm}" aria-label="Rmax (km)" />` +
        `</div>` +
        `</div>`
      );
    })
    .join('');
}

/**
 * パイロットから送られてくるTaskViewの画面写真を見ながら、クルーがゴールを
 * 1件ずつ手入力する。座標はグリッド表記（会場のUTM表記設定に従う）か
 * 「緯度, 経度」のどちらでもよい。Rmin〜Rmaxの二重円として地図に重ねる
 * （$_TVCIRCLEの描画と同じ`gv-area-fill`レイヤーを共有する）。
 */
function setupTaskCircles(): void {
  const nameInput = el<HTMLInputElement>('task-circle-name');
  const codeInput = el<HTMLInputElement>('task-circle-code');
  const altInput = el<HTMLInputElement>('task-circle-alt');
  const colorSelect = el<HTMLSelectElement>('task-circle-color');
  const rMinInput = el<HTMLInputElement>('task-circle-rmin');
  const rMaxInput = el<HTMLInputElement>('task-circle-rmax');

  colorSelect.innerHTML = taskCircleColorOptionsHtml(TASK_CIRCLE_COLOR_PRESETS[0].value);

  // JSON例のコピー。AIチャットに貼り付けて「この形式で」と頼みやすくする
  const copyBtn = el<HTMLButtonElement>('task-circle-json-copy');
  const copyBtnLabel = copyBtn.textContent ?? '';
  copyBtn.onclick = async () => {
    const text = el('task-circle-json-example').textContent ?? '';
    const ok = await copyTextToClipboard(text);
    copyBtn.textContent = ok ? 'コピーしました' : 'コピーできませんでした';
    setTimeout(() => {
      copyBtn.textContent = copyBtnLabel;
    }, 1500);
  };

  el<HTMLButtonElement>('task-circle-add').onclick = () => {
    const name = nameInput.value.trim();
    const code = codeInput.value.trim();
    if (!name) {
      setStatus('名前を入力してください', 'warn');
      return;
    }
    if (!code) {
      setStatus('座標を入力してください', 'warn');
      return;
    }
    // ゴール・3DT空域の入力と同じく選択中の会場（競技地図）を基準点にする
    const p = resolveTaskCircleCode(
      code,
      gridReferencePoint(),
      state.utmWideEasting,
      state.utmWideNorthing,
      state.venue?.goals ?? [],
    );
    if (!p) {
      const digitCount = gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing);
      setStatus(
        `座標を読み取れません（3桁のゴール番号、${digitCount}桁のグリッド表記、または「緯度, 経度」で入力してください）`,
        'warn',
      );
      return;
    }
    const altRaw = altInput.value.trim();
    state.taskCircleGoals.push({
      name,
      lat: p.lat,
      lon: p.lon,
      altFt: altRaw === '' ? null : Number(altRaw),
      color: colorSelect.value,
      rMinKm: Number(rMinInput.value) || 0,
      rMaxKm: Number(rMaxInput.value) || 0,
    });
    saveTaskCircleGoals();
    renderTaskCircleRows();
    redraw();
    setStatus(`「${name}」を追加しました`);

    // 続けて次の行を入れやすいよう、名前・座標・高度だけ空にする（色・半径は使い回すことが多い）
    nameInput.value = '';
    codeInput.value = '';
    altInput.value = '';
  };

  el<HTMLButtonElement>('task-circle-clear').onclick = () => {
    state.taskCircleGoals = [];
    saveTaskCircleGoals();
    renderTaskCircleRows();
    redraw();
  };

  // JSONインポート（手入力がだるいとの声を受けて追加。AI等でOCRしたJSONを
  // 読み込む想定。手入力と全く同じ座標解決を通すので、書式は手入力の
  // codeフィールドと同じ「グリッド表記の数字」か「緯度, 経度」が使える）
  el<HTMLInputElement>('task-circle-import').onchange = async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      const json = JSON.parse(await file.text());
      const result = parseTaskCircleImport(
        json,
        gridReferencePoint(),
        state.utmWideEasting,
        state.utmWideNorthing,
        state.venue?.goals ?? [],
      );
      state.taskCircleGoals.push(...result.goals);
      saveTaskCircleGoals();
      renderTaskCircleRows();
      redraw();
      const parts = [`${result.goals.length}件を読み込みました`];
      if (result.skipped.length > 0) {
        parts.push(`読み取れなかった項目: ${result.skipped.map((s) => `${s.name}(${s.reason})`).join('、')}`);
      }
      setStatus(parts.join(' / '), result.skipped.length > 0 ? 'warn' : 'info');
    } catch (err) {
      setStatus(`JSONを読み込めません: ${(err as Error).message}`, 'warn');
    }
    input.value = '';
  };

  // 編集（高度・色・Rmin・Rmax）と削除は委譲で1回だけ貼る（行は描き直しでDOMごと作り替わる）
  el('task-circle-rows').addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const altIdx = target.dataset.taskCircleAlt;
    const colorIdx = target.dataset.taskCircleColor;
    const rMinIdx = target.dataset.taskCircleRmin;
    const rMaxIdx = target.dataset.taskCircleRmax;
    const idx = altIdx ?? colorIdx ?? rMinIdx ?? rMaxIdx;
    if (idx == null) return;
    const goal = state.taskCircleGoals[Number(idx)];
    if (!goal) return;
    if (altIdx != null) {
      goal.altFt = target.value.trim() === '' ? null : Number(target.value);
    } else if (colorIdx != null) {
      goal.color = target.value;
    } else if (rMinIdx != null) {
      goal.rMinKm = Number(target.value) || 0;
    } else if (rMaxIdx != null) {
      goal.rMaxKm = Number(target.value) || 0;
    }
    saveTaskCircleGoals();
    redraw();
  });
  el('task-circle-rows').addEventListener('click', (e) => {
    const idx = (e.target as HTMLElement).dataset.taskCircleDel;
    if (idx == null) return;
    state.taskCircleGoals.splice(Number(idx), 1);
    saveTaskCircleGoals();
    renderTaskCircleRows();
    redraw();
  });

  renderTaskCircleRows();
}

// --- LRN/3DT 戦略プランナー ---------------------------------------------------

/**
 * プランナーに渡す風プロファイルを組み立てる。
 *
 * パイバル記録（高度が低すぎて実用にならないとの指摘）はもう使わない。
 * state.lrn3dtForm.windSourceで二者択一にする:
 * - 'tracks'（既定）: 見えている全機・読み込んだ航跡をまとめた風
 *   （collectAllTrackWindSamples()、複数機の観測はベクトル平均で合成）
 * - 'windview': WindView（大会公式パイバル情報）の貼り付けデータ
 *   （windview-store.tsにvenueごと保存済みのもの。個々のサンプルに
 *   時刻が無いため時間帯びん分けはせず高度順に並べるだけ）
 * どちらも無ければ空配列（呼び出し側で「層が足りない」を判定する）。
 */
function assembleLrnWindLayers(): LrnWindLayer[] {
  if (!state.venue) return [];
  const samples =
    state.lrn3dtForm.windSource === 'windview'
      ? loadWindviewSamples(state.venue.id)
      : collectAllTrackWindSamples();
  return samples
    .map((s) => ({ altFt: metersToFeet(s.alt), dirToTrueDeg: s.dir, speedMps: s.speed }))
    .sort((a, b) => a.altFt - b.altFt);
}

/**
 * 計算に使った円（3DTのRmin/Rmaxリング、LRN周回開始の円）を、タスクサークルと
 * 同じ仕組みで地図に描く。
 *
 * ★ 「何を前提に出した答えなのか」を地図上で確かめられるようにするためのもの。
 *   LRN周回開始の円は当初描いていなかったが、実機から「計算に使った中心点と
 *   円の線も表示してほしい」と要望があり追加した。
 */
function lrn3dtRingAreas(): CircleArea[] {
  const result = state.lrn3dtResult;
  if (!result) return [];
  // gv-lrn3dt-plan-line/point（map/layers.ts）と同じ紫にして「同じ計算の結果」と分かるようにする
  const color = '#9b5cff';
  const areas: CircleArea[] = [];

  if (result.kind === 'lrn') {
    const c = result.circle;
    if (c && c.radiusKm > 0) {
      areas.push({ group: 'lrn3dt-circle', id: 'lrn3dt-circle', lat: c.lat, lon: c.lon, radius: c.radiusKm * 1000, color });
    }
    return areas;
  }

  const { ring } = result;
  if (ring.rMaxKm > 0) {
    areas.push({ group: 'lrn3dt-ring', id: 'lrn3dt-ring_max', lat: ring.lat, lon: ring.lon, radius: ring.rMaxKm * 1000, color });
  }
  if (ring.rMinKm > 0) {
    areas.push({ group: 'lrn3dt-ring', id: 'lrn3dt-ring_min', lat: ring.lat, lon: ring.lon, radius: ring.rMinKm * 1000, color });
  }
  return areas;
}

/**
 * LRN/3DTプランナーの計算結果をLRN3DT_PLAN_SOURCEへ流すGeoJSONに変換する。
 *
 * LRNは脚の始点・終点（4点、循環モードは移行が一瞬なので中2点が重なる）を
 * 線＋点で描き、さらに**得点になる三角形を面で塗る**（成績が「面積」なので、
 * 何が評価されているのかが一目で分かるようにする）。周回開始モードでは
 * 計算に使った円の中心点も描く（円そのものはlrn3dtRingAreas()側）。
 *
 * 3DTは`ThreeDtPlan`に座標が含まれない（高度と時刻の並びのみ）ため、
 * リングはlrn3dtRingAreas()（AREA_SOURCE）に任せ、ここでは進入点だけ描く。
 *
 * `role`プロパティで点の見た目を分けている（route=ルートの頂点、
 * center=計算に使った中心点。map/layers.tsのgv-lrn3dt-plan-point参照）。
 */
function lrn3dtPlanToGeoJson(): GeoJSON.FeatureCollection {
  const result = state.lrn3dtResult;
  if (!result) return EMPTY_GEOJSON;

  const point = (lon: number, lat: number, role: string): GeoJSON.Feature => ({
    type: 'Feature',
    properties: { role },
    geometry: { type: 'Point', coordinates: [lon, lat] },
  });

  if (result.kind === 'lrn') {
    const toLonLat = (p: LrnPoint): [number, number] => {
      const ll = fromUtm({ easting: p.e, northing: p.n, zone: result.refZone });
      return [ll.lon, ll.lat];
    };
    const [leg1, leg2] = result.plan.legs;
    const coords = [leg1.from, leg1.to, leg2.from, leg2.to].map(toLonLat);
    const tri = result.plan.triangle;
    const ring = [tri.a, tri.b, tri.c].map(toLonLat);
    ring.push(ring[0]); // GeoJSONのポリゴンは始点に戻して閉じる
    const features: GeoJSON.Feature[] = [
      { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } },
      { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } },
      ...coords.map((c) => point(c[0], c[1], 'route')),
    ];
    if (result.circle) {
      features.push(point(result.circle.lon, result.circle.lat, 'center'));
    }
    return { type: 'FeatureCollection', features };
  }

  const features: GeoJSON.Feature[] = [point(result.entry.lon, result.entry.lat, 'route')];
  // 3DTも中心点を出す（リングの円だけだと、中心がどこかは目測になるため）
  features.push(point(result.ring.lon, result.ring.lat, 'center'));

  /*
   * 推奨航跡。`scoredFromPrev`（＝その区間が distanceInsideM に加算されたか）が
   * 切り替わるところで線を分け、加算された区間だけを実線で描く。
   * こうすると「結果の距離がどこで稼がれているか」が地図上でそのまま読める。
   * 分けずに1本で描くと、リングの外を流されている間も同じ線に見えてしまう。
   */
  const path = result.plan.path;
  if (path.length >= 2) {
    const toLonLat = (p: { e: number; n: number }): [number, number] => {
      const ll = fromUtm({ easting: p.e, northing: p.n, zone: result.refZone });
      return [ll.lon, ll.lat];
    };
    let run: [number, number][] = [toLonLat(path[0])];
    let runScored = path[1].scoredFromPrev;
    const flush = (): void => {
      if (run.length >= 2) {
        features.push({
          type: 'Feature',
          properties: { scored: runScored },
          geometry: { type: 'LineString', coordinates: run },
        });
      }
    };
    for (let i = 1; i < path.length; i++) {
      if (path[i].scoredFromPrev !== runScored) {
        flush();
        // 次の線は今の点から始める（間に隙間ができないよう1点重ねる）
        run = [run[run.length - 1]];
        runScored = path[i].scoredFromPrev;
      }
      run.push(toLonLat(path[i]));
    }
    flush();

    // 高度が変わる点（＝層を乗り換えたところ）に印を打つ
    for (let i = 1; i < path.length; i++) {
      if (path[i].altFt === path[i - 1].altFt) continue;
      const [lon, lat] = toLonLat(path[i]);
      features.push(point(lon, lat, 'route'));
    }
  }

  return { type: 'FeatureCollection', features };
}

/**
 * プランナーの計算結果を3D（高度のある形）で描くためのGeoJSON。
 *
 * 3D表示がOFFのときは空を返す。データ自体で出し分けるので、レイヤーの
 * visibility は触らない（3D航跡と同じ流儀）。
 *
 * 3種類を1つのソースに入れ、`kind` でレイヤーを振り分ける:
 * - `wall`   … 推奨航跡。`alt`[m] の高さまで押し上げた薄い壁
 * - `volume` … 3DTのリング（高度帯を埋めた環）とLRNの得点三角形
 *
 * ★ LRNの三角形は本来ひとつの高度に無い（A・Bは第1脚の高度、Cは第2脚の
 *   高度）。3Dでは**2つの脚の平均高度に薄い板**として置いている。
 *   得点になる面積は水平投影で決まる概念なので、高さは「だいたいこの辺り」
 *   を示す目安であって、厳密な幾何ではない。
 */
function lrn3dtPlan3dToGeoJson(): GeoJSON.FeatureCollection {
  const result = state.lrn3dtResult;
  if (!result || !state.show3d) return EMPTY_GEOJSON;

  const features: GeoJSON.Feature[] = [];
  const lines: GeoJSON.Feature[] = [];

  if (result.kind === 'lrn') {
    const toLonLat = (p: LrnPoint): [number, number] => {
      const ll = fromUtm({ easting: p.e, northing: p.n, zone: result.refZone });
      return [ll.lon, ll.lat];
    };
    const [leg1, leg2] = result.plan.legs;
    // 脚ごとに、その脚の高度で壁を立てる（移行区間も第2脚の高度で繋ぐ）
    for (const leg of [leg1, leg2]) {
      lines.push({
        type: 'Feature',
        properties: { kind: 'wall', alt: feetToMeters(leg.layer.altFt) },
        geometry: { type: 'LineString', coordinates: [toLonLat(leg.from), toLonLat(leg.to)] },
      });
    }
    // 高度移行の区間（第1脚の終点 → 第2脚の起点）
    lines.push({
      type: 'Feature',
      properties: { kind: 'wall', alt: feetToMeters((leg1.layer.altFt + leg2.layer.altFt) / 2) },
      geometry: { type: 'LineString', coordinates: [toLonLat(leg1.to), toLonLat(leg2.from)] },
    });

    /*
     * 得点三角形は**輪郭の壁**で描く（塗りの板にはしない）。
     *
     * ★ 最初は2脚の平均高度に薄い板を置いたが、誇張倍率を上げるとカメラが
     *   板の下に入り、**半透明の面が視界全体を覆って地図が読めなくなった**
     *   （実測。倍率20倍で画面がほぼ一色になる）。三角形の形は輪郭で十分
     *   伝わり、塗りは2D表示側が既に担っている。
     */
    const tri = result.plan.triangle;
    const ring = [tri.a, tri.b, tri.c, tri.a].map(toLonLat);
    const midM = feetToMeters((leg1.layer.altFt + leg2.layer.altFt) / 2);
    for (let i = 1; i < ring.length; i++) {
      lines.push({
        type: 'Feature',
        properties: { kind: 'wall', alt: midM },
        geometry: { type: 'LineString', coordinates: [ring[i - 1], ring[i]] },
      });
    }
  } else {
    const path = result.plan.path;
    for (let i = 1; i < path.length; i++) {
      const a = fromUtm({ easting: path[i - 1].e, northing: path[i - 1].n, zone: result.refZone });
      const b = fromUtm({ easting: path[i].e, northing: path[i].n, zone: result.refZone });
      lines.push({
        type: 'Feature',
        properties: {
          kind: 'wall',
          alt: feetToMeters((path[i - 1].altFt + path[i].altFt) / 2),
        },
        geometry: { type: 'LineString', coordinates: [[a.lon, a.lat], [b.lon, b.lat]] },
      });
    }

    // リングを高度帯（下限〜上限）の環として立体にする。
    // rMinが0なら穴の無いただの円柱になる
    const { ring } = result;
    const outer = geoCircleRing(ring.lat, ring.lon, ring.rMaxKm * 1000);
    const coordinates: [number, number][][] = [outer];
    if (ring.rMinKm > 0) {
      // GeoJSONの穴は外周と逆回りにする
      coordinates.push([...geoCircleRing(ring.lat, ring.lon, ring.rMinKm * 1000)].reverse());
    }
    features.push({
      type: 'Feature',
      properties: {
        kind: 'volume',
        floorM: feetToMeters(state.lrn3dtForm.altMinFt),
        ceilingM: feetToMeters(state.lrn3dtForm.altMaxFt),
      },
      geometry: { type: 'Polygon', coordinates },
    });
  }

  return {
    type: 'FeatureCollection',
    features: [...features, ...linesToWallPolygons(lines, PLAN_3D_RIBBON_WIDTH_M)],
  };
}

/**
 * プランナーダイアログの描き直し。
 * redraw() の末尾から呼ばれるので、閉じている間は即座に戻ること
 * （地図側の描画＝lrn3dtRingAreas()/lrn3dtPlanToGeoJson()はredraw()側で
 * 別途、開閉に関係なく反映する。計算済みのルートは閉じても地図に残る）。
 */
function renderLrn3dt(): void {
  const sheet = el('lrn3dt-view');
  if (sheet.hidden) return;
  const box = el('lrn3dt-result');
  const result = state.lrn3dtResult;
  box.innerHTML = result
    ? result.kind === 'lrn'
      ? buildLrnPlanSummaryHtml(result.plan) + buildLrnAdviceHtml(result.advice)
      : buildThreeDtPlanSummaryHtml(result.plan)
    : '';
}

/**
 * 会場を切り替えたときにプランナーの入力点・結果を消す実体
 * （setupLrn3dt()が入れる。setupLrn3dt()自体はboot()で一度しか呼ばれないため、
 * selectVenue()側から直接呼べるようclosePanel/offlineMapRenderと同じ受け渡し方をする）。
 *
 * ★ `state.lrn3dtForm`の`*Code`は生の緯度経度ではなくUTMグリッド表記の文字列
 *   （§105）で、どのUTMゾーンかは「今見ている会場」に暗黙に依存する。
 *   保存キー（LRN3DT_KEY）が会場ごとではなく1つしか無いため、会場を切り替えても
 *   古い会場の点が残り続け、地図上には全く無関係な場所の「開始点」等が
 *   出続けていた（利用者からの指摘）。ゴール一覧が会場ごとに切り替わるのと
 *   同じ扱いにする必要がある。数値設定（制限時間・上昇速度等）は機体側の
 *   好みなので、これらは会場をまたいで維持してよく、消すのは地図上の
 *   4点と計算結果だけにする。
 */
let resetLrn3dtForNewVenue: (() => void) | undefined;

function setupLrn3dt(): void {
  const sheet = el('lrn3dt-view');
  const modeSelect = el<HTMLSelectElement>('lrn3dt-mode');
  const windSourceSelect = el<HTMLSelectElement>('lrn3dt-wind-source');
  const groundElevationInput = el<HTMLInputElement>('lrn3dt-ground-elevation');
  const limitSecInput = el<HTMLInputElement>('lrn3dt-limit-sec');
  const altMinInput = el<HTMLInputElement>('lrn3dt-alt-min');
  const altMaxInput = el<HTMLInputElement>('lrn3dt-alt-max');
  const climbInput = el<HTMLInputElement>('lrn3dt-climb');
  const descentInput = el<HTMLInputElement>('lrn3dt-descent');
  const startInput = el<HTMLInputElement>('lrn3dt-start');
  const circleCenterInput = el<HTMLInputElement>('lrn3dt-circle-center');
  const circleRadiusInput = el<HTMLInputElement>('lrn3dt-circle-radius');
  const ringCenterInput = el<HTMLInputElement>('lrn3dt-ring-center');
  const ringRMinInput = el<HTMLInputElement>('lrn3dt-ring-rmin');
  const ringRMaxInput = el<HTMLInputElement>('lrn3dt-ring-rmax');
  const entryInput = el<HTMLInputElement>('lrn3dt-entry');
  const entryAltInput = el<HTMLInputElement>('lrn3dt-entry-alt');

  const f = state.lrn3dtForm;
  modeSelect.value = f.mode;
  windSourceSelect.value = f.windSource;
  groundElevationInput.value = String(f.groundElevationM);
  limitSecInput.value = String(f.limitSec);
  altMinInput.value = String(f.altMinFt);
  altMaxInput.value = String(f.altMaxFt);
  climbInput.value = String(f.climbMps);
  descentInput.value = String(f.descentMps);
  startInput.value = f.startCode;
  circleCenterInput.value = f.circleCenterCode;
  circleRadiusInput.value = String(f.circleRadiusKm);
  ringCenterInput.value = f.ringCenterCode;
  ringRMinInput.value = String(f.ringRMinKm);
  ringRMaxInput.value = String(f.ringRMaxKm);
  entryInput.value = f.entryCode;
  entryAltInput.value = String(f.entryAltFt);

  const applyModeVisibility = (): void => {
    for (const modeBlock of document.querySelectorAll<HTMLElement>('[data-lrn3dt-mode]')) {
      modeBlock.hidden = modeBlock.dataset.lrn3dtMode !== modeSelect.value;
    }
  };
  applyModeVisibility();

  modeSelect.onchange = () => {
    state.lrn3dtForm.mode = modeSelect.value as Lrn3dtFormState['mode'];
    saveLrn3dtForm();
    applyModeVisibility();
    // モードで意味のある入力点が変わる（例: lrn-time→3dtでstartからringCenter/entryへ）
    redrawLrn3dtPoints();
  };

  // 地図の位置を指定する4つの入力欄。コード文字列（保存・計算用）と、
  // 地図に置く実座標（state.lrn3dtPoints）を、タップ・ドラッグ・手入力の
  // どれからでも同じ経路でつなぐ
  const LRN3DT_FIELD_INPUTS: Record<Lrn3dtPointField, HTMLInputElement> = {
    start: startInput,
    circleCenter: circleCenterInput,
    ringCenter: ringCenterInput,
    entry: entryInput,
  };
  // 入力欄の文字列(*Code)と、地図に置く実座標(state.lrn3dtPoints)は別々に
  // 持っているが、保存されるのは state.lrn3dtForm の *Code だけ。「消す」
  // ボタンや地図タップ・ドラッグは長らく地図の点とDOMの入力欄しか更新して
  // おらず、state.lrn3dtForm には触れていなかった（保存されるのは「計算」を
  // 押した瞬間の値だけ）。そのため「消す」→「計算」を押さないままアプリを
  // 開き直すと、保存済みの古いコードから setupLrn3dt() 冒頭が入力欄を
  // 復元し、続く syncLrn3dtPoint() が地図の点も生き返らせていた
  // （利用者からの指摘: 「消しても新しくアプリを開くと出てくる」）。
  const LRN3DT_FIELD_FORM_KEYS: Record<Lrn3dtPointField, 'startCode' | 'circleCenterCode' | 'ringCenterCode' | 'entryCode'> = {
    start: 'startCode',
    circleCenter: 'circleCenterCode',
    ringCenter: 'ringCenterCode',
    entry: 'entryCode',
  };
  const syncLrn3dtPoint = (field: Lrn3dtPointField): void => {
    const code = LRN3DT_FIELD_INPUTS[field].value.trim();
    const resolved = code
      ? resolveTaskCircleCode(
          code,
          gridReferencePoint(),
          state.utmWideEasting,
          state.utmWideNorthing,
          state.venue?.goals ?? [],
        )
      : null;
    if (resolved) state.lrn3dtPoints[field] = resolved;
    else delete state.lrn3dtPoints[field];
    state.lrn3dtForm[LRN3DT_FIELD_FORM_KEYS[field]] = code;
    saveLrn3dtForm();
    redrawLrn3dtPoints();
  };
  // 表示はUTMグリッド表記に統一する（緯度経度の羅列は現場で読み合わせにくい、
  // という実機からの要望）。resolveTaskCircleCode側は数字だけを見るので、
  // 手入力の「緯度, 経度」もこれまで通り受け付けられる（parseLatLon）
  const gridCodeText = (coords: { lat: number; lon: number }): string => {
    const code = gridCodeOf(coords.lat, coords.lon, state.utmWideEasting, state.utmWideNorthing);
    const eastingLen = gridEastingDigits(state.utmWideEasting);
    return `${code.slice(0, eastingLen)} ${code.slice(eastingLen)}`;
  };
  const setLrn3dtPointCoords = (field: Lrn3dtPointField, coords: { lat: number; lon: number }): void => {
    const text = gridCodeText(coords);
    LRN3DT_FIELD_INPUTS[field].value = text;
    state.lrn3dtPoints[field] = coords;
    // 地図タップ・ドラッグ・現在地ボタンでの更新も保存する（syncLrn3dtPoint()と同じ理由）
    state.lrn3dtForm[LRN3DT_FIELD_FORM_KEYS[field]] = text;
    saveLrn3dtForm();
    redrawLrn3dtPoints();
  };
  for (const field of Object.keys(LRN3DT_FIELD_INPUTS) as Lrn3dtPointField[]) {
    LRN3DT_FIELD_INPUTS[field].oninput = () => syncLrn3dtPoint(field);
    // 保存済みのコードから、地図の点を初回表示できる状態にしておく
    // （ソースはまだ無いので実際の描画は addOverlayLayers() の直後まで待つ、
    //   計測モードと同じ理由。boot() 参照）
    syncLrn3dtPoint(field);
  }

  // 地図に置いた点を消す手段（利用者要望: 入力欄を手で空にする以外に方法が
  // 無く、消せないと思われていた）。入力欄も一緒に空にして、地図の点と
  // テキストが食い違わないようにする
  const LRN3DT_FIELD_CLEAR_BUTTONS: Record<Lrn3dtPointField, HTMLButtonElement> = {
    start: el<HTMLButtonElement>('lrn3dt-start-clear'),
    circleCenter: el<HTMLButtonElement>('lrn3dt-circle-center-clear'),
    ringCenter: el<HTMLButtonElement>('lrn3dt-ring-center-clear'),
    entry: el<HTMLButtonElement>('lrn3dt-entry-clear'),
  };
  for (const field of Object.keys(LRN3DT_FIELD_CLEAR_BUTTONS) as Lrn3dtPointField[]) {
    LRN3DT_FIELD_CLEAR_BUTTONS[field].onclick = () => {
      LRN3DT_FIELD_INPUTS[field].value = '';
      syncLrn3dtPoint(field);
    };
  }

  // 会場切り替え時にも同じ「4点＋結果を消す」経路を通す（selectVenue()から呼ぶ）
  resetLrn3dtForNewVenue = () => {
    for (const field of Object.keys(LRN3DT_FIELD_INPUTS) as Lrn3dtPointField[]) {
      LRN3DT_FIELD_INPUTS[field].value = '';
      syncLrn3dtPoint(field);
    }
    state.lrn3dtResult = null;
    renderLrn3dt();
  };

  const applyWindSourceVisibility = (): void => {
    for (const block of document.querySelectorAll<HTMLElement>('[data-lrn3dt-wind-source]')) {
      block.hidden = block.dataset.lrn3dtWindSource !== windSourceSelect.value;
    }
  };
  applyWindSourceVisibility();

  windSourceSelect.onchange = () => {
    state.lrn3dtForm.windSource = windSourceSelect.value as Lrn3dtFormState['windSource'];
    saveLrn3dtForm();
    applyWindSourceVisibility();
  };

  // WindView JSON例のコピー。AIチャットに貼り付けて「この形式で」と頼みやすくする
  // （task-circle-json-copyと同じ仕組み）
  const windviewCopyBtn = el<HTMLButtonElement>('windview-json-copy');
  const windviewCopyBtnLabel = windviewCopyBtn.textContent ?? '';
  windviewCopyBtn.onclick = async () => {
    const text = el('windview-json-example').textContent ?? '';
    const ok = await copyTextToClipboard(text);
    windviewCopyBtn.textContent = ok ? 'コピーしました' : 'コピーできませんでした';
    setTimeout(() => {
      windviewCopyBtn.textContent = windviewCopyBtnLabel;
    }, 1500);
  };

  // WindView JSONインポート（task-circle-importと同じ仕組み）。磁方位→真方位の
  // 変換に会場の磁気偏角キャッシュを使う（パイバル修正時と同じ取得順）
  el<HTMLInputElement>('windview-import').onchange = async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !state.venue) return;
    try {
      const json = JSON.parse(await file.text());
      const c = map.getCenter();
      const ref = { lat: c.lat, lon: c.lng };
      const declinationDeg =
        loadDeclination(state.venue.id)?.declinationDeg ?? getMagneticDeclination(ref.lat, ref.lon).declinationDeg;
      const groundElevationM = Number(groundElevationInput.value) || 0;
      const result = parseWindviewImport(json, ref, groundElevationM, declinationDeg);
      saveWindviewSamples(state.venue.id, result.samples);
      const parts = [`${result.samples.length}件を読み込みました`];
      if (result.skipped.length > 0) {
        parts.push(
          `読み取れなかった項目: ${result.skipped.map((s) => `${s.index + 1}件目(${s.reason})`).join('、')}`,
        );
      }
      setStatus(parts.join(' / '), result.skipped.length > 0 ? 'warn' : 'info');
    } catch (err) {
      setStatus(`JSONを読み込めません: ${(err as Error).message}`, 'warn');
    }
    input.value = '';
  };

  el<HTMLButtonElement>('lrn3dt-entry-here').onclick = async () => {
    try {
      const fix = await getOwnPositionOnce();
      setLrn3dtPointCoords('entry', { lat: fix.lat, lon: fix.lon });
      if (fix.altMsl != null) entryAltInput.value = String(Math.round(metersToFeet(fix.altMsl)));
    } catch (err) {
      setStatus(err instanceof Error ? err.message : String(err), 'warn');
    }
  };

  el<HTMLButtonElement>('lrn3dt-compute').onclick = () => {
    state.lrn3dtForm = {
      mode: modeSelect.value as Lrn3dtFormState['mode'],
      windSource: windSourceSelect.value as Lrn3dtFormState['windSource'],
      groundElevationM: Number(groundElevationInput.value) || 0,
      limitSec: Number(limitSecInput.value) || 0,
      altMinFt: Number(altMinInput.value) || 0,
      altMaxFt: Number(altMaxInput.value) || 0,
      climbMps: Number(climbInput.value) || 0,
      descentMps: Number(descentInput.value) || 0,
      startCode: startInput.value.trim(),
      circleCenterCode: circleCenterInput.value.trim(),
      circleRadiusKm: Number(circleRadiusInput.value) || 0,
      ringCenterCode: ringCenterInput.value.trim(),
      ringRMinKm: Number(ringRMinInput.value) || 0,
      ringRMaxKm: Number(ringRMaxInput.value) || 0,
      entryCode: entryInput.value.trim(),
      entryAltFt: Number(entryAltInput.value) || 0,
    };
    saveLrn3dtForm();
    const form = state.lrn3dtForm;

    const layers = assembleLrnWindLayers();
    if (layers.length < 2) {
      setStatus(
        state.lrn3dtForm.windSource === 'windview'
          ? '風プロファイルが不足しています（WindViewのJSONを2層以上読み込んでください）'
          : '風プロファイルが不足しています（表示中の航跡で2層以上の高度差が観測できていません）',
        'warn',
      );
      return;
    }

    const ref = gridReferencePoint();
    const resolve = (code: string): { lat: number; lon: number } | null =>
      resolveTaskCircleCode(code, ref, state.utmWideEasting, state.utmWideNorthing, state.venue?.goals ?? []);

    if (form.mode === 'lrn-time') {
      const start = resolve(form.startCode);
      if (!start) {
        setStatus('開始点の座標を読み取れません', 'warn');
        return;
      }
      const u = toUtm(start.lon, start.lat);
      const task: LrnTimeTask = {
        mode: 'time',
        limitSec: form.limitSec,
        altMinFt: form.altMinFt,
        altMaxFt: form.altMaxFt,
        climbMps: form.climbMps,
        descentMps: form.descentMps,
        start: { e: u.easting, n: u.northing },
      };
      // アドバイザーは既存プランナーと同じ探索を共有しているので、
      // ここで adviseLrn を呼べば最良案（advice.best）は planLrnTime と一致する。
      // 2度探索しないよう、プランナー本体ではなくこちらだけを呼ぶ
      const advice = adviseLrn(task, layers);
      if (!advice.best) {
        setStatus('この条件では成立するルートが見つかりませんでした', 'warn');
        state.lrn3dtResult = null;
        renderLrn3dt();
        redraw();
        return;
      }
      state.lrn3dtResult = { kind: 'lrn', plan: advice.best, advice, refZone: u.zone, start };
    } else if (form.mode === 'lrn-circle') {
      const center = resolve(form.circleCenterCode);
      if (!center) {
        setStatus('中心点の座標を読み取れません', 'warn');
        return;
      }
      const u = toUtm(center.lon, center.lat);
      const task: LrnCircleTask = {
        mode: 'circle',
        limitSec: form.limitSec,
        altMinFt: form.altMinFt,
        altMaxFt: form.altMaxFt,
        climbMps: form.climbMps,
        descentMps: form.descentMps,
        circle: { center: { e: u.easting, n: u.northing }, radiusM: form.circleRadiusKm * 1000 },
      };
      const advice = adviseLrn(task, layers);
      if (!advice.best) {
        setStatus('この条件では成立するルートが見つかりませんでした', 'warn');
        state.lrn3dtResult = null;
        renderLrn3dt();
        redraw();
        return;
      }
      state.lrn3dtResult = {
        kind: 'lrn',
        plan: advice.best,
        advice,
        refZone: u.zone,
        circle: { lat: center.lat, lon: center.lon, radiusKm: form.circleRadiusKm },
      };
    } else {
      const center = resolve(form.ringCenterCode);
      const entry = resolve(form.entryCode);
      if (!center || !entry) {
        setStatus('中心点・進入点の座標を読み取れません', 'warn');
        return;
      }
      const centerU = toUtm(center.lon, center.lat);
      const entryU = toUtm(entry.lon, entry.lat);
      const ring: ThreeDtRing = {
        center: { e: centerU.easting, n: centerU.northing },
        rMinM: form.ringRMinKm * 1000,
        rMaxM: form.ringRMaxKm * 1000,
        altMinFt: form.altMinFt,
        altMaxFt: form.altMaxFt,
      };
      const plan = planThreeDtRoute(ring, layers, {
        entry: { e: entryU.easting, n: entryU.northing },
        entryAltFt: form.entryAltFt,
        limitSec: form.limitSec,
        climbMps: form.climbMps,
        descentMps: form.descentMps,
      });
      state.lrn3dtResult = {
        kind: '3dt',
        plan,
        refZone: centerU.zone,
        ring: { lat: center.lat, lon: center.lon, rMinKm: form.ringRMinKm, rMaxKm: form.ringRMaxKm },
        entry: { lat: entry.lat, lon: entry.lon },
      };
    }

    renderLrn3dt();
    redraw();
    setStatus('計算しました');
  };

  // 計算結果（地図上のルート表示）は、モードを閉じても消えない仕様
  // （計測モードと同じ「計算済みのものは明示的に消すまで残す」考え方）。
  // 消す手段が無いと利用者から指摘があったため追加する
  el('lrn3dt-clear-result').onclick = () => {
    state.lrn3dtResult = null;
    renderLrn3dt();
    redraw();
    setStatus('プランナーの計算結果を消しました');
  };

  const LRN3DT_MODE_LABELS: Record<Lrn3dtFormState['mode'], string> = {
    'lrn-time': 'LRN（時間切り）',
    'lrn-circle': 'LRN（周回開始）',
    '3dt': '3DT（円環）',
  };

  // LINEなどへ貼り付けて共有するためのプレーンテキスト版。表示（UTMグリッド
  // 表記に統一済み）と同じ座標形式で、計算に使った起点・中心点も含める
  // （結果の数字だけ送っても、何を前提にしたかが相手に伝わらないため）
  const buildLrn3dtShareText = (): string | null => {
    const result = state.lrn3dtResult;
    if (!result) return null;
    const lines: string[] = [];
    if (result.kind === 'lrn') {
      lines.push(LRN3DT_MODE_LABELS[result.circle ? 'lrn-circle' : 'lrn-time']);
      if (result.circle) {
        lines.push(`中心点: ${gridCodeText(result.circle)}（半径${result.circle.radiusKm}km）`);
      } else if (result.start) {
        lines.push(`開始点: ${gridCodeText(result.start)}`);
      }
      lines.push('');
      lines.push(buildLrnPlanSummaryText(result.plan));
      const adviceText = buildLrnAdviceText(result.advice);
      if (adviceText) {
        lines.push('', '【飛行手順】', adviceText);
      }
    } else {
      lines.push(LRN3DT_MODE_LABELS['3dt']);
      lines.push(`中心点: ${gridCodeText(result.ring)}（半径${result.ring.rMinKm}〜${result.ring.rMaxKm}km）`);
      lines.push(`進入点: ${gridCodeText(result.entry)}`);
      lines.push('');
      lines.push(buildThreeDtPlanSummaryText(result.plan));
    }
    lines.push('', '※推奨ルートは飛行支援のための参考値です。公式スコアは主催者の採点によります。');
    return lines.join('\n');
  };

  const copyResultBtn = el<HTMLButtonElement>('lrn3dt-copy-result');
  const copyResultBtnLabel = copyResultBtn.textContent ?? '';
  copyResultBtn.onclick = async () => {
    const text = buildLrn3dtShareText();
    if (!text) {
      setStatus('コピーする計算結果がありません', 'warn');
      return;
    }
    const ok = await copyTextToClipboard(text);
    copyResultBtn.textContent = ok ? 'コピーしました' : 'コピーできませんでした';
    setTimeout(() => {
      copyResultBtn.textContent = copyResultBtnLabel;
    }, 1500);
  };

  // 最小化（見出し行だけ残して畳み、地図を触れるようにする）。
  // スマホで設定画面が全面を覆い「地図中心」で点を指定できない、という
  // 実機からの指摘への対応（style.css の #lrn3dt-view.lrn3dt-minimized 参照）
  const minimizeBtn = el<HTMLButtonElement>('lrn3dt-minimize');
  const setMinimized = (minimized: boolean): void => {
    sheet.classList.toggle('lrn3dt-minimized', minimized);
    minimizeBtn.textContent = minimized ? '設定に戻る' : '地図を見る';
  };
  minimizeBtn.onclick = () => setMinimized(!sheet.classList.contains('lrn3dt-minimized'));

  /*
   * 「地図で選ぶ」= 地図をタップして入力点を置く（利用者要望: 地図中心では
   * なく、地図上でタップ・ドラッグで位置を決めたい）。
   * ボタンを押すと自動で最小化し（地図中心が押せなかった実機の指摘への
   * 対応と同じ理由）、タップした瞬間に自動で設定へ戻る（1回で解除する
   * 「地図をタップして登録」＝自機位置と同じ流儀）。
   */
  const pickButtons: Record<Lrn3dtPointField, HTMLButtonElement> = {
    start: el<HTMLButtonElement>('lrn3dt-start-center'),
    circleCenter: el<HTMLButtonElement>('lrn3dt-circle-center-map'),
    ringCenter: el<HTMLButtonElement>('lrn3dt-ring-center-map'),
    entry: el<HTMLButtonElement>('lrn3dt-entry-map'),
  };
  const setLrn3dtPicking = (field: Lrn3dtPointField | null): void => {
    if (field) exitMeasureMode?.();
    state.mapTapMode = field ? 'pick-lrn3dt' : 'normal';
    state.lrn3dtPickField = field;
    for (const [f, btn] of Object.entries(pickButtons) as [Lrn3dtPointField, HTMLButtonElement][]) {
      const active = f === field;
      btn.classList.toggle('subtle', !active);
      btn.textContent = active ? 'タップ待ち…（もう一度押すと解除）' : '地図で選ぶ';
    }
    map.getCanvas().style.cursor = field ? 'crosshair' : '';
    setMinimized(field != null);
  };
  for (const [field, btn] of Object.entries(pickButtons) as [Lrn3dtPointField, HTMLButtonElement][]) {
    btn.onclick = () => {
      const next = state.lrn3dtPickField === field ? null : field;
      setLrn3dtPicking(next);
      if (next) setStatus(`地図をタップして${LRN3DT_FIELD_LABELS[field]}を選んでください`);
    };
  }
  map.on('click', (e) => {
    if (state.mapTapMode !== 'pick-lrn3dt' || suppressNextClick || !state.lrn3dtPickField) return;
    const field = state.lrn3dtPickField;
    const { lat, lng } = e.lngLat;
    setLrn3dtPointCoords(field, { lat, lon: lng });
    setLrn3dtPicking(null);
    setStatus(`${LRN3DT_FIELD_LABELS[field]}を地図の位置にしました: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
  });

  /*
   * 置いた入力点のドラッグ。計測モードの頂点ドラッグ（findVertex）と同じ
   * 流儀 —「候補を集めて最も近い1つを選ぶ」— を使う。3DTはリング中心と
   * 進入点が同時に出るため、自機位置の仮の点（常に高々1点）とは違い、
   * 複数点から最寄りを選ぶ必要がある。
   */
  const lrn3dtDragContainer = map.getCanvasContainer();
  const LRN3DT_GRAB_PX = 22;
  let lrn3dtDragField: Lrn3dtPointField | null = null;
  const lrn3dtPosOf = (e: PointerEvent): maplibregl.LngLat => {
    const rect = lrn3dtDragContainer.getBoundingClientRect();
    return map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
  };
  const findLrn3dtPoint = (e: PointerEvent): Lrn3dtPointField | null => {
    const rect = lrn3dtDragContainer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const hits: Array<{ field: Lrn3dtPointField; d: number }> = [];
    for (const field of lrn3dtVisiblePointFields()) {
      const coords = state.lrn3dtPoints[field];
      if (!coords) continue;
      const p = map.project([coords.lon, coords.lat]);
      const d = Math.hypot(p.x - x, p.y - y);
      if (d <= LRN3DT_GRAB_PX) hits.push({ field, d });
    }
    hits.sort((a, b) => a.d - b.d);
    return hits[0]?.field ?? null;
  };
  lrn3dtDragContainer.addEventListener('pointerdown', (e) => {
    // 他の「タップ待ち」中は、そのモードのタップ処理を優先する
    if (state.mapTapMode !== 'normal' || e.button !== 0) return;
    const field = findLrn3dtPoint(e);
    if (!field) return;
    lrn3dtDragField = field;
    map.dragPan.disable();
  });
  lrn3dtDragContainer.addEventListener('pointermove', (e) => {
    if (!lrn3dtDragField) return;
    const { lat, lng } = lrn3dtPosOf(e);
    setLrn3dtPointCoords(lrn3dtDragField, { lat, lon: lng });
  });
  const endLrn3dtDrag = (): void => {
    if (!lrn3dtDragField) return;
    const field = lrn3dtDragField;
    lrn3dtDragField = null;
    map.dragPan.enable();
    suppressNextClick = true; // 指離しが地図タップとして二重発火しないように
    const coords = state.lrn3dtPoints[field];
    if (coords) {
      setStatus(`${LRN3DT_FIELD_LABELS[field]}をドラッグで調整しました: ${coords.lat.toFixed(6)}, ${coords.lon.toFixed(6)}`);
    }
  };
  lrn3dtDragContainer.addEventListener('pointerup', endLrn3dtDrag);
  lrn3dtDragContainer.addEventListener('pointercancel', () => {
    // dragPan は経路を問わず必ず戻す（他のドラッグ実装と同じ理由）
    lrn3dtDragField = null;
    map.dragPan.enable();
  });

  el<HTMLButtonElement>('lrn3dt-open').onclick = () => {
    sheet.hidden = false;
    setMinimized(false);
    renderLrn3dt();
  };
  el('lrn3dt-close').onclick = () => {
    sheet.hidden = true;
  };
  sheet.addEventListener('click', (e) => {
    // 最小化中は暗幕自体が pointer-events: none なのでここには来ないが、
    // 通常時に暗幕をタップしたら閉じる（他のダイアログと同じ挙動）
    if (e.target === sheet) sheet.hidden = true;
  });
}

/**
 * iOSは requestPermission() が拒否されれば明示的に分かるが、Androidや
 * その他の環境は「許可はしたが方位イベントが一向に来ない」まま何も
 * エラーが起きない場合がある（端末にモーションセンサーが無い、
 * OS/ブラウザ側でセンサー利用がオフになっている等）。この時間内に一度も
 * 読み取れなければヒントを出す。
 */
const COMPASS_NO_SIGNAL_TIMEOUT_MS = 4000;

/**
 * クルー用「磁北・真北」コンパス。手持ちの磁気コンパスは磁北基準だが
 * GPS・競技の公式データは真北基準なので、スマホ自体を磁気コンパスとして
 * 使い、両方を同時に表示する。磁気偏角は会場ごとに一度取得したら保持し
 * （毎回の自動取得はしない）、数値入力で微調整できる。
 */
function setupCompass(): void {
  const sheet = el('compass-panel');
  const unsupportedBox = el('compass-unsupported');
  const permissionBox = el('compass-permission');
  const permissionBtn = el<HTMLButtonElement>('compass-permission-request');
  const mainBox = el('compass-main');
  const errorBox = el('compass-error');
  const relativeWarn = el('compass-relative-warn');
  const headingMagnetic = el('compass-heading-magnetic');
  const headingTrue = el('compass-heading-true');
  const noSignalHint = el('compass-no-signal-hint');
  const declinationInput = el<HTMLInputElement>('compass-declination-input');
  const declinationNote = el('compass-declination-note');
  const declinationRefetchBtn = el<HTMLButtonElement>('compass-declination-refetch');
  const needleMagnetic = document.getElementById('compass-needle-magnetic') as unknown as SVGGElement;
  const needleTrue = document.getElementById('compass-needle-true') as unknown as SVGGElement;

  let stopCompass: (() => void) | null = null;
  let declinationDeg: number | null = null;
  let lastMagneticHeading: number | null = null;
  let noSignalTimer: ReturnType<typeof setTimeout> | null = null;

  const clearNoSignalTimer = (): void => {
    if (noSignalTimer != null) clearTimeout(noSignalTimer);
    noSignalTimer = null;
  };

  const setNeedle = (g: SVGGElement, headingDeg: number): void => {
    g.setAttribute('transform', `rotate(${headingDeg.toFixed(1)} 100 100)`);
  };

  const showBox = (which: 'unsupported' | 'permission' | 'main'): void => {
    unsupportedBox.hidden = which !== 'unsupported';
    permissionBox.hidden = which !== 'permission';
    mainBox.hidden = which !== 'main';
  };

  const updateTrueReadout = (): void => {
    if (declinationDeg == null || lastMagneticHeading == null) {
      headingTrue.textContent = '---°';
      return;
    }
    const t = magneticToTrueHeading(lastMagneticHeading, declinationDeg);
    headingTrue.textContent = `${Math.round(t)}°`;
    setNeedle(needleTrue, t);
  };

  const onReading = (r: CompassReading): void => {
    clearNoSignalTimer();
    noSignalHint.hidden = true;
    lastMagneticHeading = r.headingDeg;
    headingMagnetic.textContent = `${Math.round(r.headingDeg)}°`;
    setNeedle(needleMagnetic, r.headingDeg);
    // 絶対方位の基準が無い（relative-alpha）場合はドリフトしうるので注意書きを出す
    relativeWarn.hidden = r.source !== 'relative-alpha';
    updateTrueReadout();
  };

  const applyDeclination = (deg: number, source: 'auto' | 'manual', save: boolean): void => {
    declinationDeg = deg;
    declinationInput.value = deg.toFixed(1);
    if (save && state.venue) {
      saveDeclination(state.venue.id, { declinationDeg: deg, source, computedAt: Date.now() });
    }
    declinationNote.textContent =
      source === 'auto'
        ? `現在地から自動計算（${deg >= 0 ? '東偏' : '西偏'} ${Math.abs(deg).toFixed(1)}°）`
        : '手動で調整した値を使用中';
    updateTrueReadout();
  };

  const fetchDeclinationFromLocation = async (): Promise<void> => {
    declinationNote.textContent = '位置情報から磁気偏角を計算中…';
    try {
      const fix = await getOwnPositionOnce();
      const { declinationDeg: d } = getMagneticDeclination(fix.lat, fix.lon);
      applyDeclination(d, 'auto', true);
    } catch (err) {
      // 取得できなくても磁北側の表示自体は続ける（真北側だけ出せない扱い）
      declinationNote.textContent = err instanceof Error ? err.message : String(err);
    }
  };

  const startSensor = (): void => {
    stopCompass?.();
    stopCompass = subscribeCompassHeading(onReading, (message) => {
      errorBox.textContent = message;
      errorBox.hidden = false;
    });
    noSignalHint.hidden = true;
    clearNoSignalTimer();
    // iOSは許可が拒否されれば明示的に分かるが、Android等は「許可はできて
    // いるはずなのにイベントが一向に来ない」まま無音で失敗しうる
    // （実機のモーションセンサー無効化・非搭載など）。一定時間で判断する
    noSignalTimer = setTimeout(() => {
      if (lastMagneticHeading == null) noSignalHint.hidden = false;
    }, COMPASS_NO_SIGNAL_TIMEOUT_MS);
  };

  const open = (): void => {
    sheet.hidden = false;
    errorBox.hidden = true;
    noSignalHint.hidden = true;
    lastMagneticHeading = null;
    headingMagnetic.textContent = '---°';
    headingTrue.textContent = '---°';

    const support = detectCompassSupport();
    if (support === 'none') {
      showBox('unsupported');
      return;
    }

    // ★ 会場ごとに一度取得したら保持する。キャッシュがあればそれを使い、
    // 無ければ現在地から自動取得する（開くたびには取得し直さない）
    const cached = state.venue ? loadDeclination(state.venue.id) : null;
    if (cached) {
      applyDeclination(cached.declinationDeg, cached.source, false);
    } else {
      void fetchDeclinationFromLocation();
    }

    // ★ このページで既に許可済みなら、開くたびにプロンプトを出させない
    //   （iOSはブラウザ側で許可状態を憶えてくれないため、アプリ側で保持する）
    if (support === 'ios-permission-required' && !hasCompassPermission()) {
      showBox('permission');
      return;
    }
    showBox('main');
    startSensor();
  };

  const close = (): void => {
    sheet.hidden = true;
    stopCompass?.();
    stopCompass = null;
    clearNoSignalTimer();
  };
  closeCompassIfOpen = close;

  el<HTMLButtonElement>('compass-open').onclick = open;
  el('compass-close').onclick = close;
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });

  permissionBtn.onclick = async () => {
    // ★ iOSの制約: requestCompassPermission はここ（クリックハンドラ内）
    // から直接呼ぶ。非同期処理を挟むと許可プロンプトが出ない
    if ((await requestCompassPermission()) !== 'granted') {
      errorBox.textContent = '方位センサーの利用が許可されませんでした（端末の設定から変更できます）';
      errorBox.hidden = false;
      return;
    }
    showBox('main');
    startSensor();
  };

  declinationInput.onchange = () => {
    const v = Number(declinationInput.value);
    if (!Number.isFinite(v)) return;
    applyDeclination(v, 'manual', true);
  };

  declinationRefetchBtn.onclick = () => void fetchDeclinationFromLocation();
}

/**
 * パイバル（係留気球）風速計測。
 *
 * クルーがヘリウム風船を紐で係留すると、風船の浮力と風の抗力のつり合いで
 * 紐が鉛直から傾く（`packages/gv-core/src/pibal.ts` に理論の詳細）。
 * この傾き角度と紐の向き（方位）を端末のセンサーで読み取り、風向・風速へ
 * 変換する。方位はコンパスと同じ `deviceorientation`（alpha）、傾きは同じ
 * イベントの beta を使う（compass.ts 側には手を入れず、tilt.ts で別購読する）。
 *
 * ★ betaの生値は「端末を縦向きに構えたときのゼロ点」が持ち方の癖で個人差が
 *   出るため、水平線を狙ったときのbeta値を1回校正してもらい、そこからの
 *   差分で傾き角を出す。校正前は標準的な持ち方（beta=90が水平）を仮定する
 *   （精度は落ちるが機能はする）。実機での係数・符号の検証は未実施
 *   （README「積み残し」のコンパスセンサー精度確認と同様の注意が要る）。
 *
 * ★ 仰角も方位も、構えている間ずっと動き続ける（手ぶれ・実際の風の変動）。
 *   ボタンで1点を「確定」させてから計算する設計は、確定した瞬間の値が
 *   代表値としてふさわしいとは限らず使いづらい（利用者フィードバック、
 *   2026-08-22）。そのため風向・風速はセンサー値が更新されるたびに常時
 *   計算し直し、常に表示する。利用者は数値が落ち着いた瞬間を自分の目で
 *   判断して「今の値で記録する」を押す（そのときの生の値をそのまま使う）。
 */
function setupPibal(): void {
  const sheet = el('pibal-panel');
  const unsupportedBox = el('pibal-unsupported');
  const permissionBox = el('pibal-permission');
  const permissionBtn = el<HTMLButtonElement>('pibal-permission-request');
  const mainBox = el('pibal-main');
  const errorBox = el('pibal-error');

  const headingReadout = el('pibal-heading-readout');

  const uncalibratedHint = el('pibal-tilt-uncalibrated-hint');
  const tiltRawReadout = el('pibal-tilt-raw-readout');
  const tiltCalibrateBtn = el<HTMLButtonElement>('pibal-tilt-calibrate-horizon');
  const tiltCalibrateNote = el('pibal-tilt-calibrate-note');
  const tiltComputedReadout = el('pibal-tilt-computed-readout');

  const stringLengthInput = el<HTMLInputElement>('pibal-string-length');
  const kInput = el<HTMLInputElement>('pibal-k-input');
  const calibRefSpeedInput = el<HTMLInputElement>('pibal-calib-ref-speed');
  const calibRefBtn = el<HTMLButtonElement>('pibal-calib-ref-apply');

  const resultPlaceholder = el('pibal-result-placeholder');
  const resultValues = el('pibal-result-values');
  const resultDirFlow = el('pibal-result-dir-flow');
  const resultDirFrom = el('pibal-result-dir-from');
  const resultSpeed = el('pibal-result-speed');
  const resultAlt = el('pibal-result-alt');
  const saveBtn = el<HTMLButtonElement>('pibal-save');

  const listBox = el('pibal-list');

  let stopHeading: (() => void) | null = null;
  let stopTilt: (() => void) | null = null;
  let lastHeading: number | null = null;
  let lastBetaRaw: number | null = null;

  let settings = loadPibalSettings();

  const currentTiltFromVertical = (): number | null => {
    if (lastBetaRaw == null) return null;
    // 端末を縦向き・画面を自分へ向けて構え、水平線(仰角0)を狙ったときの
    // beta値を基準に、そこからの増分を仰角として扱う（校正前は90を仮定）
    const horizonBeta = settings.betaAtHorizon ?? 90;
    const elevation = lastBetaRaw - horizonBeta;
    return Math.max(0, Math.min(90, 90 - elevation));
  };

  const updateTiltReadouts = (): void => {
    tiltRawReadout.textContent = lastBetaRaw != null ? `${lastBetaRaw.toFixed(1)}°` : '---°';
    const t = currentTiltFromVertical();
    tiltComputedReadout.textContent = t != null ? `${t.toFixed(1)}°` : '---°';
  };

  const refreshCalibrateNote = (): void => {
    uncalibratedHint.hidden = settings.betaAtHorizon != null;
    tiltCalibrateNote.textContent =
      settings.betaAtHorizon != null
        ? `校正済み（水平線 = 生値${settings.betaAtHorizon.toFixed(1)}°）`
        : '未校正（標準的な持ち方を仮定した参考値）';
  };

  /**
   * 「今」の生センサー値から風向・風速を計算して常時表示する。
   * heading/tilt どちらかのセンサーイベントが来るたびに呼び直す（下のonReading参照）。
   */
  const updateResult = (): void => {
    const tilt = currentTiltFromVertical();
    const k = Number(kInput.value);
    const stringLengthM = Number(stringLengthInput.value);
    const ready =
      lastHeading != null &&
      tilt != null &&
      state.venue != null &&
      Number.isFinite(k) &&
      k > 0 &&
      Number.isFinite(stringLengthM) &&
      stringLengthM > 0;
    if (!ready || lastHeading == null || tilt == null) {
      resultPlaceholder.hidden = false;
      resultValues.hidden = true;
      saveBtn.disabled = true;
      return;
    }
    const speed = windSpeedFromTilt(tilt, { k });
    const geom = pibalGeometry(stringLengthM, tilt);
    const dirFlow = ((lastHeading % 360) + 360) % 360;
    resultDirFlow.textContent = `${Math.round(dirFlow)}°`;
    resultDirFrom.textContent = `${Math.round((dirFlow + 180) % 360)}°`;
    resultSpeed.textContent = `${speed.toFixed(1)} m/s（${(speed * 3.6).toFixed(1)} km/h）`;
    resultAlt.textContent = `アンカーの高さ +${geom.heightAboveAnchorM.toFixed(1)} m`;
    resultPlaceholder.hidden = true;
    resultValues.hidden = false;
    saveBtn.disabled = false;
  };

  const renderList = (): void => {
    listBox.innerHTML = '';
    const samples = state.venue ? loadPibalSamples(state.venue.id) : [];
    if (samples.length === 0) {
      const li = document.createElement('li');
      li.className = 'pibal-list-empty';
      li.textContent = 'まだ記録がありません';
      listBox.appendChild(li);
      return;
    }
    // 新しい記録を上に
    samples
      .map((s, i) => ({ s, i }))
      .sort((a, b) => b.s.t - a.s.t)
      .forEach(({ s, i }) => {
        const li = document.createElement('li');
        li.className = 'pibal-list-item';
        const time = new Date(s.t).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
        const label = document.createElement('span');
        label.textContent = `${time}　${Math.round(s.dir)}°　${(s.speed * 3.6).toFixed(1)}km/h`;
        const delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'pibal-list-delete';
        delBtn.textContent = '削除';
        delBtn.onclick = () => {
          if (!state.venue) return;
          const cur = loadPibalSamples(state.venue.id);
          cur.splice(i, 1);
          savePibalSamples(state.venue.id, cur);
          renderList();
          renderWindProfile();
        };
        li.appendChild(label);
        li.appendChild(delBtn);
        listBox.appendChild(li);
      });
  };

  const showBox = (which: 'unsupported' | 'permission' | 'main'): void => {
    unsupportedBox.hidden = which !== 'unsupported';
    permissionBox.hidden = which !== 'permission';
    mainBox.hidden = which !== 'main';
  };

  const startSensors = (): void => {
    stopHeading?.();
    stopTilt?.();
    stopHeading = subscribeCompassHeading(
      (r) => {
        lastHeading = r.headingDeg;
        headingReadout.textContent = `${Math.round(r.headingDeg)}°`;
        updateResult();
      },
      (message) => {
        errorBox.textContent = message;
        errorBox.hidden = false;
      },
    );
    stopTilt = subscribeTilt(
      (r) => {
        lastBetaRaw = r.betaDeg;
        updateTiltReadouts();
        updateResult();
      },
      (message) => {
        errorBox.textContent = message;
        errorBox.hidden = false;
      },
    );
  };

  const open = (): void => {
    settings = loadPibalSettings();
    stringLengthInput.value = String(settings.stringLengthM);
    kInput.value = String(settings.k);
    calibRefSpeedInput.value = '';
    lastHeading = null;
    lastBetaRaw = null;
    headingReadout.textContent = '---°';
    errorBox.hidden = true;
    refreshCalibrateNote();
    updateTiltReadouts();
    updateResult();
    renderList();

    sheet.hidden = false;

    const support = detectCompassSupport();
    if (support === 'none') {
      showBox('unsupported');
      return;
    }
    // ★ コンパス窓側で既に許可済みなら、こちらでも改めてプロンプトを出させない
    //   （同じ端末の方位・傾きセンサーの許可なので、片方で許可すればもう片方も済んでいる）
    if (support === 'ios-permission-required' && !hasCompassPermission()) {
      showBox('permission');
      return;
    }
    showBox('main');
    startSensors();
  };

  const close = (): void => {
    sheet.hidden = true;
    stopHeading?.();
    stopTilt?.();
    stopHeading = null;
    stopTilt = null;
  };
  closePibalIfOpen = close;

  el<HTMLButtonElement>('pibal-open').onclick = open;
  el('pibal-close').onclick = close;
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });

  permissionBtn.onclick = async () => {
    // ★ iOSの制約: requestCompassPermission はここ（クリックハンドラ内）
    // から直接呼ぶ。非同期処理を挟むと許可プロンプトが出ない
    if ((await requestCompassPermission()) !== 'granted') {
      errorBox.textContent = 'センサーの利用が許可されませんでした（端末の設定から変更できます）';
      errorBox.hidden = false;
      return;
    }
    showBox('main');
    startSensors();
  };

  tiltCalibrateBtn.onclick = () => {
    if (lastBetaRaw == null) return;
    settings = { ...settings, betaAtHorizon: lastBetaRaw };
    savePibalSettings(settings);
    refreshCalibrateNote();
    updateTiltReadouts();
    updateResult();
  };

  stringLengthInput.onchange = () => {
    const v = Number(stringLengthInput.value);
    if (!Number.isFinite(v) || v <= 0) return;
    settings = { ...settings, stringLengthM: v };
    savePibalSettings(settings);
    updateResult();
  };

  kInput.onchange = () => {
    const v = Number(kInput.value);
    if (!Number.isFinite(v) || v <= 0) return;
    settings = { ...settings, k: v };
    savePibalSettings(settings);
    updateResult();
  };

  calibRefBtn.onclick = () => {
    const refSpeed = Number(calibRefSpeedInput.value);
    const tilt = currentTiltFromVertical();
    if (tilt == null || !Number.isFinite(refSpeed) || refSpeed <= 0) return;
    const k = calibrateKFromReference(refSpeed, tilt);
    kInput.value = k.toFixed(2);
    settings = { ...settings, k };
    savePibalSettings(settings);
    updateResult();
  };

  saveBtn.onclick = async () => {
    const tilt = currentTiltFromVertical();
    if (!state.venue || lastHeading == null || tilt == null) return;
    const k = Number(kInput.value);
    const stringLengthM = Number(stringLengthInput.value);
    if (!Number.isFinite(k) || k <= 0 || !Number.isFinite(stringLengthM) || stringLengthM <= 0) return;

    // 保存中に押し直されないよう止めるが、GPS取得中もセンサー購読・表示更新は
    // 続けたいので stopHeading/stopTilt は止めない（風は保存を待ってくれない）
    saveBtn.disabled = true;
    try {
      const fix = await getOwnPositionOnce();
      const speed = windSpeedFromTilt(tilt, { k });
      const geom = pibalGeometry(stringLengthM, tilt);
      // コンパスのheadingDegは磁北基準（compass.tsのCompassReading参照）。
      // WindSample.dirはコードベース全体で真方位の規約（types.tsのWindSample
      // 定義参照）なので、setupCompass()と同じ手順で真方位へ変換してから
      // 保存する。以前はここでこの変換が漏れており、パイバル由来のdirだけ
      // 磁北基準のまま保存されていた（docs/08参照）
      const magneticDir = ((lastHeading % 360) + 360) % 360;
      const cachedDeclination = loadDeclination(state.venue.id);
      const declinationDeg = cachedDeclination?.declinationDeg ?? getMagneticDeclination(fix.lat, fix.lon).declinationDeg;
      const sample: WindSample = {
        lat: fix.lat,
        lon: fix.lon,
        alt: (fix.altMsl ?? 0) + geom.heightAboveAnchorM,
        t: Date.now(),
        dir: magneticToTrueHeading(magneticDir, declinationDeg),
        speed,
        source: 'pibal',
        quality: pibalQuality(tilt),
      };
      const venueId = state.venue.id;
      const existing = loadPibalSamples(venueId);
      existing.push(sample);
      savePibalSamples(venueId, existing);

      renderList();
      renderWindProfile();
    } catch (err) {
      errorBox.textContent = err instanceof Error ? err.message : String(err);
      errorBox.hidden = false;
    } finally {
      updateResult();
    }
  };
}

// --- 自機位置の送信 ----------------------------------------------------------

/** モード切替のたびに呼び直す。オーディエンスでは自機位置欄ごと隠す */
let syncMyPositionVisibility: (() => void) | null = null;

function setupTransmit(): void {
  const section = el('my-position-section');
  const idInput = el<HTMLInputElement>('my-terminal-id');
  const warnBox = el('my-terminal-warn');
  const sourceSelect = el<HTMLSelectElement>('my-position-source');
  const manualBox = el('my-manual-box');
  const manualCoordsInput = el<HTMLInputElement>('my-manual-coords');
  const manualApplyBtn = el<HTMLButtonElement>('my-manual-apply');
  const readoutBox = el('my-position-readout');
  const followSelect = el<HTMLSelectElement>('follow-target');
  const noTransmitRow = el('my-no-transmit-row');
  const noTransmitCheckbox = el<HTMLInputElement>('my-no-transmit-toggle');
  const intervalRow = el('my-upload-interval-row');
  const intervalInput = el<HTMLInputElement>('my-upload-interval');
  const toggleBtn = el<HTMLButtonElement>('my-transmit-toggle');
  const infoBox = el('my-transmit-info');
  const saveTrackBtn = el<HTMLButtonElement>('my-track-save');

  let stopGeoWatch: (() => void) | null = null;
  let manualTimer: ReturnType<typeof setInterval> | null = null;
  let lastError: string | null = null;
  // GPS取得の生死を、通信（transmitter.status）とは別に持つ。
  // 'pending'は「まだ一度もGPSが成功していない」状態で、この間は通信が
  // 成功していても緑にしない（PR#98再レビュー指摘：GPS取得前に一瞬でも
  // ポーリングが通ると誤って「送信中」に見えてしまうのを防ぐため）
  let gpsStatus: 'pending' | 'ok' | 'error' = 'pending';
  // 送信中は画面消灯を防ぐ。「他アプリを前面にしている間も送信を続ける」こと自体は
  // PWAでは実現できないが、実運用で最多の停止原因（ポケット内で画面が消えて止まる）
  // はこれで防げる（docs/08参照）
  const wakeLock = new ScreenWakeLockKeeper();

  // 同じ端末IDを名乗る事故を防ぐ。原典プロトコルは端末IDだけが識別子で、
  // 衝突すると相手の航跡が自分のものと混ざって壊れる（docs/03 §5）
  // ★ 「使っている」とみなすのは、今まさに送っている端末だけ（最後の受信が15分以内）。アプリのデータが消えて
  //   自分のIDを忘れたとき、Bridge に残る自分の前回の航跡を「他の端末」と誤って警告した（docs/08 §230）
  const checkCollision = (): boolean => {
    const id = idInput.value.trim();
    const list = state.bridge ? state.bridge.terminals : (state.liveSnapshot?.terminals ?? []);
    const use = terminalIdUse(id, list, Date.now(), loadMyTerminalId());
    warnBox.hidden = !use.inUse;
    if (use.inUse && use.lastSeen != null) {
      const z = toZonedTime(use.lastSeen, state.timeZone);
      warnBox.textContent =
        `「${id}」は他の端末が送信中です（最後の受信 ${z.time} ${z.zone}）。` +
        `同じIDで送ると航跡が混ざります。自分の端末を設定し直しただけなら、そのまま開始できます`;
    }
    return use.inUse;
  };
  idInput.oninput = checkCollision;
  // 前回自分が使ったIDを覚えておき、次回の入力の手間を減らす
  if (!idInput.value) idInput.value = loadMyTerminalId();

  sourceSelect.onchange = () => {
    manualBox.hidden = sourceSelect.value !== 'manual';
    // 手入力は「固定点を他機に知らせる」ためのものなので、送信しない版は無意味
    noTransmitRow.hidden = sourceSelect.value === 'manual';
    // 手入力の送信間隔は原典互換の2.5秒固定（別枠）なので、この欄はGPSのときだけ出す
    intervalRow.hidden = sourceSelect.value === 'manual';
    syncManualMarker();
    redrawGoalBearingLine();
  };
  noTransmitRow.hidden = sourceSelect.value === 'manual';
  intervalRow.hidden = sourceSelect.value === 'manual';

  intervalInput.value = String(state.uploadIntervalSec);
  const onIntervalInput = (raw: number): void => {
    const v = Math.min(600, Math.max(5, Math.round(raw)));
    state.uploadIntervalSec = v;
    // 入力中の欄を書き換えると打てなくなるので、フォーカスされていなければ触らない
    if (document.activeElement !== intervalInput) intervalInput.value = String(v);
    try {
      localStorage.setItem(UPLOAD_INTERVAL_SEC_KEY, String(v));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  intervalInput.oninput = (e) => onIntervalInput(Number((e.target as HTMLInputElement).value));

  /**
   * 送信の有無でボタン文言を出し分ける（送信しないモードでは「送信」と言わない）。
   * 手入力ソースは常に送信する（`toggleBtn.onclick` の `willTransmit` と同じ条件）
   */
  const buttonLabel = (): { start: string; stop: string } =>
    state.noTransmit && sourceSelect.value !== 'manual'
      ? { start: '追従開始', stop: '追従停止' }
      : { start: '送信開始', stop: '送信停止' };

  /**
   * 手動座標を、地図上の仮の点（ドラッグして微調整できる）として表す。
   * ★ 最初は`maplibregl.Marker`（DOM要素・`draggable`）で実装したが、実機
   *   （iOS Safari）で座標は正しく設定されるのに地図上に点が描画されない
   *   不具合が確認された（原因未特定・このサンドボックスでは再現できず）。
   *   このアプリの他の地物（自機位置ほか）はすべてGeoJSONソース+circle
   *   レイヤーで描いており実機で確認済みの方式なので、Markerを諦めて
   *   そちらに合わせた（`MANUAL_POSITION_SOURCE`、`map/layers.ts`参照）。
   */
  const syncManualMarker = (): void => {
    const show = sourceSelect.value === 'manual' && state.manualCoords != null;
    setSourceData(map, MANUAL_POSITION_SOURCE, show ? manualPositionToGeoJson(state.manualCoords) : EMPTY_GEOJSON);
  };

  /**
   * 仮の点をドラッグする。`setupLongPress`と同じ流儀（コンテナへの生ポインタ
   * イベント＋`map.unproject`）で自前実装する。MapLibre標準のドラッグ対応
   * レイヤーAPIは無く、`queryRenderedFeatures`によるヒットテストも不要
   * （常に高々1点しか無いので、押した位置が点の近くかどうかの距離判定で足りる）。
   */
  const manualDragContainer = map.getCanvasContainer();
  let manualDragging = false;
  const manualPosOf = (e: PointerEvent): maplibregl.LngLat => {
    const rect = manualDragContainer.getBoundingClientRect();
    return map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
  };
  manualDragContainer.addEventListener('pointerdown', (e) => {
    if (sourceSelect.value !== 'manual' || !state.manualCoords || e.button !== 0) return;
    // 計測モード・プランナーのタップ待ち中は、そのモードのタップ処理を優先する
    // （掴んでも動かない代わりに、意図しない場所へ仮の点が動いてタップを
    //   飲み込む＝suppressNextClickで別モードのタップが不発になる事故を防ぐ）
    if (state.mapTapMode === 'measure' || state.mapTapMode === 'pick-lrn3dt') return;
    const rect = manualDragContainer.getBoundingClientRect();
    const screen = map.project([state.manualCoords.lon, state.manualCoords.lat]);
    const dx = e.clientX - rect.left - screen.x;
    const dy = e.clientY - rect.top - screen.y;
    if (Math.hypot(dx, dy) > 22) return; // 仮の点の近くだけ掴める（実機でのタップ精度を見込んで広め）
    manualDragging = true;
    map.dragPan.disable(); // ドラッグ中は地図自体を動かさせない
  });
  /*
   * ★ ドラッグ中も `state.manualCoords` を更新すること。
   *   以前はここで地図のソースだけを差し替えており、`state.manualCoords` は
   *   指を離すまで古い位置のままだった。ゴールへの点線は
   *   `redrawGoalBearingLine()` がこの状態を読んで描くので、ドラッグ中は
   *   距離・方位が置いた時のまま止まって見えた（実機からの指摘）。
   *   状態を単一の真実にして、点・線・座標欄をすべてそこから描き直す。
   */
  const applyManualDrag = (e: PointerEvent): { lat: number; lon: number } => {
    const { lat, lng } = manualPosOf(e);
    const coords = { lat, lon: lng };
    state.manualCoords = coords;
    manualCoordsInput.value = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    syncManualMarker();
    redrawGoalBearingLine();
    return coords;
  };
  manualDragContainer.addEventListener('pointermove', (e) => {
    if (!manualDragging) return;
    applyManualDrag(e);
  });
  const endManualDrag = (e: PointerEvent): void => {
    if (!manualDragging) return;
    manualDragging = false;
    map.dragPan.enable();
    const { lat, lon } = applyManualDrag(e);
    suppressNextClick = true; // ドラッグを終えた指離しが地図タップとして二重発火しないように
    setStatus(`ドラッグで座標を調整しました: ${lat.toFixed(6)}, ${lon.toFixed(6)}`);
  };
  manualDragContainer.addEventListener('pointerup', endManualDrag);
  manualDragContainer.addEventListener('pointercancel', () => {
    // ★ dragPan は経路を問わず必ず戻す（setupLongPress の cancelFine と同じ理由。
    //   戻し忘れると地図が動かせないまま固まる）。
    //   位置は pointermove で state ごと確定済みなので、中断してもその場に残る
    //   （点と状態が食い違うことは無い）。
    manualDragging = false;
    map.dragPan.enable();
  });

  manualApplyBtn.onclick = () => {
    const parsed = parseLatLon(manualCoordsInput.value);
    if (!parsed) {
      setStatus(
        '座標を読み取れません（度分秒か「緯度, 経度」の形式で入力してください）',
        'warn',
      );
      return;
    }
    state.manualCoords = parsed;
    syncManualMarker();
    redrawGoalBearingLine();
    render();
    setStatus(`座標を設定しました: ${parsed.lat.toFixed(6)}, ${parsed.lon.toFixed(6)}`);
  };

  // 一度置いた仮の点を消す（送信元を切り替えなくても、この場で取り消せるように）
  el<HTMLButtonElement>('my-manual-clear').onclick = () => {
    state.manualCoords = null;
    manualCoordsInput.value = '';
    syncManualMarker();
    redrawGoalBearingLine();
    render();
    setStatus('座標を消しました');
  };

  // 地図をタップして座標を決める。「タップ待ち」の間だけ有効にし、1回使ったら
  // 自動で解除する（つけっぱなしで地図を普通に操作すると誤登録するのを防ぐ）
  const pickBtn = el<HTMLButtonElement>('my-manual-pick');
  const setPositionPicking = (on: boolean): void => {
    // 計測モードとは排他。こちらを始めるときは計測モードを畳む
    if (on) exitMeasureMode?.();
    state.mapTapMode = on ? 'pick-position' : 'normal';
    pickBtn.classList.toggle('subtle', !on);
    pickBtn.textContent = on ? '地図をタップ…（もう一度押すと解除）' : '地図をタップして登録';
    map.getCanvas().style.cursor = on ? 'crosshair' : '';
  };
  pickBtn.onclick = () => {
    const next = state.mapTapMode !== 'pick-position';
    setPositionPicking(next);
    if (next) {
      // 狭い画面ではパネル（またはその外側の暗幕）が地図を覆っているため、
      // 閉じないとタップが地図まで届かない
      closePanel?.();
      setStatus('地図をタップした地点を自分の位置にします');
    }
  };
  map.on('click', (e) => {
    if (state.mapTapMode !== 'pick-position' || suppressNextClick) return;
    const { lat, lng } = e.lngLat;
    state.manualCoords = { lat, lon: lng };
    manualCoordsInput.value = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    syncManualMarker();
    redrawGoalBearingLine();
    setPositionPicking(false);
    setStatus(`地図をタップした地点を座標にしました: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
  });

  const render = () => {
    // 「送るべき位置がまだ無い」状態では、通信（transmitter.status）が
    // どうであれ緑にしない。GPSソースはgpsStatus（取得前・エラー中はNG）、
    // 手入力ソースはstate.manualCoordsの有無（未設定・消去済みならNG）で
    // 判定する。位置の準備ができていれば、以後は通信の実際の成否
    // （onPollSuccess駆動のstatus）だけで判定する
    // （PR#98再レビュー指摘：GPS/手入力の位置状態と通信状態の混同）
    const isManualSource = sourceSelect.value === 'manual';
    const positionReady = isManualSource ? state.manualCoords != null : gpsStatus === 'ok';
    const positionError = !isManualSource && gpsStatus === 'error';
    const txState: 'off' | 'sending' | 'error' =
      !state.transmitter
        ? 'off'
        : positionError
          ? 'error'
          : !positionReady
            ? 'off'
            : state.transmitter.status === 'sending'
              ? 'sending'
              : state.transmitter.status === 'error'
                ? 'error'
                : 'off';
    setTxDot(txState);
    if (!state.transmitter) {
      // stopGeoWatch が動いている＝送信しないモードでGPS追従だけしている状態。
      // 「未送信」だけだと何も起きていないように見えるため、区別して伝える
      infoBox.innerHTML = stopGeoWatch
        ? '<p class="muted">GPS追従中（送信していません）</p>'
        : '<p class="muted">未送信</p>';
      return;
    }
    const rows = [
      `送信済み ${state.transmitter.sentCount} 回`,
      state.transmitter.pendingCount > 0 ? `送信待ち ${state.transmitter.pendingCount} 行` : null,
      lastError ? `エラー: ${escapeHtml(lastError)}` : null,
    ].filter(Boolean);
    infoBox.innerHTML = rows.join('<br>');
  };

  /** 自機の現在値の読み取り窓。送信の間引きとは無関係に、GPSが届くたび即時に出す */
  const renderReadout = () => {
    const fix = state.myFix;
    if (!fix) {
      readoutBox.innerHTML = '<p class="muted">未取得</p>';
      return;
    }
    const rows = [
      `${fix.lat.toFixed(6)}, ${fix.lon.toFixed(6)}`,
      fix.altMsl == null ? null : `高度 ${Math.round(metersToFeet(fix.altMsl))} ft`,
      fix.speed == null ? null : `対地速度 ${(fix.speed * 3.6).toFixed(1)} km/h`,
      fix.heading == null ? null : `方位 ${Math.round(fix.heading)}°`,
      fix.accuracy == null ? null : `精度 ±${Math.round(fix.accuracy)} m`,
    ].filter(Boolean);
    readoutBox.innerHTML = rows.join('<br>');
  };

  renderFollowTargetOptions();
  followSelect.onchange = () => {
    state.followTarget = followSelect.value;
    // 選び直した直後は、手で動かした直後の一時停止に関係なくすぐ寄せたい
    state.followMePausedUntil = 0;
    state.followLastCenter = null;
    try {
      localStorage.setItem(FOLLOW_TARGET_KEY, state.followTarget);
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    applyMapFollow();
  };

  noTransmitCheckbox.checked = state.noTransmit;
  noTransmitCheckbox.onchange = () => {
    state.noTransmit = noTransmitCheckbox.checked;
    try {
      localStorage.setItem(NO_TRANSMIT_KEY, state.noTransmit ? '1' : '0');
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    // 次に押したときのボタン文言をすぐ反映する（追従・送信のどちらも動いていないとき）
    if (!state.transmitter && !stopGeoWatch) toggleBtn.textContent = buttonLabel().start;
  };
  // 起動時、保存済みの設定を初期表示に反映する（起動直後は必ず未起動なのでstartでよい）
  toggleBtn.textContent = buttonLabel().start;

  saveTrackBtn.hidden = state.myTrackFixes.length === 0;
  saveTrackBtn.onclick = () => {
    if (state.myTrackFixes.length === 0) return;
    const id = idInput.value.trim() || 'SELF';
    // パイロットモードでの自送信は気球自身、それ以外はチェイスクルーの車とみなす
    const source: FixSample['source'] = state.mode === 'pilot' ? 'balloon' : 'chase';
    const fixes: FixSample[] = state.myTrackFixes.map((f) => ({
      terminalId: id,
      t: f.t,
      timeQuality: 'measured',
      lat: f.lat,
      lon: f.lon,
      altMsl: f.altMsl,
      altPressure: null,
      speed: f.speed,
      heading: f.heading,
      verticalSpeed: null,
      accuracy: f.accuracy,
      altAccuracy: null,
      source,
    }));
    const safeId = id.replace(/[^A-Za-z0-9_-]/g, '_');
    const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');
    downloadTextFile(`${safeId}_${stamp}.igc`, buildIgc(id, fixes));
  };

  const stopAll = () => {
    state.transmitter?.stop();
    state.transmitter = null;
    stopGeoWatch?.();
    stopGeoWatch = null;
    if (manualTimer) clearInterval(manualTimer);
    manualTimer = null;
    gpsStatus = 'pending';
    wakeLock.stop();
    // 表示中の現在位置マーカーは消すが、記録済みの航跡ログは保存できるよう残す
    state.myFix = null;
    renderReadout();
    redraw();
  };

  // 画面が前面に戻ったとき: 自動解放された画面ロックを取り直し、送信が
  // 実際にどれくらい止まっていたかを伝える（間引き待ちの通常の谷を誤検知
  // しないよう、間引き間隔の3倍以上あいたときだけ知らせる）
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    void wakeLock.reacquire();
    const transmitter = state.transmitter;
    if (!transmitter) return;
    const message = staleSinceMessage(Date.now(), transmitter.lastSentAt, transmitter.throttleMs);
    if (message) setStatus(message, 'warn');
  });

  /** 送信中・送信しないモードでの追従中のどちらか（＝「動いている」）を1つの条件にまとめる */
  const isActive = (): boolean => !!state.transmitter || !!stopGeoWatch;

  // オーディエンスは自機位置の発信をしない（開発メモ：観戦専用、帯域も抑える）
  syncMyPositionVisibility = () => {
    const hide = state.mode === 'audience';
    section.hidden = hide;
    if (hide && isActive()) {
      stopAll();
      toggleBtn.textContent = buttonLabel().start;
      toggleBtn.classList.remove('connected');
      render();
    }
  };
  syncMyPositionVisibility();

  /**
   * GPSの継続取得だけを始める（送信は`state.transmitter`が居るときだけ、
   * `update()`経由で自動的に付随する）。`toggle()`の通常の開始と、起動時の
   * 自動開始（下記）の両方から呼ぶ共通処理。
   */
  const startGeoWatchDisplay = (): void => {
    stopGeoWatch = watchOwnPosition(
      (fix) => {
        // 表示（地図・読み取り窓）は間引かず、GPSが届くたび即座に更新する。
        // 送信側の間引き（1分）はtransmitter.update()の中だけで完結する
        gpsStatus = 'ok';
        state.myFix = fix;
        state.myTrackFixes.push(fix);
        saveTrackBtn.hidden = false;
        renderReadout();
        // 追従は redraw() の中の applyMapFollow() が面倒を見る
        // （自分だけでなく気球も追えるようにしたため、位置の出所ごとに
        //   書くのをやめて1箇所へ集約した）
        redraw();
        state.transmitter?.update({
          t: fix.t,
          lat: fix.lat,
          lon: fix.lon,
          altMsl: fix.altMsl,
          speed: fix.speed,
          heading: fix.heading,
        });
        render();
      },
      (message) => {
        gpsStatus = 'error';
        lastError = message;
        setStatus(`位置情報エラー: ${message}`, 'warn');
        render();
      },
    );
  };

  /**
   * 「送信開始／停止」ボタンの本体。上部バーの「まとめて開始／停止」からも
   * 同じものを呼ぶ（端末IDの未入力・重複確認、合言葉ゲート、画面ロックと
   * いった前提条件を二重に書かないため）。
   */
  const toggle = async (): Promise<void> => {
    // 送信しないモード（GPS追従のみ）では、端末IDは自分の航跡をIGCで
    // 保存するときのラベルにしか使わない。他機と衝突する心配が無いので、
    // 未入力チェック・衝突チェックの両方を免除する（入れてあれば衝突は従来通り見る）
    const willTransmit = !state.noTransmit || sourceSelect.value === 'manual';
    const alreadyActive = isActive();
    // 今動いている内容（実際に送信中か、GPS表示のみか）が、チェックボックスが
    // いま望んでいる内容と一致していれば「停止」だけで終わる
    if (alreadyActive && willTransmit === !!state.transmitter) {
      stopAll();
      toggleBtn.textContent = buttonLabel().start;
      toggleBtn.classList.remove('connected');
      render();
      return;
    }

    const terminalId = idInput.value.trim();
    if (willTransmit && !terminalId) {
      setStatus('端末IDを入力してください', 'warn');
      return;
    }
    // ★ ここで完全にブロックはしない。自分のIDの再接続（キャッシュを消した
    //   直後などで「自分のID」だと分からなくなった場合を含む）を確認1回だけで
    //   通せるようにする。他人との本当の衝突を防ぐ目的は、警告＋確認で足りる
    if ((willTransmit || terminalId) && checkCollision()) {
      const proceed = confirm(
        `「${terminalId}」は、いま他の端末が送信中です。\n` +
          `同じIDで送ると、航跡が混ざります。\n\n` +
          `この端末を設定し直しただけ（前と同じ端末）なら「OK」を押してください。`,
      );
      if (!proceed) {
        setStatus('端末IDが重複しています。変更してください', 'warn');
        return;
      }
    }
    if (terminalId) saveMyTerminalId(terminalId);

    // ここまで来たら確実に開始できる。動いている内容が食い違う場合
    // （例: 起動時に自動開始したGPS表示のみが動いている状態で「送信しない」
    // を外して押した、または上部バーの「接続と送信」を押した）は、ここで
    // 初めて一旦止める（検証前に止めてしまうと、端末ID未入力などで開始に
    // 失敗したときGPS表示まで失われてしまうため）
    if (alreadyActive) stopAll();

    if (willTransmit) {
      // 合言葉ゲートが掛かっている大会なら、送信を始める前に一度だけ確認する
      // （未設定・既に通っていれば即座に素通り）
      if (!(await ensureBridgeAuth(location.origin))) {
        setStatus('合言葉が確認できないため送信していません', 'warn');
        return;
      }
      // GV Bridge は vite.config.ts / 本番の Caddy 設定で /cgi-bin をこのアプリと
      // 同一オリジンに載せてあるので、自分のページの URL がそのまま送信先になる
      // ★ GPSソースだけ送信間隔を「送信間隔（秒）」欄の値に間引く（既定は
      //   DEFAULT_UPLOAD_INTERVAL_SEC=60秒。車移動を想定した暫定値だったが、
      //   状況次第で変えたいとの要望を受けて画面から入力できるようにした）。
      //   手入力の定点はそのままの原典互換間隔（2.5秒）で「生きている」ことだけ伝える
      const transmitter = new SelfPositionTransmitter({
        endpoint: `${location.origin}/cgi-bin/sr.cgi`,
        terminalId,
        throttleMs: sourceSelect.value === 'manual' ? undefined : state.uploadIntervalSec * 1000,
      });
      transmitter.onStatus = (status, message) => {
        lastError = status === 'error' ? message : null;
        render();
      };
      transmitter.start();
      state.transmitter = transmitter;
      void wakeLock.start();
    }

    if (sourceSelect.value === 'manual') {
      if (!state.manualCoords) {
        setStatus('先に「この座標を使う」で座標を設定してください', 'warn');
      }
      // 手入力の座標は動かないので、間引き間隔と同じ周期で「いまの時刻」を
      // 付け直して送り続ける（実際の測位のように新しいtで届き続けるように）。
      // 座標が未設定・後から消された間はupdate()を呼ばない（＝実際には
      // 何も送らない）が、render()は毎回呼び、その時点のstate.manualCoords
      // の有無をインジケーターへ即座に反映させる（座標が無いまま緑になる
      // 誤表示を防ぐ。PR#98再レビュー指摘）
      manualTimer = setInterval(() => {
        if (state.manualCoords && state.transmitter) {
          state.transmitter.update({
            t: Date.now(),
            lat: state.manualCoords.lat,
            lon: state.manualCoords.lon,
          });
        }
        render();
      }, 2500);
    } else {
      startGeoWatchDisplay();
    }

    toggleBtn.textContent = buttonLabel().stop;
    toggleBtn.classList.add('connected');
    render();
  };
  toggleBtn.onclick = toggle;
  toggleSelfTransmit = toggle;
  // 上部バーの「接続と送信」判定は、GPS表示のみ（起動時の自動開始）と
  // 実際の送信中を区別する必要があるため、`isActive()`ではなく実際に
  // `SelfPositionTransmitter`が居るかどうかで見る
  isSelfTransmitActive = () => !!state.transmitter;
  requestSelfTransmit = () => {
    noTransmitCheckbox.checked = false;
    noTransmitCheckbox.dispatchEvent(new Event('change'));
  };

  // 起動時に自動で「送信しない・GPS表示のみ」を始める。パイロット・クルーが
  // 何も操作しなくても自分の位置が地図に出るようにする（利用者要望）。
  // オーディエンスはこの機能自体が無い（syncMyPositionVisibility参照）。
  // 手入力ソースを自動で始める意味は無いので対象外。
  if (state.mode !== 'audience' && !isActive() && sourceSelect.value !== 'manual') {
    noTransmitCheckbox.checked = true;
    state.noTransmit = true;
    startGeoWatchDisplay();
    toggleBtn.textContent = buttonLabel().stop;
    toggleBtn.classList.add('connected');
  }

  render();
}

function renderSummary(): void {
  const flight = state.flights[state.activeFlight];
  const panel = el('summary');

  if (!flight) {
    panel.innerHTML = '<p class="muted">フライトが読み込まれていません</p>';
    return;
  }

  const s = summarize(flight.fixes, state.timeZone);
  const mode = MODES.find((m) => m.id === state.mode)!;

  // パイロットモードは情報を絞る（開発メモ「飛行中は複雑な画面を見る余裕がない」）
  const rows = mode.id === 'pilot' ? s.rows.slice(0, 3) : s.rows;

  panel.innerHTML = `
    <div class="flight-name">${escapeHtml(flight.label)}</div>
    <div class="flight-meta">${escapeHtml(flight.terminalId)} · ${escapeHtml(flight.recorder)}</div>
    <dl class="stats">
      ${rows.map((r) => `<div><dt>${r.label}</dt><dd>${r.value}</dd></div>`).join('')}
    </dl>`;
}

/**
 * パイロット用の簡易表示。
 * 狭い画面ではパネルを開かずに要点が読めるよう、地図の上に重ねる。
 * 中身は「いま何を知りたいか」で選ぶ。ライブ接続中なら自分の現在値、
 * それ以外は読み込んだフライトの要約。
 */
function renderHud(): void {
  const box = el('hud');
  const flight = state.flights[state.activeFlight];

  // 自機のGPSが取れていればそれを最優先にする（他機の値を「自分」として
  // 出してしまわないように）。無ければ従来通り、受信中の先頭の機を使う
  const terminals = sanitizedRealTerminals();
  const live = state.myFix
    ? { altPressure: null, altMsl: state.myFix.altMsl, speed: state.myFix.speed, heading: state.myFix.heading }
    : terminals[0]?.latest;

  const items: Array<{ label: string; value: string }> = live
    ? [
        { label: '高度', value: `${Math.round(metersToFeet(live.altPressure ?? live.altMsl ?? 0))} ft` },
        { label: '対地速度', value: `${((live.speed ?? 0) * 3.6).toFixed(1)} km/h` },
        { label: '方位', value: live.heading === null ? '—' : `${Math.round(live.heading)}°` },
      ]
    : flight
      ? (() => {
          const s = summarize(flight.fixes, state.timeZone);
          return s.rows.slice(0, 3).map((r) => ({ label: r.label, value: r.value }));
        })()
      : [];

  box.innerHTML = items
    .map(
      (i) =>
        `<div class="hud-item"><span class="hud-label">${escapeHtml(i.label)}</span>` +
        `<span class="hud-value">${escapeHtml(i.value)}</span></div>`,
    )
    .join('');
}

// --- ゴール（原典の Enter / Space に相当） --------------------------------------

/**
 * 距離・方位の基準になる「いまの自分の位置」。
 * 自機のGPSが最優先、無ければライブ受信中の値、それも無ければ
 * 読み込んだフライトの最後の点を使う。
 */
function currentPosition(): { lat: number; lon: number } | null {
  if (state.myFix) return { lat: state.myFix.lat, lon: state.myFix.lon };
  const terminals = sanitizedRealTerminals();
  const live = terminals[0]?.latest;
  if (live) return { lat: live.lat, lon: live.lon };
  const flight = state.flights[state.activeFlight];
  const last = flight?.fixes[flight.fixes.length - 1];
  return last ? { lat: last.lat, lon: last.lon } : null;
}

/**
 * グリッド表記の手入力・取込みで使う基準点。選択中の会場（競技地図）→
 * 手元の位置 → 地図の中心の順。理由は`venueReferencePoint`（goals.ts）を参照
 */
function gridReferencePoint(): { lat: number; lon: number } {
  const c = map.getCenter();
  return venueReferencePoint(state.venue) ?? currentPosition() ?? { lat: c.lat, lon: c.lng };
}

function persistGoals(): void {
  if (state.venue) {
    saveGoals(state.venue.id, { goals: state.goals, selectedKey: state.selectedGoalKey });
  }
}

/**
 * 一覧へ追加して、その点を選択する。
 * 二重に入れないのは会場の公式ゴール（同じ番号）を選び直したときだけ。
 * 座標から入れたゴールは鍵が毎回新しいので、同じ地点でも重ねて追加できる
 * （PZの矩形の角のように、至近距離の別の点を打つ用途があるため）。
 */
function addGoal(goal: DeclaredGoal): void {
  const existing = state.goals.find((g) => g.key === goal.key);
  if (!existing) state.goals.push(goal);
  state.selectedGoalKey = goal.key;
  state.goalCardClosed = false;
  persistGoals();
  redraw();
  setStatus(
    existing ? `ゴール ${existing.name} を選択しました` : `ゴール ${goal.name} を一覧に追加しました`,
  );
}

/**
 * 複数件をまとめて一覧へ追加する（TaskBoardからの一括取込用）。
 * `addGoal`を件数ぶん呼ぶと保存・再描画がその都度走って無駄なので、
 * 最後に1回だけ`persistGoals()`/`redraw()`する。どれか1件を選択状態には
 * しない（複数件をまとめて見せる場面なので、特定の1件を選ぶ意味が無い）。
 */
function addGoals(goals: DeclaredGoal[]): void {
  for (const goal of goals) {
    if (!state.goals.some((g) => g.key === goal.key)) state.goals.push(goal);
  }
  state.goalCardClosed = false;
  persistGoals();
  redraw();
}

/** 選択は常に1点。同じ点を選び直したら選択を外す（原典の Space と同じトグル） */
function toggleGoalSelection(key: string): void {
  state.selectedGoalKey = state.selectedGoalKey === key ? null : key;
  persistGoals();
  redraw();
}

/**
 * 選択を一覧の並び順で前後へ動かす。
 * 一覧を開かなくても（畳んだバーからも）次に狙う点へ進められるようにするため。
 */
function cycleGoalSelection(dir: 1 | -1): void {
  if (state.goals.length === 0) return;
  const idx = state.goals.findIndex((g) => g.key === state.selectedGoalKey);
  const next =
    idx === -1
      ? dir === 1
        ? 0
        : state.goals.length - 1
      : (idx + dir + state.goals.length) % state.goals.length;
  state.selectedGoalKey = state.goals[next].key;
  persistGoals();
  redraw();
}

/**
 * ゴールを一覧の中で1つ上（-1）・下（+1）へ動かす。並びは保存される（会場ごと）ので、
 * 次に開いたときも同じ順。選択の前後切り替え（cycleGoalSelection）も、この並び順で進む
 */
function moveGoal(key: string, dir: -1 | 1): void {
  const i = state.goals.findIndex((g) => g.key === key);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= state.goals.length) return;
  [state.goals[i], state.goals[j]] = [state.goals[j], state.goals[i]];
  persistGoals();
}

function removeGoal(key: string): void {
  state.goals = state.goals.filter((g) => g.key !== key);
  if (state.selectedGoalKey === key) state.selectedGoalKey = null;
  persistGoals();
  redraw();
}

/** 距離と方位。方位は競技と同じく方眼（UTM グリッド）北を基準にする */
function goalMetricsHtml(g: DeclaredGoal, here: { lat: number; lon: number } | null): string {
  if (!here) return '<span>現在地なし</span>';
  return (
    `<span><b>${(distanceMeters(here, g) / 1000).toFixed(2)}</b> km</span>` +
    `<span><b>${Math.round(bearingDeg(here, g))}</b>°</span>`
  );
}

function goalRowHtml(g: DeclaredGoal, here: { lat: number; lon: number } | null, index = 0, count = 1): string {
  const selected = g.key === state.selectedGoalKey;
  // 表記をトグルした前後でゴールが混在しても、保存済みの東距桁数
  // （`eastingDigits`。無ければ長さから推測）から分割位置を決める
  // （現在の設定には依存しない。9桁は「東距5桁+北距4桁」と
  // 「東距4桁+北距5桁」のどちらもあり得るため、長さだけでは決まらない）
  const eastingLen = eastingDigitsOf(g);
  const eight = `${g.eight.slice(0, eastingLen)} ${g.eight.slice(eastingLen)}`;
  // ★ 名前の下に出す位置は、**今の表記の設定**（東距・北距を4桁か5桁か。「表示」メニュー）に従う
  //   グリッド表記にする（"1693/8342"）。名前だけではどのゴールか確かめられない、との指摘
  //   （2026-10-29）。保存済みの`eight`ではなく座標から求め直すので、設定を切り替えても合う。
  //   以前の右端の長いUTM（"52 616937E 3683420N"）はスマホ幅で切れて読めず、隠していた
  const gridE = gridEastingDigits(state.utmWideEasting);
  const code = gridCodeForDisplay(g.lat, g.lon, state.utmWideEasting, state.utmWideNorthing);
  const grid = `${code.slice(0, gridE)}/${code.slice(gridE)}`;
  // 座標から入れたゴールは名前そのものがグリッド表記。同じものを二度出さない
  const showGrid = g.name.replace(/\D/g, '') !== code;

  return (
    `<div class="goal-row${selected ? ' selected' : ''}" data-key="${g.key}">` +
    // ★ 名前自体を編集ボタンにする（別枠のボタンを増やすと、高度・判定容積
    //   まで出す狭い行で✕ボタンが行の外へはみ出た。実測で判明）。
    //   文字を足さず、下線だけでタップできることを示す（別枠のボタン分の
    //   固定幅を増やさないため）
    `<span class="goal-row-id">` +
    `<button type="button" class="goal-row-name" data-edit="${g.key}" ` +
    `aria-label="${escapeHtml(g.name)} を編集">${escapeHtml(g.name)}</button>` +
    (showGrid ? `<span class="goal-row-grid" title="グリッド表記（表示の設定に従う）">${grid}</span>` : '') +
    `</span>` +
    `<span class="goal-row-metrics">${goalMetricsHtml(g, here)}</span>` +
    (goalReorderMode
      ? `<span class="goal-row-move">` +
        `<button type="button" class="goal-move" data-move-up="${g.key}" ${index === 0 ? 'disabled ' : ''}` +
        `aria-label="${escapeHtml(g.name)} を1つ上へ">▲</button>` +
        `<button type="button" class="goal-move" data-move-down="${g.key}" ${index === count - 1 ? 'disabled ' : ''}` +
        `aria-label="${escapeHtml(g.name)} を1つ下へ">▼</button>` +
        `</span>`
      : `<label class="goal-row-alt">` +
    `<input class="goal-alt" type="number" inputmode="numeric" step="100" ` +
    `placeholder="—" data-alt="${g.key}" value="${g.altFt ?? ''}" ` +
    `aria-label="${escapeHtml(g.name)} の設定高度（ft）">ft` +
    `</label>`) +
    // 空中ゴールの判定容積。高度を入れたときだけ意味を持つので、そのときだけ出す。
    // ★ 寸法は大会のタスクデータ側の値なので、既定値を入れずに空欄で始める
    (!goalReorderMode && g.altFt != null
      ? `<label class="goal-row-alt goal-row-vol">` +
        `<input class="goal-radius" type="number" inputmode="numeric" step="10" min="0" ` +
        `placeholder="半径" data-radius="${g.key}" value="${g.goalRadiusM ?? ''}" ` +
        `aria-label="${escapeHtml(g.name)} の判定半径（m）">m` +
        `<input class="goal-alt-tol" type="number" inputmode="numeric" step="10" min="0" ` +
        `placeholder="±" data-alttol="${g.key}" value="${g.altToleranceFt ?? ''}" ` +
        `aria-label="${escapeHtml(g.name)} の高度許容差（ft）">ft` +
        `</label>`
      : '') +
    `<button type="button" class="goal-del" data-del="${g.key}" ` +
    `aria-label="${escapeHtml(g.name)} を一覧から外す">✕</button>` +
    `</div>`
  );
}

/**
 * 一覧の作り直しは「顔ぶれか選択が変わったとき」だけにする。
 *
 * ★ ライブ受信中は redraw() が数秒おきに走る。毎回 innerHTML を差し替えると
 *   設定高度を打っている最中にフォーカスが飛んで入力できない。距離・方位は
 *   その場で書き換えるだけにして、入力欄には触らないこと。
 */
let goalRowsSignature = '';

/** 一覧の並べ替え中か（各行に ▲▼ が出て、高度などの入力欄は隠れる） */
let goalReorderMode = false;

function renderGoalCard(): void {
  const card = el('goal-card');
  const opener = el('goal-card-open');
  const list = el('goal-list');

  const showCard = !state.goalCardClosed;
  card.hidden = !showCard;
  opener.hidden = showCard;

  // 前後へ切り替えるボタンは、動かす先が無いとき（0〜1件）は押せなくする
  const canCycle = state.goals.length > 1;
  for (const b of document.querySelectorAll<HTMLButtonElement>('.goal-cycle-prev, .goal-cycle-next')) {
    b.disabled = !canCycle;
  }

  // 畳んでいるときも、選択中のゴールだけはボタンの右に出しておく
  if (!showCard) {
    const here = currentPosition();
    const sel = state.goals.find((g) => g.key === state.selectedGoalKey);
    el('goal-fab-info').innerHTML = sel
      ? `<span class="sel">${escapeHtml(sel.name)}</span>` +
        goalMetricsHtml(sel, here) +
        (sel.altFt == null ? '' : `<span><b>${sel.altFt}</b> ft</span>`)
      : state.goals.length
        ? `<span>${state.goals.length} 件・未選択</span>`
        : '';
  }

  if (showCard) {
    const here = currentPosition();
    // 表記の設定（東距・北距の桁数）も入れる。切り替えたら、名前の下のグリッド表記を作り直す
    const sig =
      state.goals.map((g) => `${g.key}:${g.altFt ?? ''}`).join('|') +
      `/${state.selectedGoalKey}/${state.utmWideEasting ? 1 : 0}${state.utmWideNorthing ? 1 : 0}/${goalReorderMode ? 'r' : ''}`;
    if (sig !== goalRowsSignature) {
      list.innerHTML = state.goals.length
        ? state.goals.map((g, i) => goalRowHtml(g, here, i, state.goals.length)).join('')
        : '<p class="goal-empty">地図のゴールを押して「ゴールに設定」、' +
          'または「番号入力」から追加します。</p>';
      goalRowsSignature = sig;
    } else {
      for (const row of list.querySelectorAll<HTMLElement>('.goal-row')) {
        const g = state.goals.find((x) => x.key === row.dataset.key);
        const box = row.querySelector('.goal-row-metrics');
        if (g && box) box.innerHTML = goalMetricsHtml(g, here);
      }
    }
  }
  syncGoalCardHeight();
}

/**
 * 凡例と縮尺ラベルを、カード（または開き直しボタン）の上へ逃がす。
 *
 * ★ 高さだけを渡して CSS 側で足し算すると、下端の起点が画面ごとに違う
 *   （広い画面は 12px、狭い画面は 8px＋つまみ＋セーフエリア）ぶんを
 *   二重に持つことになり、必ずどちらかがずれる。ここでは
 *   **地図領域の下端からカード上端までの距離**を実測して渡し、
 *   CSS は max() で「最低でもこれだけ空ける」と書くだけにする。
 */
function syncGoalCardHeight(): void {
  /** 地図領域の下端から、その要素の上端までの距離（＋余白）。隠れていれば0 */
  const clearanceOf = (elem: HTMLElement): string => {
    if (elem.hidden) return '0px';
    const parent = elem.offsetParent as HTMLElement | null;
    const parentH = parent?.clientHeight ?? window.innerHeight;
    return `${Math.max(0, parentH - elem.offsetTop + 8)}px`;
  };

  const card = el('goal-card');
  // ★ 畳んだときのゴールカードは #goal-card-open（コンパクトな左寄せのFAB、
  //   凡例のある右下とは重ならない）に変わる。以前はこのFABの位置でも
  //   避ける計算をしていたが、横に重ならない相手を避ける意味は無く、
  //   FABの上端ぶん凡例が無意味に高く浮いてしまっていた（利用者からの
  //   指摘: 「高度のバーはゴールのバーより一段上がったまま」）。
  //   全幅に広がる開いたカード（#goal-card）のときだけ避け、畳んだときは
  //   0（＝CSS側の最低値`34px`まで下がる）にする。カードが開けば
  //   （＝ゴール一覧が表示される場面、通常ゴールが入って初めて開く）、
  //   自然に凡例も押し上げられる。
  document.documentElement.style.setProperty('--goal-card', card.hidden ? '0px' : clearanceOf(card));
  // ★ 計測カードはゴールカードの上に積むので、凡例・縮尺ラベルはこちらの
  //   高さぶんも避ける必要がある。設定を忘れると凡例が計測カードの裏に潜る
  //   （実際に一度潜らせた）
  document.documentElement.style.setProperty('--measure-card', clearanceOf(el('measure-card')));

  // ★ 上のコメントの通り#goal-card-open（畳んだFAB）は普段横に重ならない
  //   ので避ける対象から外しているが、選択中ゴールの距離・方位・切替
  //   ボタンが付くとFABが伸びて、右下の凡例（.legend）と実際に重なる
  //   ことがある（利用者からの指摘、2026-09-13）。z-indexで凡例を前面に
  //   出しただけでは凡例の裏でFABの文字が見切れるので、実際に重なって
  //   いるときだけ凡例を押し上げて完全に避ける（重ならないときは今まで
  //   通り0のまま、無意味に高く浮かせない）
  const fab = el('goal-card-open');
  // ★ 縮尺（距離）ラベルは右下の凡例と違い、この畳んだFABと**同じ左下の角**に
  //   出る。ゴール一覧を畳むと--goal-cardは0に戻るため、この実測値を渡して
  //   おかないと縮尺ラベルがFABの裏に完全に隠れる（利用者からの指摘、
  //   2026-09-14:「距離のバーがゴールリストを閉じていると隠れてみえない」）。
  //   凡例（右下）の方は横に重なったときだけ避ければよいので、従来通り
  //   --goal-fab-overlap（下）を使い分ける
  document.documentElement.style.setProperty('--goal-fab', clearanceOf(fab));

  const legend = document.querySelector<HTMLElement>('.legend');
  let fabOverlap = '0px';
  if (!fab.hidden && legend) {
    const fabRect = fab.getBoundingClientRect();
    const legendRect = legend.getBoundingClientRect();
    if (fabRect.right > legendRect.left && fabRect.bottom > legendRect.top) {
      fabOverlap = clearanceOf(fab);
    }
  }
  document.documentElement.style.setProperty('--goal-fab-overlap', fabOverlap);

  // ★ 地図右上のpitch/回転ボタン列（PitchControl）は、縦の狭い横向き画面
  //   （iPhone横持ち852×393等）では画面の縦いっぱいまで伸び、右下の凡例と
  //   実際に重なることがある（コードレビュー指摘、2026-09-15）。重なって
  //   いるときだけ、地図領域の下端からボタン列の上端までの距離ぶん凡例を
  //   押し上げて完全に避ける（重ならない通常の画面では0のまま）。
  const ctrlGroup = document.getElementById('pitch-up-btn')?.closest<HTMLElement>('.maplibregl-ctrl-group');
  let ctrlOverlap = '0px';
  if (ctrlGroup && legend) {
    const ctrlRect = ctrlGroup.getBoundingClientRect();
    const legendRect = legend.getBoundingClientRect();
    const overlaps =
      ctrlRect.left < legendRect.right &&
      ctrlRect.right > legendRect.left &&
      ctrlRect.top < legendRect.bottom &&
      ctrlRect.bottom > legendRect.top;
    if (overlaps) {
      const mapArea = document.querySelector('.map-area');
      const mapBottom = mapArea?.getBoundingClientRect().bottom ?? window.innerHeight;
      ctrlOverlap = `${Math.max(0, mapBottom - ctrlRect.top + 8)}px`;
    }
  }
  document.documentElement.style.setProperty('--ctrl-overlap', ctrlOverlap);
}

/**
 * 一覧の✎ボタンから#goal-newダイアログを編集モードで開くための橋渡し。
 * setupGoalNew()側で実体を入れる（boot()内でsetupGoalCard()より後に
 * 呼ばれるが、どちらも起動時に同期的に済むのでクリック時には必ず入っている）。
 */
let openGoalEditDialog: ((key: string) => void) | null = null;

function setupGoalCard(): void {
  const list = el('goal-list');

  el('goal-card-close').onclick = () => {
    state.goalCardClosed = true;
    renderGoalCard();
  };
  el('goal-clear-all').onclick = () => {
    if (state.goals.length === 0) return;
    if (!confirm(`ゴール一覧の${state.goals.length}件をすべて消します。よろしいですか？`)) return;
    state.goals = [];
    state.selectedGoalKey = null;
    persistGoals();
    goalRowsSignature = ''; // 顔ぶれが変わったので作り直させる
    renderGoalCard();
    redraw();
  };
  el('goal-card-open').querySelector<HTMLButtonElement>('.goal-fab-open')!.onclick = () => {
    state.goalCardClosed = false;
    renderGoalCard();
  };

  // 選択中ゴールへの点線モード。「subtle」の有無でON/OFFを表す
  // （番号入力ボタンと同じ塗り＝ON、輪郭だけ＝OFF）
  const bearingToggle = el<HTMLButtonElement>('goal-bearing-toggle');
  const syncBearingToggle = (): void => {
    bearingToggle.classList.toggle('subtle', !state.showGoalBearingLine);
    bearingToggle.setAttribute('aria-pressed', String(state.showGoalBearingLine));
  };
  syncBearingToggle();
  bearingToggle.onclick = () => {
    state.showGoalBearingLine = !state.showGoalBearingLine;
    syncBearingToggle();
    redraw();
  };

  // 前後へ切り替えるボタンは、開いたカードのヘッダーと畳んだバーの両方にある
  for (const b of document.querySelectorAll<HTMLButtonElement>('.goal-cycle-prev')) {
    b.onclick = () => cycleGoalSelection(-1);
  }
  for (const b of document.querySelectorAll<HTMLButtonElement>('.goal-cycle-next')) {
    b.onclick = () => cycleGoalSelection(1);
  }

  const reorderToggle = el<HTMLButtonElement>('goal-reorder-toggle');
  reorderToggle.onclick = () => {
    goalReorderMode = !goalReorderMode;
    reorderToggle.classList.toggle('subtle', !goalReorderMode);
    reorderToggle.setAttribute('aria-pressed', String(goalReorderMode));
    reorderToggle.textContent = goalReorderMode ? '並べ替え終わり' : '並べ替え';
    renderGoalCard();
  };

  list.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const move = t.closest<HTMLButtonElement>('.goal-move');
    if (move) {
      const key = move.dataset.moveUp ?? move.dataset.moveDown;
      if (key && !move.disabled) {
        moveGoal(key, move.dataset.moveUp ? -1 : 1);
        goalRowsSignature = ''; // 並びが変わったので作り直させる
        renderGoalCard();
        // 動かした行が見える位置に残るようにする（押した行は作り直しで新しい要素になる）
        list.querySelector<HTMLElement>(`.goal-row[data-key="${CSS.escape(key)}"]`)?.scrollIntoView({ block: 'nearest' });
        redraw();
      }
      return;
    }
    const del = t.closest<HTMLElement>('.goal-del');
    if (del?.dataset.del) {
      removeGoal(del.dataset.del);
      return;
    }
    const edit = t.closest<HTMLElement>('.goal-row-name');
    if (edit?.dataset.edit) {
      openGoalEditDialog?.(edit.dataset.edit);
      return;
    }
    // 高度の入力欄を押しただけで選択が動くと、打ち込む前に作り直されてしまう
    if (t.closest('.goal-alt')) return;
    const row = t.closest<HTMLElement>('.goal-row');
    if (row?.dataset.key) toggleGoalSelection(row.dataset.key);
  });

  // change（＝確定時）で拾う。input だと1文字ごとに作り直しが走る
  list.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    /** 空欄・数値でないものは null にする（未設定を意味する） */
    const numOrNull = (input: HTMLInputElement): number | null => {
      const v = Number(input.value);
      return input.value.trim() === '' || !Number.isFinite(v) ? null : Math.round(v);
    };

    const altInput = target.closest<HTMLInputElement>('.goal-alt');
    if (altInput?.dataset.alt) {
      const g = state.goals.find((x) => x.key === altInput.dataset.alt);
      if (!g) return;
      g.altFt = numOrNull(altInput);
      // 高度を消したら、空中ゴールの判定容積も意味を失うので一緒に片付ける
      if (g.altFt == null) {
        g.goalRadiusM = null;
        g.altToleranceFt = null;
      }
      persistGoals();
      goalRowsSignature = ''; // 高度は署名に含まれるので作り直させる
      renderGoalCard();
      renderCrossSection();
      return;
    }

    // 空中ゴールの判定容積（半径・上下許容差）。こちらは署名に含めないので
    // 行を作り直さない（入力中にフォーカスが飛ばないようにするため）
    const radiusInput = target.closest<HTMLInputElement>('.goal-radius');
    if (radiusInput?.dataset.radius) {
      const g = state.goals.find((x) => x.key === radiusInput.dataset.radius);
      if (!g) return;
      g.goalRadiusM = numOrNull(radiusInput);
      persistGoals();
      renderCrossSection();
      return;
    }
    const tolInput = target.closest<HTMLInputElement>('.goal-alt-tol');
    if (tolInput?.dataset.alttol) {
      const g = state.goals.find((x) => x.key === tolInput.dataset.alttol);
      if (!g) return;
      g.altToleranceFt = numOrNull(tolInput);
      persistGoals();
      renderCrossSection();
    }
  });

  window.addEventListener('resize', syncGoalCardHeight);
}

/**
 * 設定の自動保存と復元（docs/08 §231）。起動の処理が終わったあとに見張りを始めるだけで、
 * 起動の時点では何も保存しない（起動を遅くしない）。保存は約1時間ごと、使っていないときに
 */
function setupSettingsBackup(): void {
  const backup = new SettingsBackup({
    baseUrl: location.origin,
    // 送信の欄に入っている端末ID（未入力なら前に使ったID）
    getTerminalId: () => el<HTMLInputElement>('my-terminal-id').value.trim() || loadMyTerminalId(),
    app: BUILD_ID,
  });
  const info = el('settings-backup-info');
  const fmt = (ms: number | null): string => {
    if (!ms) return '—';
    const z = toZonedTime(ms, state.timeZone);
    // "2026-11-02 09:12" → "11/02 09:12"
    return z.dateTime.slice(5).replace('-', '/');
  };
  const renderInfo = (): void => {
    const meta = loadBackupMeta();
    const local = loadLocalSnapshots()[0];
    info.innerHTML =
      `<div class="pwa-row"><span>端末の中</span><b>${fmt(local?.savedAt ?? null)}</b></div>` +
      `<div class="pwa-row"><span>サーバー</span><b>${fmt(meta.lastServerAt)}${meta.lastServerId ? `（${escapeHtml(meta.lastServerId)}）` : ''}</b></div>`;
  };
  backup.onStamp = (r) => {
    renderInfo();
    if (r.server === 'error') console.warn('[settings-backup] サーバーへ保存できませんでした');
  };
  renderInfo();
  backup.start();

  el<HTMLButtonElement>('settings-backup-now').onclick = async () => {
    await ensureBridgeAuth(location.origin);
    const r = await backup.stamp();
    if (!r) return;
    setStatus(
      r.server === 'saved' || r.server === 'same'
        ? '設定を保存しました（端末の中とサーバー）'
        : r.server === 'no-id'
          ? '設定を端末の中に保存しました（端末IDが無いので、サーバーには保存していません）'
          : '設定を端末の中に保存しました（サーバーには保存できませんでした）',
      r.server === 'error' ? 'warn' : 'info',
    );
  };

  // 復元の画面
  const sheet = el('settings-restore');
  const idInput = el<HTMLInputElement>('settings-restore-id');
  const list = el('settings-restore-list');
  const rowHtml = (snap: { savedAt: number; keys: number }, where: 'local' | 'server'): string =>
    `<div class="settings-restore-row"><span>${fmt(snap.savedAt)}（${where === 'local' ? '端末の中' : 'サーバー'}・${snap.keys}項目）</span>` +
    `<button type="button" data-restore-${where}="${snap.savedAt}">この時点に戻す</button></div>`;
  const renderLocal = (): string => {
    const local = loadLocalSnapshots();
    return local.length ? local.map((s) => rowHtml({ savedAt: s.savedAt, keys: Object.keys(s.items).length }, 'local')).join('') : '';
  };
  const restore = (snap: SettingsSnapshot): void => {
    if (!confirm(`${fmt(snap.savedAt)} の設定に戻して、アプリを読み込み直します。よろしいですか？`)) return;
    applySnapshot(snap);
    location.reload();
  };
  const open = (): void => {
    idInput.value = el<HTMLInputElement>('my-terminal-id').value.trim() || loadMyTerminalId();
    list.innerHTML = renderLocal() || '<p class="muted">端末の中には保存がありません。端末IDを入れてサーバーから探してください</p>';
    sheet.hidden = false;
    closePanel?.();
  };
  el<HTMLButtonElement>('settings-restore-open').onclick = open;
  el<HTMLButtonElement>('settings-restore-from-id').onclick = open;
  el('settings-restore-close').onclick = () => {
    sheet.hidden = true;
  };
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) sheet.hidden = true;
  });
  el<HTMLButtonElement>('settings-restore-search').onclick = async () => {
    const id = idInput.value.trim();
    if (!id) {
      setStatus('端末IDを入れてください', 'warn');
      return;
    }
    if (!(await ensureBridgeAuth(location.origin))) {
      setStatus('合言葉が確認できなかったため、サーバーから探せません', 'warn');
      return;
    }
    list.innerHTML = '<p class="muted">探しています…</p>';
    try {
      const snaps = await backup.listServer(id);
      list.innerHTML =
        (snaps.length
          ? snaps.map((s) => rowHtml(s, 'server')).join('')
          : `<p class="muted">サーバーに「${escapeHtml(id)}」の保存はありません</p>`) + renderLocal();
    } catch (e) {
      list.innerHTML = `<p class="muted">探せませんでした: ${escapeHtml((e as Error).message)}</p>` + renderLocal();
    }
  };
  list.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const localBtn = t.closest<HTMLElement>('[data-restore-local]');
    if (localBtn) {
      const snap = loadLocalSnapshots().find((s) => String(s.savedAt) === localBtn.dataset.restoreLocal);
      if (snap) restore(snap);
      return;
    }
    const serverBtn = t.closest<HTMLElement>('[data-restore-server]');
    if (serverBtn) {
      try {
        restore(await backup.fetchServer(idInput.value, Number(serverBtn.dataset.restoreServer)));
      } catch (err) {
        setStatus(`復元できませんでした: ${(err as Error).message}`, 'warn');
      }
    }
  });

  // 端末IDが空のとき（データが消えた直後など）は、送信の欄に復元への近道を出す
  const idBox = el<HTMLInputElement>('my-terminal-id');
  const syncShortcut = (): void => {
    el('settings-restore-from-id').hidden = idBox.value.trim() !== '';
  };
  idBox.addEventListener('input', syncShortcut);
  syncShortcut();
}

/**
 * ゴール番号／グリッド表記の入力（原典の Enter キー）。
 *
 * 桁数で解釈を切り替えるのは原典と同じ。3桁なら会場のゴール番号、
 * それ以上（8桁 or 9桁。表記設定による）なら座標として読む。
 * どちらとして読んでいるかは常に画面に出す。
 */
function setupGoalEntry(): void {
  const sheet = el('goal-entry');
  const digitsBox = el('goal-entry-digits');
  const hintBox = el('goal-entry-hint');
  const okBtn = el<HTMLButtonElement>('goal-entry-ok');
  let digits = '';

  const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'];
  el('goal-entry-keys').innerHTML = KEYS.map(
    (k) =>
      `<button type="button" class="goal-entry-key${/\d/.test(k) ? '' : ' wide-key'}" ` +
      `data-key="${k}">${k}</button>`,
  ).join('');

  /** いま入力されている数字を地点に解決する。できなければ null */
  const resolve = (): DeclaredGoal | null => {
    const digitCount = gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing);
    if (digits.length === 3) {
      const mark = state.venue?.goals.find((g) => g.name === digits);
      return mark ? goalFromMark(mark) : null;
    }
    if (digits.length === digitCount) {
      // 会場に同じマスのゴールがあるなら、正式な番号と座標のほうを採る
      const mark = state.venue
        ? findMarkByGridCode(state.venue.goals, digits, state.utmWideEasting, state.utmWideNorthing)
        : null;
      if (mark) return goalFromMark(mark);

      // グリッド表記は100km/1000km区画を持たないので基準が要る。選択中の会場（競技地図）が基準
      const p = resolveGridCode(digits, gridReferencePoint(), state.utmWideEasting, state.utmWideNorthing);
      const eastingLen = gridEastingDigits(state.utmWideEasting);
      // 復元した点からグリッド表記を計算し直さないこと（makeGoal の注記を参照）
      return p
        ? makeGoal(
            `${digits.slice(0, eastingLen)} ${digits.slice(eastingLen)}`,
            p.lat,
            p.lon,
            digits,
            eastingLen,
          )
        : null;
    }
    return null;
  };

  const render = (): void => {
    const digitCount = gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing);
    digitsBox.textContent = digits || '—';
    const found = resolve();
    okBtn.disabled = !found;
    hintBox.classList.toggle('ok', !!found);

    if (digits.length === 0) {
      hintBox.textContent = `3桁でゴール番号、${digitCount}桁で座標（${digitCount}デジット）`;
    } else if (digits.length < 3) {
      hintBox.textContent = 'ゴール番号なら、あと ' + (3 - digits.length) + ' 桁';
    } else if (digits.length === 3) {
      hintBox.textContent = found
        ? `ゴール ${found.name}`
        : `この会場に ${digits} は見つかりません`;
    } else if (digits.length < digitCount) {
      hintBox.textContent = `座標として読みます（あと ${digitCount - digits.length} 桁）`;
    } else {
      // 会場のゴールに吸い付いたときは、そのことが分かる言い方にする
      const eastingLen = gridEastingDigits(state.utmWideEasting);
      const asCoord = `${digits.slice(0, eastingLen)} ${digits.slice(eastingLen)}`;
      hintBox.textContent = !found
        ? '座標に変換できません'
        : found.name === asCoord
          ? `座標 ${asCoord}`
          : `ゴール ${found.name}（同じマス）`;
    }
  };

  const close = (): void => {
    sheet.hidden = true;
  };

  el('goal-entry-keys').addEventListener('click', (e) => {
    const k = (e.target as HTMLElement).closest<HTMLElement>('.goal-entry-key')?.dataset.key;
    if (!k) return;
    if (k === 'C') digits = '';
    else if (k === '⌫') digits = digits.slice(0, -1);
    else if (digits.length < gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing)) digits += k;
    render();
  });

  el('goal-entry-open').onclick = () => {
    digits = '';
    render();
    sheet.hidden = false;
  };
  el('goal-entry-cancel').onclick = close;
  okBtn.onclick = () => {
    const g = resolve();
    if (!g) return;
    addGoal(g);
    close();
    // 入れた点が画面の外だと「入ったのか分からない」ので寄せる
    map.easeTo({ center: [g.lon, g.lat], zoom: Math.max(map.getZoom(), 12.5), duration: 600 });
  };

  // 背景を押しても閉じる（シートの中は素通しさせない）
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });

  // 物理キーボードがあるなら原典どおり Enter で開き、数字も直接打てる
  window.addEventListener('keydown', (e) => {
    if (sheet.hidden) {
      const typing = (e.target as HTMLElement)?.closest('input, select, textarea');
      if (e.key === 'Enter' && !typing) {
        e.preventDefault();
        el<HTMLButtonElement>('goal-entry-open').click();
      }
      return;
    }
    if (/^\d$/.test(e.key)) {
      if (digits.length < 8) digits += e.key;
      render();
    } else if (e.key === 'Backspace') {
      digits = digits.slice(0, -1);
      render();
    } else if (e.key === 'Enter') {
      okBtn.click();
    } else if (e.key === 'Escape') {
      close();
    }
  });

  render();
}

/*
 * 拡大鏡は「別の地図をもう1つ立ち上げてズームする」方式を最初に試したが、
 * 同じ PMTiles ソースを共有する2つ目の MapLibre インスタンスがタイルを
 * 読み込めない（isSourceLoaded は true なのに queryRenderedFeatures が
 * 常に0件になる）現象に当たった。原因は特定できなかったが、そもそも
 * 本家GVの拡大窓も「いま描画済みの画面を拡大する」ものなので、こちらの
 * ほうが素直。**メイン地図のcanvasを直接クロップして拡大表示**する。
 */
const MAGNIFIER_SIZE = 176; // .magnifier-map の高さ（style.css）と合わせる
const MAGNIFIER_SCALE = 4; // 拡大率

/** 拡大鏡を出し、狙っている地点の詳細（緯度経度・8デジット・UTM）を出す */
function showMagnifierAt(lngLat: maplibregl.LngLat): void {
  el('magnifier').hidden = false;

  const src = map.getCanvas();
  const dpr = src.width / src.clientWidth; // CSS px → 実ピクセルの比
  const pt = map.project(lngLat); // メイン地図コンテナ基準のCSS px

  const dest = el<HTMLCanvasElement>('magnifier-canvas');
  if (dest.width !== MAGNIFIER_SIZE * dpr || dest.height !== MAGNIFIER_SIZE * dpr) {
    dest.width = MAGNIFIER_SIZE * dpr;
    dest.height = MAGNIFIER_SIZE * dpr;
  }
  const ctx = dest.getContext('2d');
  if (ctx) {
    const cropCss = MAGNIFIER_SIZE / MAGNIFIER_SCALE;
    const sx = (pt.x - cropCss / 2) * dpr;
    const sy = (pt.y - cropCss / 2) * dpr;
    const sw = cropCss * dpr;
    const sh = cropCss * dpr;
    ctx.imageSmoothingEnabled = true;
    ctx.clearRect(0, 0, dest.width, dest.height);
    ctx.drawImage(src, sx, sy, sw, sh, 0, 0, dest.width, dest.height);
  }

  el('magnifier-info').innerHTML =
    `${lngLat.lat.toFixed(6)}, ${lngLat.lng.toFixed(6)}` + formatUtm(lngLat.lat, lngLat.lng);
}

function hideMagnifier(): void {
  el('magnifier').hidden = true;
}

/**
 * 地図の長押し。
 *
 * MapLibre の contextmenu は端末によって touch で飛んでこないので、
 * ポインタ操作から自前で判定する。
 *
 * 押してすぐは「地図を動かす操作か」を10pxの移動量で判定し、動いたら諦める。
 * 動かないまま一定時間経つと拡大鏡を出す**微調整モード**に入り、そこから先は
 * 指を動かしても地図は動かさず、拡大鏡で狙いを合わせてから指を離した位置を
 * 登録する（原典GVの画面右下の拡大窓と同じ役割）。
 *
 * 「常に表示」（state.showMagnifier）がONのときは、押していないときの
 * ホバーでも拡大鏡がカーソルを追従する。微調整モードとは独立した表示で、
 * 微調整を終えてもマウスが地図上にある限り出続ける。
 */
function setupLongPress(onLongPress: (lngLat: maplibregl.LngLat) => void): void {
  const container = map.getCanvasContainer();
  let armTimer: number | null = null;
  let startX = 0;
  let startY = 0;
  let fine = false;

  const clearArmTimer = (): void => {
    if (armTimer !== null) {
      clearTimeout(armTimer);
      armTimer = null;
    }
  };

  const posOf = (e: PointerEvent): maplibregl.LngLat => {
    const rect = container.getBoundingClientRect();
    return map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
  };

  /** 微調整モードを終える（キャンセル）。地図が動かせないまま固まらないよう、経路を問わず必ずここを通す */
  const cancelFine = (): void => {
    if (!fine) return;
    fine = false;
    map.dragPan.enable();
    if (!state.showMagnifier) hideMagnifier();
  };

  /** 微調整モードを終え、狙った地点を確定する */
  const commitFine = (e: PointerEvent): void => {
    if (!fine) return;
    fine = false;
    map.dragPan.enable();
    if (!state.showMagnifier) hideMagnifier();
    suppressNextClick = true;
    onLongPress(posOf(e));
  };

  container.addEventListener('pointerdown', (e) => {
    suppressNextClick = false; // 前回の長押しの後始末
    // pointerup/pointercancel を取りこぼして微調整モードが残っていた場合の保険
    cancelFine();
    if (e.button !== 0) return; // 右クリック等は対象外
    // ★ 位置指定中・計測中は長押しを効かせない。効かせると、計測の点を
    //   置こうとしてゆっくりタップしただけでゴール登録の窓が開いてしまう
    if (state.mapTapMode !== 'normal') return;
    clearArmTimer();
    startX = e.clientX;
    startY = e.clientY;
    armTimer = window.setTimeout(() => {
      armTimer = null;
      fine = true;
      map.dragPan.disable(); // 微調整中は地図自体を動かさせない
      showMagnifierAt(posOf(e));
    }, 350);
  });

  container.addEventListener('pointermove', (e) => {
    if (fine) {
      showMagnifierAt(posOf(e));
      return;
    }
    // 拡大鏡が出る前に10px動いたら、地図を動かす操作とみなす
    if (armTimer !== null && Math.hypot(e.clientX - startX, e.clientY - startY) > 10) clearArmTimer();
    // 「常に表示」なら、押していない通常のホバーでも追従させる
    if (state.showMagnifier) showMagnifierAt(posOf(e));
  });

  container.addEventListener('pointerup', (e) => {
    clearArmTimer();
    commitFine(e);
  });
  container.addEventListener('pointercancel', () => {
    clearArmTimer();
    cancelFine();
  });
  container.addEventListener('pointerleave', () => {
    clearArmTimer();
    cancelFine();
    hideMagnifier(); // 地図の外に出たら、常時表示中でも消す
  });
  // 2本指（拡大縮小）で始まった操作は長押し・微調整にしない
  container.addEventListener('touchstart', (e) => {
    if (e.touches.length <= 1) return;
    clearArmTimer();
    cancelFine();
  });
}

/**
 * 長押しした地点をゴールとして登録する窓。
 *
 * グリッド表記（8桁 or 9桁。表記設定による）と仮の名前を入れた状態で開き、
 * どちらも直せる。グリッド表記を直すと、その場で地点を引き直して距離を
 * 出し直す。
 *
 * ★ グリッド表記を**直していない**間は、そこから逆算した10mマスの角ではなく、
 *   長押し（拡大鏡での微調整含む）でつかんだ本来の座標をそのまま登録する。
 *   グリッド表記は10m単位に丸めた表記なので、いったんそこを経由すると
 *   最大10mずれる。狙った角の位置を精密に登録したい用途（PZの矩形など）
 *   でこれがずれの原因になっていた（本人のデバッグにより特定、2026-08-10）。
 *   グリッド表記を手で直したときだけは、他に座標の手がかりが無いので
 *   そこから逆算する。
 */
function setupGoalNew(): void {
  const sheet = el('goal-new');
  const titleBox = el('goal-new-title');
  const eightBox = el<HTMLInputElement>('goal-new-eight');
  const nameBox = el<HTMLInputElement>('goal-new-name');
  const altBox = el<HTMLInputElement>('goal-new-alt');
  const hintBox = el('goal-new-hint');
  const navigateBtn = el<HTMLButtonElement>('goal-new-navigate');
  const okBtn = el<HTMLButtonElement>('goal-new-ok');
  /** 長押しした地点（新規登録時）。グリッド表記を直したときの復元の基準にも使う。
   *  編集時は、編集対象の現在地点をここに入れて同じ仕組みを使い回す */
  let origin: { lat: number; lon: number } | null = null;
  /** open() 時に origin から自動で入れたグリッド表記。これと違えば「手で直した」とみなす */
  let originCode = '';
  /** 編集モードのとき、更新対象のゴールの鍵。新規登録時はnull（一度入力したゴールを
   *  再編集したい・名前や数値を直したいとの要望で追加。2026-09-14） */
  let editingKey: string | null = null;

  const digitsOf = (): string => eightBox.value.replace(/\D/g, '');

  /** 空欄・数値でないものは null にする（未設定を意味する。ゴール一覧の高度入力と同じ規約） */
  const altOrNull = (): number | null => {
    const v = Number(altBox.value);
    return altBox.value.trim() === '' || !Number.isFinite(v) ? null : Math.round(v);
  };

  /**
   * いまの入力から登録される地点。無効なら null。
   *
   * ★ 高度はここで登録時に入れられるようにしてある（以前は登録後に
   *   一覧の高度欄で別途入れる必要があり、PDG/FONのように高度が要る
   *   課題では手間だった、との指摘を受けて追加。2026-08-22）。
   *   一覧の高度欄はそのまま残るので、後から直すこともできる。
   */
  const resolved = (): DeclaredGoal | null => {
    const code = digitsOf();
    const digitCount = gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing);
    if (code.length !== digitCount || !origin) return null;
    const eastingLen = gridEastingDigits(state.utmWideEasting);
    const name = nameBox.value.trim() || `${code.slice(0, eastingLen)} ${code.slice(eastingLen)}`;
    const altFt = altOrNull();
    if (code === originCode) return { ...makeGoal(name, origin.lat, origin.lon, code, eastingLen), altFt };
    const p = resolveGridCode(code, origin, state.utmWideEasting, state.utmWideNorthing);
    if (!p) return null;
    return { ...makeGoal(name, p.lat, p.lon, code, eastingLen), altFt };
  };

  const render = (): void => {
    const g = resolved();
    okBtn.disabled = !g;
    navigateBtn.disabled = !g;
    hintBox.classList.toggle('ok', !!g);
    if (!g) {
      const digitCount = gridCodeDigitCount(state.utmWideEasting, state.utmWideNorthing);
      hintBox.textContent = `${digitCount}デジットは数字${digitCount}桁で入れてください`;
      return;
    }
    const here = currentPosition();
    hintBox.textContent = here
      ? `現在地から ${(distanceMeters(here, g) / 1000).toFixed(2)} km ・ ${Math.round(bearingDeg(here, g))}°`
      : (editingKey ? '更新できます' : '登録できます');
  };

  /** P1, P2 … のうち、まだ使っていないもの */
  const provisionalName = (): string => {
    for (let i = 1; ; i++) {
      const n = `P${i}`;
      if (!state.goals.some((g) => g.name === n)) return n;
    }
  };

  const open = (lngLat: maplibregl.LngLat): void => {
    editingKey = null;
    titleBox.textContent = 'この地点をゴールに登録';
    okBtn.textContent = '登録';
    origin = { lat: lngLat.lat, lon: lngLat.lng };
    originCode = gridCodeOf(origin.lat, origin.lon, state.utmWideEasting, state.utmWideNorthing);
    const eastingLen = gridEastingDigits(state.utmWideEasting);
    eightBox.value = `${originCode.slice(0, eastingLen)} ${originCode.slice(eastingLen)}`;
    nameBox.value = provisionalName();
    altBox.value = '';
    render();
    sheet.hidden = false;
  };

  /**
   * 一度入力したゴールを再編集する（名前・グリッド表記・設定高度を直す）。
   * 「新規登録」と同じダイアログ・同じ検証ロジックを使い回し、OK時に
   * addGoal()（新規追加）ではなく既存のゴールへ上書きする点だけが違う
   * （鍵はmakeGoal()のたびに新しく振られるため、既存の鍵はここで保持する）。
   */
  const openEdit = (key: string): void => {
    const g = state.goals.find((x) => x.key === key);
    if (!g) return;
    editingKey = key;
    titleBox.textContent = 'ゴールを編集';
    okBtn.textContent = '更新';
    origin = { lat: g.lat, lon: g.lon };
    originCode = g.eight;
    const eastingLen = eastingDigitsOf(g);
    eightBox.value = `${g.eight.slice(0, eastingLen)} ${g.eight.slice(eastingLen)}`;
    nameBox.value = g.name;
    altBox.value = g.altFt != null ? String(g.altFt) : '';
    render();
    sheet.hidden = false;
  };

  const close = (): void => {
    sheet.hidden = true;
  };

  /** ナビはGoogleマップに任せたいとの要望。現在地からこの地点への経路案内を開く */
  navigateBtn.onclick = () => {
    const g = resolved();
    if (!g) return;
    openNavigation(g.lat, g.lon);
  };

  eightBox.addEventListener('input', render);
  nameBox.addEventListener('input', render);
  el('goal-new-cancel').onclick = close;
  okBtn.onclick = () => {
    const g = resolved();
    if (!g) return;
    if (editingKey) {
      const existing = state.goals.find((x) => x.key === editingKey);
      if (existing) {
        existing.name = g.name;
        existing.lat = g.lat;
        existing.lon = g.lon;
        existing.eight = g.eight;
        existing.eastingDigits = g.eastingDigits;
        existing.altFt = g.altFt;
        // 高度を消したら、空中ゴールの判定容積も意味を失うので一緒に片付ける
        // （一覧の高度欄と同じ規約。main.ts内の.goal-alt変更処理を参照）
        if (existing.altFt == null) {
          existing.goalRadiusM = null;
          existing.altToleranceFt = null;
        }
        state.selectedGoalKey = existing.key;
        state.goalCardClosed = false;
        persistGoals();
        goalRowsSignature = ''; // 顔ぶれの値が変わったので作り直させる
        renderGoalCard();
        redraw();
        setStatus(`ゴール ${existing.name} を更新しました`);
      }
      close();
      return;
    }
    addGoal(g);
    close();
  };
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });

  setupLongPress(open);
  openGoalEditDialog = openEdit;
}

/**
 * パイロットモードへの切り替え時にだけ聞くパスワード。
 * ★ 本格的な認証ではない。誤操作で航跡が乱立するのを防ぐための足止め
 *   （開発メモ：パイロットモードは間違いが許されないため、慎重に扱う）。
 */
const PILOT_MODE_PASSWORD = '8080';

function openPilotGate(): void {
  const sheet = el('pilot-gate');
  const pwBox = el<HTMLInputElement>('pilot-gate-pw');
  const hintBox = el('pilot-gate-hint');
  pwBox.value = '';
  hintBox.textContent = '';
  hintBox.classList.remove('error');
  sheet.hidden = false;
  pwBox.focus();
}

function setupPilotGate(): void {
  const sheet = el('pilot-gate');
  const pwBox = el<HTMLInputElement>('pilot-gate-pw');
  const hintBox = el('pilot-gate-hint');
  const okBtn = el<HTMLButtonElement>('pilot-gate-ok');

  const close = (): void => {
    sheet.hidden = true;
  };

  const submit = (): void => {
    if (pwBox.value === PILOT_MODE_PASSWORD) {
      close();
      applyMode('pilot');
      return;
    }
    // 間違えたら確実にクルーモードへ戻す（パイロットモードに誤って入らせない）
    close();
    applyMode('crew');
    setStatus('パスワードが違います', 'warn');
  };

  okBtn.onclick = submit;
  el('pilot-gate-cancel').onclick = close;
  pwBox.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit();
  });
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });
}

/**
 * 「更新する」の適用関数を、確実に画面が変わるようラップする。
 *
 * apply()（registerSW側のupdateSW(true)、または手動SKIP_WAITING）は
 * 内部で controllerchange を待ってからリロードする作りだが、実機PC
 * ブラウザ（Edge）で「押しても何も起きない」事例が報告された。
 * clients.claim() 済みでも、タブの状態や拡張機能などブラウザ側の
 * 事情で controllerchange が取りこぼされる可能性を消せないため、
 * 一定時間で強制リロードするフォールバックを必ず添える。
 * ★ 新しい Service Worker は skipWaiting() の時点で既に有効化されている
 *   ため、フォールバックのリロードだけでも新しい版は正しく読み込まれる。
 */
function guardedUpdateApply(apply: () => void): () => void {
  return () => {
    let done = false;
    const reload = () => {
      if (done) return;
      done = true;
      location.reload();
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });
    }
    apply();
    setTimeout(reload, 3000);
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

/** テキストをファイルとしてブラウザにダウンロードさせる */
function downloadTextFile(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * テキストをクリップボードへコピーする。`navigator.clipboard`が使えない
 * （非対応・許可が下りない）環境向けに、非表示textareaへの選択＋
 * `execCommand('copy')`のフォールバックも持つ。成功したかを返す
 */
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 下のフォールバックへ
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * ポップアップ用の座標表記。
 *
 * 競技で座標を指定するのは常に**グリッド表記**（東距・北距をそれぞれ 10m 単位に
 * 落とし、100km 区画内の 4 桁ずつを並べたもの。表記設定がONなら東距だけ
 * 1000km区画基準の5桁になる）なので、それを主役にする。
 * ただしグリッド表記は区画の情報を持たず、区画をまたぐ会場では
 * 単独だと曖昧になる。突き合わせができるよう、フルの UTM も控えめに添える。
 */
function formatUtm(lat: number, lon: number): string {
  const u = toUtm(lon, lat);
  const code = gridCodeOf(lat, lon, state.utmWideEasting, state.utmWideNorthing);
  const eastingLen = gridEastingDigits(state.utmWideEasting);
  return (
    `<div class="utm">` +
    `<span class="utm-8digit">${code.slice(0, eastingLen)} ${code.slice(eastingLen)}</span>` +
    `<span class="utm-full">${u.zone} ${Math.round(u.easting)}E ${Math.round(u.northing)}N</span>` +
    `</div>`
  );
}

/**
 * ある矩形が地図に重なっているぶんだけ、見えている範囲の中心へ地図の
 * 中心をずらすための offset。重ならない・部分的な重なり（隅など）で
 * 判断がつかない場合は null（このオーバーレイは無視してよい、の意味）。
 *
 * 下からのシートは横幅いっぱいを覆う → 見える範囲は上側なので中心を押し上げる。
 * 左からの引き出しは高さいっぱいを覆う → 見える範囲は右側なので中心を押し出す。
 */
function overlapOffset(mapRect: DOMRect, otherRect: DOMRect): [number, number] | null {
  const overlapW = Math.max(0, Math.min(mapRect.right, otherRect.right) - Math.max(mapRect.left, otherRect.left));
  const overlapH = Math.max(0, Math.min(mapRect.bottom, otherRect.bottom) - Math.max(mapRect.top, otherRect.top));
  if (overlapW === 0 || overlapH === 0) return null;
  if (overlapW >= mapRect.width * 0.9) return [0, -overlapH / 2];
  if (overlapH >= mapRect.height * 0.9) return [overlapW / 2, 0];
  return null;
}

/**
 * パネル・シートが地図の上に覆いかぶさっている（狭い画面のオーバーレイ）ぶんだけ、
 * 見えている範囲の中心へ地図の中心をずらすための offset。
 *
 * デスクトップ幅ではパネルは横並びのサイドバーで地図と重ならないため、
 * 実測の重なりが 0 になり自然と [0, 0] を返す（幅で分岐する条件式を
 * 別途持たなくて済む）。
 *
 * ★ #live-view（接続と送信）も§132で「暗幕を透明化し地図を見せる」
 *   シートになったため、パネルと同じくここで重なりを見る必要がある。
 *   見ないと、追従中の端末を選んだ瞬間に地図の中心が画面の真ん中＝
 *   シートの下に隠れてしまう（利用者からの指摘、2026-09-12）。
 *   #live-view自体（.goal-entryの外枠）はinset:0で画面全体を覆う
 *   （中身を透明化しているだけ）ので、重なり判定は内側の
 *   .goal-entry-box（実際に見えているシート本体）で行うこと。
 *
 * ★ #display-view（表示）も同じ「暗幕を透明化し地図を見せる」シートだが、
 *   ここでは見ていなかった。表示ダイアログを開いたまま追従対象の位置が
 *   更新されると、対象がシートの下に隠れる（コードレビュー指摘、
 *   2026-09-13）。#live-viewと全く同じ形なので同じやり方で見る。
 *
 * ★ ただし上の`overlapOffset()`をそのまま使うと直らなかった
 *   （再レビュー指摘、2026-09-13）: `.goal-entry-box`は
 *   `width: min(380px, 100%)`で幅380px頭打ちのため、画面幅が
 *   約422px（380÷0.9）を超えるスマートフォン横向き・タブレット・PCでは
 *   `overlapW >= mapRect.width * 0.9`を満たせず、縦方向にどれだけ
 *   重なっていても補正が一切効かなかった。`overlapOffset()`の90%判定は
 *   `.panel`（左右ドッキングもありうる汎用の重なり）向けのヒューリス
 *   ティックなので、ここでは使わない。`#display-view`/`#live-view`は
 *   `.goal-entry:not([hidden]) { align-items: flex-end; justify-content:
 *   center; }`により常に画面下端に接し・横方向は中央寄せと決まっている
 *   ので、幅が90%に届かなくても、縦方向の重なりぶんだけ確実に押し上げれば
 *   見える位置になる（横に逃がす判断が要らない）。
 * ★ ただしPC（`isDesktopLayout()`）では、この2つは地図の左上（サイドパネルの
 *   すぐ右）に出る（style.css、2026-10-06）。その場合は縦ではなく横方向の
 *   重なりぶん右へ寄せる。
 */
function mapCenterOffset(): [number, number] {
  const mapRect = map.getContainer().getBoundingClientRect();

  const panelExpanded = document.body.classList.contains('panel-open') || document.body.classList.contains('panel-half');
  if (panelExpanded) {
    const offset = overlapOffset(mapRect, el('panel').getBoundingClientRect());
    if (offset) return offset;
  }

  for (const viewId of ['display-view', 'live-view']) {
    const view = document.getElementById(viewId);
    const viewBox = view?.querySelector('.goal-entry-box');
    if (view && !view.hidden && viewBox) {
      const boxRect = viewBox.getBoundingClientRect();
      if (isDesktopLayout()) {
        // PCではシートは地図の左上（サイドパネルのすぐ右）に出る（style.css）。
        // 横方向の重なりぶん右へ寄せる
        const overlapW = Math.max(0, Math.min(mapRect.right, boxRect.right) - Math.max(mapRect.left, boxRect.left));
        if (overlapW > 0) return [overlapW / 2, 0];
        continue;
      }
      const overlapH = Math.max(0, Math.min(mapRect.bottom, boxRect.bottom) - Math.max(mapRect.top, boxRect.top));
      if (overlapH > 0) return [0, -overlapH / 2];
    }
  }

  return [0, 0];
}

function fitToFlight(): void {
  const flight = state.flights[state.activeFlight];
  const bounds = flight ? boundsOf(flight.fixes) : null;
  if (bounds) map.fitBounds(bounds, { padding: 80, duration: 600 });
}

// --- UI ---------------------------------------------------------------------

function renderVenueSelect(): void {
  const select = el<HTMLSelectElement>('venue-select');
  select.innerHTML = state.venues
    .map((v) => `<option value="${v.id}">${escapeHtml(v.name)}</option>`)
    .join('');
  select.onchange = () => void selectVenue(select.value);
}

/** モードを実際に切り替える。パイロットゲート通過後もここを通す */
function applyMode(id: ModeId): void {
  state.mode = id;
  document.body.dataset.mode = state.mode;
  renderModeSelect();
  renderSummary();
  renderHud();
  syncMyPositionVisibility?.();
  // クルー以外に切り替えたらコンパスのボタンはCSSで消えるが、開いたまま
  // だと方位センサーの購読が裏で動き続けてしまうので明示的に閉じる
  if (id !== 'crew') {
    closeFinder?.();
    closeCompassIfOpen?.();
    closePibalIfOpen?.();
  }
  // モードで地図の見え方が変わるので、寸法を取り直す
  map.resize();
}

/**
 * 役割モードのドロップダウン。以前は3つのボタンを並べていたが、常時
 * 幅を取る必要は無く、上部バーの接続・送信インジケーター（常に文字
 * ラベル付きで出したい、利用者からの要望）の場所を空けるため畳んだ
 * （2026-09-13）。optionsの組み立ては初回だけでよい（毎回作り直すと
 * ドロップダウンを開いている最中に中身が消える）。
 */
function renderModeSelect(): void {
  const select = el<HTMLSelectElement>('mode-select');
  if (select.options.length !== MODES.length) {
    select.innerHTML = MODES.map(
      (m) => `<option value="${m.id}" title="${escapeHtml(m.description)}">${escapeHtml(m.label)}</option>`,
    ).join('');
    select.onchange = () => {
      const target = select.value as ModeId;
      // パイロットモードだけはパスワード確認を挟む（航跡の乱立を防ぐ）。
      // ゲートで弾かれる可能性があるので、確定するまで見た目を戻しておく
      if (target === 'pilot' && state.mode !== 'pilot') {
        select.value = state.mode;
        openPilotGate();
        return;
      }
      applyMode(target);
    };
  }
  select.value = state.mode;
}

/**
 * 現在の state（basemap・PMTiles URL・配色）でスタイルを差し替える。
 * ベースマップの切り替えと、PMTiles の配色切り替えの両方から呼ぶ共通処理。
 */
function applyBasemapStyle(): void {
  map.setStyle(buildStyle(state.basemap, { pmtilesUrl: pmtilesUrlOf(), mapTheme: state.mapTheme }));
  // スタイル差し替えでソースが消えるので、読み込み後に描き直す
  map.once('styledata', () => {
    addOverlayLayers(map);
    applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
    applyHeightScale(map, state.heightScale);
    refreshCompMapLayer();
    void applyTerrainState();
    redraw();
  });
}

function renderBasemapSelect(): void {
  const select = el<HTMLSelectElement>('basemap-select');
  select.innerHTML = BASEMAPS.map(
    (b) => `<option value="${b.id}">${escapeHtml(b.label)}${b.offline ? '' : '（通信あり）'}</option>`,
  ).join('');
  select.value = state.basemap;
  select.onchange = () => {
    state.basemap = select.value as BasemapId;
    const option = BASEMAPS.find((b) => b.id === state.basemap)!;
    setStatus(option.note ?? '', option.offline ? 'info' : 'warn');
    applyBasemapStyle();
  };
}

/** 現在の会場のオフライン地図ファイル。無ければ undefined */
function pmtilesUrlOf(): string | undefined {
  if (!state.venue) return undefined;
  return new URL(
    `${import.meta.env.BASE_URL}venues/${state.venue.id}/basemap.pmtiles`,
    location.href,
  ).toString();
}

/** 現在の会場のオフライン地形データファイル。無ければ undefined */
function terrainUrlOf(): string | undefined {
  if (!state.venue) return undefined;
  return new URL(
    `${import.meta.env.BASE_URL}venues/${state.venue.id}/terrain.pmtiles`,
    location.href,
  ).toString();
}

/**
 * 現在の会場の競技地図タイル設定。会場に無ければ null。
 *
 * ★ `{z}/{x}/{y}` はMapLibreが文字列置換で使うプレースホルダなので、
 *   `pmtilesUrlOf` と違って `new URL()` を通してはいけない
 *   （URLエンコードされて `%7Bz%7D` になり、二度と置換されなくなる）。
 *   相対パスのまま渡せばよい（`data/venue.ts` の `fetch` と同じ流儀）。
 */
function compMapConfigOf(venue: Venue | null): CompMapLayerConfig | null {
  if (!venue?.compMap) return null;
  return {
    tileUrlTemplate: `${import.meta.env.BASE_URL}venues/${venue.id}/compmap/{z}/{x}/{y}.jpg`,
    manifest: venue.compMap,
  };
}

/**
 * 競技地図オーバーレイのraster sourceを現在の会場に合わせて差し替え、
 * 表示ON/OFF・不透明度を復元する。
 *
 * `setCompMapSource` はタイルURLが会場ごとに違う（`addSource`時に焼き込まれ、
 * 他のオーバーレイのように`setData`では更新できない）ため、他のソースのように
 * 「一度足せば済む」ではなく**会場切り替えのたびに呼び直す**必要がある。
 * `addOverlayLayers(map)`のあと（スタイル差し替えでソースが消えた直後）にも、
 * 会場切り替え自体（スタイル差し替えを伴わない場合もある）でも呼ぶこと。
 */
function refreshCompMapLayer(): void {
  setCompMapSource(map, compMapConfigOf(state.venue));
  applyCompMapVisibility(map, state.showCompMap);
  applyCompMapOpacity(map, state.compMapOpacity);
}

// --- オフライン地図・地形データ（PMTiles）のダウンロード ------------------------
//
// ★ 地形データ（terrain.pmtiles）も「会場ごとに1ファイル、事前にWi-Fiで
//   取得しておけば現地では通信不要」という全く同じ形式なので、この節の
//   仕組みをそのまま共用する（setupOfflinePmtilesSection関数）。

/**
 * オフライン地図・地形データ、どちらか1つぶんのダウンロードUIを配線する。
 *
 * ★ ダウンロード時にキャッシュへ書き込むキーと、実際にMapLibre/地形設定が
 *   叩くURLは、必ず同じ`urlOf()`の戻り値を使うこと。別々に組み立てたURL
 *   文字列（相対/絶対の違いなど）を使うと、キャッシュに保存したはずなのに
 *   Rangeリクエスト側では別キー扱いになり、「ダウンロードしたのにオフラインで
 *   効かない」という気付きにくい不具合になる（オフライン地図で実際に注意した
 *   点、pmtilesUrlOfのコメント参照）。
 */
function setupOfflinePmtilesSection(opts: {
  urlOf: () => string | undefined;
  sectionId: string;
  infoId: string;
  downloadBtnId: string;
  deleteBtnId: string;
  downloadedLabel: (bytes: number) => string;
  notDownloadedLabel: string;
  downloadedStatus: string;
  deletedStatus: string;
}): { render: () => Promise<void>; cancel: () => void } {
  const section = el(opts.sectionId);
  const infoBox = el(opts.infoId);
  const downloadBtn = el<HTMLButtonElement>(opts.downloadBtnId);
  const deleteBtn = el<HTMLButtonElement>(opts.deleteBtnId);

  let abort: AbortController | null = null;
  const cancel = (): void => {
    abort?.abort();
    abort = null;
  };

  const render = async (): Promise<void> => {
    const config = state.venue ? mapConfigOf(state.venue.id) : null;
    const url = opts.urlOf();
    if (!config || !url) {
      section.hidden = true;
      return;
    }
    section.hidden = false;

    if (abort) return; // ダウンロード中は render() で状態を上書きしない

    const status = await offlineMapStatus(url);
    deleteBtn.hidden = !status.downloaded;
    if (status.downloaded) {
      infoBox.textContent = opts.downloadedLabel(status.bytes ?? 0);
      downloadBtn.textContent = '取り直す';
    } else {
      infoBox.textContent = opts.notDownloadedLabel;
      downloadBtn.textContent = 'ダウンロード';
    }
  };

  downloadBtn.onclick = async () => {
    const url = opts.urlOf();
    if (!url) return;

    if (abort) {
      cancel();
      infoBox.textContent = 'キャンセルしました';
      downloadBtn.textContent = 'ダウンロード';
      return;
    }

    const controller = new AbortController();
    abort = controller;
    downloadBtn.textContent = 'キャンセル';
    deleteBtn.hidden = true;

    try {
      await downloadOfflineMap(
        url,
        (loaded, total) => {
          const l = formatBytes(loaded);
          infoBox.textContent = total
            ? `取得中… ${l} / ${formatBytes(total)}（${Math.round((loaded / total) * 100)}%）`
            : `取得中… ${l}`;
        },
        controller.signal,
      );
      abort = null;
      await render();
      setStatus(opts.downloadedStatus);
    } catch (e) {
      abort = null;
      if ((e as Error).name === 'AbortError') return; // キャンセルは既に表示済み
      infoBox.textContent = `取得に失敗しました: ${(e as Error).message}`;
      downloadBtn.textContent = 'ダウンロード';
    }
  };

  deleteBtn.onclick = async () => {
    const url = opts.urlOf();
    if (!url) return;
    await deleteOfflineMap(url);
    await render();
    setStatus(opts.deletedStatus);
  };

  return { render, cancel };
}

/** 進行中のダウンロードを中断するための口。会場切り替え時に使う */
let cancelOfflineMapDownload: () => void = () => {};
/** 会場切り替え等のタイミングで、外部から表示を更新し直すための口 */
let offlineMapRender: () => Promise<void> = async () => {};
let cancelTerrainDownload: () => void = () => {};
let terrainRender: () => Promise<void> = async () => {};

async function setupOfflineMap(): Promise<void> {
  const { render, cancel } = setupOfflinePmtilesSection({
    urlOf: pmtilesUrlOf,
    sectionId: 'offline-map-section',
    infoId: 'offline-map-info',
    downloadBtnId: 'offline-map-download',
    deleteBtnId: 'offline-map-delete',
    downloadedLabel: (bytes) => `取得済み（${formatBytes(bytes)}）`,
    notDownloadedLabel:
      '未取得。Wi-Fi 環境での取得を推奨します（会場ぶんで数十MBの通信）。' +
      '「キャッシュを消して再読み込み」を行うと、このデータも一緒に消えます。',
    downloadedStatus: 'オフライン地図を取得しました',
    deletedStatus: 'オフライン地図を削除しました',
  });
  await render();
  cancelOfflineMapDownload = cancel;
  offlineMapRender = render;
}

async function setupOfflineTerrain(): Promise<void> {
  const { render, cancel } = setupOfflinePmtilesSection({
    urlOf: terrainUrlOf,
    sectionId: 'terrain-section',
    infoId: 'terrain-info',
    downloadBtnId: 'terrain-download',
    deleteBtnId: 'terrain-delete',
    downloadedLabel: (bytes) => `取得済み（${formatBytes(bytes)}）`,
    notDownloadedLabel:
      '未取得でも、通信があればオンラインで地形を使えます（都度取得）。' +
      'オフラインでも使いたい場合のみダウンロードしてください（会場ぶんで数MB程度）。',
    downloadedStatus: '地形データを取得しました',
    deletedStatus: '地形データを削除しました',
  });
  await render();
  cancelTerrainDownload = cancel;
  terrainRender = render;
}

/**
 * 惑星全体のMapterhorn地形PMTiles。オンライン時、会場ぶんの事前ダウンロードが
 * 無くても使えるようにするための直接参照（PMTilesのRange Requestにより、
 * 惑星全体をダウンロードせず必要な部分だけ都度取得される）。
 */
const TERRAIN_ONLINE_URL = 'pmtiles://https://download.mapterhorn.com/planet.pmtiles';

/**
 * fill-extrusionの絶対高度（alt/floorM/ceilingM/altM）を使うフィーチャ群に、
 * その地点の地形標高（groundM）を追加する。地形が無効・未読込みなら
 * `queryTerrainElevation`はnullを返すので0扱いになり、既存の式と完全に一致する
 * （layers.tsの`aboveGround`参照）。
 *
 * ★ 代表点は先頭の座標で十分。fill-extrusion自体もフィーチャ単位
 *   （centroid）でしか地形標高を見ないため（シェーダーソースで確認済み）、
 *   長い壁・広いポリゴンでもこれ以上細かく分けても意味が無い。
 * ★ `state.terrainEnabled`がfalseのときは呼び出し自体を省く
 *   （無効な地形でqueryTerrainElevationを呼び続けるのは無駄なため）。
 * ★ 高度の色分け（対地/海抜、§144・§147）はこの関数を使わない。あちらは
 *   会場につき1つの基準標高（state.groundRefFt、利用者が確認・修正できる
 *   値）を一律に引くだけの単純な計算にしてある（layers.tsの
 *   `altitudeColorExpression`参照）。
 */
/**
 * 地形の標高（queryTerrainElevation）の覚え書き。3Dの壁・稜線は数秒おきに
 * 作り直すが、同じ場所の地形は変わらないので、毎回問い合わせない
 * （2026-10-10、3D表示が重い件の計測で、問い合わせが処理時間の約2割を
 * 占めていた、docs/08 §195）。約20mの格子ごとに覚える。まだ地形タイルが
 * 読めていない（null）結果は覚えない。新しい地形タイルが届いたら捨てる
 * （setupTerrainRidgeLineRefresh、より細かいデータで取り直すため）。
 */
const groundElevationCache = new Map<string, number>();
const GROUND_CACHE_GRID = 5000; // 1/5000度 ≒ 20m
const GROUND_CACHE_MAX = 200_000;

function groundElevationAt(lon: number, lat: number): number | null {
  const key = `${Math.round(lon * GROUND_CACHE_GRID)},${Math.round(lat * GROUND_CACHE_GRID)}`;
  const hit = groundElevationCache.get(key);
  if (hit !== undefined) return hit;
  const elevation = map.queryTerrainElevation([lon, lat]);
  if (elevation == null) return null;
  if (groundElevationCache.size >= GROUND_CACHE_MAX) groundElevationCache.clear();
  groundElevationCache.set(key, elevation);
  return elevation;
}

function withGroundElevation(fc: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
  if (!state.terrainEnabled) return fc;
  for (const f of fc.features) {
    const pt = firstCoordOf(f.geometry);
    if (!pt) continue;
    const elevation = groundElevationAt(pt[0], pt[1]);
    f.properties = { ...(f.properties ?? {}), groundM: elevation ?? 0 };
  }
  return fc;
}

function firstCoordOf(geom: GeoJSON.Geometry): [number, number] | null {
  switch (geom.type) {
    case 'Point':
      return geom.coordinates as [number, number];
    case 'LineString':
      return (geom.coordinates[0] as [number, number]) ?? null;
    case 'Polygon':
      return (geom.coordinates[0]?.[0] as [number, number]) ?? null;
    case 'MultiPolygon':
      return (geom.coordinates[0]?.[0]?.[0] as [number, number]) ?? null;
    default:
      return null;
  }
}

/** PZ/SA非表示・会場未選択時の空配列。毎回`[]`を作るとキャッシュが毎秒外れるため共有する */
const NO_PZSA_ZONES: PzSaZone[] = [];

/**
 * PZ/SAの地図用GeoJSON（円・多角形）のキャッシュ（tubeの3Dは
 * `updatePzSaTubes()`が別に面倒を見る）。
 *
 * ★ redraw()はライブ受信のたびに（ライブ中はほぼ毎秒）走るが、PZ/SAの元データ
 *   （state.venue.pzsa.zones）は会場を切り替えるまで不変。以前は毎回ゼロから
 *   組み立て直し、地形標高の問い合わせ・setData()による再構築まで走っていた。
 *   同じ参照を返せばsetSourceData()側でsetData()も省かれる。
 * ★ 地形タイルの読込状況はキーに含められない（状態フラグを介さずに変わる）
 *   ため、地形タイル到着時に`invalidatePzSaGeoJson()`で明示的に捨てる
 *   （`setupTerrainRidgeLineRefresh()`参照。稜線と同じ方式）。
 */
let pzsaGeoJsonCache: {
  zones: PzSaZone[];
  terrainEnabled: boolean;
  circle: GeoJSON.FeatureCollection;
  poly: GeoJSON.FeatureCollection;
} | null = null;

function invalidatePzSaGeoJson(): void {
  pzsaGeoJsonCache = null;
  pzsaTubeInputs = null;
}

function pzSaGeoJsonFor(zones: PzSaZone[]): NonNullable<typeof pzsaGeoJsonCache> {
  if (
    pzsaGeoJsonCache &&
    pzsaGeoJsonCache.zones === zones &&
    pzsaGeoJsonCache.terrainEnabled === state.terrainEnabled
  ) {
    return pzsaGeoJsonCache;
  }
  pzsaGeoJsonCache = {
    zones,
    terrainEnabled: state.terrainEnabled,
    circle: withGroundElevation(pzSaToGeoJson(zones)),
    poly: withGroundElevation(pzSaPolygonsToGeoJson(zones)),
  };
  return pzsaGeoJsonCache;
}

/** PZ/SAの地図表示（円・多角形・tubeの3D）を今の状態に合わせる。入力が変わっていなければ何もしない */
function refreshPzSaLayers(): void {
  const zones = state.showPzSa && state.venue?.pzsa ? state.venue.pzsa.zones : NO_PZSA_ZONES;
  const geo = pzSaGeoJsonFor(zones);
  setSourceData(map, PZSA_SOURCE, geo.circle);
  setSourceData(map, PZSA_POLY_SOURCE, geo.poly);
  updatePzSaTubes();
}

/**
 * tube（線状PZ）の3D表示（pzsaTubeLayer）のメッシュを、入力が変わったときだけ
 * 作り直す。fill-extrusionと違い、高さの誇張倍率・地形標高は頂点に焼き込む
 * （稜線ラインと同じ）ので、それらが変わったときも作り直す必要がある。
 * redraw()と、誇張倍率を変えたとき（applyHeightScaleの直後）に呼ぶこと。
 */
let pzsaTubeInputs: {
  zones: PzSaZone[];
  heightScale: number;
  terrainEnabled: boolean;
} | null = null;

function updatePzSaTubes(): void {
  const zones =
    state.show3d && state.showPzSa && state.venue?.pzsa ? state.venue.pzsa.zones : NO_PZSA_ZONES;
  if (
    pzsaTubeInputs &&
    pzsaTubeInputs.zones === zones &&
    pzsaTubeInputs.heightScale === state.heightScale &&
    pzsaTubeInputs.terrainEnabled === state.terrainEnabled
  ) {
    return;
  }
  pzsaTubeInputs = { zones, heightScale: state.heightScale, terrainEnabled: state.terrainEnabled };
  updatePzSaTubeMesh(zones, {
    heightScale: state.heightScale,
    groundElevation: (lon, lat) =>
      state.terrainEnabled ? map.queryTerrainElevation([lon, lat]) ?? 0 : 0,
  });
}

/**
 * 稜線ラインを壁の天面からわずかに浮かせる高さ [m]。壁本体（fill-extrusion）の
 * 天面と全く同じ高さに置くと、深度バッファ上で座標が一致してしまい
 * Zファイティングが起きて線が消える（壁が先に書き込んだ深度と等しいか
 * それより奥と判定され、GL_LESSの深度テストに落ちる）。§162の
 * 旧稜線キャップ（fill-extrusionの薄板）で「天面ちょうどから上へ足す」と
 * 同じ理由で採用していた対策を、カスタムレイヤーでも踏襲する。
 */
const RIDGE_LINE_LIFT_M = 3;

/**
 * 1本の航跡（保存済みフライト or ライブ端末）の実測点を、そのままRidgeLineLayer
 * の頂点へ変換する。CPU側で点を増やす細分化はしない（§164で撤回済み。
 * 実データの点数のまま渡し、点と点の間の補間はGPUのラスタライザに委ねる）。
 *
 * ★ 高さ・色は、既存のfill-extrusion（gv-track-3d-wall）の式
 *   （heightScaleExpression/altitudeColorExpression）と完全に同じ結果に
 *   なるようJS側で再現する。地形標高（groundM）は壁と同じ理由
 *   （`aboveGround`参照）で先に差し引いてから誇張倍率を掛け、最後に
 *   足し戻す（`setTerrain()`有効時にMapLibreが自動でやる加算を、
 *   カスタムレイヤーでは自分で行う必要があるため）。壁の天面に対して
 *   RIDGE_LINE_LIFT_Mぶんだけ上乗せし、Zファイティングを避ける。
 */
/**
 * 稜線ラインを白に寄せる度合い（0〜1）。壁本体と全く同じ高度色（0）だと、
 * 低高度（青緑系、暗い背景に対して明るい）ではよく見えるのに、高高度
 * （紫〜マゼンタ系、壁本体自体がもともと明るい色）では壁とほぼ同化して
 * 見分けがつかない、という高度依存の見えにくさが実測で確認された
 * （利用者の指摘「地上付近しか見えない」）。色相を保ったまま白へ
 * 寄せることで、壁本体の色によらず常に「壁より明るい」状態を保証し、
 * どの高度でも壁との明度差で見分けられるようにする。
 */
const RIDGE_LINE_WHITEN = 0.45;

/**
 * 稜線の点。色は既定では高度の色分け（白寄せ）。`solidColorHex`を渡すと
 * その色1色にする（ライブの機体は、アイコン・ラベルと同じ機体ごとの色に
 * する。みな同じ水色で見分けられないとの指摘、docs/08 §196）。
 */
function ridgeLinePointsFromFixes(fixes: FixSample[], solidColorHex?: string): RidgeLinePoint[] {
  const scale = state.heightScale;
  const groundRefM = currentGroundRefM();
  const solid = solidColorHex ? hexToRgba01(solidColorHex) : null;
  return fixes.map((f) => {
    const altRaw = f.altPressure ?? f.altMsl ?? 0;
    const groundM = state.terrainEnabled ? groundElevationAt(f.lon, f.lat) ?? 0 : 0;
    const effectiveAltM = groundM + Math.max(altRaw - groundM, 0) * scale + RIDGE_LINE_LIFT_M;
    if (solid) return { lon: f.lon, lat: f.lat, effectiveAltM, color: solid };
    const colorHex = colorForAltitudeMode(altRaw, state.altitudeStops, state.altitudeMode, groundRefM);
    const [r, g, b, a] = hexToRgba01(colorHex);
    const color: [number, number, number, number] = [
      r + (1 - r) * RIDGE_LINE_WHITEN,
      g + (1 - g) * RIDGE_LINE_WHITEN,
      b + (1 - b) * RIDGE_LINE_WHITEN,
      a,
    ];
    return { lon: f.lon, lat: f.lat, effectiveAltM, color };
  });
}

/**
 * 保存済み航跡（trackRidgeLine）を直近に作り直したときの入力の特徴量。
 * ライブ更新（`redraw()`）のたびに`updateRidgeLines()`は呼ばれるが、
 * 保存済み航跡（過去ログ・IGC）は他機のライブ位置とは無関係に不変な
 * ことがほとんどで、変わっていないのに毎回、頂点配列の組み立て
 * （区間ごとに6頂点へ展開）と4本のバッファ再アップロードをするのは
 * 無駄（コードレビュー指摘、2026-09-15：「ライブ更新ごとに変化していない
 * 過去航跡まで全再構築・再送している」。実機でPCの負荷が重くなる一因）。
 * この特徴量が変わっていないときだけ、保存済みぶんの再構築を省く
 * （ライブ端末ぶん`liveTrackRidgeLine`は実際に毎回変わりうるデータなので
 * 引き続き毎回作り直す）。
 */
let trackRidgeLineSignature = '';

/** 保存済み航跡の内容を変更・置換したときに、稜線のCPU/GPUバッファを作り直させる。 */
function invalidateTrackRidgeLines(): void {
  trackRidgeLineSignature = '';
  live3dSignature = ''; // ライブ端末の壁・稜線も、次の updateLive3d() ですぐ作り直す
}

/**
 * 稜線ライン（RidgeLineLayer）の頂点を今の状態から作り直す。fill-extrusion
 * の壁と違い、ジオメトリを自前のバッファへ都度アップロードする方式なので、
 * 高さの誇張倍率・色分けの基準を変えるだけの操作（applyHeightScale/
 * applyAltitudeColorsのようなpaintプロパティの差し替え）では追従できない。
 * そのため、両者を呼ぶ箇所には必ずこの関数も呼ぶこと（redraw()からは
 * 常に呼ばれるので、大半のケースはそちらで足りる）。
 */
function updateRidgeLines(): void {
  if (!state.show3d) {
    trackRidgeLine.setStrips([]);
    trackRidgeLineSignature = '';
    updateLive3d(); // 壁・稜線を空にする（表示設定が変わったのですぐ反映される）
    map.triggerRepaint();
    return;
  }
  const visibleFlights = state.flights.filter((_, i) => !state.hiddenFlights.has(i));
  // ★ ここに使う値だけを特徴量にする。地形の実際のサンプル結果
  //   （queryTerrainElevation）はタイル読込で状態フラグを介さずに変わりうる
  //   ため、この特徴量には含めない——その代わり、地形タイル読込完了時に
  //   `trackRidgeLineSignature`を明示的に無効化する
  //   （`setupTerrainRidgeLineRefresh()`参照）
  const sig = JSON.stringify([
    visibleFlights.map((f) => [f.terminalId, f.fixes.length]),
    state.heightScale,
    state.altitudeMode,
    state.altitudeStops,
    state.terrainEnabled,
    state.groundRefFt,
    state.threeDDetail,
  ]);
  if (sig !== trackRidgeLineSignature) {
    // 壁と同じく3D用に間引いた点で作る（simplifyFixesFor3d、docs/08 §195）
    trackRidgeLine.setStrips(visibleFlights.map((f) => ridgeLinePointsFromFixes(simplifyFixesFor3d(f.fixes, state.threeDDetail.stepM, state.threeDDetail.altStepM))));
    trackRidgeLineSignature = sig;
  }

  // ライブ端末の稜線は、壁と一緒に updateLive3d() が（間引いた間隔で）作り直す
  updateLive3d();
  map.triggerRepaint();
}

/**
 * ライブ端末の3Dの壁（LIVE_TRACK_3D_SOURCE）と稜線（liveTrackRidgeLine）を
 * 作り直す。
 *
 * ★ なぜ間引くか（2026-10-10、競技中に「3D表示がとても重くハングする。
 *   複数の航跡になると重い」と指摘、docs/08 §195）。以前はライブ更新
 *   （ほぼ毎秒）のたびに全機・全点の壁と稜線を作り直して地図へ渡し直して
 *   いた（7機×1時間で壁2万5千枚・約9MB）。
 *   - 点が増えただけ（同じ機体・同じ表示設定）なら、最短
 *     LIVE_3D_MIN_INTERVAL_MS おきにまとめる。機体の矢印（現在位置）は
 *     redraw() が毎回すぐ動かすので、壁の先端が最大3秒遅れるだけ
 *   - 表示設定・機体の顔ぶれが変わったときは、すぐ作り直す
 *   - 何も変わっていなければ作り直さない
 */
function updateLive3d(): void {
  const terminals = liveTerminals().filter((t) => terminalShown(t.terminalId));
  const settings = JSON.stringify([
    state.show3d,
    state.show3dSmooth,
    state.threeDDetail,
    state.terrainEnabled,
    state.heightScale,
    state.altitudeMode,
    state.altitudeStops,
    state.groundRefFt,
    terminals.map((t) => [t.terminalId, colorForTerminal(t.terminalId)]),
  ]);
  // 点の数と先頭の時刻（上限で古い点が捨てられたことも分かるように）
  const sig = `${settings}|${terminals.map((t) => `${t.fixes.length}:${t.fixes[0]?.t ?? 0}`).join(',')}`;
  if (sig === live3dSignature) return;
  const onlyGrew = live3dSignature.startsWith(`${settings}|`);
  const wait = live3dBuiltAt + LIVE_3D_MIN_INTERVAL_MS - Date.now();
  if (onlyGrew && wait > 0) {
    live3dTimer ??= setTimeout(() => {
      live3dTimer = null;
      updateLive3d();
    }, wait);
    return;
  }
  if (live3dTimer) {
    clearTimeout(live3dTimer);
    live3dTimer = null;
  }
  live3dSignature = sig;
  live3dBuiltAt = Date.now();
  setSourceData(
    map,
    LIVE_TRACK_3D_SOURCE,
    state.show3d ? withGroundElevation(liveTracks3dToGeoJson(terminals, 50, state.show3dSmooth, state.threeDDetail)) : EMPTY_GEOJSON,
  );
  liveTrackRidgeLine.setStrips(
    // 稜線は機体ごとの色（アイコン・ラベルと同じ）。壁は既定では高度の色分け（地図右の「単色」ボタンで機体色にも切り替わる）
    state.show3d
      ? terminals.map((t) => ridgeLinePointsFromFixes(
              simplifyFixesFor3d(t.fixes, state.threeDDetail.stepM, state.threeDDetail.altStepM),
              colorForTerminal(t.terminalId),
            ))
      : [],
  );
  map.triggerRepaint();
}

/**
 * 地形（DEM）タイルが新しく読み込まれるたびに、保存済み航跡の稜線を
 * 作り直す。
 *
 * ★ なぜ必要か（コードレビュー指摘、2026-09-15）: `ridgeLinePointsFromFixes()`は
 *   `map.queryTerrainElevation()`がまだそのタイルを読めず`null`を返す間、
 *   `?? 0`で標高0mとして高度を確定し、頂点バッファへ焼き込む。fill-extrusion
 *   の壁はシェーダー側で毎フレーム地形標高を自動サンプルし直すため後から
 *   自然に追従するが、稜線はJS側で一度作ったバッファのままなので、地形が
 *   後から読み込めても再構築しない限りズレたまま（壁だけ地形に追従し、
 *   稜線が標高0基準のまま浮く/沈む）。
 * ★ `updateRidgeLines()`の特徴量（`trackRidgeLineSignature`）には
 *   地形タイルの読込状況は含めていない（状態フラグとしては変化しない
 *   ため）ので、ここで明示的に無効化して次回`updateRidgeLines()`が
 *   確実に作り直すようにする。
 * ★ 地形タイルは短時間に何枚も届く（パン・ズーム時は特に）ため、都度
 *   フル再構築すると本末転倒。届いてから少し待ち、その間に来た分は
 *   1回にまとめるスロットルを掛ける。
 */
function setupTerrainRidgeLineRefresh(): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  map.on('sourcedata', (e) => {
    if (e.sourceId !== TERRAIN_SOURCE || !e.isSourceLoaded) return;
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      groundElevationCache.clear(); // 新しい（より細かい）地形で取り直す
      invalidateTrackRidgeLines(); // 次のupdateRidgeLines()で確実に作り直す
      // PZ/SAの地形標高も読込前の0のまま焼き込まれているので作り直す。
      // ★ redraw()を待たずにここで反映すること（redraw()はライブ受信が無い間は
      //   走らないため、待つと地形が届いても古い高さのまま残る）
      invalidatePzSaGeoJson();
      refreshPzSaLayers();
      updateRidgeLines();
    }, 500);
  });
}

/**
 * 3D表示の地形設定を、現在の状態（ON/OFF・会場・オンライン/オフライン）に
 * 合わせて反映する。優先順位:
 *   1. OFFなら地形なし（今まで通り平坦）
 *   2. 会場ぶんの地形データをダウンロード済みならそれを使う（オフライン可）
 *   3. 未ダウンロードでもオンラインなら惑星全体のPMTilesをそのまま使う
 *   4. オフラインかつ未ダウンロードなら地形なし（今まで通り平坦にフォールバック）
 *
 * スタイル差し替え（`map.setStyle`）はソースを消すため、その後の
 * `styledata`ハンドラから必ず呼び直すこと（`applyBasemapStyle`等を参照）。
 */
async function applyTerrainState(): Promise<void> {
  groundElevationCache.clear(); // 地形の出どころ（会場の地形データ・オンライン）が変わりうる
  if (!state.terrainEnabled) {
    setTerrainSource(map, null);
    return;
  }

  const localUrl = terrainUrlOf();
  if (localUrl) {
    const status = await offlineMapStatus(localUrl);
    if (status.downloaded) {
      setTerrainSource(map, `pmtiles://${localUrl}`);
      return;
    }
  }

  if (navigator.onLine) {
    setTerrainSource(map, TERRAIN_ONLINE_URL);
    return;
  }

  setTerrainSource(map, null);
}

function setupTerrainToggle(): void {
  const checkbox = el<HTMLInputElement>('terrain-enabled');
  checkbox.checked = state.terrainEnabled;
  checkbox.onchange = () => {
    state.terrainEnabled = checkbox.checked;
    void (async () => {
      await applyTerrainState();
      // ON/OFF切り替えで、各fill-extrusionのgroundMを今の状態で作り直す
      // （withGroundElevationはstate.terrainEnabledを見て挙動を変えるため）
      redraw();
    })();
  };
}

function renderFlightList(): void {
  const box = el('flight-list');
  if (state.flights.length === 0) {
    box.innerHTML = '<p class="muted">なし</p>';
    return;
  }
  box.innerHTML = state.flights
    .map(
      (f, i) =>
        `<div class="flight-row">` +
        `<input type="checkbox" data-vis-index="${i}" ${state.hiddenFlights.has(i) ? '' : 'checked'} title="地図上の航跡を表示/非表示" />` +
        `<button type="button" data-index="${i}" class="${i === state.activeFlight ? 'active' : ''}">${escapeHtml(f.label)}</button>` +
        `</div>`,
    )
    .join('');
  for (const btn of box.querySelectorAll<HTMLButtonElement>('button')) {
    btn.onclick = () => {
      state.activeFlight = Number(btn.dataset.index);
      // 名前を選んだのに航跡が消えたままだと分かりにくいので、選ぶと表示も戻す
      state.hiddenFlights.delete(state.activeFlight);
      renderFlightList();
      redraw();
      fitToFlight();
    };
  }
  for (const cb of box.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
    cb.onchange = () => {
      const i = Number(cb.dataset.visIndex);
      if (cb.checked) state.hiddenFlights.delete(i);
      else state.hiddenFlights.add(i);
      redraw();
    };
  }
}

/**
 * PZ（禁止・要注意区域）/ SA（要注意エリア）の出所表示。
 *
 * 「表示・非表示」だけでなく、**どのファイルの・いつの情報か**を常に
 * 見えるようにしておく。安全に関わる情報を、利用者が気付かないまま
 * 古いデータで見てしまう事態を避けるため（本人要望、2026-08-09）。
 */
/** 選んでいる会場の公式ページへのリンク（結果・競技者一覧・WatchMeFly）。無い会場では欄ごと隠す */
function renderVenueLinks(): void {
  const section = el('venue-links-section');
  const links = state.venue ? venueLinksOf(state.venue.id) : [];
  section.hidden = links.length === 0;
  el('venue-links').innerHTML = links
    .map((l) => `<a href="${escapeHtml(l.url)}" target="_blank" rel="noopener"${l.title ? ` title="${escapeHtml(l.title)}"` : ''}>${escapeHtml(l.label)}</a>`)
    .join('・');
}

function renderPzSaInfo(): void {
  const box = el('pzsa-info');
  const section = el('pzsa-section');
  const data = state.venue?.pzsa;
  if (!data) {
    section.hidden = true;
    return;
  }
  section.hidden = false;
  const pzCount = data.zones.filter((z) => z.type === 'PZ').length;
  const saCount = data.zones.filter((z) => z.type === 'SA').length;
  // 出所リンクは、会場設定側の上書き（VenueMapConfig.pzsaSourceUrl、git管理下で
  // 通常デプロイで直せる）を優先し、無ければpzsa.json自体のsource（git管理外、
  // 手動配置）を使う。理由はvenues.tsのコメント参照
  const sourceUrl = mapConfigOf(state.venue?.id ?? '')?.pzsaSourceUrl ?? data.source;
  box.innerHTML =
    `${escapeHtml(data.title)}（PZ ${pzCount}件 ・ SA ${saCount}件）<br>` +
    `元ファイル: ${escapeHtml(state.venue?.pzsaFileName ?? '')} ・ 情報更新日: ${escapeHtml(data.lastUpdate)}<br>` +
    `<a href="${escapeHtml(sourceUrl)}" target="_blank" rel="noopener">${escapeHtml(sourceUrl)}</a>`;
}

const NOTAM_REGIME_SOURCE_LABEL: Record<ActiveNotamRegime['regimeSource'], string> = {
  auto: '自動判定',
  'manual-regime': '手動指定',
  'manual-holiday': '祝日指定による手動',
};

/** renderNotamAreas() が直近で描画した体制の組み合わせ（再描画要否の判定用） */
let lastRenderedNotamRegimeKey = '';

/**
 * NOTAM高度制限区域（BluePZ）の出所・現在の体制・手動上書きの表示。
 * 会場が複数のNOTAMエリアを持つ可能性があるため、常に配列をループして
 * 描画する（「入間基地」のような個別事情はここにハードコードしない。
 * ゾーンのoverridesに何が定義されているかを見て動的にチェックボックスを
 * 生成するだけ）。
 *
 * redraw()から毎秒呼ばれるため、体制（regime）が実際に切り替わった時だけ
 * DOMを再構築する。無条件にinnerHTMLを書き換えると、操作中のセレクト/
 * チェックボックスが毎秒作り直されて操作が壊れてしまうため。
 */
function renderNotamAreas(): void {
  const section = el('notam-section');
  const box = el('notam-areas');
  const areas = state.venue?.notam;
  if (!areas || areas.length === 0) {
    section.hidden = true;
    box.innerHTML = '';
    lastRenderedNotamRegimeKey = '';
    return;
  }
  section.hidden = false;

  const activeByArea = new Map(currentNotamRegimes().map((r) => [r.area, r]));

  const regimeKey = areas
    .map((area) => `${area.title}:${activeByArea.get(area)?.regime.id ?? ''}`)
    .join('|');
  if (regimeKey === lastRenderedNotamRegimeKey) return;
  lastRenderedNotamRegimeKey = regimeKey;

  box.innerHTML = areas
    .map((area, idx) => {
      const active = activeByArea.get(area);
      const override = state.notamOverrides[idx] ?? { regimeId: null, treatAsHoliday: false };
      const regimeOptions = area.regimes
        .map(
          (r) =>
            `<option value="${escapeHtml(r.id)}"${override.regimeId === r.id ? ' selected' : ''}>${escapeHtml(r.label)}</option>`,
        )
        .join('');
      const overrideRows = (active?.regime.zones ?? [])
        .flatMap((z) => z.overrides ?? [])
        .map(
          (ov) =>
            `<label class="check">` +
            `<input type="checkbox" class="notam-override-toggle" data-override-id="${escapeHtml(ov.id)}"` +
            `${state.notamActiveOverrideIds.has(ov.id) ? ' checked' : ''} />` +
            `<span>${escapeHtml(ov.label)}</span></label>`,
        )
        .join('');
      return (
        `<div class="notam-area">` +
        `<strong>${escapeHtml(area.title)}</strong><br>` +
        `元ファイル: ${escapeHtml(state.venue?.notamFileName ?? '')} ・ 情報更新日: ${escapeHtml(area.lastUpdate)}<br>` +
        `<a href="${escapeHtml(area.source)}" target="_blank" rel="noopener">${escapeHtml(area.source)}</a>` +
        `<div class="notam-regime-status">現在の体制: <b>${escapeHtml(active?.regime.label ?? '(該当なし)')}</b>` +
        `（${active ? NOTAM_REGIME_SOURCE_LABEL[active.regimeSource] : '-'}）</div>` +
        `<label class="field-block"><span>体制の手動切り替え</span>` +
        `<select data-notam-area-idx="${idx}" class="notam-regime-select">` +
        `<option value=""${override.regimeId ? '' : ' selected'}>自動判定</option>${regimeOptions}</select></label>` +
        `<label class="check"><input type="checkbox" data-notam-area-idx="${idx}" class="notam-holiday-toggle"` +
        `${override.treatAsHoliday ? ' checked' : ''} /><span>今日は祝日として扱う</span></label>` +
        overrideRows +
        `</div>`
      );
    })
    .join('');
}

/**
 * NOTAM管理欄（体制の手動切替・祝日指定・条件付き高度引き上げ）は
 * renderNotamAreas() でDOMを丸ごと作り直すので、リスナーは委譲で1回だけ
 * 貼っておく（再描画のたびに貼り直さない）。
 */
function setupNotamAreaControls(): void {
  const box = el('notam-areas');
  box.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if (target.matches('.notam-regime-select')) {
      const idx = Number((target as HTMLSelectElement).dataset.notamAreaIdx);
      const value = (target as HTMLSelectElement).value;
      if (state.notamOverrides[idx]) state.notamOverrides[idx].regimeId = value || null;
    } else if (target.matches('.notam-holiday-toggle')) {
      const idx = Number((target as HTMLInputElement).dataset.notamAreaIdx);
      if (state.notamOverrides[idx]) {
        state.notamOverrides[idx].treatAsHoliday = (target as HTMLInputElement).checked;
      }
    } else if (target.matches('.notam-override-toggle')) {
      const id = (target as HTMLInputElement).dataset.overrideId ?? '';
      if ((target as HTMLInputElement).checked) state.notamActiveOverrideIds.add(id);
      else state.notamActiveOverrideIds.delete(id);
    } else {
      return;
    }
    renderNotamAreas();
    redraw();
  });
}

/**
 * アプリ自体の状態。
 *
 * docs/04 §3 の方針「消えないではなく、**消えても即座に検知して直す**」に沿って、
 * 保存領域の状況を隠さず出す。とくに iOS は、ホーム画面に追加していないと
 * 7日間の未使用で保存領域を消すので、未インストールであることは警告に値する。
 */
function renderPwaInfo(storage: StorageState | null): void {
  const box = el('pwa-info');
  const installed = isInstalled();

  const rows: string[] = [];

  // Service Worker も navigator.storage も、HTTPS か localhost（Secure Context）
  // でなければ動かない。LAN の IP を http:// で開いていると対象外になり、
  // 「保存領域」や「使用量」が何も表示されない、という分かりにくい形で症状が出る。
  // 原因を画面から直接気付けるようにしておく。
  if (!window.isSecureContext) {
    rows.push(
      `<div class="pwa-row warn"><span>接続</span><b>保護されていません</b></div>`,
      `<p class="muted pwa-hint">http://（LANのIPなど）で開いているため、オフライン化や` +
        `保存領域の情報が使えません。HTTPS（Tailscale Funnel 等のトンネル）か、` +
        `この端末自身で localhost を開いてください。</p>`,
    );
  }

  if (installed) {
    rows.push(`<div class="pwa-row"><span>起動元</span><b>ホーム画面</b></div>`);
  } else {
    rows.push(
      `<div class="pwa-row warn"><span>起動元</span><b>ブラウザ</b></div>`,
      `<p class="muted pwa-hint">ホーム画面に追加してください。ブラウザのままだと、` +
        `しばらく使わないと地図データが消されることがあります。</p>`,
    );
  }

  if (storage) {
    rows.push(
      `<div class="pwa-row"><span>保存領域</span><b>${
        storage.persisted ? '保護あり' : '保護なし'
      }</b></div>`,
    );
    if (storage.usage !== null) {
      const quota = storage.quota !== null ? ` / ${formatBytes(storage.quota)}` : '';
      rows.push(
        `<div class="pwa-row"><span>使用量</span><b>${formatBytes(storage.usage)}${quota}</b></div>`,
      );
    }
  }

  box.innerHTML = rows.join('');

  /*
   * 「いま動いているのは新しい版か」「更新が待っているか」は、折りたたみの
   * 「アプリ」セクションではなく、常に見える「更新を確認」ボタンの真下に出す
   * （利用者要望: 以前はここが折りたたみの奥に隠れていて気付けなかった）。
   * Service Worker は古い版を配り続けることがあるので、これが無いと
   * 更新したつもりで古いまま、という事故に気付けない
   */
  const versionBox = el('pwa-version-info');
  versionBox.innerHTML = `<div class="pwa-row"><span>版</span><b>${escapeHtml(BUILD_ID)}</b></div>`;

  // 待機中の新しい版があれば、ここに常設で出す（状態表示は上書きされるため）
  if (state.updateApply) {
    const notice = document.createElement('div');
    notice.className = 'pwa-update';
    notice.innerHTML = `<span>新しい版があります</span>`;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '更新する';
    btn.onclick = () => {
      // クリックが効いているかどうか自体が分からないという報告があったため、
      // 押した瞬間に見た目を変えて反応を確認できるようにする
      btn.disabled = true;
      btn.textContent = '更新中…';
      state.updateApply?.();
    };
    notice.appendChild(btn);
    versionBox.appendChild(notice);
  }
}

/** state.groundRefFtをmに変換したもの。未設定（null）なら0（＝海抜と同じ） */
function currentGroundRefM(): number {
  return state.groundRefFt == null ? 0 : feetToMeters(state.groundRefFt);
}

/**
 * 対地(AGL)への切り替え・会場切り替え時、まだ基準標高を一度も設定していなければ、
 * 地形データから候補値を自動で入れる（無ければ何もしない＝手入力待ち）。
 * 以後は利用者の入力を上書きしない。
 *
 * ★ selectVenue()からも呼べるよう、setupInteractions()の中からここへ引き上げた
 *   （コードレビュー指摘、2026-09-13）。要素はその都度引き直すので、
 *   呼び出し側で参照をキャッシュしておく必要はない。
 */
function suggestGroundRefFt(): void {
  if (state.groundRefFt != null) return;
  const center = map.getCenter();
  const elevationM = map.queryTerrainElevation([center.lng, center.lat]);
  if (elevationM == null) return;
  state.groundRefFt = Math.round(metersToFeet(elevationM));
  el<HTMLInputElement>('ground-ref-input').value = String(state.groundRefFt);
}

function renderLegend(): void {
  // タイトルに基準（海抜/対地）を出す。「表示」で切り替えたことに気付けるように
  el('legend-title').textContent = `高度（${state.altitudeMode === 'agl' ? '対地' : '海抜'}）`;
  // 境界値はフィート側で切りの良い数値に決めてあるので、そのまま表示する
  el('legend-scale').innerHTML = state.altitudeStops
    .map((s) => `<span class="legend-item"><i style="background:${s.color}"></i>${s.ft}ft</span>`)
    .join('');
}

// --- 会場の切り替え ----------------------------------------------------------

async function selectVenue(id: string): Promise<void> {
  const manifest = state.venues.find((v) => v.id === id);
  if (!manifest) return;

  // プランナーの点を消すかどうかの判定用（下のresetLrn3dtForNewVenue参照）。
  // undefined＝起動直後の初回呼び出し（前回と同じ会場に戻すだけ）なので、
  // このときは消さない。実際に別会場へ切り替わったときだけ消す
  const previousVenueId = state.venue?.id;

  // 表示は常に「いま選んでいる会場」のものである前提。切り替えずに裏で
  // 別会場ぶんの転送を続けさせない
  cancelOfflineMapDownload();
  cancelTerrainDownload();

  setStatus(`${manifest.name} を読み込み中…`);
  try {
    const venue = await loadVenue(manifest);
    state.venue = venue;
    state.flights = [...venue.flights];
    // 過去ログ由来のマーク・競技エリアは、読み込み時の会場のUTMゾーンで
    // 座標へ復元されている。別会場へ持ち越すと前会場のデータが混在するため、
    // 会場内蔵フライトと同じタイミングで破棄する。
    state.archiveMarks = [];
    state.archiveCircles = [];
    invalidateTrackRidgeLines();
    state.activeFlight = 0;
    state.hiddenFlights = new Set();

    // QNH・対地高度の基準標高は会場（＝タスク）ごとに変わる値。会場を
    // 切り替えると読み込み済みフライトが会場の生データ（未補正）に丸ごと
    // 置き換わるため、古いQNH設定を残したまま次にsetQnh()すると、
    // 一度も補正していない新しい会場のデータから誤って補正量を引いて
    // しまう（コードレビュー指摘、2026-09-13）。setQnh()（差分を今の
    // フライトへ適用する関数）は使わず、素通しの値へ直接戻す
    state.qnhHpa = null;
    el<HTMLInputElement>('qnh-input').value = '';
    state.groundRefFt = null;
    el<HTMLInputElement>('ground-ref-input').value = '';
    if (state.altitudeMode === 'agl') {
      // 地形タイルの読み込み・地図のflyTo完了を待ってから候補値を試す
      // （直後だと旧会場の中心・未読み込みの地形を見てしまい信頼できない）
      map.once('idle', () => {
        suggestGroundRefFt();
        applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
        updateRidgeLines();
        renderLegend();
      });
    }

    void offlineMapRender();
    void terrainRender();

    // ゴールは会場ごとに憶えておく。別会場のゴールが地図の外に残らないよう、
    // 会場を切り替えたらその会場の分だけを読み直す
    const saved = loadGoals(venue.id);
    state.goals = saved.goals;
    state.selectedGoalKey = saved.selectedKey;
    state.goalCardClosed = saved.goals.length === 0;
    goalRowsSignature = '';

    // インポートしたスケッチも会場ごと（ゴールと同じ理由）
    state.sketches = loadSavedSketches(venue.id);
    state.sketchVisible = true;
    el<HTMLInputElement>('toggle-sketch').checked = true;
    renderSketchInfo();
    redrawSketch();

    // 次に開いたときも同じ会場に戻す（＝その会場のゴールがそのまま出る）
    try {
      localStorage.setItem(VENUE_STORAGE_KEY, venue.id);
    } catch {
      // 保存できなくても表示は続けられる
    }

    const mapConfig = mapConfigOf(venue.id);
    // 刻み幅は利用者の好み（state.altitudeStepFt、heightScaleと同じく永続化）
    // で決める。会場ごとの既定値上書きは、利用者が自分で調整できるように
    // なった（2026-09-13）ため廃止した
    state.altitudeStops = buildAltitudeStops(state.altitudeStepFt);
    applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
    applyHeightScale(map, state.heightScale);
    // 競技地図タイルは会場ごとにURLが違うので、basemapがpmtiles以外で
    // スタイル差し替えが起きない場合でもここで必ず差し替える
    refreshCompMapLayer();
    renderLegend();

    // UTM方眼は会場のbboxだけで決まる静的な形なので、切り替え時に1回だけ組む
    state.gridGeoJson = mapConfig ? utmGridGeoJson(mapConfig.bbox) : null;

    renderFlightList();
    renderPzSaInfo();
    renderVenueLinks();
    // 会場ごとにNOTAMエリア数が異なるため、手動上書きの配列を毎回作り直す
    // （切り替え前の値を次の会場へ持ち越さない）
    state.notamOverrides = (venue.notam ?? []).map(() => ({ regimeId: null, treatAsHoliday: false }));
    state.notamActiveOverrideIds.clear();
    renderNotamAreas();
    // プランナーの入力点・計算結果も同様に会場ごと（UTMグリッド表記は暗黙に
    // ゾーンへ依存するため、前の会場の点を残すと無関係な場所に出続ける）。
    // 起動直後（前回と同じ会場に戻る1回目の呼び出し）では消さない
    // ——フライト中のリロードで消えると入れ直しになるため（§99と同じ理由）
    if (previousVenueId && previousVenueId !== venue.id) resetLrn3dtForNewVenue?.();
    redraw();

    if (venue.flights.length > 0) {
      fitToFlight();
    } else if (mapConfig) {
      map.flyTo({ center: mapConfig.center, zoom: mapConfig.initialZoom, duration: 600 });
    } else if (venue.calibration?.points.length) {
      const p = venue.calibration.points[0];
      map.flyTo({ center: [p.lon, p.lat], zoom: 11, duration: 600 });
    }

    if (state.basemap === 'pmtiles') {
      map.setStyle(buildStyle('pmtiles', { pmtilesUrl: pmtilesUrlOf(), mapTheme: state.mapTheme }));
      map.once('styledata', () => {
        addOverlayLayers(map);
        applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
        applyHeightScale(map, state.heightScale);
        refreshCompMapLayer();
        void applyTerrainState();
        redraw();
      });
    } else {
      // 上のsetStyleが走らなかった場合（オフライン地図以外の地図選択）は
      // スタイル差し替えによる地形ソースの消失が起きないので、ここで直接
      // 会場ぶんの地形設定を反映する
      void applyTerrainState();
    }

    const parts = [
      `ゴール ${venue.goals.length} 点`,
      venue.calibration ? `競技地図 ${venue.calibration.scale} dot/km` : null,
      venue.flights.length ? `フライト ${venue.flights.length} 件` : 'フライトなし',
    ].filter(Boolean);
    if (venue.warnings.length > 0) {
      // オフラインで一部ファイルが未キャッシュだった場合など。地図やオフライン地図の
      // 既定選択は止めず、欠けているものだけを伝える
      setStatus(`${venue.name}：${parts.join(' · ')}（一部読み込めず: ${venue.warnings.join(' / ')}）`, 'warn');
    } else {
      setStatus(`${venue.name}：${parts.join(' · ')}`);
    }
  } catch (e) {
    setStatus(`読み込みに失敗しました: ${(e as Error).message}`, 'warn');
  }
}

// --- IGC の読み込み ---------------------------------------------------------

function setupFileInput(): void {
  const input = el<HTMLInputElement>('igc-input');
  input.onchange = async () => {
    const files = [...(input.files ?? [])];
    if (files.length === 0) return;
    for (const file of files) {
      try {
        const flight = await loadFlightFromFile(file);
        // IGCのaltPressureは標準気圧基準の生値。既に他のフライトへ
        // 適用済みのQNH補正ぶんをこの新しいフライトにも合わせておく
        // （setQnh参照）。まだ誰もQNHを設定していなければ、IGC自身が
        // 記録していた値（対応する記録計のみ）を候補として自動で使う
        const delta = qnhCorrectionM(state.qnhHpa);
        if (delta !== 0) {
          for (const fix of flight.fixes) {
            if (fix.altPressure != null) fix.altPressure += delta;
          }
        }
        state.flights.push(flight);
        if (state.qnhHpa == null && flight.qnh != null) setQnh(flight.qnh);
      } catch (e) {
        setStatus(`${file.name}: ${(e as Error).message}`, 'warn');
      }
    }
    state.activeFlight = state.flights.length - 1;
    renderFlightList();
    redraw();
    fitToFlight();
    input.value = '';
  };
}

// --- インポートしたスケッチ（TVのXDD等） -------------------------------------

/** #sketch-info・#sketch-delete-all の表示を state.sketches に合わせて更新する */
function renderSketchInfo(): void {
  const info = el('sketch-info');
  const deleteBtn = el<HTMLButtonElement>('sketch-delete-all');
  if (state.sketches.length === 0) {
    info.textContent = '未読み込み';
    deleteBtn.hidden = true;
    return;
  }
  info.innerHTML = state.sketches
    .map((s) => `<div>${escapeHtml(s.name || 'スケッチ')}（図形 ${s.polygons.length} 個）</div>`)
    .join('');
  deleteBtn.hidden = false;
}

function setupSketchInput(): void {
  const input = el<HTMLInputElement>('sketch-input');
  input.onchange = async () => {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    try {
      const sketch = await loadSketchFromFile(file);
      const entry: SketchEntry = { ...sketch, id: `sk${Date.now().toString(36)}-${state.sketches.length}` };
      state.sketches = [...state.sketches, entry];
      state.sketchVisible = true;
      el<HTMLInputElement>('toggle-sketch').checked = true;
      if (state.venue) saveSketches(state.venue.id, state.sketches);
      renderSketchInfo();
      redrawSketch();
      setStatus(`スケッチを読み込みました（図形 ${sketch.polygons.length} 個）`);
    } catch (e) {
      setStatus(`${file.name}: ${(e as Error).message}`, 'warn');
    }
  };
  // 大会中は日・タスクごとに何度も読み込むため、1件ずつではなく
  // まとめて消す（利用者からの指摘、2026-09-13）
  el<HTMLButtonElement>('sketch-delete-all').onclick = () => {
    if (state.sketches.length > 1 && !confirm(`読み込んだスケッチ${state.sketches.length}件をすべて消します。よろしいですか？`)) {
      return;
    }
    state.sketches = [];
    if (state.venue) clearSavedSketches(state.venue.id);
    renderSketchInfo();
    redrawSketch();
    setStatus('スケッチをすべて削除しました');
  };
  renderSketchInfo();
}

/**
 * TaskBoard（タスクシート翻訳共有アプリ、姉妹プロジェクト）のタスクシート
 * から、大会側設定のゴールを取り込む。
 *
 * ライブ接続で設定済みのブリッジURL（#relay-url）をそのまま使い、
 * ブリッジの`/api/taskboard`経由で取得する（archive読込と同じ流儀で、
 * 別途URL入力欄は設けない。上流のSupabaseへの接続はブリッジが受け持つ:
 * apps/bridge/src/taskboard.ts）。
 *
 * 同じタスクシートを選び直したときは、前回そのシートから取り込んだ
 * ゴール（鍵が`taskboard:<flightKey>:`で始まるもの）だけを先に取り除いて
 * から積み直す。これにより、TaskBoard側で座標が修正された後の再取込みで
 * 古いゴールが残らない（利用者確認済みの挙動）。
 */
function setupTaskboardImport(): void {
  const sheet = el('taskboard-import');
  const listBox = el('taskboard-flight-list');
  const hintBox = el('taskboard-import-hint');

  const close = (): void => {
    sheet.hidden = true;
  };

  const bridgeUrl = (): string => el<HTMLInputElement>('relay-url').value.trim();

  el('taskboard-import-open').onclick = async () => {
    const base = bridgeUrl();
    if (!base) {
      setStatus('TaskBoard取込: ライブ接続のURL（GV BridgeのURL）を先に入力してください', 'warn');
      return;
    }
    // ★ 合言葉のCookieが無い端末（PCでブラウザのデータを消した・別のブラウザ等）では、ここで401になり、
    //   以前は「HTTP 401」と出るだけで入力画面が出なかった（2026-10-30、佐賀の朝に PC で発覚。スマホは
    //   Cookieが残っていて取り込めた）。ライブ接続と同じ確認を先に通し、必要なら合言葉の入力画面を出す
    if (!(await ensureBridgeAuth(base))) {
      setStatus('TaskBoard取込: 合言葉が確認できなかったため、取り込めません', 'warn');
      return;
    }
    listBox.innerHTML = '<p class="muted">タスクシート一覧を取得中…</p>';
    hintBox.textContent = '';
    sheet.hidden = false;
    try {
      const res = await fetch(`${base}/api/taskboard?action=flights`);
      if (res.status === 401) throw new Error('合言葉の確認が必要です（開き直して、合言葉を入れてください）');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as TaskBoardFlightsResponse;
      // アーカイブ済み（大会が終わって保管された）シートは選ぶ意味が無いので出さない
      const flights = (json.flights ?? [])
        .filter((f) => !f.archived)
        .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
      if (flights.length === 0) {
        listBox.innerHTML = '<p class="muted">タスクシートがまだありません</p>';
        return;
      }
      listBox.innerHTML = flights
        .map(
          (f) =>
            `<button type="button" class="wide subtle" data-flight-key="${escapeHtml(f.key)}">` +
            `${escapeHtml(f.label)}<br>` +
            `<span class="muted">${escapeHtml(f.competitionName)} ・ ${escapeHtml(f.date)} ・ タスク${f.taskCount}件</span>` +
            `</button>`,
        )
        .join('');
    } catch (e) {
      listBox.innerHTML = '';
      hintBox.textContent = `タスクシート一覧を取得できませんでした: ${(e as Error).message}`;
    }
  };

  listBox.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-flight-key]');
    const flightKey = btn?.dataset.flightKey;
    if (!flightKey) return;
    const base = bridgeUrl();
    btn.disabled = true;
    hintBox.textContent = '取り込み中…';
    try {
      // 一覧を開いてからCookieが切れた場合も、ここで合言葉を確かめ直す
      if (!(await ensureBridgeAuth(base))) throw new Error('合言葉が確認できませんでした');
      const res = await fetch(`${base}/api/taskboard?action=flight&key=${encodeURIComponent(flightKey)}`);
      if (res.status === 401) throw new Error('合言葉の確認が必要です（開き直して、合言葉を入れてください）');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const outer = (await res.json()) as TaskBoardFlightResponse;
      const flightData = JSON.parse(outer.data) as TaskBoardFlightData;
      const { goals, skipped } = parseTaskBoardFlight(flightData, flightKey, gridReferencePoint(), state.venue?.goals ?? []);
      // 同じシートから前回取り込んだぶんだけを取り除いてから積み直す
      const prefix = taskBoardGoalKeyPrefix(flightKey);
      state.goals = state.goals.filter((g) => !g.key.startsWith(prefix));
      addGoals(goals);
      close();
      // ゴール番号のものは、選択中の会場のゴール一覧で位置を決めている（会場を選び違えていると
      // 別の場所に出るので、どの会場で決めたかを伝える）
      const numbered = goals.some((g) => /:n\d+$/.test(g.key));
      const summary =
        `TaskBoardから${goals.length}件のゴールを取り込みました` +
        (numbered && state.venue ? `（ゴール番号は「${state.venue.name}」のゴール一覧で位置を決めました）` : '') +
        (skipped.length ? `（${skipped.length}件読み取れず: ${skipped.map((s) => s.name).join('・')}）` : '');
      setStatus(summary, skipped.length ? 'warn' : undefined);
      if (goals.length > 0) {
        map.easeTo({ center: [goals[0].lon, goals[0].lat], zoom: Math.max(map.getZoom(), 11), duration: 600 });
      }
    } catch (e) {
      hintBox.textContent = `取り込みに失敗しました: ${(e as Error).message}`;
    } finally {
      btn.disabled = false;
    }
  });

  el('taskboard-import-cancel').onclick = close;
  sheet.addEventListener('click', (e) => {
    if (e.target === sheet) close();
  });
}

/**
 * 本家サーバーの過去ログ（アーカイブ）を読み込む。
 *
 * ライブ接続で設定済みのブリッジURL（#relay-url）をそのまま使う
 * （アーカイブもブリッジの`/api/archive`経由でしか取れないため、
 * 別途URL入力欄は設けない）。IGC読み込みと同じ「一括で航跡一覧に
 * 追加する」流儀に合わせる。
 *
 * ★ 航跡（フライト）だけでなく、そのログに含まれる他機が宣言した
 *   ゴール（マーク点）・競技エリア（円）も一緒に取り込んで地図に出す
 *   （利用者からの指摘、2026-09-15：「過去ログ機能でも無視しないように
 *   して」。以前は`loadArchiveFlights`が航跡以外を全部捨てていた）。
 *   ライブ受信と同じ`LIVE_MARK_SOURCE`/`AREA_SOURCE`に相乗りさせる
 *   （`state.archiveMarks`/`state.archiveCircles`、`redraw()`側で
 *   ライブぶんと合成する）。
 */
function setupArchiveLoad(): void {
  const dateInput = el<HTMLInputElement>('archive-date');
  const sessionSelect = el<HTMLSelectElement>('archive-session');
  const button = el<HTMLButtonElement>('archive-load');

  button.onclick = async () => {
    const dateYmd = dateInput.value.replace(/-/g, '');
    if (!/^\d{8}$/.test(dateYmd)) {
      setStatus('過去ログ: 日付を選んでください', 'warn');
      return;
    }
    const bridgeUrl = el<HTMLInputElement>('relay-url').value.trim();
    if (!bridgeUrl) {
      setStatus('過去ログ: ライブ接続のURL（GV BridgeのURL）を先に入力してください', 'warn');
      return;
    }
    const fileName = archiveFileName(dateYmd, sessionSelect.value);
    // 合言葉のCookieが無い端末では401になるので、読む前に確かめる（TaskBoard取込と同じ理由）
    if (!(await ensureBridgeAuth(bridgeUrl))) {
      setStatus('過去ログ: 合言葉が確認できなかったため、読み込めません', 'warn');
      return;
    }
    button.disabled = true;
    setStatus(`過去ログ（${fileName}）を取得中…`);
    try {
      const venueMap = state.venue ? mapConfigOf(state.venue.id) : null;
      const { flights, marks, circles } = await loadArchiveFlights(bridgeUrl, fileName, venueMap?.utmZone);
      state.flights.push(...flights);
      state.activeFlight = state.flights.length - 1;
      state.archiveMarks.push(...marks);
      state.archiveCircles.push(...circles);
      renderFlightList();
      redraw();
      fitToFlight();
      const extra = [
        marks.length ? `ゴール${marks.length}件` : null,
        circles.length ? `競技エリア${circles.length}件` : null,
      ].filter((s): s is string => s !== null);
      setStatus(
        `過去ログ（${fileName}）から${flights.length}機ぶんの航跡を読み込みました` +
          (extra.length ? `（${extra.join('・')}も表示）` : ''),
      );
    } catch (e) {
      setStatus(`過去ログ（${fileName}）: ${(e as Error).message}`, 'warn');
    } finally {
      button.disabled = false;
    }
  };
}

// --- ゴールのクリック --------------------------------------------------------

/**
 * タップ位置に最も近い点を選ぶ。
 *
 * 当たり判定を指に合わせて広げた結果、1回のタップに複数の点が入るように
 * なった。MapLibre が返す順は描画順で、狙った点とは限らない。
 */
function nearestFeature(
  features: maplibregl.MapGeoJSONFeature[] | undefined,
  lngLat: maplibregl.LngLat,
): maplibregl.MapGeoJSONFeature | null {
  let best: maplibregl.MapGeoJSONFeature | null = null;
  let bestDist = Infinity;
  for (const f of features ?? []) {
    if (f.geometry.type !== 'Point') continue;
    const [lon, lat] = f.geometry.coordinates;
    const d = (lon - lngLat.lng) ** 2 + (lat - lngLat.lat) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = f;
    }
  }
  return best;
}

/** 航跡上で、その位置に一番近い実測点 */
function nearestFix(lngLat: maplibregl.LngLat): FixSample | null {
  let best: FixSample | null = null;
  let bestDist = Infinity;
  for (const flight of state.flights.filter((_, i) => !state.hiddenFlights.has(i))) {
    for (const f of flight.fixes) {
      const d = (f.lon - lngLat.lng) ** 2 + (f.lat - lngLat.lat) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = f;
      }
    }
  }
  return best;
}

function fixReadoutHtml(f: FixSample): string {
  const z = toZonedTime(f.t, state.timeZone);
  return (
    `<span>高度 <b>${Math.round(metersToFeet(f.altPressure ?? f.altMsl ?? 0))}</b> ft</span>` +
    `<span>対地速度 <b>${((f.speed ?? 0) * 3.6).toFixed(1)}</b> km/h</span>` +
    `<span>方位 <b>${Math.round(f.heading ?? 0)}</b>°</span>` +
    `<span>昇降 <b>${(f.verticalSpeed ?? 0).toFixed(1)}</b> m/s</span>` +
    `<span>${z.time} ${z.zone}</span>`
  );
}

/** ライブ航跡（履歴線）上で、その位置に一番近い実測点。非表示にした端末は除く */
function nearestLiveFix(lngLat: maplibregl.LngLat): { fix: FixSample; terminalId: string } | null {
  const terminals = liveTerminals().filter((t) => terminalShown(t.terminalId));
  let best: { fix: FixSample; terminalId: string } | null = null;
  let bestDist = Infinity;
  for (const t of terminals) {
    for (const f of t.fixes) {
      const d = (f.lon - lngLat.lng) ** 2 + (f.lat - lngLat.lat) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = { fix: f, terminalId: t.terminalId };
      }
    }
  }
  return best;
}

function liveFixReadoutHtml(terminalId: string, f: FixSample): string {
  return `<span><b>${escapeHtml(terminalId)}</b></span>` + fixReadoutHtml(f);
}

/**
 * 長押しの直後に来る click を1回だけ捨てるための印。
 * 長押しの指を離すと click も発火するので、これが無いと登録の窓を出した
 * 直後にゴールのポップアップが重なって開く。
 */
let suppressNextClick = false;

function setupInteractions(): void {
  const popup = new maplibregl.Popup({ closeButton: false, offset: 10 });

  /*
   * ★ mouseleave で popup.remove() しないこと。
   *   ポップアップは点の少し上（offset）にボタンごと出るので、指を点から
   *   ボタンへ動かす途中で当たり判定の外に一瞬出る。その瞬間に
   *   mouseleave が飛んで popup ごと消え、ボタンの click が届かなくなる
   *   （「ゴールに設定」が反応しない不具合の原因だった）。閉じるのは
   *   Popup 既定の closeOnClick（地図の他の場所を押したら閉じる）に任せる。
   */
  map.on('mouseenter', 'gv-goal-hit', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'gv-goal-hit', () => (map.getCanvas().style.cursor = ''));
  // タスクシートのカーソルはタップで消す
  map.on('click', 'gv-tbc-halo', () => {
    state.taskboardCursor = null;
    redraw();
  });
  map.on('mouseenter', 'gv-tbc-halo', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'gv-tbc-halo', () => (map.getCanvas().style.cursor = ''));

  map.on('click', 'gv-goal-hit', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const f = nearestFeature(e.features, e.lngLat);
    if (!f) return;
    const [lon, lat] = (f.geometry as GeoJSON.Point).coordinates;
    const name = String(f.properties?.name ?? '');
    // 会場の公式ゴールは番号で一意（同じ番号を押し直しても増やさず選択だけ移す）
    const goal = { ...makeGoal(name, lat, lon), key: officialGoalKey(name) };
    const listed = state.goals.some((g) => g.key === goal.key);
    const selected = state.selectedGoalKey === goal.key;

    popup
      .setLngLat([lon, lat])
      .setHTML(
        `<strong>${escapeHtml(name)}</strong>` +
          formatUtm(lat, lon) +
          `<button type="button" class="popup-goal-set"${selected ? ' disabled' : ''}>` +
          (selected ? '選択中' : listed ? 'このゴールを選択' : 'ゴールに設定') +
          `</button>`,
      )
      .addTo(map);

    // setHTML で中身ごと入れ替わるので、開くたびに繋ぎ直す
    popup.getElement()?.querySelector<HTMLButtonElement>('.popup-goal-set')?.addEventListener(
      'click',
      () => {
        addGoal(goal);
        popup.remove();
      },
      { once: true },
    );
  });

  // 航跡は高度帯ごとの線にまとめてあるので、読み値はカーソル位置に最も近い
  // 実測点から引く。そのほうが正確で、表示も1点単位になる。
  map.on('mousemove', 'gv-track-hit', (e) => {
    const best = nearestFix(e.lngLat);
    if (best) el('readout').innerHTML = fixReadoutHtml(best);
  });
  map.on('mouseleave', 'gv-track-hit', () => {
    el('readout').innerHTML = '';
  });

  /*
   * 指にはホバーが無いので、航跡はタップでも読めるようにする。
   * ゴールや他機と重なったときはそちらを優先する（点のほうが狙って
   * 押しているはずで、線は広い当たり判定に入りやすいだけ）。
   */
  map.on('click', 'gv-track-hit', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const covered = map.queryRenderedFeatures(e.point, {
      layers: ['gv-goal-hit', 'gv-live-hit'].filter((l) => map.getLayer(l)),
    });
    if (covered.length > 0) return;

    const best = nearestFix(e.lngLat);
    if (!best) return;
    el('readout').innerHTML = fixReadoutHtml(best);
    popup
      .setLngLat([best.lon, best.lat])
      .setHTML(`<div class="popup-fix">${fixReadoutHtml(best)}</div>` + formatUtm(best.lat, best.lon))
      .addTo(map);
  });

  /*
   * ライブ航跡（履歴線）も同様にタップで読める。現在地点は gv-live-hit が
   * すでに拾うので、そちらと自分の保存済みフライトの線（gv-track-hit）が
   * 重なった場合はそちらを優先する（IGCの詳細な実測点のほうが精度が高い）。
   */
  map.on('mousemove', 'gv-live-track-hit', (e) => {
    const best = nearestLiveFix(e.lngLat);
    if (best) el('readout').innerHTML = liveFixReadoutHtml(best.terminalId, best.fix);
  });
  map.on('mouseleave', 'gv-live-track-hit', () => {
    el('readout').innerHTML = '';
  });
  map.on('click', 'gv-live-track-hit', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const covered = map.queryRenderedFeatures(e.point, {
      layers: ['gv-goal-hit', 'gv-live-hit', 'gv-track-hit'].filter((l) => map.getLayer(l)),
    });
    if (covered.length > 0) return;

    const best = nearestLiveFix(e.lngLat);
    if (!best) return;
    el('readout').innerHTML = liveFixReadoutHtml(best.terminalId, best.fix);
    popup
      .setLngLat([best.fix.lon, best.fix.lat])
      .setHTML(
        `<div class="popup-fix"><strong>${escapeHtml(best.terminalId)}</strong>` +
          fixReadoutHtml(best.fix) +
          `</div>` +
          formatUtm(best.fix.lat, best.fix.lon),
      )
      .addTo(map);
  });

  // 同じ理由（上の gv-goal-hit のコメント参照）で mouseleave では消さない
  map.on('mouseenter', 'gv-live-hit', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'gv-live-hit', () => (map.getCanvas().style.cursor = ''));
  map.on('click', 'gv-live-hit', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const f = nearestFeature(e.features, e.lngLat);
    if (!f) return;
    const [lon, lat] = (f.geometry as GeoJSON.Point).coordinates;
    const speedKmh = f.properties?.speedKmh;
    const heading = f.properties?.heading;
    const z = f.properties?.t != null ? toZonedTime(f.properties.t, state.timeZone) : null;
    popup
      .setLngLat([lon, lat])
      .setHTML(
        `<strong>${escapeHtml(String(f.properties?.terminalId ?? ''))}</strong><br>` +
          `高度 ${f.properties?.altFt ?? '?'} ft<br>` +
          `対地速度 ${speedKmh == null ? '—' : speedKmh} km/h ・ 方位 ${heading == null ? '—' : heading}°<br>` +
          (z ? `${z.time} ${z.zone}` : '') +
          formatUtm(lat, lon),
      )
      .addTo(map);
  });

  el<HTMLInputElement>('toggle-goals').onchange = (e) => {
    state.showGoals = (e.target as HTMLInputElement).checked;
    redraw();
  };
  el<HTMLInputElement>('toggle-grid').onchange = (e) => {
    state.showGrid = (e.target as HTMLInputElement).checked;
    redraw();
  };
  el<HTMLInputElement>('toggle-pzsa').onchange = (e) => {
    state.showPzSa = (e.target as HTMLInputElement).checked;
    redraw();
  };
  el<HTMLInputElement>('toggle-sketch').onchange = (e) => {
    state.sketchVisible = (e.target as HTMLInputElement).checked;
    redrawSketch();
  };
  // 競技地図オーバーレイ。会場にタイルが無ければ何も描かれない
  // （applyCompMapVisibility/applyCompMapOpacityはレイヤーが無ければ何もしない）
  const compMapOpacitySlider = el<HTMLInputElement>('compmap-opacity-slider');
  const compMapOpacityInput = el<HTMLInputElement>('compmap-opacity-input');
  const onCompMapOpacityInput = (raw: number): void => {
    const pct = Math.min(100, Math.max(0, Math.round(raw)));
    const v = pct / 100;
    state.compMapOpacity = v;
    compMapOpacitySlider.value = String(pct);
    compMapOpacityInput.value = String(pct);
    applyCompMapOpacity(map, v);
    try {
      localStorage.setItem(COMPMAP_OPACITY_KEY, String(v));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  compMapOpacitySlider.oninput = (e) => onCompMapOpacityInput(Number((e.target as HTMLInputElement).value));
  compMapOpacityInput.oninput = (e) => onCompMapOpacityInput(Number((e.target as HTMLInputElement).value));
  el<HTMLInputElement>('toggle-compmap').onchange = (e) => {
    state.showCompMap = (e.target as HTMLInputElement).checked;
    compMapOpacitySlider.disabled = !state.showCompMap;
    compMapOpacityInput.disabled = !state.showCompMap;
    applyCompMapVisibility(map, state.showCompMap);
  };
  el<HTMLInputElement>('toggle-notam').onchange = (e) => {
    state.showNotam = (e.target as HTMLInputElement).checked;
    redraw();
  };
  el<HTMLInputElement>('toggle-hazards').onchange = (e) => {
    state.showHazards = (e.target as HTMLInputElement).checked;
    redraw();
  };
  // 幹線道路の詳細度。データは全レベル取得済みなので、レイヤーのフィルターを
  // 切り替えるだけで済む（Overpassへの再問い合わせは不要）
  el<HTMLSelectElement>('hazard-road-level').onchange = (e) => {
    const level = Number((e.target as HTMLSelectElement).value);
    state.hazardRoadLevel = level;
    map.setFilter('gv-hazard-line', hazardRoadLevelFilter(level));
    try {
      localStorage.setItem(HAZARD_ROAD_LEVEL_KEY, String(level));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  // ○（gv-live-mark）自体は残し、名前ラベルだけを消せるようにする。
  // データには手を入れず、レイヤーのvisibilityだけ切り替えれば十分
  el<HTMLInputElement>('toggle-mark-labels').onchange = (e) => {
    const visible = (e.target as HTMLInputElement).checked;
    map.setLayoutProperty('gv-live-mark-label', 'visibility', visible ? 'visible' : 'none');
  };
  el<HTMLInputElement>('toggle-magnifier').onchange = (e) => {
    state.showMagnifier = (e.target as HTMLInputElement).checked;
    if (!state.showMagnifier) hideMagnifier();
  };

  // 高度の色分け・刻み幅と基準（海抜/対地）。3D表示のON/OFFとは独立
  // （2D表示の航跡の色にも効く）
  const altitudeStepSlider = el<HTMLInputElement>('altitude-step-slider');
  const altitudeStepInput = el<HTMLInputElement>('altitude-step-input');
  const onAltitudeStepInput = (raw: number): void => {
    const v = Math.min(ALTITUDE_STEP_MAX, Math.max(ALTITUDE_STEP_MIN, Math.round(raw / 100) * 100));
    state.altitudeStepFt = v;
    altitudeStepSlider.value = String(v);
    altitudeStepInput.value = String(v);
    state.altitudeStops = buildAltitudeStops(v);
    applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
    updateRidgeLines();
    renderLegend();
    try {
      localStorage.setItem(ALTITUDE_STEP_KEY, String(v));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  altitudeStepSlider.oninput = (e) => onAltitudeStepInput(Number((e.target as HTMLInputElement).value));
  altitudeStepInput.oninput = (e) => onAltitudeStepInput(Number((e.target as HTMLInputElement).value));

  const groundRefRow = el('ground-ref-row');
  const groundRefInput = el<HTMLInputElement>('ground-ref-input');
  /** 表示ダイアログの基準標高欄は、対地(AGL)を選んでいるときだけ意味がある */
  const updateGroundRefVisibility = (): void => {
    groundRefRow.hidden = state.altitudeMode !== 'agl';
  };
  // suggestGroundRefFt()はselectVenue()からも呼べるようモジュール直下にある
  groundRefInput.onchange = () => {
    const raw = groundRefInput.value.trim();
    state.groundRefFt = raw === '' ? null : Number(raw);
    applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
    updateRidgeLines();
    renderLegend();
  };

  el<HTMLSelectElement>('altitude-mode').onchange = (e) => {
    state.altitudeMode = (e.target as HTMLSelectElement).value === 'agl' ? 'agl' : 'msl';
    updateGroundRefVisibility();
    if (state.altitudeMode === 'agl') suggestGroundRefFt();
    applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
    updateRidgeLines();
    renderLegend();
    try {
      localStorage.setItem(ALTITUDE_MODE_KEY, state.altitudeMode);
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  updateGroundRefVisibility();

  // QNH補正。標準気圧(1013.25hPa)からのズレぶんを、読み込み済み全フライトの
  // altPressureへ反映し直す（setQnh参照）。ライブ受信にはaltPressure自体が
  // 無いので関係無い
  const qnhInput = el<HTMLInputElement>('qnh-input');
  qnhInput.onchange = () => {
    const raw = qnhInput.value.trim();
    setQnh(raw === '' ? null : Number(raw));
  };
  el<HTMLButtonElement>('qnh-clear').onclick = () => setQnh(null);

  // 3D表示。pitch=0（真上から見た状態）だとfill-extrusionの側面
  // （＝高さの表現そのもの）が見えないため、ONにする瞬間に地図を傾ける。
  // 既に自分で傾けていた場合は上書きしない。OFFに戻すときは、自動で
  // 傾けた分だけ戻す（自分で傾けていた分はそのまま）
  const heightScaleSlider = el<HTMLInputElement>('height-scale-slider');
  const heightScaleInput = el<HTMLInputElement>('height-scale-input');
  const onHeightScaleInput = (raw: number) => {
    const v = Math.min(HEIGHT_SCALE_MAX, Math.max(HEIGHT_SCALE_MIN, Math.round(raw)));
    state.heightScale = v;
    heightScaleSlider.value = String(v);
    heightScaleInput.value = String(v);
    applyHeightScale(map, v);
    updateRidgeLines();
    updatePzSaTubes();
    try {
      localStorage.setItem(HEIGHT_SCALE_KEY, String(v));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  heightScaleSlider.oninput = (e) => onHeightScaleInput(Number((e.target as HTMLInputElement).value));
  heightScaleInput.oninput = (e) => onHeightScaleInput(Number((e.target as HTMLInputElement).value));

  // カメラの傾き。寝かせるほど高さが直接読めるようになる
  const viewPitchSlider = el<HTMLInputElement>('view-pitch-slider');
  const viewPitchInput = el<HTMLInputElement>('view-pitch-input');
  /** スライダーの表示だけを合わせる（地図側から来た変化を映すときに使う） */
  const showViewPitch = (v: number): void => {
    const shown = String(Math.round(v));
    viewPitchSlider.value = shown;
    // 入力中の欄を書き換えると打てなくなるので、フォーカスされていなければ触らない
    if (document.activeElement !== viewPitchInput) viewPitchInput.value = shown;
  };
  const onViewPitchInput = (raw: number): void => {
    const v = Math.min(MAX_PITCH, Math.max(0, Math.round(raw)));
    state.viewPitch = v;
    showViewPitch(v);
    // duration:0 で即座に反映する（スライダーを動かしている最中はアニメが邪魔になる）
    map.easeTo({ pitch: v, duration: 0 });
    try {
      localStorage.setItem(VIEW_PITCH_KEY, String(v));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  };
  viewPitchSlider.oninput = (e) => onViewPitchInput(Number((e.target as HTMLInputElement).value));
  viewPitchInput.oninput = (e) => onViewPitchInput(Number((e.target as HTMLInputElement).value));

  // pitch はドラッグ（右ドラッグ・2本指）やコンパスでも変わるので、
  // スライダーの表示を地図に追従させる。★ 保存は pitchend のときだけ。
  // 毎フレーム localStorage に書くと、傾けている間ずっと書き込みが走る
  map.on('pitch', () => showViewPitch(map.getPitch()));
  map.on('pitchend', () => {
    // 3D表示OFFのときの傾きは「好み」ではないので覚えない
    // （2Dで少し傾けただけの状態が、次に3DをONにしたときの既定になってしまう）
    if (!state.show3d) return;
    state.viewPitch = Math.round(map.getPitch());
    try {
      localStorage.setItem(VIEW_PITCH_KEY, String(state.viewPitch));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
  });

  const show3dSmoothCheckbox = el<HTMLInputElement>('toggle-3d-smooth');
  show3dSmoothCheckbox.onchange = (e) => {
    state.show3dSmooth = (e.target as HTMLInputElement).checked;
    try {
      localStorage.setItem(SHOW_3D_SMOOTH_KEY, state.show3dSmooth ? '1' : '0');
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    redraw();
  };

  // 3Dの壁・稜線の細かさ（docs/08 §197）。選択肢を選ぶか、数値を直接入れる
  const threeDDetailSelect = el<HTMLSelectElement>('three-d-detail-select');
  const threeDStepInput = el<HTMLInputElement>('three-d-step-input');
  const threeDAltStepInput = el<HTMLInputElement>('three-d-alt-step-input');
  const showThreeDDetail = (): void => {
    threeDDetailSelect.value = threeDDetailPresetId(state.threeDDetail);
    threeDStepInput.value = String(state.threeDDetail.stepM);
    threeDAltStepInput.value = String(state.threeDDetail.altStepM);
  };
  const setThreeDDetail = (detail: ThreeDDetail): void => {
    state.threeDDetail = normalizeThreeDDetail(detail);
    try {
      localStorage.setItem(THREE_D_DETAIL_KEY, JSON.stringify(state.threeDDetail));
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    showThreeDDetail();
    redraw();
  };
  threeDDetailSelect.replaceChildren(
    ...THREE_D_DETAIL_PRESETS.map((p) => new Option(`${p.label}（${p.detail.stepM}m / ${p.detail.altStepM}m）`, p.id)),
    new Option('カスタム（下の数値）', 'custom'),
  );
  showThreeDDetail();
  threeDDetailSelect.onchange = () => {
    const preset = THREE_D_DETAIL_PRESETS.find((p) => p.id === threeDDetailSelect.value);
    if (preset) setThreeDDetail(preset.detail);
  };
  // 入力途中（空欄など）で作り直さないよう、確定（change）したときだけ反映する
  threeDStepInput.onchange = threeDAltStepInput.onchange = () =>
    setThreeDDetail({ stepM: Number(threeDStepInput.value), altStepM: Number(threeDAltStepInput.value) });

  const toggleNotam3d = el<HTMLInputElement>('toggle-notam-3d');
  toggleNotam3d.onchange = (e) => {
    state.showNotam3d = (e.target as HTMLInputElement).checked;
    redraw();
  };

  /*
   * 3D表示の入り切り。「表示」の中のチェックボックスと、地図右上の
   * 「3D」ボタンの2か所から同じ経路を通す（利用者から「3D表示が『表示』の
   * 中にあるのが分かりにくい」との指摘でボタンを新設した。どちらから
   * 切り替えても、もう一方の見た目が必ず追随する）。
   */
  const toggle3dCheckbox = el<HTMLInputElement>('toggle-3d');
  const setShow3d = (on: boolean): void => {
    state.show3d = on;
    toggle3dCheckbox.checked = on;
    // ボタンは「押すとどうなるか」を出す（3D中は次に押すと2Dへ戻るので「2D」）
    const button = document.getElementById('view-3d-toggle');
    if (button) {
      button.textContent = on ? '2D' : '3D';
      button.setAttribute('aria-pressed', String(on));
      button.setAttribute('aria-label', on ? '2D表示に戻す' : '3D表示にする');
    }
    heightScaleSlider.disabled = !state.show3d;
    heightScaleInput.disabled = !state.show3d;
    viewPitchSlider.disabled = !state.show3d;
    viewPitchInput.disabled = !state.show3d;
    show3dSmoothCheckbox.disabled = !state.show3d;
    toggleNotam3d.disabled = !state.show3d;
    el<HTMLButtonElement>('pitch-up-btn').disabled = !state.show3d;
    el<HTMLButtonElement>('pitch-down-btn').disabled = !state.show3d;
    if (state.show3d) {
      if (map.getPitch() < 5) {
        state.pitchBeforeShow3d = map.getPitch();
        // 覚えておいた好みの角度で開く（既定60なので、触っていなければ従来通り）
        map.easeTo({ pitch: state.viewPitch, duration: 600 });
      } else {
        state.pitchBeforeShow3d = null;
      }
    } else if (state.pitchBeforeShow3d != null) {
      map.easeTo({ pitch: state.pitchBeforeShow3d, duration: 600 });
      state.pitchBeforeShow3d = null;
    }
    redraw();
  };
  toggle3dCheckbox.onchange = (e) => setShow3d((e.target as HTMLInputElement).checked);
  // 地図右上のボタンからも同じ経路を通す（ボタンは boot() より前に作られるので
  // ここで実体を渡す。closePanel と同じ受け渡し方）
  applyShow3d = setShow3d;
  setShow3d(state.show3d); // ボタンの文字を今の状態に合わせる

  // ライブ航跡の色（高度の色分け／機体ごとの単色）。色の式だけを差し替える軽い操作で、形は作り直さない
  applyLiveTrackColor = (mode) => {
    state.liveTrackColorMode = mode;
    setLiveTrackColorMode(mode);
    try {
      localStorage.setItem(LIVE_TRACK_COLOR_KEY, mode);
    } catch {
      // 保存できなくても今回のセッション中は効く
    }
    applyAltitudeColors(map, state.altitudeStops, state.altitudeMode, currentGroundRefM());
    renderTrackColorButton();
    renderLive(); // 一覧の「航跡の色」ボタンの文字を合わせる
    setStatus(mode === 'terminal' ? 'ライブ航跡: 機体ごとの単色（アイコンと同じ色）' : 'ライブ航跡: 高度の色分け');
  };

  // PZ/SA。円（Point）と多角形（Polygon）の2レイヤーで同じ内容の詳細を出す
  const showPzSaPopup = (lngLat: maplibregl.LngLat, p: GeoJSON.GeoJsonProperties): void => {
    if (!p) return;
    const typeLabel = p.pzsaType === 'PZ' ? 'PZ（禁止・要注意区域）' : 'SA（要注意エリア）';
    const sizeInfo = p.radius != null ? ` ・ 半径 ${p.radius} m` : '';
    popup
      .setLngLat(lngLat)
      .setHTML(
        `<strong>${escapeHtml(String(p.label ?? ''))}</strong>（${typeLabel}）<br>` +
          `${escapeHtml(String(p.kind ?? ''))}${p.altFt != null ? ` ・ 高度制限 ${p.altFt} ft` : ''}${sizeInfo}<br>` +
          (p.note ? `${escapeHtml(String(p.note))}<br>` : '') +
          formatUtm(lngLat.lat, lngLat.lng),
      )
      .addTo(map);
  };

  // 同じ理由（gv-goal-hit のコメント参照）で mouseleave では消さない
  map.on('mouseenter', 'gv-pzsa-fill', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'gv-pzsa-fill', () => (map.getCanvas().style.cursor = ''));
  map.on('click', 'gv-pzsa-fill', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const f = nearestFeature(e.features, e.lngLat);
    if (!f) return;
    const [lon, lat] = (f.geometry as GeoJSON.Point).coordinates;
    showPzSaPopup(new maplibregl.LngLat(lon, lat), f.properties);
  });
  map.on('mouseenter', 'gv-pzsa-poly-fill', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'gv-pzsa-poly-fill', () => (map.getCanvas().style.cursor = ''));
  map.on('click', 'gv-pzsa-poly-fill', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const f = e.features?.[0];
    if (!f) return;
    showPzSaPopup(e.lngLat, f.properties);
  });

  // NOTAM高度制限区域（BluePZ）
  const showNotamPopup = (lngLat: maplibregl.LngLat, p: GeoJSON.GeoJsonProperties): void => {
    if (!p) return;
    const altLine =
      p.effectiveAltFt !== p.altFt
        ? `高度制限 ${p.effectiveAltFt} ft（通常 ${p.altFt} ft）`
        : `高度制限 ${p.altFt} ft`;
    popup
      .setLngLat(lngLat)
      .setHTML(
        `<strong>${escapeHtml(String(p.label ?? ''))}</strong>（${escapeHtml(String(p.regimeLabel ?? ''))}）<br>` +
          `${altLine}<br>` +
          (p.note ? `${escapeHtml(String(p.note))}<br>` : '') +
          formatUtm(lngLat.lat, lngLat.lng),
      )
      .addTo(map);
  };
  map.on('mouseenter', 'gv-notam-fill', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'gv-notam-fill', () => (map.getCanvas().style.cursor = ''));
  map.on('click', 'gv-notam-fill', (e) => {
    if (suppressNextClick || state.mapTapMode !== 'normal') return;
    const f = e.features?.[0];
    if (!f) return;
    showNotamPopup(e.lngLat, f.properties);
  });

  el<HTMLButtonElement>('fit-button').onclick = fitToFlight;

  const tzSelect = el<HTMLSelectElement>('timezone-select');
  tzSelect.innerHTML = TIME_ZONES.map(
    (z) => `<option value="${z.id}">${escapeHtml(z.label)}</option>`,
  ).join('');
  tzSelect.value = state.timeZone;
  tzSelect.onchange = () => {
    state.timeZone = tzSelect.value as TimeZoneId;
    try {
      localStorage.setItem(TZ_STORAGE_KEY, state.timeZone);
    } catch {
      // 保存できなくても選択自体は今回のセッション中は効く
    }
    // 現在表示中の時刻表記を即座に描き直す（次のデータ更新を待たない）
    renderLive();
    renderSummary();
  };

  const themeSelect = el<HTMLSelectElement>('map-theme-select');
  themeSelect.value = state.mapTheme;
  themeSelect.onchange = () => {
    state.mapTheme = themeSelect.value as MapTheme;
    try {
      localStorage.setItem(MAP_THEME_KEY, state.mapTheme);
    } catch {
      // 保存できなくても選択自体は今回のセッション中は効く
    }
    // PMTiles を選んでいないときは見た目に影響しないが、次に選んだときのために
    // 常にスタイルを更新しておく（現在の選択と食い違わせない）
    applyBasemapStyle();
  };

  const utmWideEastingCheckbox = el<HTMLInputElement>('toggle-utm-wide-easting');
  utmWideEastingCheckbox.checked = state.utmWideEasting;
  utmWideEastingCheckbox.onchange = () => {
    state.utmWideEasting = utmWideEastingCheckbox.checked;
    renderGoalCard();
    try {
      localStorage.setItem(UTM_WIDE_EASTING_KEY, state.utmWideEasting ? '1' : '0');
    } catch {
      // 保存できなくても選択自体は今回のセッション中は効く
    }
  };

  const utmWideNorthingCheckbox = el<HTMLInputElement>('toggle-utm-wide-northing');
  utmWideNorthingCheckbox.checked = state.utmWideNorthing;
  utmWideNorthingCheckbox.onchange = () => {
    state.utmWideNorthing = utmWideNorthingCheckbox.checked;
    renderGoalCard();
    try {
      localStorage.setItem(UTM_WIDE_NORTHING_KEY, state.utmWideNorthing ? '1' : '0');
    } catch {
      // 保存できなくても選択自体は今回のセッション中は効く
    }
  };
}

// --- 起動 -------------------------------------------------------------------

async function boot(): Promise<void> {
  // 合言葉ゲートが要るならここで待つ（要らない・既に通った端末なら素通り）。
  // 会場データの読み込みより前に置いて、通っていない人には何も配らない
  await ensureAppUnlocked();

  document.body.dataset.mode = state.mode;
  renderModeSelect();
  renderBasemapSelect();
  renderLegend();
  setupFileInput();
  setupSketchInput();
  setupArchiveLoad();
  setupInteractions();
  setupPanelToggle();
  // 地図右上のアイコン（表示・接続と送信）は setupPanelToggle() の後に配線する
  // （閉じる先の closePanel がそこで入るため）
  setupMapSheets();
  toggleTaskboard = setupTaskboardPanel({ onShowTarget: showTaskboardTarget }).toggle;
  setupOfficialPibal();
  setupArrowKeyZoom();
  setupBridgeAuthGate();
  setupLive();
  setupTerminalColors();
  setupWindProfile();
  setupCrossSection();
  setupApt();
  setupAirspace();
  setupTaskCircles();
  setupLrn3dt();
  setupMeasure();
  setupCompass();
  setupPibal();
  setupNotamAreaControls();
  setupTransmit();
  setupNotamBanner();
  setupFinder();
  setupGoalCard();
  setupGoalEntry();
  setupGoalNew();
  setupTaskboardImport();
  setupPilotGate();
  // 上部バーのワンアクション操作。setupLive()・setupTransmit()・
  // setupPanelToggle() が入れる橋渡しを使うので、それらより後に呼ぶ
  setupQuickBar();
  // selectVenue() が後段で offlineMapRender()・terrainRender() を呼ぶため、
  // それより先に配線を終えておく（await しないと、まれに間に合わない可能性がある）
  await setupOfflineMap();
  await setupOfflineTerrain();
  setupTerrainToggle();
  setupTerrainRidgeLineRefresh();
  renderPwaInfo(null);
  setupSettingsBackup();

  // 保存領域の保護要求と、その結果の表示（docs/04 §3）。
  // プリキャッシュの完了前に estimate() を呼ぶと使用量が 0 のままになるので、
  // 取り込みが終わった時点でも取り直す。
  const refreshPwaInfo = () => void requestPersistentStorage().then(renderPwaInfo);

  // Service Worker の登録。更新は自動適用せず、押されたときだけ適用する
  // （飛行中に勝手にリロードされるのを避けるため）
  setupPwa({
    onUpdateReady: (apply) => {
      // 状態表示は後続のメッセージで消えるので、パネル側にも保持する
      const guardedApply = guardedUpdateApply(apply);
      state.updateApply = guardedApply;
      refreshPwaInfo();
      setStatus('新しい版があります', 'info', { label: '更新', onClick: guardedApply });
    },
    onOfflineReady: () => {
      setStatus('オフラインでも起動できるようになりました');
      refreshPwaInfo();
    },
  });

  refreshPwaInfo();
  // 画面に戻ってきたときにも取り直す。保存領域が消されていれば気付けるように
  // （docs/04 §3「消えても即座に検知して直す」）
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshPwaInfo();
  });

  // 古い版に閉じ込められたときの脱出口。Service Worker が更新を配らない
  // 状態になると、画面からはどうにもできなくなるため必ず用意しておく
  el<HTMLButtonElement>('pwa-check-update').onclick = async () => {
    setStatus('更新を確認しています…');
    const result = await checkForUpdate();
    if (result.status === 'updated') {
      // 適用の導線はパネル側に常設されるものへ寄せる（状態表示は消えるため）。
      // ★ setupPwa() 側の onUpdateReady が既にセットしていれば、そちらが
      //   registerSW() 由来の正しい適用関数なのでそれを優先する。
      //   まだ未設定なら、ここで直接 SKIP_WAITING を送る正しい手順を行う
      //   （単なる location.reload() だと、まだ古い Service Worker が
      //   制御権を持ったままなので中身が変わらない事故になっていた）
      state.updateApply ??= guardedUpdateApply(() => {
        void (async () => {
          const reg = await navigator.serviceWorker.getRegistration();
          if (!reg?.waiting) {
            location.reload();
            return;
          }
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        })();
      });
      refreshPwaInfo();
      setStatus('新しい版が見つかりました。「更新する」を押してください');
    } else if (result.status === 'server-differs') {
      // ★ サーバーは別の版を配っているのに、Service Worker が新版を用意できない。
      //   ほぼサーバー側のデプロイが止まっている（2026-08-19〜21 に実際に発生。
      //   deploy/update.sh が古いブランチを見ていた）。ここを「最新の版です」と
      //   出していたせいで、2日間気付けなかった
      const sv = result.server;
      setStatus(
        `サーバーの版と一致しません（この端末 ${BUILD_ID} ／ サーバー ${sv.buildId}）。` +
          'サーバー側の反映を確認してください',
        'warn',
      );
    } else if (result.status === 'current') {
      setStatus(`最新の版です（${BUILD_ID}）`);
    } else {
      setStatus('更新を確認できません（オフライン、またはHTTPSでない接続）', 'warn');
    }
  };

  el<HTMLButtonElement>('pwa-reset').onclick = () => {
    // オフライン用のデータも消えるので、意図しない実行を防ぐ
    if (!confirm('保存されているデータを全部消して読み込み直します。よろしいですか？')) return;
    setStatus('消去して読み込み直しています…');
    void resetAndReload();
  };

  // スタイルの読み込みを待つ。`load` は稀に取りこぼすので `isStyleLoaded` も見る
  if (!map.isStyleLoaded()) {
    await new Promise<void>((resolve) => {
      const done = () => resolve();
      map.once('load', done);
      map.once('idle', done);
    });
  }
  addOverlayLayers(map);
  void applyTerrainState();
  // 保存済みの計測の図形・プランナーの入力点は、ここで初めて描ける
  // （ソースが作られた直後）。
  // ★ setupMeasure()/setupLrn3dt() は addOverlayLayers() より前に走るので
  //   そちらでは描けず、redraw() は会場の読み込みに失敗すると一度も走らない
  //   ことがある（会場が無い状態でリロードすると図形が出ない不具合を踏んだ）
  redrawMeasure();
  redrawSketch();
  redrawLrn3dtPoints();
  // レイヤーはDEFAULT_HAZARD_ROAD_LEVELで作られるので、保存済みの設定と
  // 違っていればここで実際のフィルターに反映する
  if (state.hazardRoadLevel !== DEFAULT_HAZARD_ROAD_LEVEL) {
    map.setFilter('gv-hazard-line', hazardRoadLevelFilter(state.hazardRoadLevel));
  }
  el<HTMLSelectElement>('hazard-road-level').value = String(state.hazardRoadLevel);
  // 高度の色分け・刻み幅と基準も、保存済みの設定を表示だけ合わせる
  // （実際の反映はaddOverlayLayers直後・会場読み込み時に既に済んでいる）
  el<HTMLInputElement>('altitude-step-slider').value = String(state.altitudeStepFt);
  el<HTMLInputElement>('altitude-step-input').value = String(state.altitudeStepFt);
  el<HTMLSelectElement>('altitude-mode').value = state.altitudeMode;
  // 高さの誇張倍率も、保存済みの設定がDEFAULT_HEIGHT_SCALEと違えば反映する
  // （3D表示自体はshow3d:falseで始まるので、レイヤーのfeatureはまだ空）
  if (state.heightScale !== DEFAULT_HEIGHT_SCALE) {
    applyHeightScale(map, state.heightScale);
  }
  el<HTMLInputElement>('height-scale-slider').value = String(state.heightScale);
  el<HTMLInputElement>('height-scale-input').value = String(state.heightScale);
  // 競技地図の不透明度も、保存済みの設定を表示だけ合わせる（表示ON/OFF自体はセッション限りでfalse始まり）
  {
    const pct = String(Math.round(state.compMapOpacity * 100));
    el<HTMLInputElement>('compmap-opacity-slider').value = pct;
    el<HTMLInputElement>('compmap-opacity-input').value = pct;
  }
  // 傾きは保存値を表示だけ合わせる。地図はまだ傾けない（3DがONになった時点で傾く）
  el<HTMLInputElement>('view-pitch-slider').value = String(state.viewPitch);
  el<HTMLInputElement>('view-pitch-input').value = String(state.viewPitch);
  el<HTMLInputElement>('toggle-3d-smooth').checked = state.show3dSmooth;

  try {
    state.venues = await loadVenueList();
    renderVenueSelect();
    if (state.venues.length > 0) {
      const last = loadLastVenue();
      const start = state.venues.some((v) => v.id === last) ? last! : state.venues[0].id;
      el<HTMLSelectElement>('venue-select').value = start;
      await selectVenue(start);

      // オフライン地図を既に取得済みなら、「地図なし」より優先してそちらを既定にする
      if (state.basemap === 'none') {
        const url = pmtilesUrlOf();
        const status = url ? await offlineMapStatus(url) : null;
        if (status?.downloaded) {
          state.basemap = 'pmtiles';
          el<HTMLSelectElement>('basemap-select').value = 'pmtiles';
          applyBasemapStyle();
        }
      }
    }
  } catch (e) {
    setStatus(`会場一覧を読み込めません: ${(e as Error).message}`, 'warn');
  }

  document.body.classList.add('ready');
}

// 起動に失敗したまま黙って固まらないよう、必ず画面に出す
void boot().catch((e: unknown) => {
  setStatus(`起動に失敗しました: ${(e as Error).message}`, 'warn');
  document.body.classList.add('ready');
});

// 開発時の調査用
Object.assign(window as unknown as Record<string, unknown>, { gv: { map, state } });
